<template>
  <div class="support-calc">

  <!-- Feature header — names the tool inside the Divii panel. Shown to every
       role (view-only and party mode included). -->
  <header class="calc-title">
    <h1>Child Support &amp; Spousal Support Calculator</h1>
  </header>

  <!-- ═════════════════════════════════════════════════════════════════════
       ROLE GATE — Divii "Role Level Per Agreement" (matterContext.roleLevel).
       Level 1 (professional / full access): the complete tabbed workspace.
       Level 2 (a party with limited access): PARTY MODE only — enter their
       own income and their own budget, nothing else. What each level sees is
       decided centrally in the `roleCaps` computed.
       NOTE: this is a UI gate. LegalApp's server must ALSO scope writes by
       role when persisting update-state (see INTEGRATION.md) — browser-side
       gating alone is not security.
  ═════════════════════════════════════════════════════════════════════ -->
  <template v-if="canViewCase">

  <!-- Read-only banner (view / contribute-view profiles) -->
  <p v-if="readOnlyCase" class="ro-banner">
    <template v-if="roleUserName">Signed in as <strong>{{ roleUserName }}</strong>. </template>
    <template v-if="accessProfile === 'contribute-view' && partyLinkMissing">
      Viewing only. To enter your own information, open the
      <strong>Your info</strong> tab and pick your name first.
    </template>
    <template v-else-if="accessProfile === 'contribute-view'">
      You can edit your own information on the <strong>Your info</strong> tab.
      The rest of the calculation is shown for viewing only.
    </template>
    <template v-else>
      View only. You can browse every part of this calculation, but nothing can
      be changed from this account.
    </template>
  </p>

  <!-- ═════════════════════════════════════════════════════════════════════
       GLOBAL CASE-LEVEL CONTROLS — tax year, etc.
       Tax year is global to the case (not per-step) because it affects every
       tax, credit, and benefit calculation. Defaults to current year for new
       cases; switch to a prior year for arrears.
  ═════════════════════════════════════════════════════════════════════ -->
  <div class="case-controls">
    <label class="case-controls__field">
      <span class="case-controls__label">Tax year</span>
      <select v-model.number="taxYear" class="case-controls__select" :disabled="readOnlyCase">
        <option v-for="y in TAX_YEAR_OPTIONS" :key="y" :value="y">
          {{ y }}{{ TAX_YEARS_PLACEHOLDER_FLAG[y] ? ' (data pending)' : '' }}
        </option>
      </select>
    </label>
    <p v-if="isPlaceholderTaxYear" class="case-controls__warning">
      ⚠️ {{ taxYear }} reference data not yet populated — calculations currently use 2026 tax brackets, credits, and benefits. Numbers shown are illustrative only for {{ taxYear }} arrears calculations until proper data is added.
    </p>
    <p v-else-if="isPartialTaxYearForProvince" class="case-controls__warning">
      ⚠️ {{ taxYear }} data is populated for federal + BC only. Other provinces still use 2026 brackets — expect drift on {{ effectiveProvince }} arrears calculations until the provincial 2025 schedule is added.
    </p>
  </div>

  <!-- ─── Support tabs ────────────────────────────────────────────────────
       Calculator (Means) leads. The spousal-support tabs — Budget (Needs),
       SSAG Questions (Circumstances), Recommendation — appear only once
       "Is there an entitlement to spousal support?" is answered Yes
       (ssagApplicable), so a child-support-only file stays a simple
       calculator. The whole bar hides when only Calculator would show. -->
  <nav v-if="ssagApplicable || accessProfile === 'contribute-view'" class="ws-tabs" role="tablist">
    <button v-if="accessProfile === 'contribute-view'" type="button" role="tab"
            :aria-selected="workspaceTab === 'myinfo'"
            :class="['ws-tab', 'ws-tab-mine', { on: workspaceTab === 'myinfo' }]" @click="workspaceTab = 'myinfo'">
      <span class="ws-tab-name">✎ Your info</span>
      <span class="ws-tab-sub">income and expenses</span>
    </button>
    <button type="button" role="tab" :aria-selected="workspaceTab === 'means'"
            :class="['ws-tab', { on: workspaceTab === 'means' }]" @click="workspaceTab = 'means'">
      <span class="ws-tab-name"><span class="ws-tab-num">1</span> Calculator</span>
      <span class="ws-tab-sub">means</span>
    </button>
    <template v-if="ssagApplicable">
      <button type="button" role="tab" :aria-selected="workspaceTab === 'needs'"
              :class="['ws-tab', { on: workspaceTab === 'needs' }]" @click="workspaceTab = 'needs'">
        <span class="ws-tab-name"><span class="ws-tab-num">2</span> Budget</span>
        <span class="ws-tab-sub">needs</span>
      </button>
      <button type="button" role="tab" :aria-selected="workspaceTab === 'circumstances'"
              :class="['ws-tab', { on: workspaceTab === 'circumstances' }]" @click="workspaceTab = 'circumstances'">
        <span class="ws-tab-name"><span class="ws-tab-num">3</span> SSAG Questions</span>
        <span class="ws-tab-sub">circumstances</span>
      </button>
      <button v-if="canViewRecommendation" type="button" role="tab" :aria-selected="workspaceTab === 'recommendation'"
              :class="['ws-tab', { on: workspaceTab === 'recommendation' }]" @click="workspaceTab = 'recommendation'">
        <span class="ws-tab-name"><span class="ws-tab-num">4</span> Recommendation</span>
        <span class="ws-tab-sub">where in the range</span>
      </button>
      <button type="button" role="tab" :aria-selected="workspaceTab === 'worksheet'"
              :class="['ws-tab', { on: workspaceTab === 'worksheet' }]" @click="workspaceTab = 'worksheet'">
        <span class="ws-tab-name"><span class="ws-tab-num">5</span> Worksheet</span>
        <span class="ws-tab-sub">scenarios and analysis</span>
      </button>
    </template>
  </nav>

  <!-- Read-only wrapper: a disabled fieldset natively disables every nested
       input, select, and button (view / contribute-view profiles) while the
       content stays fully visible. The tab bar above stays interactive. -->
  <fieldset :disabled="readOnlyCase" class="ro-fieldset">

  <!-- ─── MEANS TAB (wizard) + WORKSHEET TAB (the breakdown workspace).
       With spousal entitlement, Calculator = the wizard and Worksheet = the
       breakdown as tab 5. Without entitlement there are no tabs, and the
       wizard/workspace toggle behaves as it always did (viewMode). -->
  <div v-show="workspaceTab === 'means' || workspaceTab === 'worksheet'">

  <!-- ═════════════════════════════════════════════════════════════════════
       BUILDER→CALCULATOR SYNC BANNER — shown when party/children details in
       the Matter differ from what's saved in the calculator (see runPropSync).
       Sits above both wizard and workspace so it's visible in either mode.
  ═════════════════════════════════════════════════════════════════════ -->
  <div v-if="syncConflicts" class="sync-banner" role="alert">
    <div class="sync-banner-text">
      <strong>Party/children details changed in the Matter</strong> — update the calculator?
      <ul class="sync-banner-list">
        <li v-for="c in syncConflicts.changes" :key="c.key">{{ c.label }}</li>
      </ul>
    </div>
    <span class="sync-banner-actions">
      <button type="button" class="btn-primary sync-apply-btn" @click="applySyncChanges">Update</button>
      <button type="button" class="sync-dismiss-link" @click="dismissSyncBanner">Dismiss</button>
    </span>
  </div>

  <!-- ═════════════════════════════════════════════════════════════════════
       WIZARD MODE — step-by-step input flow (default for first-time users)
  ═════════════════════════════════════════════════════════════════════ -->
  <div v-if="showWizardView" class="wizard-wrap">

    <!-- ═══════════════════════════════════════════════════════════
         STEP 1: CASE BASICS
    ═══════════════════════════════════════════════════════════ -->
    <section class="step" v-if="step === 1">
      <h2>Case Basics</h2>

      <div class="row-2">
        <div class="field">
          <label>Cohabitation / Marriage Date</label>
          <input type="date" v-model="cohabitationDate" />
        </div>
        <div class="field">
          <label>Separation Date</label>
          <input type="date" v-model="separationDate" @change="onSepDateChange" />
        </div>
      </div>
      <div class="hint" v-if="yearsMarried">
        Length of relationship: <strong>{{ relationshipLengthLabel }}</strong>
        <span v-if="tableYear"> · Using {{ tableYear }} Federal Child Support Tables</span>
      </div>
      <div class="hint" v-else-if="tableYear">Using {{ tableYear }} Federal Child Support Tables</div>

      <div class="field">
        <label>Province / Territory</label>
        <!-- Province comes in as a prop from the parent app.
             If not provided, show a selector as fallback. -->
        <span v-if="province">{{ provinceName }}</span>
        <select v-else v-model="provinceLocal">
          <option v-for="(name, code) in PROVINCES" :key="code" :value="code">{{ name }}</option>
        </select>
      </div>

      <div class="row-2">
        <div class="field">
          <label>Payor Name</label>
          <input type="text" v-model="payorName" placeholder="Party A" />
        </div>
        <div class="field">
          <label>Recipient Name</label>
          <input type="text" v-model="recipientName" placeholder="Party B" />
        </div>
      </div>
      <div class="row-2">
        <div class="field">
          <label>{{ payorName }} — Date of Birth</label>
          <input type="date" v-model="payorDOB" />
          <span class="hint" v-if="payorAgeAtSeparation != null">
            Age at separation: {{ payorAgeAtSeparation }}
          </span>
          <label class="disability-check">
            <input type="checkbox" v-model="payorDisabled" />
            Has a disability (claims Disability Tax Credit)
          </label>
        </div>
        <div class="field">
          <label>{{ recipientName }} — Date of Birth</label>
          <input type="date" v-model="recipientDOB" />
          <span class="hint" v-if="recipientAgeAtSeparation != null">
            Age at separation: {{ recipientAgeAtSeparation }}
          </span>
          <label class="disability-check">
            <input type="checkbox" v-model="recipientDisabled" />
            Has a disability (claims Disability Tax Credit)
          </label>
        </div>
      </div>
      <div class="swap-row">
        <button class="btn-ghost-sm" type="button" @click="swapParties" title="Reverse who is payor and who is recipient">
          ⇅ Swap payor &amp; recipient
        </button>
      </div>

      <button class="btn-primary" @click="step = 2" :disabled="!canProceed1">
        Next: Income →
      </button>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         STEP 2: INCOME — searchable line items per party
    ═══════════════════════════════════════════════════════════ -->
    <section class="step" v-if="step === 2">
      <h2>Income</h2>
      <p class="sub">
        Add income, deductions, and adjustments for each party. Search by label or T1 line number.
        <span class="sub-hint">Tip: append <code>m</code>, <code>w</code>, <code>b</code>, or <code>t</code> to any dollar value to convert from monthly, weekly, biweekly, or twice-monthly into an annual figure (e.g. <code>4000m</code> → $48,000/yr).</span>
      </p>

      <!-- ─── Auto-payor indicator + Switch link ─────────────────────────
           Shows the current direction ("Daniel pays Jane") with a one-click
           swap. When the auto-detector sees a clearer payor based on income,
           it highlights with a suggestion badge until the lawyer chooses. -->
      <div class="payor-indicator" :class="{ 'payor-indicator--suggested': suggestedSwap }">
        <span class="payor-indicator-text">
          <strong>{{ payorName }}</strong> pays <strong>{{ recipientName }}</strong>
        </span>
        <span v-if="suggestedSwap" class="payor-indicator-suggestion">
          ⓘ Based on income, <strong>{{ recipientName }}</strong> is likely the payor
        </span>
        <button
          type="button"
          class="payor-switch-link"
          @click="switchPayor"
          title="Swap payor and recipient — flips names, incomes, parenting, and SSAG direction"
        >↔ Switch payor</button>
        <button
          v-if="suggestedSwap"
          type="button"
          class="payor-dismiss-link"
          @click="dismissPayorSuggestion"
          title="Keep the current direction — the suggestion will stop appearing"
        >Keep as-is</button>
      </div>

      <!-- Payor income picker -->
      <div class="income-party">
        <div class="party-header">
          <h3>{{ payorName }}</h3>
          <span class="party-total">Guidelines Income: <strong>${{ fmt(payorIncome) }}</strong></span>
        </div>
        <div class="item-list">
          <div v-for="item in payorItemsRaw" :key="item.id" class="income-row">
            <div class="row-label">
              <span class="row-title">{{ incomeTypeMeta(item.typeId)?.label || item.typeId }}</span>
              <span v-if="item.selfReported" class="self-reported-chip" :title="'Entered by ' + (item.enteredBy || 'the party themselves') + '. Review before relying on it.'">self-reported<template v-if="item.enteredBy"> · {{ item.enteredBy }}</template></span>
              <span class="row-line" v-if="incomeTypeMeta(item.typeId)?.t1Line">Line {{ incomeTypeMeta(item.typeId).t1Line }}</span>
            </div>
            <div class="input-prefix row-amount">
              <span>$</span>
              <input type="text" inputmode="numeric"
                     :value="displayAmount(item)"
                     @focus="onAmountFocus(item)"
                     @blur="onAmountBlur()"
                     @input="updateIncomeAmount(item.id, parseDollarInput($event.target.value))"
                     placeholder="0" />
            </div>
            <button class="btn-remove" @click="removeIncomeItem(item.id)" title="Remove">×</button>
          </div>
          <div v-if="payorItemsRaw.length === 0" class="empty-row">No income items yet.</div>
        </div>
        <div class="picker-wrap">
          <button class="btn-add-income" @click="openPickerFor === 'A' ? openPickerFor = null : (openPickerFor = 'A', pickerSearch = '', $nextTick(() => $refs.pickerInputA?.focus()))">
            + Add income
          </button>
          <div v-if="openPickerFor === 'A'" class="picker-popover">
            <input ref="pickerInputA" type="text" v-model="pickerSearch" placeholder="Search income, deduction, T1 line…" class="picker-search" @keydown.esc="openPickerFor = null" />
            <div class="picker-results">
              <template v-for="cat in incomeCategories">
                <div v-if="filteredTypesByCategory(cat).length" :key="cat" class="picker-group">
                  <div class="picker-group-header">{{ cat }}</div>
                  <button v-for="type in filteredTypesByCategory(cat)" :key="type.id"
                          class="picker-item"
                          @click="addIncomeItem('A', type.id); openPickerFor = null; pickerSearch = ''">
                    <span class="picker-item-label">{{ type.label }}</span>
                    <span class="picker-item-line" v-if="type.t1Line && type.t1Line !== '—'">Line {{ type.t1Line }}</span>
                  </button>
                </div>
              </template>
              <div v-if="totalFilteredCount === 0" class="picker-empty">No matches. Try another keyword or T1 line number.</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Recipient income picker (mirror of above) -->
      <div class="income-party">
        <div class="party-header">
          <h3>{{ recipientName }}</h3>
          <span class="party-total">Guidelines Income: <strong>${{ fmt(recipientIncome) }}</strong></span>
        </div>
        <div class="item-list">
          <div v-for="item in recipientItemsRaw" :key="item.id" class="income-row">
            <div class="row-label">
              <span class="row-title">{{ incomeTypeMeta(item.typeId)?.label || item.typeId }}</span>
              <span v-if="item.selfReported" class="self-reported-chip" :title="'Entered by ' + (item.enteredBy || 'the party themselves') + '. Review before relying on it.'">self-reported<template v-if="item.enteredBy"> · {{ item.enteredBy }}</template></span>
              <span class="row-line" v-if="incomeTypeMeta(item.typeId)?.t1Line">Line {{ incomeTypeMeta(item.typeId).t1Line }}</span>
            </div>
            <div class="input-prefix row-amount">
              <span>$</span>
              <input type="text" inputmode="numeric"
                     :value="displayAmount(item)"
                     @focus="onAmountFocus(item)"
                     @blur="onAmountBlur()"
                     @input="updateIncomeAmount(item.id, parseDollarInput($event.target.value))"
                     placeholder="0" />
            </div>
            <button class="btn-remove" @click="removeIncomeItem(item.id)" title="Remove">×</button>
          </div>
          <div v-if="recipientItemsRaw.length === 0" class="empty-row">No income items yet.</div>
        </div>
        <div class="picker-wrap">
          <button class="btn-add-income" @click="openPickerFor === 'B' ? openPickerFor = null : (openPickerFor = 'B', pickerSearch = '', $nextTick(() => $refs.pickerInputB?.focus()))">
            + Add income
          </button>
          <div v-if="openPickerFor === 'B'" class="picker-popover">
            <input ref="pickerInputB" type="text" v-model="pickerSearch" placeholder="Search income, deduction, T1 line…" class="picker-search" @keydown.esc="openPickerFor = null" />
            <div class="picker-results">
              <template v-for="cat in incomeCategories">
                <div v-if="filteredTypesByCategory(cat).length" :key="cat" class="picker-group">
                  <div class="picker-group-header">{{ cat }}</div>
                  <button v-for="type in filteredTypesByCategory(cat)" :key="type.id"
                          class="picker-item"
                          @click="addIncomeItem('B', type.id); openPickerFor = null; pickerSearch = ''">
                    <span class="picker-item-label">{{ type.label }}</span>
                    <span class="picker-item-line" v-if="type.t1Line && type.t1Line !== '—'">Line {{ type.t1Line }}</span>
                  </button>
                </div>
              </template>
              <div v-if="totalFilteredCount === 0" class="picker-empty">No matches. Try another keyword or T1 line number.</div>
            </div>
          </div>
        </div>
      </div>

      <div class="step-nav">
        <button class="btn-ghost" @click="step = 1">← Back</button>
        <button class="btn-primary" @click="step = 3">Next: Children →</button>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         STEP 3: CHILDREN
    ═══════════════════════════════════════════════════════════ -->
    <section class="step" v-if="step === 3">
      <h2>Children</h2>
      <p class="sub">
        If there are children of the relationship, add each one below. If there are no
        children, leave this section empty — SSAG will use the
        <strong>without-child</strong> formula instead of the with-child formula.
      </p>

      <div v-if="children.length === 0" class="empty-children-card">
        <p>No children added.</p>
        <p class="hint">Skip this step if there are no children of the relationship.</p>
      </div>

      <div v-for="(child, i) in children" :key="i" class="child-card">
        <div class="child-header">
          <span>Child {{ i + 1 }}</span>
          <button class="btn-remove" @click="removeChild(i)">×</button>
        </div>

        <div class="row-2">
          <div class="field">
            <label>Name (optional)</label>
            <input type="text" v-model="child.name" :placeholder="`Child ${i + 1}`" />
          </div>
          <div class="field">
            <label>Date of Birth</label>
            <input type="date" v-model="child.dob" />
          </div>
        </div>

        <!-- Age display (uses today, not separation date) -->
        <div class="child-age" v-if="child.dob">
          Age: {{ childAge(child.dob) }}
          <span class="tag-aom" v-if="isAtAOM(child.dob)">At/over age of majority</span>
        </div>

        <!-- Over AOM: support-still-payable question -->
        <div class="field" v-if="isAtAOM(child.dob)">
          <label>Is monthly support still payable?</label>
          <div class="radio-row">
            <label><input type="radio" :value="true" v-model="child.isDependent" /> Yes (s.3(2) applies)</label>
            <label><input type="radio" :value="false" v-model="child.isDependent" /> No — exclude from calculation</label>
          </div>
          <p class="hint warn" v-if="child.isDependent">
            ⚠ Child is at or over the age of majority. Support is determined under s.3(2) — the court may depart from the table amount.
          </p>
        </div>

        <!-- Parenting arrangement (only if child is included) -->
        <div class="field" v-if="childIsIncluded(child)">
          <label>Parenting Arrangement</label>
          <div class="radio-row">
            <label>
              <input type="radio" value="primaryA" v-model="child.parenting" />
              Primary with {{ payorName }}
            </label>
            <label>
              <input type="radio" value="primaryB" v-model="child.parenting" />
              Primary with {{ recipientName }}
            </label>
            <label>
              <input type="radio" value="shared" v-model="child.parenting" />
              Shared (≥40% each)
            </label>
          </div>
        </div>

        <!-- Child disability (DTC). Drives the Child Disability Benefit (added
             to the recipient's benefits) + the disability amount transferred to
             the claiming parent. -->
        <div class="field" v-if="childIsIncluded(child)">
          <label class="disability-check">
            <input type="checkbox" v-model="child.disabled" />
            {{ child.name || 'This child' }} has a disability (claims Disability Tax Credit)
          </label>
        </div>

        <!-- Eligible Dependant Credit. Three cases:
             • Primary custody → only the custodial parent can claim, so we
               auto-assign and show a read-only note (no question asked).
             • Shared (eligible) → per-child selector, pre-selected by Divii
               (DIVII PATCH, no Auto option). Auto-balances: assigning one
               parent auto-assigns the other to a different shared child.
             • Oldest of 3+ shared kids → not eligible (they age out first). -->
        <div class="field hint" v-if="childIsIncluded(child) && edcAgeEligible(child) && child.parenting === 'primaryA'">
          Eligible Dependant Credit claimed by {{ payorName }} (primary parent).
        </div>
        <div class="field hint" v-if="childIsIncluded(child) && edcAgeEligible(child) && child.parenting === 'primaryB'">
          Eligible Dependant Credit claimed by {{ recipientName }} (primary parent).
        </div>
        <div class="field" v-if="childIsIncluded(child) && child.parenting === 'shared' && edcEligibleChildren.includes(child)">
          <label>Eligible Dependant Credit</label>
          <div class="radio-row">
            <label>
              <input type="radio" :name="'edc-' + i" :checked="child.edcClaimedBy === 'A'"
                     @change="setEDCClaimedBy(child, 'A')" />
              {{ payorName }} claims
            </label>
            <label>
              <input type="radio" :name="'edc-' + i" :checked="child.edcClaimedBy === 'B'"
                     @change="setEDCClaimedBy(child, 'B')" />
              {{ recipientName }} claims
            </label>
            <!-- DIVII PATCH: no "Auto" or "Neither" options. A shared child is always
                 claimed by one parent: Divii pre-selects one (see materializeEDCClaims)
                 and the lawyer changes it if they wish. -->
          </div>
          <p class="hint">
            CRA rule: each parent can claim EDC for only one dependant per year.
            Divii pre-selects the youngest shared child for {{ recipientName }} and the
            next for {{ payorName }}; picking a parent for one child moves the other
            parent to another shared child. This is the claim for the {{ taxYear }} tax
            year; recording a scenario writes it into the agreement as the starting
            point of its claiming rule.
          </p>
        </div>
        <div class="field hint" v-if="childIsIncluded(child) && edcAgeEligible(child) && child.parenting === 'shared' && sharedChildren.length >= 3 && !edcEligibleChildren.includes(child)">
          EDC not selectable for this child — with 3+ shared children, the EDC
          is most valuable claimed on the two youngest (this child will age out
          before they do).
        </div>
      </div>

      <button class="btn-ghost" @click="addChild">+ Add Child</button>

      <div class="step-nav">
        <button class="btn-ghost" @click="step = 2">← Back</button>
        <button class="btn-primary" @click="step = children.length === 0 ? 5 : 4">
          Next: {{ children.length === 0 ? 'Spousal Support' : 'Section 7' }} →
        </button>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         STEP 4: SECTION 7 EXPENSES
    ═══════════════════════════════════════════════════════════ -->
    <section class="step" v-if="step === 4">
      <h2>Section 7 Special Expenses</h2>
      <p class="sub">Optional. Add special or extraordinary expenses for each child.</p>

      <div v-for="(child, ci) in includedChildren" :key="ci" class="s7-child">
        <h3>{{ child.name || `Child ${children.indexOf(child) + 1}` }}</h3>

        <div v-for="(exp, ei) in child.s7expenses" :key="ei" class="s7-expense">
          <div class="row-3">
            <div class="field">
              <label>Category</label>
              <select v-model="exp.category">
                <option v-for="cat in S7_CATEGORIES" :key="cat.value" :value="cat.value">{{ cat.label }}</option>
              </select>
            </div>
            <div class="field">
              <label>Subsidy / Benefit</label>
              <div class="input-prefix"><span>$</span>
                <input type="text" inputmode="numeric"
                       :value="exp.subsidy ? fmt(exp.subsidy) : ''"
                       @input="exp.subsidy = parseDollarInput($event.target.value)"
                       placeholder="0" />
              </div>
            </div>
          </div>
          <!-- Per-party paid + claimed grid (DM-style). The total gross
               expense is derived as paidByA + paidByB. The "Claimed"
               amount per party is the T1 line 21400 (childcare) or
               medical credit they file — it reduces THEIR tax bill at
               THEIR marginal rate. -->
          <table class="s7-input-grid">
            <thead>
              <tr>
                <th></th>
                <th>{{ payorName }}</th>
                <th>{{ recipientName }}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>Paid <span class="hint-tiny">(annual cash paid)</span></th>
                <td>
                  <div class="input-prefix"><span>$</span>
                    <input type="text" inputmode="numeric"
                           :value="exp.paidByA ? fmt(exp.paidByA) : ''"
                           @input="exp.paidByA = parseDollarInput($event.target.value)"
                           placeholder="0" />
                  </div>
                </td>
                <td>
                  <div class="input-prefix"><span>$</span>
                    <input type="text" inputmode="numeric"
                           :value="exp.paidByB ? fmt(exp.paidByB) : ''"
                           @input="exp.paidByB = parseDollarInput($event.target.value)"
                           placeholder="0" />
                  </div>
                </td>
              </tr>
              <tr>
                <th>Claimed <span class="hint-tiny">(annual tax deduction)</span></th>
                <td>
                  <div class="input-prefix"><span>$</span>
                    <input type="text" inputmode="numeric"
                           :value="exp.claimedByA ? fmt(exp.claimedByA) : ''"
                           @input="exp.claimedByA = parseDollarInput($event.target.value)"
                           placeholder="0" />
                  </div>
                </td>
                <td>
                  <div class="input-prefix"><span>$</span>
                    <input type="text" inputmode="numeric"
                           :value="exp.claimedByB ? fmt(exp.claimedByB) : ''"
                           @input="exp.claimedByB = parseDollarInput($event.target.value)"
                           placeholder="0" />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="hint" style="margin: 0.4rem 0 0;">
            <strong>Tip:</strong> the "Claimed" amount reduces the claiming party's tax bill at their marginal rate (e.g. T1 line 21400 for childcare). Leave both Claimed fields at 0 to fall back to the legacy "lower-income parent claims the full gross" approximation.
          </p>
          <button class="btn-remove-sm" @click="removeS7(child, ei)">Remove expense</button>
        </div>

        <button class="btn-ghost-sm" @click="addS7(child)">+ Add expense for {{ child.name || 'this child' }}</button>
      </div>

      <div class="step-nav">
        <button class="btn-ghost" @click="step = 3">← Back</button>
        <button class="btn-primary" @click="step = 5">Next: Spousal Support →</button>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         STEP 5: SUPPORT SUMMARY — what the numbers say, before the
         Budget / SSAG Questions / Recommendation flow and the Worksheet.
    ═══════════════════════════════════════════════════════════ -->
    <section class="step step-summary" v-if="step === 5">
      <h2>Support Summary</h2>

      <!-- Child support figures (shown whenever table children exist) -->
      <div class="ssag-range cs-summary" v-if="csgChildren.length">
        <h3>Child Support</h3>
        <div v-if="soleAChildren.length > 0" class="bd-row">
          <span>{{ soleAChildren.length }} child{{ soleAChildren.length > 1 ? 'ren' : '' }} primary with {{ payorName }}</span>
          <span>{{ recipientName }} pays <strong>${{ fmt(csRecipientToPayor) }}/mo</strong></span>
        </div>
        <div v-if="soleBChildren.length > 0" class="bd-row">
          <span>{{ soleBChildren.length }} child{{ soleBChildren.length > 1 ? 'ren' : '' }} primary with {{ recipientName }}</span>
          <span>{{ payorName }} pays <strong>${{ fmt(csPayorToRecipient) }}/mo</strong></span>
        </div>
        <div v-if="sharedChildren.length > 0">
          <div class="bd-row">
            <span>{{ payorName }} table ({{ sharedChildren.length }} shared)</span>
            <strong>${{ fmt(sharedTableA) }}/mo</strong>
          </div>
          <div class="bd-row">
            <span>{{ recipientName }} table</span>
            <strong>${{ fmt(sharedTableB) }}/mo</strong>
          </div>
        </div>
        <div class="bd-total">
          <span>Net Child Support</span>
          <strong>{{ netCSPayer }} pays ${{ fmt(netCS) }}/mo</strong>
        </div>
      </div>

      <div class="field">
        <label>Is there an entitlement to spousal support?</label>
        <div class="radio-row">
          <label><input type="radio" :value="true" v-model="ssagApplicable" /> Yes</label>
          <label><input type="radio" :value="false" v-model="ssagApplicable" /> No</label>
        </div>
      </div>

      <div v-if="ssagApplicable">
        <div class="field">
          <label>Length of Relationship</label>
          <div class="ws-static" v-if="yearsMarried">
            <strong>{{ relationshipLengthLabel }}</strong>
            <span class="hint" style="margin-left: 0.5rem;">
              ({{ formattedCohabitationDate }} → {{ formattedSeparationDate }})
            </span>
          </div>
          <div class="hint warn" v-else>
            Set both cohabitation and separation dates in Step 1 to compute length.
          </div>
        </div>

        <div class="field">
          <label>Spousal Support Payable by</label>
          <div class="radio-row">
            <label><input type="radio" value="A" v-model="ssagPayor" /> {{ payorName }}</label>
            <label><input type="radio" value="B" v-model="ssagPayor" /> {{ recipientName }}</label>
          </div>
        </div>

        <!-- SSAG range display -->
        <div class="ssag-range" v-if="ssagRange">
          <h3>SSAG Range</h3>
          <div class="range-row">
            <div class="range-cell">
              <span class="range-label">Low</span>
              <span class="range-val">${{ fmt(ssagRange.low) }}/mo</span>
            </div>
            <div class="range-cell mid">
              <span class="range-label">Mid</span>
              <span class="range-val">${{ fmt(ssagRange.mid) }}/mo</span>
            </div>
            <div class="range-cell">
              <span class="range-label">High</span>
              <span class="range-val">${{ fmt(ssagRange.high) }}/mo</span>
            </div>
          </div>
          <div class="duration-row">
            <span v-if="ssagRange.indefinite">Duration: Indefinite ({{ rule65Triggered ? 'Rule of 65' : '≥ 20 year marriage' }})</span>
            <span v-else>Duration: {{ ssagRange.durationLow.toFixed(1) }}–{{ ssagRange.durationHigh.toFixed(1) }} years</span>
          </div>
          <p class="hint warn" v-if="rule65Triggered" style="margin: 0.6rem 0 0;">
            ⚠ <strong>SSAG Rule of 65</strong> applies: recipient is {{ ssagRecipientAgeAtSeparation }} at separation
            and the marriage is {{ relationshipLengthLabel }} ({{ ssagRecipientAgeAtSeparation + yearsMarried }} ≥ 65).
            Spousal support duration is <strong>indefinite</strong> (subject to review).
          </p>
          <p class="hint" v-if="durationTestApplied === 'age-of-children'" style="margin: 0.4rem 0 0;">
            <strong>Age-of-children test</strong> extends duration to {{ ageOfChildrenDuration.toFixed(1) }} years
            (until the last child finishes high school) — longer than the length-of-marriage test ({{ (yearsMarried * 0.5).toFixed(1) }}–{{ (yearsMarried * 1).toFixed(1) }} yr).
          </p>
          <p class="hint warn" v-if="ssagAboveCeiling" style="margin: 0.4rem 0 0;">
            ⚠ Payor gross income (${{ fmt(ssagPayorGross) }}) exceeds the SSAG <strong>ceiling of $350,000</strong>.
            Above this point the formulas are guidelines only — courts have broader discretion to depart from the range.
          </p>
          <p class="hint warn" v-if="ssagBelowFloor" style="margin: 0.4rem 0 0;">
            ⚠ Payor gross income (${{ fmt(ssagPayorGross) }}) is below the SSAG <strong>floor of $20,000</strong>.
            SSAG generally indicates no spousal support is owed at this income level.
          </p>
          <p class="hint" v-else-if="ssagInLowIncomeBand" style="margin: 0.4rem 0 0;">
            Payor income is in the <strong>$20,000–$30,000 low-income band</strong> — SSAG indicates reduced/discretionary support.
          </p>
          <p class="hint" v-if="custodialPayorScenario" style="margin: 0.4rem 0 0;">
            <strong>Custodial-payor formula</strong> (SSAG Ch. 8) — SS payor has primary care of the children.
            Per SSAG, the <strong>without-child formula</strong> is applied to the gross income gap (1.5–2% × years),
            while duration uses the longer of length-of-marriage or age-of-children tests.
          </p>
          <p class="hint" v-if="splitCustodyScenario" style="margin: 0.4rem 0 0;">
            <strong>Split custody</strong> — children are primarily with each parent. CSG set-off applies via each
            parent's notional table amount; SSAG range is computed accordingly.
          </p>
          <p class="hint" v-if="withoutChildHitsCap" style="margin: 0.4rem 0 0;">
            <strong>Long-marriage cap applied:</strong> SSAG percentages capped at 37.5% (low) / 50% (high) of the
            gross income gap for marriages ≥ 25 years.
          </p>
          <p class="hint" style="margin: 0.4rem 0 0; color: var(--text-subtle);">
            <strong>Restructuring:</strong> SSAG amounts and durations can be restructured (longer duration at
            lower amount, shorter duration at higher amount, or lump-sum equivalents). Consult SSAG Ch. 10.
            <br />
            <strong>Self-sufficiency:</strong> The recipient's earning capacity may justify imputed income or
            time-limited support — discretionary, not auto-applied here.
          </p>
        </div>

        <!-- Flow guidance: amount/duration selection now lives in the
             Worksheet tab (step 5 is a summary; the sliders moved there). -->
        <p class="adv-pointer" v-if="ssagRange">
          These are the starting numbers. Next, enter each party's
          <button type="button" class="adv-pointer-link" @click="workspaceTab = 'needs'">Budget</button>,
          answer the
          <button type="button" class="adv-pointer-link" @click="workspaceTab = 'circumstances'">SSAG Questions</button>,
          review the
          <button type="button" class="adv-pointer-link" @click="workspaceTab = 'recommendation'">Recommendation</button>,
          then explore amounts and scenarios in the
          <button type="button" class="adv-pointer-link" @click="workspaceTab = 'worksheet'">Worksheet</button>.
        </p>

        <!-- Documented-divergence notes (audited vs published CRA/statute rules) -->
        <div class="calc-notes" v-if="calcDivergenceNotes.length">
          <div class="calc-notes-head">Calculation notes</div>
          <ul>
            <li v-for="(n, i) in calcDivergenceNotes" :key="'cn' + i">{{ n }}</li>
          </ul>
        </div>
      </div>

      <div class="step-nav">
        <button class="btn-ghost" @click="step = 4">← Back</button>
        <!-- With spousal entitlement the flow continues via the tabs (Budget →
             Questions → Recommendation → Worksheet); without entitlement there
             are no tabs, so the breakdown opens in place. -->
        <button class="btn-primary" v-if="ssagApplicable" @click="workspaceTab = 'needs'">
          Next: Budget →
        </button>
        <button class="btn-primary" v-else @click="viewMode = 'workspace'">
          Open Worksheet →
        </button>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         STEP 6 (RESULTS) — REMOVED. The standalone Results page duplicated
         the workspace breakdown and the step-5 amount/duration controls, so
         step 5 now opens the workspace directly. Kept the marker comment so
         the step-numbering history is clear.
    ═══════════════════════════════════════════════════════════ -->

    <!-- Step indicator (5 steps; the old Results page is gone). -->
    <div class="step-dots">
      <span v-for="n in 5" :key="n"
        :class="['dot', { active: step === n, done: step > n }]"
        @click="step > n ? step = n : null">
      </span>
    </div>
  </div>
  <!-- ═════════════════════ END WIZARD MODE ═════════════════════ -->

  <!-- ═════════════════════════════════════════════════════════════════════
       WORKSPACE MODE — single-page view.
       Inputs on the left, live breakdown on the right.
  ═════════════════════════════════════════════════════════════════════ -->
  <div v-if="showWorksheetView" class="workspace">
    <!-- Sticky case header -->
    <header class="workspace-header">
      <div class="workspace-title">
        <span class="workspace-case">{{ payorName }} &amp; {{ recipientName }}</span>
        <span class="workspace-meta">
          {{ provinceName }}
          <template v-if="formattedCohabitationDate || formattedSeparationDate">
            ·
            <template v-if="formattedCohabitationDate">{{ formattedCohabitationDate }}</template>
            <template v-else>(no cohab date)</template>
            →
            <template v-if="formattedSeparationDate">{{ formattedSeparationDate }}</template>
            <template v-else>(no sep date)</template>
          </template>
          <template v-if="yearsMarried"> · {{ relationshipLengthLabel }}</template>
          <template v-if="tableYear"> · {{ tableYear }} tables</template>
        </span>
      </div>
      <div class="workspace-actions">
        <button class="btn-ghost-sm" @click="workspaceTab = 'means'; viewMode = 'wizard'; step = 1">← Reopen wizard</button>
      </div>
    </header>

    <!-- ─── Case Scenarios panel ─────────────────────────────────────
         Named snapshots of the full input set (e.g., "Husband's position",
         "Wife's position", "Mediator's imputed"). The active scenario
         auto-saves as inputs change; switching loads a saved snapshot. -->
    <section class="case-scenarios-panel">
      <div class="case-scenarios-row">
        <label class="case-scenarios-label">
          <span class="case-scenarios-icon">📂</span> Case scenarios:
        </label>
        <select
          class="case-scenarios-select"
          :value="activeCaseScenarioId || ''"
          @change="onScenarioSelectChange($event)"
          :disabled="!caseScenarios.length">
          <option value="" disabled>{{ caseScenarios.length ? 'Choose a scenario…' : 'No saved scenarios' }}</option>
          <option v-for="s in caseScenarios" :key="s.id" :value="s.id">{{ s.name }}{{ s.id === activeCaseScenarioId && activeScenarioDirty ? ' •' : '' }}</option>
        </select>
        <button class="btn-primary btn-ghost-sm" @click="saveActiveScenario" :disabled="!activeCaseScenarioId || !activeScenarioDirty" title="Update the active scenario with the current inputs">
          💾 Save changes
        </button>
        <button class="btn-ghost-sm" @click="openSaveNewScenarioPrompt" title="Save the current inputs as a new named scenario">
          💾 Save as new…
        </button>
        <button class="btn-ghost-sm" @click="duplicateCaseScenario(activeCaseScenarioId)" :disabled="!activeCaseScenarioId" title="Copy the active scenario and start editing the copy">
          📋 Duplicate
        </button>
        <button class="btn-ghost-sm" @click="openRenameScenarioPrompt(activeCaseScenarioId)" :disabled="!activeCaseScenarioId" title="Rename the active scenario">
          ✎ Rename
        </button>
        <button class="btn-ghost-sm" @click="confirmDeleteActiveScenario" :disabled="!activeCaseScenarioId" title="Delete the active scenario">
          🗑 Delete
        </button>
        <!-- DIVII FIX — a link, so the read-only fieldset does not disable it. -->
        <a
          href="#"
          role="button"
          class="btn-primary scenario-download-btn"
          @click.prevent="printWorksheet"
          title="Print or save a worksheet PDF — includes all scenarios across multiple pages if needed"
        >📄 Download worksheet</a>
      </div>
      <p class="case-scenarios-hint" v-if="activeCaseScenarioId && activeScenarioDirty">
        Unsaved changes to <strong>{{ activeCaseScenarioName }}</strong>. Click "Save changes" to update it, or "Save as new…" to fork a separate scenario.
      </p>
      <p class="case-scenarios-hint" v-else-if="activeCaseScenarioId">
        <strong>{{ activeCaseScenarioName }}</strong> loaded. Edits stay live until you "Save changes" (updates this scenario) or "Save as new…" (forks a new one).
      </p>
      <p class="case-scenarios-hint" v-else-if="caseScenarios.length">
        Pick a scenario above to load its inputs, or "Save as new…" to snapshot what you have now.
      </p>
      <p class="case-scenarios-hint" v-else>
        No scenarios saved yet. Click "Save as new…" to capture the current inputs as a starting point — useful when parties disagree on income or you want to compare positions.
      </p>
    </section>

    <!-- Scenario naming prompt — used by both Save-as-new and Rename -->
    <div v-if="scenarioPromptOpen" class="scenario-prompt-overlay" @click.self="closeScenarioPrompt">
      <div class="scenario-prompt-dialog">
        <h3 class="scenario-prompt-title">
          {{ scenarioPromptMode === 'rename' ? 'Rename scenario' : 'Save scenario as…' }}
        </h3>
        <label class="scenario-prompt-label">
          Scenario name
          <input
            ref="scenarioPromptInput"
            type="text"
            v-model="scenarioPromptName"
            @keydown.enter="submitScenarioPrompt"
            @keydown.escape="closeScenarioPrompt"
            placeholder="e.g. Husband's position — $95k imputed"
            class="scenario-prompt-input"
            autofocus
          />
        </label>
        <div class="scenario-prompt-actions">
          <button class="btn-ghost-sm" @click="closeScenarioPrompt">Cancel</button>
          <button class="btn-primary" @click="submitScenarioPrompt" :disabled="!scenarioPromptName.trim()">
            {{ scenarioPromptMode === 'rename' ? 'Rename' : 'Save scenario' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Apply-to-Agreement confirm dialog. Spousal support can be recorded as a
         monthly award OR a lump sum — a drafting choice the numbers can't infer.
         The lawyer reviews both figures and picks one here, then explicitly
         sends; nothing leaves the calculator until "Send to agreement". -->
    <div v-if="applyToAgreementEnabled && applyDialogScenario" class="scenario-prompt-overlay" @click.self="closeApplyDialog">
      <div class="scenario-prompt-dialog apply-dialog">
        <h3 class="scenario-prompt-title">Record “{{ applyDialogScenario.name || 'scenario' }}” in agreement</h3>
        <!-- DIVII PATCH: wording matches what the host does (confirm, then write). -->
        <p class="apply-dialog-sub">
          Choose how spousal support is recorded. Recording updates the builder’s
          Child Support, Special Expenses, Spousal Support and party income entries;
          Divii shows you each change and asks you to confirm before anything is written.
        </p>
        <p v-if="recordedProp" class="apply-dialog-sub">
          Last recorded: {{ recordedProp.scenarioLabel }}<span v-if="recordedProp.mode"> ({{ recordedProp.mode === 'lumpSum' ? 'lump sum' : 'monthly' }})</span><span v-if="recordedProp.at"> on {{ String(recordedProp.at).slice(0, 10) }}</span>.
        </p>
        <label class="apply-option" :class="{ 'apply-option--active': applyDialogMode === 'periodic' }">
          <input type="radio" value="periodic" v-model="applyDialogMode" />
          <span class="apply-option-body">
            <span class="apply-option-title">Monthly support</span>
            <span class="apply-option-figure">${{ fmt(applyDialogScenario.ssMonthly) }}/mo</span>
          </span>
        </label>
        <!-- Term applies to monthly support only. Defaults to indefinite for
             ≥20-year marriages / Rule-of-65 cases, but the lawyer can choose
             either way (a deliberate drafting decision). -->
        <div v-if="applyDialogMode === 'periodic'" class="apply-term">
          <span class="apply-term-label">Support term</span>
          <label class="apply-term-opt">
            <input type="radio" :value="true" v-model="applyDialogIndefinite" />
            Indefinite <span class="apply-term-aux">(no fixed end — subject to review)</span>
          </label>
          <label class="apply-term-opt">
            <input type="radio" :value="false" v-model="applyDialogIndefinite" />
            For
            <input type="number" class="apply-term-years" min="0" step="0.25"
                   :value="applyDialogYears"
                   @input="applyDialogYears = Number($event.target.value) || 0"
                   @focus="applyDialogIndefinite = false"
                   :disabled="applyDialogIndefinite" />
            years
          </label>
        </div>
        <label class="apply-option" :class="{ 'apply-option--active': applyDialogMode === 'lumpSum' }">
          <input type="radio" value="lumpSum" v-model="applyDialogMode" />
          <span class="apply-option-body">
            <span class="apply-option-title">Lump sum</span>
            <span class="apply-option-figure">${{ fmt(applyDialogLumpAmount) }} <small>(NPV @ 1.81%, {{ applyDialogLumpBasis === 'payorCost' ? "payor's after-tax cost" : 'midpoint' }})</small></span>
          </span>
        </label>
        <!-- DIVII PATCH: the two lump-sum figures are selectable, mirroring the
             pension calculator's pre-tax / post-tax rows. The chosen one is
             written to the builder's lump sum spousal payment. -->
        <div v-if="applyDialogMode === 'lumpSum'" class="apply-term apply-lump-basis">
          <span class="apply-term-label">Lump sum figure</span>
          <label class="apply-term-opt">
            <input type="radio" value="midpoint" v-model="applyDialogLumpBasis" />
            Midpoint ${{ fmt(lumpSumMidpoint(applyDialogScenario)) }}
            <span class="apply-term-aux">(average of the payor's after-tax cost and the recipient's after-tax benefit; the usual split-the-difference figure)</span>
          </label>
          <label class="apply-term-opt">
            <input type="radio" value="payorCost" v-model="applyDialogLumpBasis" />
            Payor's after-tax cost ${{ fmt(lumpSumPayorCost(applyDialogScenario)) }}
            <span class="apply-term-aux">(what the monthly stream would cost the payor after tax over the term)</span>
          </label>
        </div>
        <div class="scenario-prompt-actions">
          <button class="btn-ghost-sm" @click="closeApplyDialog">Cancel</button>
          <button class="btn-primary" @click="confirmApplyDialog">→ Record in agreement</button>
        </div>
      </div>
    </div>

    <div class="workspace-grid">
      <!-- ─── LEFT: INPUTS ──────────────────────────────────────────── -->
      <aside class="workspace-inputs">
        <!-- Parties & Case — read-only summary; edit in wizard -->
        <details class="ws-section" open>
          <summary>Parties &amp; Case</summary>
          <div class="ws-section-body ws-summary">
            <div class="ws-summary-row">
              <span class="ws-summary-label">Parties</span>
              <span class="ws-summary-value">
                {{ payorName }}<span v-if="payorAgeAtSeparation != null" class="ws-summary-aux"> ({{ payorAgeAtSeparation }})</span> <em>(payor)</em>
                · {{ recipientName }}<span v-if="recipientAgeAtSeparation != null" class="ws-summary-aux"> ({{ recipientAgeAtSeparation }})</span> <em>(recipient)</em>
                <!-- Inline Switch button removed — duplicated the
                     "Swap payor & recipient" button at the bottom of
                     this section. Switching is still accessible via
                     that bottom button and the auto-detect suggestion
                     row below when applicable. -->
              </span>
            </div>
            <div class="ws-summary-row" v-if="suggestedSwap">
              <span class="ws-summary-label"></span>
              <span class="ws-summary-value ws-summary-suggestion">
                ⓘ Based on income, <strong>{{ recipientName }}</strong> is likely the payor.
                <button type="button" class="ws-switch-link" @click="switchPayor">Switch payor</button>
                <button type="button" class="ws-dismiss-link" @click="dismissPayorSuggestion">Keep as-is</button>
              </span>
            </div>
            <div class="ws-summary-row" v-if="cohabitationDate">
              <span class="ws-summary-label">Date of Cohabitation</span>
              <span class="ws-summary-value">{{ formattedCohabitationDate || '—' }}</span>
            </div>
            <div class="ws-summary-row" v-if="separationDate">
              <span class="ws-summary-label">Date of Separation</span>
              <span class="ws-summary-value">
                {{ formattedSeparationDate || '—' }}
                <span class="ws-summary-aux" v-if="yearsMarried">({{ relationshipLengthLabel }})</span>
              </span>
            </div>
            <div class="ws-summary-row">
              <span class="ws-summary-label">Province</span>
              <span class="ws-summary-value">
                {{ provinceName || 'Not set' }}
                <span class="ws-summary-aux" v-if="tableYear"> · {{ tableYear }} tables</span>
              </span>
            </div>
            <!-- Party self-identification claims — who each party's entry
                 surface is locked to. Reset frees the party for
                 re-identification (existing entries keep their stamps). -->
            <div class="ws-summary-row" v-if="partyClaims.A">
              <span class="ws-summary-label">{{ payorName }} entry</span>
              <span class="ws-summary-value">
                locked to <strong>{{ partyClaims.A.name || 'a linked user account' }}</strong>
                <button v-if="accessProfile === 'full'" type="button" class="ws-dismiss-link" @click="resetPartyClaim('A')" title="Clear this claim so the party can re-identify">Reset</button>
              </span>
            </div>
            <div class="ws-summary-row" v-if="partyClaims.B">
              <span class="ws-summary-label">{{ recipientName }} entry</span>
              <span class="ws-summary-value">
                locked to <strong>{{ partyClaims.B.name || 'a linked user account' }}</strong>
                <button v-if="accessProfile === 'full'" type="button" class="ws-dismiss-link" @click="resetPartyClaim('B')" title="Clear this claim so the party can re-identify">Reset</button>
              </span>
            </div>
            <div class="ws-summary-edit">
              <button class="btn-ghost-sm" @click="swapParties" title="Reverse who is payor and who is recipient">⇅ Swap payor &amp; recipient</button>
              <button class="btn-ghost-sm" @click="workspaceTab = 'means'; viewMode = 'wizard'; step = 1">Edit in wizard →</button>
            </div>
          </div>
        </details>

        <!-- Income — both parties -->
        <details class="ws-section" open>
          <summary>Income</summary>
          <div class="ws-section-body">
            <!-- Payor -->
            <div class="income-party">
              <div class="party-header">
                <h3>{{ payorName }}</h3>
                <span class="party-total">Guidelines: <strong>${{ fmt(payorIncome) }}</strong></span>
              </div>
              <div class="item-list">
                <div v-for="item in payorItemsRaw" :key="item.id" class="income-row">
                  <div class="row-label">
                    <span class="row-title">{{ incomeTypeMeta(item.typeId)?.label || item.typeId }}</span>
                    <span class="row-line" v-if="incomeTypeMeta(item.typeId)?.t1Line">Line {{ incomeTypeMeta(item.typeId).t1Line }}</span>
                  </div>
                  <div class="input-prefix row-amount">
                    <span>$</span>
                    <input type="text" inputmode="numeric"
                           :value="displayAmount(item)"
                           @focus="onAmountFocus(item)"
                           @blur="onAmountBlur()"
                           @input="updateIncomeAmount(item.id, parseDollarInput($event.target.value))"
                           placeholder="0" />
                  </div>
                  <button class="btn-remove" @click="removeIncomeItem(item.id)" title="Remove">×</button>
                </div>
                <div v-if="payorItemsRaw.length === 0" class="empty-row">No income items yet.</div>
              </div>
              <div class="picker-wrap">
                <button class="btn-add-income" @click="openPickerFor === 'A_ws' ? openPickerFor = null : (openPickerFor = 'A_ws', pickerSearch = '', $nextTick(() => $refs.wsPickerA?.focus()))">
                  + Add income
                </button>
                <div v-if="openPickerFor === 'A_ws'" class="picker-popover">
                  <input ref="wsPickerA" type="text" v-model="pickerSearch" placeholder="Search income, deduction, T1 line…" class="picker-search" @keydown.esc="openPickerFor = null" />
                  <div class="picker-results">
                    <template v-for="cat in incomeCategories">
                      <div v-if="filteredTypesByCategory(cat).length" :key="cat" class="picker-group">
                        <div class="picker-group-header">{{ cat }}</div>
                        <button v-for="type in filteredTypesByCategory(cat)" :key="type.id"
                                class="picker-item"
                                @click="addIncomeItem('A', type.id); openPickerFor = null; pickerSearch = ''">
                          <span class="picker-item-label">{{ type.label }}</span>
                          <span class="picker-item-line" v-if="type.t1Line && type.t1Line !== '—'">Line {{ type.t1Line }}</span>
                        </button>
                      </div>
                    </template>
                    <div v-if="totalFilteredCount === 0" class="picker-empty">No matches.</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Recipient -->
            <div class="income-party">
              <div class="party-header">
                <h3>{{ recipientName }}</h3>
                <span class="party-total">Guidelines: <strong>${{ fmt(recipientIncome) }}</strong></span>
              </div>
              <div class="item-list">
                <div v-for="item in recipientItemsRaw" :key="item.id" class="income-row">
                  <div class="row-label">
                    <span class="row-title">{{ incomeTypeMeta(item.typeId)?.label || item.typeId }}</span>
                    <span class="row-line" v-if="incomeTypeMeta(item.typeId)?.t1Line">Line {{ incomeTypeMeta(item.typeId).t1Line }}</span>
                  </div>
                  <div class="input-prefix row-amount">
                    <span>$</span>
                    <input type="text" inputmode="numeric"
                           :value="displayAmount(item)"
                           @focus="onAmountFocus(item)"
                           @blur="onAmountBlur()"
                           @input="updateIncomeAmount(item.id, parseDollarInput($event.target.value))"
                           placeholder="0" />
                  </div>
                  <button class="btn-remove" @click="removeIncomeItem(item.id)" title="Remove">×</button>
                </div>
                <div v-if="recipientItemsRaw.length === 0" class="empty-row">No income items yet.</div>
              </div>
              <div class="picker-wrap">
                <button class="btn-add-income" @click="openPickerFor === 'B_ws' ? openPickerFor = null : (openPickerFor = 'B_ws', pickerSearch = '', $nextTick(() => $refs.wsPickerB?.focus()))">
                  + Add income
                </button>
                <div v-if="openPickerFor === 'B_ws'" class="picker-popover">
                  <input ref="wsPickerB" type="text" v-model="pickerSearch" placeholder="Search income, deduction, T1 line…" class="picker-search" @keydown.esc="openPickerFor = null" />
                  <div class="picker-results">
                    <template v-for="cat in incomeCategories">
                      <div v-if="filteredTypesByCategory(cat).length" :key="cat" class="picker-group">
                        <div class="picker-group-header">{{ cat }}</div>
                        <button v-for="type in filteredTypesByCategory(cat)" :key="type.id"
                                class="picker-item"
                                @click="addIncomeItem('B', type.id); openPickerFor = null; pickerSearch = ''">
                          <span class="picker-item-label">{{ type.label }}</span>
                          <span class="picker-item-line" v-if="type.t1Line && type.t1Line !== '—'">Line {{ type.t1Line }}</span>
                        </button>
                      </div>
                    </template>
                    <div v-if="totalFilteredCount === 0" class="picker-empty">No matches.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </details>

        <!-- Children — read-only compact table; edit in wizard -->
        <details class="ws-section" :open="children.length > 0">
          <summary>Children <span class="ws-count">({{ children.length }})</span></summary>
          <div class="ws-section-body">
            <table v-if="children.length" class="children-mini">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Age</th>
                  <th>Lives with</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(child, i) in children" :key="i">
                  <td>{{ child.name || ('Child ' + (i + 1)) }}</td>
                  <td>
                    <template v-if="child.dob">{{ childAge(child.dob) }}<span v-if="isAtAOM(child.dob)" class="tag-aom">AOM</span></template>
                    <span v-else class="text-subtle">—</span>
                  </td>
                  <td>{{ childParentingLabel(child) }}</td>
                </tr>
              </tbody>
            </table>
            <div v-else class="empty-row">No children yet.</div>
            <button class="btn-ghost-sm ws-edit-link" @click="workspaceTab = 'means'; viewMode = 'wizard'; step = 4">Edit children in wizard →</button>
          </div>
        </details>

        <!-- SSAG settings — minimal: applies toggle + payor -->
        <details class="ws-section" :open="ssagApplicable">
          <summary>Spousal Support (SSAG)</summary>
          <div class="ws-section-body ws-summary">
            <label class="ws-inline-toggle">
              <input type="checkbox" v-model="ssagApplicable" /> SSAG analysis applies
            </label>
            <div class="ws-summary-row" v-if="ssagApplicable">
              <span class="ws-summary-label">Payor</span>
              <span class="ws-summary-value">
                <select v-model="ssagPayor" class="ws-inline-select">
                  <option value="A">{{ payorName }}</option>
                  <option value="B">{{ recipientName }}</option>
                </select>
              </span>
            </div>
            <div class="ws-summary-row warn-row" v-if="ssagApplicable && rule65Triggered">
              <span class="ws-summary-label">⚠ Rule 65</span>
              <span class="ws-summary-value">
                Recipient {{ ssagRecipientAgeAtSeparation }} + {{ relationshipLengthLabel }} ≥ 65 — duration indefinite
              </span>
            </div>
            <div class="ws-summary-row warn-row" v-if="ssagApplicable && durationTestApplied === 'age-of-children'">
              <span class="ws-summary-label">Age-of-kids</span>
              <span class="ws-summary-value">
                Duration extended to {{ ageOfChildrenDuration.toFixed(1) }} yr (until last kid finishes HS)
              </span>
            </div>
            <div class="ws-summary-row warn-row" v-if="ssagApplicable && ssagAboveCeiling">
              <span class="ws-summary-label">⚠ Ceiling</span>
              <span class="ws-summary-value">
                Payor income &gt; $350K — formulas are guidelines only
              </span>
            </div>
            <div class="ws-summary-row warn-row" v-if="ssagApplicable && ssagBelowFloor">
              <span class="ws-summary-label">⚠ Floor</span>
              <span class="ws-summary-value">
                Payor income &lt; $20K — SSAG indicates no SS owed
              </span>
            </div>
            <div class="ws-summary-row warn-row" v-if="ssagApplicable && custodialPayorScenario">
              <span class="ws-summary-label">Custodial payor</span>
              <span class="ws-summary-value">
                SS payor has primary care — without-child amount formula (Ch. 8) applied
              </span>
            </div>
            <div class="ws-summary-row warn-row" v-if="ssagApplicable && splitCustodyScenario">
              <span class="ws-summary-label">Split custody</span>
              <span class="ws-summary-value">
                Children primarily with each parent — notional table set-off applied
              </span>
            </div>
            <div class="ws-summary-row warn-row" v-if="ssagApplicable && withoutChildHitsCap">
              <span class="ws-summary-label">Cap applied</span>
              <span class="ws-summary-value">
                Long-marriage cap: 37.5% / 50% of gross income gap
              </span>
            </div>
            <div class="hint warn" v-if="ssagApplicable && !yearsMarried" style="margin: 0;">
              Set cohabitation &amp; separation dates to compute length.
            </div>
            <!-- Amount/duration selection lives in the breakdown column to the
                 right (Spousal Support block) — not duplicated here. -->
          </div>
        </details>
      </aside>

      <!-- ─── RIGHT: BREAKDOWN ──────────────────────────────────────── -->
      <section class="workspace-breakdown">
        <!-- Child Support -->
        <div class="breakdown-block">
          <div class="breakdown-title">Child Support</div>
          <div v-if="includedChildren.length === 0" class="breakdown-empty">
            Add children to see CSG calculation.
          </div>
          <template v-else>
            <div v-if="soleAChildren.length > 0" class="bd-row">
              <span>{{ soleAChildren.length }} child{{ soleAChildren.length > 1 ? 'ren' : '' }} primary with {{ payorName }}</span>
              <span>{{ recipientName }} pays <strong>${{ fmt(csRecipientToPayor) }}/mo</strong></span>
            </div>
            <div v-if="soleBChildren.length > 0" class="bd-row">
              <span>{{ soleBChildren.length }} child{{ soleBChildren.length > 1 ? 'ren' : '' }} primary with {{ recipientName }}</span>
              <span>{{ payorName }} pays <strong>${{ fmt(csPayorToRecipient) }}/mo</strong></span>
            </div>
            <div v-if="sharedChildren.length > 0">
              <div class="bd-row">
                <span>{{ payorName }} table</span>
                <strong>${{ fmt(sharedTableA) }}/mo</strong>
              </div>
              <div class="bd-row">
                <span>{{ recipientName }} table</span>
                <strong>${{ fmt(sharedTableB) }}/mo</strong>
              </div>
              <div class="bd-row">
                <span>Set-off</span>
                <strong>{{ sharedNetPayer }} pays ${{ fmt(sharedNet) }}/mo</strong>
              </div>
            </div>
            <div class="bd-total">
              <span>Net Child Support</span>
              <strong>${{ fmt(netCS) }}/mo by {{ netCSPayer }}</strong>
            </div>
            <!-- DIVII PATCH: a matter with no spousal support claim has no
                 scenario table, so the child support figures get their own
                 Record button here. When SSAG applies the scenario table's
                 button is used instead, so spousal support goes with them. -->
            <div v-if="applyToAgreementEnabled && !(ssagApplicable && ssagRange)" class="bd-record">
              <button
                type="button"
                class="apply-btn"
                :class="{ 'apply-btn--current': appliedScenarioId === 'child-support-only' }"
                :disabled="!canRecordProp"
                @click="onApplyChildSupportOnly"
                :title="!canRecordProp
                  ? 'Your role on this agreement cannot record into the builder.'
                  : 'Record the child support amounts, Section 7 proportions and party incomes in the agreement builder.'"
              >
                <span v-if="appliedScenarioId === 'child-support-only'" class="apply-btn-badge">✓ In agreement · child support</span>
                <span v-else>→ Record child support in agreement</span>
              </button>
            </div>
          </template>
        </div>

        <!-- Section 7 detail table is rendered full-width BELOW the
             workspace-grid, not in this right-column breakdown — five
             columns need the room and this column is too narrow. -->

        <!-- Spousal Support — range + slider -->
        <div class="breakdown-block" v-if="ssagApplicable && ssagRange">
          <div class="breakdown-title">Spousal Support ({{ formulaLabel }})</div>
          <div class="ssag-range-row">
            <div class="range-cell">
              <span class="range-label">Low</span>
              <span class="range-val">${{ fmt(ssagRange.low) }}</span>
            </div>
            <div class="range-cell mid">
              <span class="range-label">Mid</span>
              <span class="range-val">${{ fmt(ssagRange.mid) }}</span>
            </div>
            <div class="range-cell">
              <span class="range-label">High</span>
              <span class="range-val">${{ fmt(ssagRange.high) }}</span>
            </div>
          </div>
          <div class="bd-row duration-row">
            <span v-if="ssagRange.indefinite">Duration: Indefinite ({{ rule65Triggered ? 'Rule of 65' : '≥ 20 year marriage' }})</span>
            <span v-else>Duration: {{ ssagRange.durationLow.toFixed(1) }}–{{ ssagRange.durationHigh.toFixed(1) }} years</span>
          </div>
          <div class="slider-block">
            <label class="slider-label">
              Selected amount: <strong>${{ fmt(ssagSelected) }}/mo</strong>
            </label>
            <input type="range"
                   :min="ssagRange.low"
                   :max="ssagRange.high"
                   v-model.number="ssagSelected"
                   step="10" />
            <div class="slider-labels">
              <span>Low ${{ fmt(ssagRange.low) }}</span>
              <span>High ${{ fmt(ssagRange.high) }}</span>
            </div>
            <div class="slider-extras">
              <label class="amount-input-label">
                Set specific amount:
                <span class="dollar-prefix">$</span><input type="number"
                       class="amount-input"
                       v-model.number="ssagSelected"
                       min="0" step="1" /><span class="amount-suffix">/mo</span>
              </label>
              <button class="btn-ghost-sm equalize-btn"
                      @click="setEqualizeNDIAmount"
                      :disabled="equalizeNDIAmount == null"
                      :title="equalizeNDIAmount == null ? '50/50 NDI not reachable within range' : 'Set amount that equalizes NDI between parties'">
                ⚖ Equalize NDI<span v-if="equalizeNDIAmount != null"> (${{ fmt(equalizeNDIAmount) }}/mo)</span>
              </button>
            </div>
          </div>
          <div class="slider-block" v-if="ssagRange.durationHigh > ssagRange.durationLow">
            <label class="slider-label">
              Selected duration: <strong>{{ ssagSelectedDuration.toFixed(2).replace(/\.?0+$/, '') }} year{{ ssagSelectedDuration === 1 ? '' : 's' }}</strong>
              <span v-if="ssagRange.indefinite" class="dur-indef-tag" style="margin-left: 0.4rem;">∞ indefinite</span>
            </label>
            <input type="range"
                   :min="Math.floor(ssagRange.durationLow)"
                   :max="Math.ceil(ssagRange.durationHigh)"
                   v-model.number="ssagSelectedDuration"
                   step="0.25" />
            <div class="slider-labels">
              <span>Low {{ ssagRange.durationLow.toFixed(1) }} yr</span>
              <span>High {{ ssagRange.durationHigh.toFixed(1) }} yr</span>
            </div>
          </div>

        </div>

        <!-- Scenario actions -->
        <div class="scenario-actions" v-if="ssagApplicable && ssagRange">
          <button class="btn-primary" @click="addScenario">+ Add scenario column</button>
          <button class="btn-ghost-sm" @click="resetScenarios" title="Restore SSAG Low / Mid / High">Reset to Low / Mid / High</button>
        </div>
      </section>
    </div>

    <!-- ═══════════════════════════════════════════════════════════
         SECTION 7 SPECIAL EXPENSES — full-width detail block.
         Sits below the input/breakdown grid (where the right column
         was too narrow to hold the 5-column table) and above the
         scenarios table. Mirrors the PDF worksheet's full-width
         layout for s.7 details.
    ═══════════════════════════════════════════════════════════ -->
    <!-- ─── Undue hardship (s.10) — Schedule II standards-of-living test.
         Appears once any household or hardship input exists. ─── -->
    <section class="ws-s7-fullwidth" v-if="schIIComparison">
      <div class="s7-detail-head">
        <h2 class="ws-s7-fullwidth-title">Undue Hardship — Household Standards of Living (Sch. II)</h2>
      </div>
      <table class="s7-detail-table schii-table">
        <thead>
          <tr><th></th><th>{{ payorName }}'s household</th><th>{{ recipientName }}'s household</th></tr>
        </thead>
        <tbody>
          <tr><td>Household size</td><td>{{ schIIComparison.A.size }}</td><td>{{ schIIComparison.B.size }}</td></tr>
          <tr><td>Adjusted household income</td><td>${{ fmt(schIIComparison.A.income) }}</td><td>${{ fmt(schIIComparison.B.income) }}</td></tr>
          <tr><td>Low-income measure (Sch. II)</td><td>${{ fmt(schIIComparison.A.lim) }}</td><td>${{ fmt(schIIComparison.B.lim) }}</td></tr>
          <tr class="schii-ratio-row"><td><strong>Standard-of-living ratio</strong></td>
            <td><strong>{{ schIIComparison.A.ratio.toFixed(2) }}</strong></td>
            <td><strong>{{ schIIComparison.B.ratio.toFixed(2) }}</strong></td></tr>
        </tbody>
      </table>
      <p class="hint" style="margin: 0.5rem 0 0;">
        <template v-if="schIIComparison.lower === 'A'">
          {{ payorName }}'s household has the lower standard of living.
          A hardship claim by {{ recipientName }} must fail on this test (s.10(3)).
        </template>
        <template v-else-if="schIIComparison.lower === 'B'">
          {{ recipientName }}'s household has the lower standard of living.
          A hardship claim by {{ payorName }} must fail on this test (s.10(3)).
        </template>
        <template v-else>The households have equal standards of living on this test.</template>
        Income adjustments applied: partner and other-adult income added, the child support
        in issue moved between households, support paid to others and claimed hardship
        amounts deducted. Spousal support in issue is not folded in; compare scenarios for that.
        Passing this test does not itself establish hardship (s.10(1) circumstances must also be shown).
      </p>
    </section>

    <section class="ws-s7-fullwidth" v-if="hasS7">
      <div class="s7-detail-head">
        <h2 class="ws-s7-fullwidth-title">Section 7 Special Expenses</h2>
        <span class="s7-detail-sub">Apportioned {{ payorName }} {{ payorPct }}% · {{ recipientName }} {{ recipientPct }}%</span>
      </div>
      <table class="s7-detail-table">
        <thead>
          <tr>
            <th>Expense</th>
            <th class="num" colspan="2">{{ payorName }}<br/><span class="hint-tiny">paid / claimed</span></th>
            <th class="num" colspan="2">{{ recipientName }}<br/><span class="hint-tiny">paid / claimed</span></th>
            <th class="num">Net cost/yr</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in s7Results" :key="i">
            <td>
              <span class="s7-detail-child">{{ row.child }}</span>
              <span class="s7-detail-cat"> — {{ row.category }}</span>
            </td>
            <td class="num">${{ fmt(row.paidByA) }}</td>
            <td class="num s7-claimed">${{ fmt(row.claimedByA) }}</td>
            <td class="num">${{ fmt(row.paidByB) }}</td>
            <td class="num s7-claimed">${{ fmt(row.claimedByB) }}</td>
            <td class="num">${{ fmt(row.netCost) }}</td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td>Annual obligation</td>
            <td class="num" colspan="2">${{ fmt(s7PayorConceptualAnnual) }}<span class="s7-detail-mo"> (${{ fmt(s7PayorConceptualMonthly) }}/mo)</span></td>
            <td class="num" colspan="2">${{ fmt(s7RecipientConceptualAnnual) }}<span class="s7-detail-mo"> (${{ fmt(s7RecipientConceptualMonthly) }}/mo)</span></td>
            <td class="num">${{ fmt(s7TotalNetAnnual) }}</td>
          </tr>
        </tfoot>
      </table>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         SUPPORT SCENARIOS — side-by-side comparison table.
         Always shows 3 default columns (SSAG Low / Mid / High) that update
         live as inputs change. User-saved scenarios append after as frozen
         snapshots, with the slider amount they were saved at.
    ═══════════════════════════════════════════════════════════ -->
    <section class="scenarios scenarios-wrap" v-if="ssagApplicable && ssagRange">
      <div class="scenarios-header">
        <h2>Support Scenarios</h2>
        <span class="scenarios-meta">
          Monthly $ · {{ scenarios.length }} column{{ scenarios.length === 1 ? '' : 's' }}
          <span v-if="displayedScenarioChunks.length > 1"> · {{ displayedScenarioChunks.length }} tables</span>
        </span>
      </div>
      <table v-for="(chunk, chunkIdx) in displayedScenarioChunks" :key="'chunk-' + chunkIdx" class="scenarios-table">
        <colgroup>
          <col class="col-label" />
          <!-- Two sub-columns per scenario (party A, party B). `col-scen-start`
               (the party-A column, odd i) carries the bold accent divider BETWEEN
               scenarios; `col-scen-alt` tints every other scenario group so Low /
               Mid / High read as distinct vertical blocks. Borders live on the
               cols (not the cells) so the header and body grid lines always align. -->
          <col v-for="i in chunk.length * 2" :key="'col-' + i"
               :class="['col-scen', { 'col-scen-start': i % 2 === 1, 'col-scen-alt': Math.floor((i - 1) / 2) % 2 === 1 }]" />
          <col class="col-spacer" />
        </colgroup>
        <thead>
          <tr class="scenario-header-row">
            <th class="row-label-head"></th>
            <th v-for="s in chunk" :key="s.id" colspan="2" class="scenario-name-cell" :class="{ 'is-overridden': s.ssOverridden }">
              <div class="scenario-name-wrap">
                <input type="text" :value="s.name" @input="setScenarioName(s.id, $event.target.value)" class="scenario-name-edit" />
                <button class="btn-remove" @click="removeScenario(s.id)" title="Delete column">×</button>
              </div>
              <!-- SS amount input lives in the header so the lawyer adjusts
                   it up here, then the body table shows the resulting
                   per-party direction (payor in parens, recipient positive).
                   Duration input is in its own body row above Lump Sum. -->
              <div class="scen-inputs">
                <label class="scen-input">
                  <span class="scen-input-label">SS</span>
                  <span class="scen-input-prefix">$</span>
                  <input type="text" inputmode="numeric"
                         :value="fmt(s.ssMonthly)"
                         @input="setScenarioSS(s.id, parseDollarInput($event.target.value))"
                         class="scen-input-amount" />
                  <span class="scen-input-suffix">/mo</span>
                </label>
              </div>
            </th>
            <th class="spacer-cell"></th>
          </tr>
          <tr class="scenario-party-row">
            <th class="row-label-head"></th>
            <template v-for="s in chunk">
              <th class="party-head" :key="s.id + '-p-a'">{{ s.b.a.name }}</th>
              <th class="party-head" :key="s.id + '-p-b'">{{ s.b.b.name }}</th>
            </template>
            <th class="spacer-cell"></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="row-label">Gross Income</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-g-a'">${{ fmt(s.b.a.grossM) }}</td>
              <td :key="s.id + '-g-b'">${{ fmt(s.b.b.grossM) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr>
            <td class="row-label">Taxes and Deductions</td>
            <template v-for="s in chunk">
              <td class="neg" :key="s.id + '-t-a'">({{ fmt(s.b.a.taxesM) }})</td>
              <td class="neg" :key="s.id + '-t-b'">({{ fmt(s.b.b.taxesM) }})</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr>
            <td class="row-label">Benefits and Credits</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-b-a'">${{ fmt(s.b.a.benefitsM) }}</td>
              <td :key="s.id + '-b-b'">${{ fmt(s.b.b.benefitsM) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <!-- Special Expenses (s.7) — cash outflow per party. Placed
               with the other deductions, before Spousal Support
               (mirrors DM's row order). -->
          <tr v-if="hasS7">
            <td class="row-label" title="Each party's actual out-of-pocket payment for s.7 expenses this month. Cash outflow reduces NDI directly.">Special Expenses (s.7)</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-s7p-a'" :class="{ neg: s.b.a.s7PaidM > 0 }">{{ s.b.a.s7PaidM > 0 ? `(${fmt(s.b.a.s7PaidM)})` : '$0' }}</td>
              <td :key="s.id + '-s7p-b'" :class="{ neg: s.b.b.s7PaidM > 0 }">{{ s.b.b.s7PaidM > 0 ? `(${fmt(s.b.b.s7PaidM)})` : '$0' }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <!-- Cash Flow Adjustments — post-tax outflows that reduce
               NDI but don't affect taxable income (SSAG Ch. 6.6).
               Only renders when at least one cfaItem is non-zero. -->
          <tr v-if="(payorCFAAnnual > 0) || (recipientCFAAnnual > 0)">
            <td class="row-label" title="SSAG Ch. 6.6 — post-tax outflows like mandatory pension contributions beyond Schedule III, after-tax disability premiums, or court-ordered support to other dependants.">Cash Flow Adjustments</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-cfa-a'" :class="{ neg: s.b.a.cfaM > 0 }">{{ s.b.a.cfaM > 0 ? `(${fmt(s.b.a.cfaM)})` : '$0' }}</td>
              <td :key="s.id + '-cfa-b'" :class="{ neg: s.b.b.cfaM > 0 }">{{ s.b.b.cfaM > 0 ? `(${fmt(s.b.b.cfaM)})` : '$0' }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <!-- Spousal Support body row — read-only display per party,
               using fmtSigned to show payor's deduction in parens and
               recipient's receipt as positive. The actual SS amount /
               duration controls live in the scenario column header above. -->
          <tr class="ss-row">
            <td class="row-label">Spousal Support</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-ss-a'" :class="{ neg: s.b.a.ssM < 0 }">{{ fmtSigned(s.b.a.ssM) }}</td>
              <td :key="s.id + '-ss-b'" :class="{ neg: s.b.b.ssM < 0 }">{{ fmtSigned(s.b.b.ssM) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr>
            <td class="row-label">Child Support (Table)</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-cs-a'" :class="{ neg: s.b.a.csM < 0 }">{{ fmtSigned(s.b.a.csM) }}</td>
              <td :key="s.id + '-cs-b'" :class="{ neg: s.b.b.csM < 0 }">{{ fmtSigned(s.b.b.csM) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr v-if="hasS7">
            <td class="row-label" title="Settlement between parties to balance the s.7 apportionment. Positive = this party is owed money back; negative = this party owes the other party.">Child Support (s.7 Payment)</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-s7s-a'" :class="{ neg: s.b.a.s7SettlementM < 0 }">{{ fmtSigned(s.b.a.s7SettlementM) }}</td>
              <td :key="s.id + '-s7s-b'" :class="{ neg: s.b.b.s7SettlementM < 0 }">{{ fmtSigned(s.b.b.s7SettlementM) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr class="total-row">
            <td class="row-label">Net Disposable Income (NDI)</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-ndi-a'">${{ fmt(s.b.a.ndiM) }}</td>
              <td :key="s.id + '-ndi-b'">${{ fmt(s.b.b.ndiM) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr class="pie-row">
            <td class="row-label"><span class="row-label-text">NDI Share</span></td>
            <td v-for="s in chunk" :key="s.id + '-pie'" colspan="2" class="pie-cell">
              <div class="pie-wrap">
                <svg class="ndi-pie" viewBox="0 0 100 100" role="img" :aria-label="`${s.b.a.name} ${s.b.a.pct.toFixed(1)}%, ${s.b.b.name} ${s.b.b.pct.toFixed(1)}%`">
                  <circle cx="50" cy="50" r="48" fill="#3F6772" />
                  <path v-if="piePath(s.b.b.pct)" :d="piePath(s.b.b.pct)" fill="#e8b340" />
                </svg>
                <div class="pie-labels">
                  <span class="pie-label">
                    <span class="pie-swatch" style="background:#3F6772"></span>
                    {{ s.b.a.name }} {{ s.b.a.pct.toFixed(1) }}%
                  </span>
                  <span class="pie-label">
                    <span class="pie-swatch" style="background:#e8b340"></span>
                    {{ s.b.b.name }} {{ s.b.b.pct.toFixed(1) }}%
                  </span>
                </div>
              </div>
            </td>
            <td class="spacer-cell"></td>
          </tr>
          <tr>
            <td class="row-label" title="Federal CSG s.7(2): special and extraordinary expenses are shared in proportion to each parent's income, adjusted for spousal support paid/received. The dollar settlement above already reflects this apportionment.">CSG Special Expenses Apportioning %</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-s7-a'">{{ s.b.a.s7Pct.toFixed(1) }}%</td>
              <td :key="s.id + '-s7-b'">{{ s.b.b.s7Pct.toFixed(1) }}%</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr>
            <td class="row-label" title="Net cost (payor) and net benefit (recipient) of spousal support after tax, since SS is deductible to the payor and taxable to the recipient.">After-tax Cost/Benefit of Spousal Support</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-aftertax-a'" :class="{ neg: s.b.a.ssAfterTaxM < 0 }">{{ s.b.a.ssAfterTaxM < 0 ? `(${fmt(-s.b.a.ssAfterTaxM)})` : `$${fmt(s.b.a.ssAfterTaxM)}` }}</td>
              <td :key="s.id + '-aftertax-b'" :class="{ neg: s.b.b.ssAfterTaxM < 0 }">{{ s.b.b.ssAfterTaxM < 0 ? `(${fmt(-s.b.b.ssAfterTaxM)})` : `$${fmt(s.b.b.ssAfterTaxM)}` }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <!-- ─── Life Insurance section ─────────────────────────────
               To secure both child support and spousal support payments,
               the recipient typically requires the payor to maintain life
               insurance for the NPV of the obligation. Three rows:
                 • Child Support (CSG Table only) — NPV over CS duration
                 • [Recipient]'s after-tax benefit — NPV of after-tax SS stream
                 • Life Insurance Estimate — sum of the two
               Values appear only on the recipient's side; payor side is $0
               (life insurance is on the payor's life, recipient is the
               beneficiary). -->
          <tr class="section-divider">
            <td colspan="100%" class="section-divider-cell">Life Insurance (to secure support)</td>
          </tr>
          <tr>
            <td class="row-label" title="NPV of CSG Table child support over the duration kids remain dependent (until last child finishes high school).">Child Support (CSG Table only)</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-li-cs-a'">${{ fmt(lifeInsuranceCSValueA(s)) }}</td>
              <td :key="s.id + '-li-cs-b'">${{ fmt(lifeInsuranceCSValueB(s)) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr>
            <td class="row-label" title="NPV of the after-tax spousal support benefit being secured. Shown in the payor's column — life insurance is on the payor's life with the recipient as beneficiary.">After-tax spousal support</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-li-ss-a'">${{ fmt(lifeInsuranceSSValueA(s)) }}</td>
              <td :key="s.id + '-li-ss-b'">${{ fmt(lifeInsuranceSSValueB(s)) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr class="total-row">
            <td class="row-label" title="Total life insurance face value needed to secure both child support and the after-tax benefit of spousal support over their respective durations, discounted at 1.81%.">Life Insurance Estimate</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-li-total-a'">${{ fmt(lifeInsuranceCSValueA(s) + lifeInsuranceSSValueA(s)) }}</td>
              <td :key="s.id + '-li-total-b'">${{ fmt(lifeInsuranceCSValueB(s) + lifeInsuranceSSValueB(s)) }}</td>
            </template>
            <td class="spacer-cell"></td>
          </tr>

          <!-- ─── Spousal Support Lump Sum section ─────────────────── -->
          <tr class="section-divider">
            <td colspan="100%" class="section-divider-cell">Spousal Support Lump Sum (NPV)</td>
          </tr>
          <!-- Duration input row — drives the lump-sum NPV math below.
               Defaults to the slider value (ssagSelectedDuration);
               per-column overrides let the lawyer model "5 yr vs 10 yr"
               on the same SS amount. -->
          <tr class="ss-row">
            <td class="row-label">Duration (years)</td>
            <td v-for="s in chunk" :key="s.id + '-dur'" colspan="2" class="ss-input-cell">
              <div class="ss-input-wrap">
                <input type="number" min="0" step="0.25"
                       :value="s.durationYears != null ? Number(s.durationYears).toFixed(2).replace(/\.?0+$/, '') : ''"
                       @input="setScenarioDuration(s.id, $event.target.value)"
                       class="ss-input dur-input" />
                <span class="ss-suffix">yr{{ s.durationYears === 1 ? '' : 's' }}</span>
                <span v-if="ssagRange && ssagRange.indefinite && !s.durationOverridden" class="dur-indef-tag" title="Marriage ≥ 20 years — duration is indefinite under SSAG">∞</span>
              </div>
            </td>
            <td class="spacer-cell"></td>
          </tr>
          <tr class="total-row">
            <td class="row-label" title="Net Present Value of the after-tax SS stream over the chosen duration. Discount rate 1.81% (Long-Term Government of Canada Real Return Bond — already inflation-adjusted). Payments and tax rates assumed constant. Not discounted for recipient's life expectancy. Lump sum payments are non-deductible to payor and non-taxable to recipient.">Lump Sum (NPV @ 1.81%)</td>
            <template v-for="s in chunk">
              <td :key="s.id + '-lumpsum-a'" :class="{ neg: lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears) < 0 }">
                {{ lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears) < 0
                    ? `($${fmt(-lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears))})`
                    : `$${fmt(lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears))}` }}
              </td>
              <td :key="s.id + '-lumpsum-b'" :class="{ neg: lumpSumValue(s.b.b.ssAfterTaxM, s.durationYears) < 0 }">
                {{ lumpSumValue(s.b.b.ssAfterTaxM, s.durationYears) < 0
                    ? `($${fmt(-lumpSumValue(s.b.b.ssAfterTaxM, s.durationYears))})`
                    : `$${fmt(lumpSumValue(s.b.b.ssAfterTaxM, s.durationYears))}` }}
              </td>
            </template>
            <td class="spacer-cell"></td>
          </tr>
          <tr class="total-row">
            <td class="row-label" title="Average of the payor's after-tax lump-sum cost and the recipient's after-tax lump-sum benefit. Useful as a 'split-the-difference' negotiation figure since the two figures differ due to different marginal tax rates between the parties.">Lump Sum Midpoint</td>
            <td v-for="s in chunk" :key="s.id + '-lumpmid'" colspan="2" class="ss-input-cell">
              ${{ fmt(lumpSumMidpoint(s)) }}
            </td>
            <td class="spacer-cell"></td>
          </tr>
          <!-- ─── Apply to Agreement row ─────────────────────────────
               One button per scenario column. Click writes this scenario's
               numbers (CS, s.7, SS periodic + lump, per-party income) into
               LegalApp's agreement form via the `apply` event. The
               "Currently in agreement" badge marks whichever scenario was
               most recently applied — survives panel close/reopen via
               the update-state event. -->
          <tr class="apply-row" v-if="applyToAgreementEnabled">
            <td class="row-label apply-row-label">Record in agreement</td>
            <td v-for="s in chunk" :key="s.id + '-apply'" colspan="2" class="apply-cell">
              <!-- Opens a confirm dialog where the lawyer picks how spousal
                   support is recorded (monthly vs lump sum) and reviews what
                   will be written, before anything is sent back to LegalApp.
                   DIVII PATCH: disabled for read-only roles (canRecordProp);
                   badge reads the host's record when it supplies one. -->
              <button
                type="button"
                class="apply-btn"
                :class="{ 'apply-btn--current': appliedScenarioId === s.id }"
                :disabled="!canRecordProp"
                @click="openApplyDialog(s)"
                :title="!canRecordProp
                  ? 'Your role on this agreement cannot record into the builder.'
                  : appliedScenarioId === s.id
                    ? ('Currently in the agreement as ' + (appliedMode === 'lumpSum' ? 'a lump sum' : 'monthly support') + '. Click to review or record again.')
                    : 'Review these numbers and record them in the agreement builder.'"
              >
                <span v-if="appliedScenarioId === s.id" class="apply-btn-badge">✓ In agreement · {{ appliedMode === 'lumpSum' ? 'lump sum' : 'monthly' }}</span>
                <span v-else>→ Record in agreement</span>
              </button>
            </td>
            <td class="spacer-cell"></td>
          </tr>
        </tbody>
      </table>
      <!-- Methodology note for the lump-sum row, shown once below the tables -->
      <p class="lump-sum-note">
        <strong>Lump Sum methodology:</strong> NPV of after-tax SS stream over chosen duration, discounted at 1.81%
        (Long-Term GoC Real Return Bond — real rate, so payments aren't separately inflated). Payments and tax
        rates assumed constant; not discounted for recipient's life expectancy. Lump-sum payments are
        non-deductible to the payor and non-taxable to the recipient.
      </p>
      <!-- CGEB methodology note — shown when the new federal grocery credit is in play. -->
      <p class="lump-sum-note" v-if="cgebActive">
        <strong>Canada Groceries &amp; Essentials Benefit (CGEB):</strong> replaces the GST/HST credit from
        July 2026 (Bill C-19). The 2026&ndash;27 figure shown includes both the recurring +25% increase
        <em>and</em> the one-time top-up paid June 2026 &mdash; matching the recipient's actual 2026&ndash;27
        benefit. Note the one-time top-up does <em>not</em> recur, so the recipient's
        benefit &mdash; and the support that flows from it &mdash; may warrant review for 2027 onward.
      </p>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         RESTRUCTURING (SSAG Ch. 10) — full-width, below scenarios.
         Opt-in: a one-line disclosure that expands the lump-sum +
         monthly back-solver. Sits here because it's a what-if on the
         chosen award, downstream of the scenarios comparison.
    ═══════════════════════════════════════════════════════════ -->
    <section class="ws-restructure-fullwidth" v-if="ssagApplicable && ssagRange">
      <button type="button" class="restructure-toggle" @click="showRestructure = !showRestructure"
              :aria-expanded="showRestructure ? 'true' : 'false'">
        <span class="restructure-caret">{{ showRestructure ? '▾' : '▸' }}</span>
        ⚖ Restructure (lump sum + monthly)
      </button>
      <!-- Empty state — the toggle is visible but there's no non-zero SS
           amount to restructure (basis scenario is $0/mo, e.g. SSAG range
           collapsed to $0 or a $0 what-if column picked as basis). Without
           this the panel expands to nothing, which reads as broken. -->
      <div class="restructure-panel restructure-empty" v-if="showRestructure && !restructurePackage">
        No spousal support amount to restructure — the selected basis scenario
        is $0/month. Restructuring needs a non-zero monthly award to split into
        a lump sum + reduced monthly.
      </div>
      <div class="restructure-panel" v-if="showRestructure && restructurePackage">
        <p class="restructure-intro">
          Splits the chosen scenario
          (<strong>{{ restructurePackage.basisLabel }} — ${{ fmt(restructurePackage.basisAmount) }}/mo</strong>)
          into an up-front lump sum plus a smaller monthly, keeping the same
          after-tax value. The lump sum is tax-free; the monthly remains
          taxable/deductible.
        </p>
        <div class="restructure-inputs">
          <label class="restructure-field">
            Scenario
            <select class="restructure-select" v-model="restructureScenarioId">
              <option v-for="s in displayedScenarios" :key="s.id" :value="s.id">
                {{ s.name }} (${{ fmt(s.ssMonthly) }}/mo)
              </option>
            </select>
          </label>
          <label class="restructure-field">
            Lump sum (paid now)
            <span class="dollar-prefix">$</span><input type="number" class="amount-input"
                   v-model.number="restructureLumpSum" min="0" step="1000" />
          </label>
          <label class="restructure-field">
            Stream duration
            <input type="number" class="amount-input"
                   :value="restructurePackage.streamDur"
                   @input="restructureDurationYears = Number($event.target.value)"
                   min="0" step="0.5" /><span class="amount-suffix">yrs</span>
          </label>
          <label class="restructure-field">
            Discount rate
            <input type="number" class="amount-input"
                   v-model.number="restructureDiscountRate" min="0" step="0.01" /><span class="amount-suffix">%</span>
          </label>
        </div>
        <div class="restructure-result">
          <div class="restructure-result-main">
            <span class="restructure-result-label">Resulting monthly</span>
            <span class="restructure-result-val">${{ fmt(restructurePackage.monthly) }}/mo</span>
          </div>
          <div class="restructure-pkg">
            Package: <strong>${{ fmt(restructurePackage.lump) }}</strong> lump
            + <strong>${{ fmt(restructurePackage.monthly) }}/mo</strong>
            for {{ restructurePackage.streamDur.toFixed(2).replace(/\.?0+$/, '') }} yrs
            ≈ <strong>${{ fmt(restructurePackage.target) }}</strong> after-tax value.
          </div>
          <div class="restructure-badge" :class="restructurePackage.lumpExceedsAward ? 'out' : 'in'">
            <template v-if="restructurePackage.lumpExceedsAward">
              ⚠ Lump sum alone (${{ fmt(restructurePackage.lump) }}) exceeds the selected award's
              total value (${{ fmt(restructurePackage.target) }}) — reduce the lump sum.
            </template>
            <template v-else>
              ✓ Preserves the selected award's overall after-tax value
              (${{ fmt(restructurePackage.target) }}).
              <span v-if="!restructurePackage.withinRange" class="restructure-subnote">
                The ${{ fmt(restructurePackage.monthly) }}/mo periodic portion sits
                {{ restructurePackage.monthly < ssagRange.low ? 'below' : 'above' }} the standalone
                SSAG range (${{ fmt(ssagRange.low) }}–${{ fmt(ssagRange.high) }}) — expected when part
                of the award is shifted to a lump sum or the duration is changed. Restructuring is
                permitted under SSAG Ch. 10 provided the overall amount stays within range.
              </span>
            </template>
          </div>
          <p class="restructure-note">
            Full lump-sum option: <strong>${{ fmt(restructurePackage.target) }}</strong>
            (entire award paid up front). Lump value = midpoint of payor's after-tax
            cost and recipient's after-tax benefit, present-valued at {{ restructureDiscountRate }}%.
          </p>
        </div>
      </div>
    </section>
  </div>
  <!-- ═════════════════════ END WORKSPACE MODE ═════════════════════ -->
  </div>
  <!-- ─── END MEANS TAB ─── -->

  <!-- ═══════════════════════════════════════════════════════════
       NEEDS TAB — party budgets (Form F8 Part 2 aligned).
       Top-level: usable without going through the calculator.
  ═══════════════════════════════════════════════════════════ -->
  <section v-show="workspaceTab === 'needs'" class="ws-needs">
      <p class="ws-tab-intro">
        Enter each party's expected monthly expenses <strong>after
        separation</strong>, using best estimates where the exact amount is not
        yet known. The schedule follows BC Form F8 (Financial Statement,
        Part 2), so these numbers can carry into a financial statement later.
        Enter amounts at whatever frequency you know them and they are
        converted to monthly. Lines the calculator already knows (taxes, CPP,
        EI, pension deductions, prior support) are filled in automatically.
      </p>

      <div class="budget-colhead">
        <span class="budget-colhead-item">Expense</span>
        <span class="budget-colhead-amt">{{ payorName }}</span>
        <span class="budget-colhead-amt">{{ recipientName }}</span>
        <span class="budget-colhead-freq">Frequency</span>
      </div>

      <div v-for="g in F8_BUDGET_GROUPS" :key="g.key" class="budget-group">
        <div class="budget-group-head">
          <span class="budget-group-name">{{ g.label }}<span v-if="g.hint" class="budget-group-hint"> ({{ g.hint }})</span></span>
          <span class="budget-group-sub">
            ${{ fmt(budgetGroupSubtotal(g.key, 'a')) }} · ${{ fmt(budgetGroupSubtotal(g.key, 'b')) }}
          </span>
        </div>

        <!-- Auto rows (from the calculation) -->
        <div v-for="r in (g.key === 'compulsory' ? budgetAutoRows.compulsory : g.key === 'supportOthers' ? budgetAutoRows.supportOthers : [])"
             :key="g.key + r.label" class="budget-row budget-row-auto">
          <span class="budget-line-label">{{ r.label }} <span class="budget-auto-chip">from calculation</span></span>
          <span class="budget-cell-auto">${{ fmt(Math.round(r.a)) }}</span>
          <span class="budget-cell-auto">${{ fmt(Math.round(r.b)) }}</span>
          <span class="budget-cell-freq-auto">monthly</span>
        </div>
        <div v-if="g.key === 'compulsory' && !budgetAutoRows.compulsory.length" class="budget-row budget-row-auto">
          <span class="budget-line-label budget-empty-note">Filled automatically once incomes are entered on the Means tab.</span>
        </div>

        <!-- Standard F8 lines -->
        <div v-for="ln in g.lines" :key="g.key + '|' + ln" class="budget-row">
          <span class="budget-line-label">{{ ln }}</span>
          <input type="number" min="0" class="budget-amt" placeholder="0"
                 v-model.number="budgetLines[g.key + '|' + ln].a" />
          <input type="number" min="0" class="budget-amt" placeholder="0"
                 v-model.number="budgetLines[g.key + '|' + ln].b" />
          <select class="budget-freq" v-model="budgetLines[g.key + '|' + ln].freq">
            <option v-for="f in BUDGET_FREQS" :key="f.value" :value="f.value">{{ f.label }}</option>
          </select>
          <span></span>
          <!-- Converted-to-monthly echo: makes non-monthly entries self-evident -->
          <div v-if="budgetEcho(budgetLines[g.key + '|' + ln])" class="budget-echo">
            {{ budgetEcho(budgetLines[g.key + '|' + ln]) }}
          </div>
        </div>

        <!-- Custom "Other [specify]" rows -->
        <div v-for="(c, i) in (budgetCustom[g.key] || [])" :key="c.id" class="budget-row">
          <input type="text" class="budget-custom-label" placeholder="Describe this expense…" v-model="c.label" />
          <input type="number" min="0" class="budget-amt" placeholder="0" v-model.number="c.a" />
          <input type="number" min="0" class="budget-amt" placeholder="0" v-model.number="c.b" />
          <select class="budget-freq" v-model="c.freq">
            <option v-for="f in BUDGET_FREQS" :key="f.value" :value="f.value">{{ f.label }}</option>
          </select>
          <button type="button" class="budget-remove" @click="removeBudgetCustomRow(g.key, i)" title="Remove line">×</button>
          <div v-if="budgetEcho(c)" class="budget-echo">{{ budgetEcho(c) }}</div>
        </div>
        <button v-if="g.key !== 'compulsory'" type="button" class="budget-add" @click="addBudgetCustomRow(g.key)">
          + Other [specify]
        </button>
      </div>

      <!-- Totals + NDI comparison -->
      <div class="budget-summary">
        <div class="budget-summary-row">
          <span class="budget-summary-label">Total monthly expenses (Form F8)</span>
          <span class="budget-summary-amt">${{ fmt(budgetTotalMonthly.a) }}</span>
          <span class="budget-summary-amt">${{ fmt(budgetTotalMonthly.b) }}</span>
        </div>
        <div class="budget-summary-row budget-summary-living">
          <span class="budget-summary-label">Monthly living expenses
            <span class="budget-summary-note">(entered lines only; excludes taxes, CPP/EI, and support already inside the net-income figures)</span>
          </span>
          <span class="budget-summary-amt">${{ fmt(budgetLivingMonthly.a) }}</span>
          <span class="budget-summary-amt">${{ fmt(budgetLivingMonthly.b) }}</span>
        </div>

        <div v-if="ssagRange && needsComparison.hasBudgets" class="budget-vs-ndi">
          <div class="budget-vs-head">Budget vs. net disposable income across the range</div>
          <table class="budget-vs-table">
            <thead>
              <tr>
                <th></th>
                <th>Support</th>
                <th colspan="2">{{ payorName }}</th>
                <th colspan="2">{{ recipientName }}</th>
              </tr>
              <tr class="budget-vs-subhead">
                <th></th><th>$/mo</th>
                <th>NDI</th><th>vs budget</th>
                <th>NDI</th><th>vs budget</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in needsComparison.rows" :key="row.k">
                <td class="budget-vs-point">{{ row.k === 'low' ? 'Low' : row.k === 'mid' ? 'Mid' : 'High' }}</td>
                <td>${{ fmt(row.ss) }}</td>
                <td>${{ fmt(Math.round(row.a.ndi)) }}</td>
                <td :class="row.a.delta >= 0 ? 'budget-ok' : 'budget-short'">
                  {{ row.a.delta >= 0 ? '+' : '−' }}${{ fmt(Math.abs(row.a.delta)) }}
                </td>
                <td>${{ fmt(Math.round(row.b.ndi)) }}</td>
                <td :class="row.b.delta >= 0 ? 'budget-ok' : 'budget-short'">
                  {{ row.b.delta >= 0 ? '+' : '−' }}${{ fmt(Math.abs(row.b.delta)) }}
                </td>
              </tr>
            </tbody>
          </table>
          <p class="budget-vs-note">
            A shortfall that persists at the top of the range quantifies need; a budget met
            at the low end suggests the low end suffices; a payor who cannot cover their own
            budget signals a limit on ability to pay. The Recommendation tab weighs these
            automatically.
          </p>
        </div>
        <p v-else-if="!ssagRange" class="budget-vs-note">
          Enter incomes on the Means tab to compare these budgets against each party's
          net disposable income across the support range.
        </p>
      </div>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         CIRCUMSTANCES TAB — the SSAG factor questions
    ═══════════════════════════════════════════════════════════ -->
    <section v-show="workspaceTab === 'circumstances'" class="ws-circumstances">
      <p class="ws-tab-intro">
        Plain-language questions about the relationship and each party's situation,
        drawn from the SSAG location-in-range factors. The answers, together with the
        budgets and the calculation, drive the Recommendation tab.
      </p>
      <ssag-range-advisor v-if="ssagRange" :context="advisorContext" :answers="advisorAnswers" mode="questions" />
      <p v-else class="ws-tab-empty">Complete the calculation on the Means tab first.</p>
    </section>

    <!-- ═══════════════════════════════════════════════════════════
         RECOMMENDATION TAB — needs + means + circumstances together
    ═══════════════════════════════════════════════════════════ -->
    <section v-show="workspaceTab === 'recommendation' && canViewRecommendation" class="ws-recommendation">
      <template v-if="ssagRange">
        <div v-if="needsComparison.hasBudgets" class="rec-budget-strip">
          <div class="budget-vs-head">Needs check (from the budgets)</div>
          <table class="budget-vs-table">
            <thead>
              <tr class="budget-vs-subhead">
                <th></th><th>Support $/mo</th>
                <th>{{ payorName }} vs budget</th>
                <th>{{ recipientName }} vs budget</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in needsComparison.rows" :key="row.k">
                <td class="budget-vs-point">{{ row.k === 'low' ? 'Low' : row.k === 'mid' ? 'Mid' : 'High' }}</td>
                <td>${{ fmt(row.ss) }}</td>
                <td :class="row.a.delta >= 0 ? 'budget-ok' : 'budget-short'">
                  {{ row.a.delta >= 0 ? 'covered, +' : 'short by ' }}${{ fmt(Math.abs(row.a.delta)) }}
                </td>
                <td :class="row.b.delta >= 0 ? 'budget-ok' : 'budget-short'">
                  {{ row.b.delta >= 0 ? 'covered, +' : 'short by ' }}${{ fmt(Math.abs(row.b.delta)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p v-else class="ws-tab-intro">
          No budgets entered yet. The recommendation below rests on the calculation and
          the question answers; add budgets on the Needs tab to ground it in each
          party's actual expenses.
        </p>
        <ssag-range-advisor :context="advisorContext" :answers="advisorAnswers" mode="result" />
      </template>
      <p v-else class="ws-tab-empty">Complete the calculation on the Means tab first (incomes and SSAG applicability).</p>
    </section>

  <!-- ═════════════════════════════════════════════════════════════════
       PRINTABLE WORKSHEET — hidden on screen, visible only when the
       lawyer clicks "Download worksheet" and the print dialog opens.

       Pagination plan:
         • Page 1: header strip + Matter facts + Income tables + Children
                   + first scenario chunk (up to 3 columns)
         • Pages 2+: continuation header + next 3 scenarios per page
         • Last page: methodology footer

       Each scenario column shows the full breakdown (gross → taxes →
       benefits → CS → NDI → s.7 → after-tax → life insurance → lump
       sum) plus an NDI-share pie chart, mirroring what the lawyer sees
       on screen. Lawyer picks "Save as PDF" in the print dialog. ═══ -->
  <div class="print-worksheet" aria-hidden="true">

    <!-- ════════════ Page 1 ════════════ -->
    <section class="worksheet-page">
      <!-- Header strip: logo left, case meta right, teal accent rule. -->
      <header class="ws-header">
        <img src="/divii-logo.png" alt="Divii" class="ws-logo" />
        <div class="ws-header-meta">
          <div class="ws-case-name">{{ payorName }} &amp; {{ recipientName }}</div>
          <div class="ws-doc-title">Support Calculation Worksheet</div>
          <div class="ws-date">{{ formattedTodayDate }} · {{ provinceName }} · Tax year {{ taxYear }}</div>
        </div>
      </header>

      <!-- ═══════════════════════════════════════════════════════════════
           TOP SECTION — two-column layout
           Left: Calculation Input (parties, income line items, dates, options)
           Right: Cautions + Child Support Guidelines + Spousal Support Advisory
                  Guidelines mini-tables
      ═══════════════════════════════════════════════════════════════ -->
      <div class="ws-page-grid">

        <!-- ─── LEFT: Calculation Input ─── -->
        <div class="ws-input-col">
          <h3 class="ws-section-title">Calculation Input</h3>

          <!-- Each party gets a compact card: name, age, residency, then
               income line items in a tight two-col table. -->
          <div class="ws-input-party">
            <div class="ws-input-party-head">
              <span class="ws-input-name">{{ payorName }}</span>
              <span class="ws-input-meta-line">
                <span v-if="payorAgeAtSeparation != null">{{ payorAgeAtSeparation }}</span><span v-if="payorAgeAtSeparation != null && effectiveProvince">, </span>Resident of {{ effectiveProvince }}
              </span>
            </div>
            <table class="ws-input-income">
              <tbody>
                <tr v-for="item in payorItemsRaw" :key="item.id">
                  <td>{{ incomeTypeMeta(item.typeId) && incomeTypeMeta(item.typeId).label || item.typeId }}</td>
                  <td class="num">${{ fmt(item.amount) }}</td>
                </tr>
                <tr v-if="!payorItemsRaw.length" class="empty"><td colspan="2">— no income entered —</td></tr>
              </tbody>
              <tfoot>
                <tr><td>Guidelines Income</td><td class="num">${{ fmt(payorIncome) }}</td></tr>
              </tfoot>
            </table>
            <div class="ws-input-note" v-if="edcClaim === 'none' || (edcClaim === 'B' && (sharedChildren.length || soleBChildren.length))">
              Dependent credit not claimed by {{ payorName }}
            </div>
          </div>

          <div class="ws-input-party">
            <div class="ws-input-party-head">
              <span class="ws-input-name">{{ recipientName }}</span>
              <span class="ws-input-meta-line">
                <span v-if="recipientAgeAtSeparation != null">{{ recipientAgeAtSeparation }}</span><span v-if="recipientAgeAtSeparation != null && effectiveProvince">, </span>Resident of {{ effectiveProvince }}
              </span>
            </div>
            <table class="ws-input-income">
              <tbody>
                <tr v-for="item in recipientItemsRaw" :key="item.id">
                  <td>{{ incomeTypeMeta(item.typeId) && incomeTypeMeta(item.typeId).label || item.typeId }}</td>
                  <td class="num">${{ fmt(item.amount) }}</td>
                </tr>
                <tr v-if="!recipientItemsRaw.length" class="empty"><td colspan="2">— no income entered —</td></tr>
              </tbody>
              <tfoot>
                <tr><td>Guidelines Income</td><td class="num">${{ fmt(recipientIncome) }}</td></tr>
              </tfoot>
            </table>
          </div>

          <!-- Compact metadata block — children, dates, options -->
          <div class="ws-input-meta-block">
            <div class="ws-input-row" v-if="children.length">
              <span class="ws-input-label">Children</span>
              <span class="ws-input-value">
                <span v-for="(c, i) in children" :key="i">{{ c.name || '—' }}<span class="ws-input-aux"> ({{
                  c.parenting === 'primaryA' ? 'with ' + payorName :
                  c.parenting === 'primaryB' ? 'with ' + recipientName :
                  c.parenting === 'shared' ? 'shared' : '—' }})</span><span v-if="i < children.length - 1">, </span></span>
              </span>
            </div>
            <div class="ws-input-row" v-if="hasS7">
              <span class="ws-input-label">s.7 Apportioning</span>
              <span class="ws-input-value">
                {{ payorName }} {{ payorPct }}% · {{ recipientName }} {{ recipientPct }}%
              </span>
            </div>
            <div class="ws-input-row" v-if="cohabitationDate || separationDate">
              <span class="ws-input-label">Relationship Dates</span>
              <span class="ws-input-value">
                Cohab. {{ formattedCohabitationDate || '—' }} · Sep. {{ formattedSeparationDate || '—' }}
                <span v-if="yearsMarried" class="ws-input-aux">({{ relationshipLengthLabel }})</span>
              </span>
            </div>
            <div class="ws-input-row">
              <span class="ws-input-label">Calculation/Tax Year</span>
              <span class="ws-input-value">{{ taxYear }}</span>
            </div>
          </div>

          <!-- ─── s.7 Special Expenses detail (DM-style per-expense list)
               Columns: each parent's CONCEPTUAL share of the net cost (their
               apportionment % × net cost). This matches what's deducted from
               NDI on the scenarios table below, and is the number lawyers
               actually need for drafting (who owes what each year).
               NOT shown: cash-settlement amounts (who owes whom AFTER
               accounting for receipts already paid). Those can swing wildly
               negative when one parent has fronted a big lump sum, which
               confuses more than it clarifies. ─── -->
          <div class="ws-s7-block" v-if="hasS7">
            <div class="ws-s7-head">
              <span>Section 7 Special Expenses</span>
              <span class="ws-s7-sub">Apportioned {{ payorName }} {{ payorPct }}% · {{ recipientName }} {{ recipientPct }}%</span>
            </div>
            <!-- DM-style: per-expense paid/claimed for each party.
                 The actual tax benefit shows up in each party's Taxes
                 line in the scenarios table below; the apportioned
                 settlement shows in the Child Support (s.7 Payment) row. -->
            <table class="ws-s7-table">
              <thead>
                <tr>
                  <th>Expense</th>
                  <th class="num">{{ payorName }}<br/><span class="ws-s7-sub">paid / claimed</span></th>
                  <th class="num">{{ recipientName }}<br/><span class="ws-s7-sub">paid / claimed</span></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, i) in s7Results" :key="i">
                  <td>
                    <span class="ws-s7-child">{{ row.child }}</span>
                    <span class="ws-s7-cat">— {{ row.category }}</span>
                  </td>
                  <td class="num">${{ fmt(row.paidByA) }} / ${{ fmt(row.claimedByA) }}</td>
                  <td class="num">${{ fmt(row.paidByB) }} / ${{ fmt(row.claimedByB) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr>
                  <td>Annual obligation</td>
                  <td class="num">${{ fmt(s7PayorConceptualAnnual) }}<span class="ws-s7-mo"> (${{ fmt(s7PayorConceptualMonthly) }}/mo)</span></td>
                  <td class="num">${{ fmt(s7RecipientConceptualAnnual) }}<span class="ws-s7-mo"> (${{ fmt(s7RecipientConceptualMonthly) }}/mo)</span></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- ─── RIGHT: Cautions + CSG + SSAG ─── -->
        <div class="ws-output-col">

          <!-- Cautions and Overrides — only when at least one fires -->
          <div class="ws-output-card ws-output-cautions"
               v-if="payorIncome > 150000 || payorIncome > 350000 || recipientIncome > 350000 || sharedChildren.length || (includedChildren.length && includedChildren.some(c => ageAtDate(c.dob, separationDate) >= aom))">
            <div class="ws-output-card-head">Cautions and Overrides</div>
            <ul class="ws-cautions-list">
              <li v-if="payorIncome > 150000 && payorIncome <= 350000">
                <strong>Child Support (Table):</strong> {{ payorName }}'s income over $150,000;
                CSG Table Amount may be inappropriate (s.4).
              </li>
              <li v-if="payorIncome > 350000 || recipientIncome > 350000">
                <strong>SSAG ceiling:</strong> payor income over $350,000;
                SSAG range is illustrative — court has discretion above the ceiling.
              </li>
              <li v-if="includedChildren.length && includedChildren.some(c => ageAtDate(c.dob, separationDate) >= aom)">
                <strong>Child Support (Table):</strong> one or more children at age of majority or older;
                CSG Table Amount may be inappropriate (s.3(2)).
              </li>
              <li v-if="sharedChildren.length">
                <strong>Shared custody (s.9):</strong> set-off is one factor; court also considers
                increased costs and each party's condition / means / needs.
              </li>
              <li v-if="rule65Triggered">
                <strong>Rule of 65:</strong> recipient age {{ ssagRecipientAgeAtSeparation }} + marriage
                {{ relationshipLengthLabel }} ≥ 65 → SSAG duration indefinite (subject to review).
              </li>
            </ul>
          </div>

          <!-- Child Support Guidelines (Monthly) -->
          <div class="ws-output-card" v-if="includedChildren.length">
            <div class="ws-output-card-head">Child Support Guidelines (Monthly)</div>
            <table class="ws-mini-table">
              <thead>
                <tr>
                  <th></th>
                  <th>{{ payorName }}</th>
                  <th>{{ recipientName }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Annual Guidelines Income</td>
                  <td class="num">${{ fmt(payorIncome) }}</td>
                  <td class="num">${{ fmt(recipientIncome) }}</td>
                </tr>
                <tr v-if="sharedChildren.length">
                  <td>CSG Table Amount</td>
                  <td class="num">${{ fmt(sharedTableA) }}</td>
                  <td class="num">${{ fmt(sharedTableB) }}</td>
                </tr>
                <tr v-else>
                  <td>CSG Table Amount</td>
                  <td class="num">${{ fmt(csPayorToRecipient) }}</td>
                  <td class="num">${{ fmt(csRecipientToPayor) }}</td>
                </tr>
                <tr v-if="sharedChildren.length">
                  <td>CSG Table Amount Offset</td>
                  <td class="num">${{ fmt(Math.abs(sharedTableA - sharedTableB)) }}</td>
                  <td class="num">—</td>
                </tr>
                <tr class="ws-mini-total">
                  <td>Child Support (Table)</td>
                  <td class="num">${{ netCSPayer === payorName ? fmt(netCS) : '—' }}</td>
                  <td class="num">${{ netCSPayer === recipientName ? fmt(netCS) : '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Spousal Support Advisory Guidelines (Monthly) -->
          <div class="ws-output-card" v-if="ssagApplicable && ssagRange">
            <div class="ws-output-card-head">Spousal Support Advisory Guidelines (Monthly)</div>
            <div class="ws-ssag-meta">
              <div><strong>Length of marriage/cohabitation:</strong> {{ relationshipLengthLabel }}</div>
              <div><strong>Recipient's age at separation:</strong> {{ ssagRecipientAgeAtSeparation }} years</div>
            </div>
            <div class="ws-ssag-formula">"{{ formulaLabel }}" Formula</div>
            <table class="ws-mini-table ws-ssag-range">
              <thead>
                <tr>
                  <th>Low</th>
                  <th>Mid</th>
                  <th>High</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="num">${{ fmt(ssagRange.low) }}</td>
                  <td class="num">${{ fmt(ssagRange.mid) }}</td>
                  <td class="num">${{ fmt(ssagRange.high) }}</td>
                </tr>
              </tbody>
            </table>
            <p class="ws-ssag-note">
              <strong>${{ fmt(ssagRange.low) }}–${{ fmt(ssagRange.high) }}/mo</strong> for
              <strong v-if="ssagRange.indefinite">indefinite</strong>
              <strong v-else>{{ ssagRange.durationLow.toFixed(1) }}–{{ ssagRange.durationHigh.toFixed(1) }} yr</strong>,
              subject to variation.
            </p>
          </div>

        </div>
      </div>

      <!-- ─── Scenarios chunk 1 ─── -->
      <section class="ws-section ws-section--scenarios" v-if="displayedScenarioChunks.length">
        <h3 class="ws-section-title">
          Support Scenarios
          <span v-if="displayedScenarioChunks.length > 1" class="ws-section-subtitle">
            Page 1 of {{ displayedScenarioChunks.length }} · {{ displayedScenarios.length }} scenarios total
          </span>
        </h3>
        <div class="ws-scenarios-wrap" v-if="displayedScenarioChunks[0]">
          <table class="ws-scenarios-table">
            <colgroup>
              <col class="col-label" />
              <col v-for="i in displayedScenarioChunks[0].length * 2" :key="'p1col-' + i" />
            </colgroup>
            <thead>
              <tr class="row-scenario-name">
                <th></th>
                <template v-for="s in displayedScenarioChunks[0]">
                  <th :key="s.id + '-h'" colspan="2" class="th-scenario">{{ s.name }}</th>
                </template>
              </tr>
              <tr class="row-party-head">
                <th class="th-monthly">Monthly $</th>
                <template v-for="s in displayedScenarioChunks[0]">
                  <th :key="s.id + '-pa'">{{ payorName }}</th>
                  <th :key="s.id + '-pb'">{{ recipientName }}</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="td-label">Gross Income</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-g-a'" class="num">{{ fmtSigned(s.b.a.grossM) }}</td>
                  <td :key="s.id + '-g-b'" class="num">{{ fmtSigned(s.b.b.grossM) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Taxes and Deductions</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-t-a'" class="num neg">({{ fmt(s.b.a.taxesM) }})</td>
                  <td :key="s.id + '-t-b'" class="num neg">({{ fmt(s.b.b.taxesM) }})</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Benefits and Credits</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-bn-a'" class="num">{{ fmtSigned(s.b.a.benefitsM) }}</td>
                  <td :key="s.id + '-bn-b'" class="num">{{ fmtSigned(s.b.b.benefitsM) }}</td>
                </template>
              </tr>
              <!-- Special Expenses (s.7) — cash outflow, with other deductions -->
              <tr v-if="hasS7">
                <td class="td-label">Special Expenses (s.7)</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-s7p-a'" class="num neg">{{ s.b.a.s7PaidM > 0 ? `(${fmt(s.b.a.s7PaidM)})` : '$0' }}</td>
                  <td :key="s.id + '-s7p-b'" class="num neg">{{ s.b.b.s7PaidM > 0 ? `(${fmt(s.b.b.s7PaidM)})` : '$0' }}</td>
                </template>
              </tr>
              <!-- Cash Flow Adjustments — only when present -->
              <tr v-if="(payorCFAAnnual > 0) || (recipientCFAAnnual > 0)">
                <td class="td-label">Cash Flow Adjustments</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-cfa-a'" class="num neg">{{ s.b.a.cfaM > 0 ? `(${fmt(s.b.a.cfaM)})` : '$0' }}</td>
                  <td :key="s.id + '-cfa-b'" class="num neg">{{ s.b.b.cfaM > 0 ? `(${fmt(s.b.b.cfaM)})` : '$0' }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Spousal Support</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-ss-a'" class="num neg">{{ fmtCostPayor(s.ssMonthly) }}</td>
                  <td :key="s.id + '-ss-b'" class="num">${{ fmt(s.ssMonthly) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Child Support (Table)</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-cs-a'" class="num" :class="{ neg: s.b.a.csM < 0 }">{{ fmtSigned(s.b.a.csM) }}</td>
                  <td :key="s.id + '-cs-b'" class="num" :class="{ neg: s.b.b.csM < 0 }">{{ fmtSigned(s.b.b.csM) }}</td>
                </template>
              </tr>
              <tr v-if="hasS7">
                <td class="td-label">Child Support (s.7 Payment)</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-s7s-a'" class="num" :class="{ neg: s.b.a.s7SettlementM < 0 }">{{ fmtSigned(s.b.a.s7SettlementM) }}</td>
                  <td :key="s.id + '-s7s-b'" class="num" :class="{ neg: s.b.b.s7SettlementM < 0 }">{{ fmtSigned(s.b.b.s7SettlementM) }}</td>
                </template>
              </tr>
              <tr class="row-total">
                <td class="td-label">Net Disposable Income (NDI)</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-ndi-a'" class="num">{{ fmtSigned(s.b.a.ndiM) }}</td>
                  <td :key="s.id + '-ndi-b'" class="num">{{ fmtSigned(s.b.b.ndiM) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Percent of NDI</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-pct-a'" class="num">{{ s.b.a.pct.toFixed(1) }}%</td>
                  <td :key="s.id + '-pct-b'" class="num">{{ s.b.b.pct.toFixed(1) }}%</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">CSG Special Expenses Apportioning %</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-s7-a'" class="num">{{ s.b.a.s7Pct.toFixed(1) }}%</td>
                  <td :key="s.id + '-s7-b'" class="num">{{ s.b.b.s7Pct.toFixed(1) }}%</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">After-tax Cost / Benefit of Spousal Support</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-at-a'" class="num" :class="{ neg: s.b.a.ssAfterTaxM < 0 }">{{ fmtSigned(s.b.a.ssAfterTaxM) }}</td>
                  <td :key="s.id + '-at-b'" class="num" :class="{ neg: s.b.b.ssAfterTaxM < 0 }">{{ fmtSigned(s.b.b.ssAfterTaxM) }}</td>
                </template>
              </tr>

              <!-- ─── Life Insurance section (to secure support) ─── -->
              <tr class="row-section-break">
                <td colspan="100%" class="td-section-break">Life Insurance Estimate (to secure support)</td>
              </tr>
              <tr>
                <td class="td-label">Child Support (CSG Table only)</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-li-cs-a'" class="num">{{ fmtSigned(lifeInsuranceCSValueA(s)) }}</td>
                  <td :key="s.id + '-li-cs-b'" class="num">{{ fmtSigned(lifeInsuranceCSValueB(s)) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">After-tax spousal support</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-li-ss-a'" class="num">{{ fmtSigned(lifeInsuranceSSValueA(s)) }}</td>
                  <td :key="s.id + '-li-ss-b'" class="num">{{ fmtSigned(lifeInsuranceSSValueB(s)) }}</td>
                </template>
              </tr>
              <tr class="row-total">
                <td class="td-label">Total Life Insurance</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-li-tot-a'" class="num">{{ fmtSigned(lifeInsuranceCSValueA(s) + lifeInsuranceSSValueA(s)) }}</td>
                  <td :key="s.id + '-li-tot-b'" class="num">{{ fmtSigned(lifeInsuranceCSValueB(s) + lifeInsuranceSSValueB(s)) }}</td>
                </template>
              </tr>

              <!-- ─── Spousal Support Lump Sum (NPV) ─── -->
              <tr class="row-section-break">
                <td colspan="100%" class="td-section-break">Spousal Support Lump Sum (NPV @ 1.81%)</td>
              </tr>
              <tr>
                <td class="td-label">{{ payorName }}'s after-tax cost</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-lump-a'" class="num" :class="{ neg: lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears) < 0 }">{{ fmtSigned(lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears)) }}</td>
                  <td :key="s.id + '-lump-b'" class="num">—</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">After-tax spousal support</td>
                <template v-for="s in displayedScenarioChunks[0]">
                  <td :key="s.id + '-lumpb-a'" class="num">—</td>
                  <td :key="s.id + '-lumpb-b'" class="num">{{ fmtSigned(lumpSumValue(s.b.b.ssAfterTaxM, s.durationYears)) }}</td>
                </template>
              </tr>
              <tr class="row-total">
                <td class="td-label">Lump Sum Midpoint</td>
                <td v-for="s in displayedScenarioChunks[0]" :key="s.id + '-lumpmid'" colspan="2" class="num">
                  {{ fmtSigned(lumpSumMidpoint(s)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

    </section>

    <!-- ════════════ Pages 2+: additional scenario chunks ════════════ -->
    <section
      v-for="(chunk, chunkIdx) in displayedScenarioChunks.slice(1)"
      :key="'wsp-' + chunkIdx"
      class="worksheet-page worksheet-page--continuation"
    >
      <header class="ws-header ws-header--continuation">
        <img src="/divii-logo.png" alt="Divii" class="ws-logo" />
        <div class="ws-header-meta">
          <div class="ws-case-name">{{ payorName }} &amp; {{ recipientName }}</div>
          <div class="ws-doc-title">Support Scenarios (continued)</div>
          <div class="ws-date">{{ provinceName }} · Tax year {{ taxYear }}</div>
        </div>
      </header>

      <section class="ws-section ws-section--scenarios">
        <h3 class="ws-section-title">
          Support Scenarios
          <span class="ws-section-subtitle">Page {{ chunkIdx + 2 }} of {{ displayedScenarioChunks.length }}</span>
        </h3>
        <div class="ws-scenarios-wrap">
          <table class="ws-scenarios-table">
            <colgroup>
              <col class="col-label" />
              <col v-for="i in chunk.length * 2" :key="'p2col-' + chunkIdx + '-' + i" />
            </colgroup>
            <thead>
              <tr class="row-scenario-name">
                <th></th>
                <template v-for="s in chunk">
                  <th :key="s.id + '-h'" colspan="2" class="th-scenario">{{ s.name }}</th>
                </template>
              </tr>
              <tr class="row-party-head">
                <th class="th-monthly">Monthly $</th>
                <template v-for="s in chunk">
                  <th :key="s.id + '-pa'">{{ payorName }}</th>
                  <th :key="s.id + '-pb'">{{ recipientName }}</th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="td-label">Gross Income</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-g-a'" class="num">{{ fmtSigned(s.b.a.grossM) }}</td>
                  <td :key="s.id + '-g-b'" class="num">{{ fmtSigned(s.b.b.grossM) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Taxes and Deductions</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-t-a'" class="num neg">({{ fmt(s.b.a.taxesM) }})</td>
                  <td :key="s.id + '-t-b'" class="num neg">({{ fmt(s.b.b.taxesM) }})</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Benefits and Credits</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-bn-a'" class="num">{{ fmtSigned(s.b.a.benefitsM) }}</td>
                  <td :key="s.id + '-bn-b'" class="num">{{ fmtSigned(s.b.b.benefitsM) }}</td>
                </template>
              </tr>
              <tr v-if="hasS7">
                <td class="td-label">Special Expenses (s.7)</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-s7p-a'" class="num neg">{{ s.b.a.s7PaidM > 0 ? `(${fmt(s.b.a.s7PaidM)})` : '$0' }}</td>
                  <td :key="s.id + '-s7p-b'" class="num neg">{{ s.b.b.s7PaidM > 0 ? `(${fmt(s.b.b.s7PaidM)})` : '$0' }}</td>
                </template>
              </tr>
              <tr v-if="(payorCFAAnnual > 0) || (recipientCFAAnnual > 0)">
                <td class="td-label">Cash Flow Adjustments</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-cfa-a'" class="num neg">{{ s.b.a.cfaM > 0 ? `(${fmt(s.b.a.cfaM)})` : '$0' }}</td>
                  <td :key="s.id + '-cfa-b'" class="num neg">{{ s.b.b.cfaM > 0 ? `(${fmt(s.b.b.cfaM)})` : '$0' }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Spousal Support</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-ss-a'" class="num neg">{{ fmtCostPayor(s.ssMonthly) }}</td>
                  <td :key="s.id + '-ss-b'" class="num">${{ fmt(s.ssMonthly) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Child Support (Table)</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-cs-a'" class="num" :class="{ neg: s.b.a.csM < 0 }">{{ fmtSigned(s.b.a.csM) }}</td>
                  <td :key="s.id + '-cs-b'" class="num" :class="{ neg: s.b.b.csM < 0 }">{{ fmtSigned(s.b.b.csM) }}</td>
                </template>
              </tr>
              <tr v-if="hasS7">
                <td class="td-label">Child Support (s.7 Payment)</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-s7s-a'" class="num" :class="{ neg: s.b.a.s7SettlementM < 0 }">{{ fmtSigned(s.b.a.s7SettlementM) }}</td>
                  <td :key="s.id + '-s7s-b'" class="num" :class="{ neg: s.b.b.s7SettlementM < 0 }">{{ fmtSigned(s.b.b.s7SettlementM) }}</td>
                </template>
              </tr>
              <tr class="row-total">
                <td class="td-label">Net Disposable Income (NDI)</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-ndi-a'" class="num">{{ fmtSigned(s.b.a.ndiM) }}</td>
                  <td :key="s.id + '-ndi-b'" class="num">{{ fmtSigned(s.b.b.ndiM) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">Percent of NDI</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-pct-a'" class="num">{{ s.b.a.pct.toFixed(1) }}%</td>
                  <td :key="s.id + '-pct-b'" class="num">{{ s.b.b.pct.toFixed(1) }}%</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">CSG Special Expenses Apportioning %</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-s7-a'" class="num">{{ s.b.a.s7Pct.toFixed(1) }}%</td>
                  <td :key="s.id + '-s7-b'" class="num">{{ s.b.b.s7Pct.toFixed(1) }}%</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">After-tax Cost / Benefit of Spousal Support</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-at-a'" class="num" :class="{ neg: s.b.a.ssAfterTaxM < 0 }">{{ fmtSigned(s.b.a.ssAfterTaxM) }}</td>
                  <td :key="s.id + '-at-b'" class="num" :class="{ neg: s.b.b.ssAfterTaxM < 0 }">{{ fmtSigned(s.b.b.ssAfterTaxM) }}</td>
                </template>
              </tr>

              <!-- ─── Life Insurance section (to secure support) ─── -->
              <tr class="row-section-break">
                <td colspan="100%" class="td-section-break">Life Insurance Estimate (to secure support)</td>
              </tr>
              <tr>
                <td class="td-label">Child Support (CSG Table only)</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-li-cs-a'" class="num">{{ fmtSigned(lifeInsuranceCSValueA(s)) }}</td>
                  <td :key="s.id + '-li-cs-b'" class="num">{{ fmtSigned(lifeInsuranceCSValueB(s)) }}</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">After-tax spousal support</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-li-ss-a'" class="num">{{ fmtSigned(lifeInsuranceSSValueA(s)) }}</td>
                  <td :key="s.id + '-li-ss-b'" class="num">{{ fmtSigned(lifeInsuranceSSValueB(s)) }}</td>
                </template>
              </tr>
              <tr class="row-total">
                <td class="td-label">Total Life Insurance</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-li-tot-a'" class="num">{{ fmtSigned(lifeInsuranceCSValueA(s) + lifeInsuranceSSValueA(s)) }}</td>
                  <td :key="s.id + '-li-tot-b'" class="num">{{ fmtSigned(lifeInsuranceCSValueB(s) + lifeInsuranceSSValueB(s)) }}</td>
                </template>
              </tr>

              <!-- ─── Spousal Support Lump Sum (NPV) ─── -->
              <tr class="row-section-break">
                <td colspan="100%" class="td-section-break">Spousal Support Lump Sum (NPV @ 1.81%)</td>
              </tr>
              <tr>
                <td class="td-label">{{ payorName }}'s after-tax cost</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-lump-a'" class="num" :class="{ neg: lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears) < 0 }">{{ fmtSigned(lumpSumValue(s.b.a.ssAfterTaxM, s.durationYears)) }}</td>
                  <td :key="s.id + '-lump-b'" class="num">—</td>
                </template>
              </tr>
              <tr>
                <td class="td-label">After-tax spousal support</td>
                <template v-for="s in chunk">
                  <td :key="s.id + '-lumpb-a'" class="num">—</td>
                  <td :key="s.id + '-lumpb-b'" class="num">{{ fmtSigned(lumpSumValue(s.b.b.ssAfterTaxM, s.durationYears)) }}</td>
                </template>
              </tr>
              <tr class="row-total">
                <td class="td-label">Lump Sum Midpoint</td>
                <td v-for="s in chunk" :key="s.id + '-lumpmid'" colspan="2" class="num">
                  {{ fmtSigned(lumpSumMidpoint(s)) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

    </section>
  </div>

  </fieldset>
  <!-- ─── END READ-ONLY WRAPPER ─── -->

  </template>
  <!-- ═════════════════════ END LEVEL-1 VIEW ═════════════════════ -->

  <!-- ═════════════════════════════════════════════════════════════════════
       PARTY MODE (level-2 users) — the party enters THEIR OWN income and
       THEIR OWN budget. No other party's data, no ranges, no recommendation.
       Entries are stamped self-reported for level-1 review.
  ═════════════════════════════════════════════════════════════════════ -->
  <!-- Party self-identification — a contributor with no party link picks who
       they are ONCE; the choice locks that party's entry surface to them
       (only a full-access user can reset it). Never guesses. -->
  <div v-if="showPartyPicker" class="party-mode">
    <header class="party-mode-head">
      <h2>Who are you?</h2>
      <p class="party-mode-sub" v-if="!allPartiesClaimed">
        Pick your name to start entering your income and expenses. This
        choice is saved: only you will be able to edit this information
        afterward. If you pick the wrong name, the professional on this file
        can reset it.
      </p>
    </header>
    <div v-if="allPartiesClaimed" class="ro-banner">
      Both parties on this agreement have already identified themselves from
      other accounts, so there is nothing left to claim here. If one of those
      claims is wrong, ask the professional on this file to reset it.
    </div>
    <div v-else class="party-claim-card">
      <label class="party-claim-field">
        I am
        <select v-model="claimPartyPick" class="party-claim-select">
          <option value="" disabled>— choose your name —</option>
          <option value="A" :disabled="!!(partyClaims.A && partyClaims.A.userKey !== currentUserKey)">
            {{ payorName }}{{ partyClaims.A && partyClaims.A.userKey !== currentUserKey ? ' (already claimed)' : '' }}
          </option>
          <option value="B" :disabled="!!(partyClaims.B && partyClaims.B.userKey !== currentUserKey)">
            {{ recipientName }}{{ partyClaims.B && partyClaims.B.userKey !== currentUserKey ? ' (already claimed)' : '' }}
          </option>
        </select>
      </label>
      <button class="btn-primary" :disabled="claimPartyPick !== 'A' && claimPartyPick !== 'B'" @click="claimParty">
        This is me — start entering →
      </button>
    </div>
  </div>

  <div v-if="showPartyEntry" class="party-mode">
    <header class="party-mode-head">
      <h2>Your financial information</h2>
      <p class="party-mode-sub">
        <template v-if="roleDisplayName">
          Signed in as <strong>{{ roleDisplayName }}</strong>, entering
          information for <strong>{{ rolePartyName }}</strong>.
        </template>
        <template v-else>{{ rolePartyName }},</template>
        Fill in the income and monthly expenses below. Everything saves
        automatically and is shared with the professional on this file for
        review. You will not see the other party's information here.
      </p>
    </header>

    <!-- ─── Your income ─── -->
    <section class="party-section">
      <h3>1. Your income</h3>
      <p class="party-hint">Annual amounts. Search by name or by the line number on your tax return.</p>
      <div class="income-list">
        <div v-for="item in rolePartyItems" :key="item.id" class="income-row">
          <div class="row-label">
            <span class="row-title">{{ incomeTypeMeta(item.typeId)?.label || item.typeId }}</span>
            <span class="row-line" v-if="incomeTypeMeta(item.typeId)?.t1Line">Line {{ incomeTypeMeta(item.typeId).t1Line }}</span>
          </div>
          <div class="input-prefix row-amount">
            <span>$</span>
            <input type="number" min="0" :value="item.amount || 0"
                   @input="updateIncomeAmount(item.id, Number($event.target.value) || 0)"
                   placeholder="0" />
          </div>
          <button class="btn-remove" @click="removeIncomeItem(item.id)" title="Remove">×</button>
        </div>
        <div v-if="rolePartyItems.length === 0" class="empty-row">No income entered yet.</div>
      </div>
      <div class="picker-wrap">
        <button class="btn-add-income" @click="openPickerFor === rolePartyId ? openPickerFor = null : (openPickerFor = rolePartyId, pickerSearch = '')">
          + Add income
        </button>
        <div v-if="openPickerFor === rolePartyId" class="picker-popover">
          <input type="text" v-model="pickerSearch" placeholder="Search income, deduction, T1 line…" class="picker-search" @keydown.esc="openPickerFor = null" />
          <div class="picker-results">
            <template v-for="cat in incomeCategories">
              <div v-if="filteredTypesByCategory(cat).length" :key="cat" class="picker-group">
                <div class="picker-group-header">{{ cat }}</div>
                <button v-for="type in filteredTypesByCategory(cat)" :key="type.id"
                        class="picker-item"
                        @click="addIncomeItem(rolePartyId, type.id); openPickerFor = null; pickerSearch = ''">
                  <span class="picker-item-label">{{ type.label }}</span>
                  <span class="picker-item-line" v-if="type.t1Line && type.t1Line !== '—'">Line {{ type.t1Line }}</span>
                </button>
              </div>
            </template>
            <div v-if="totalFilteredCount === 0" class="picker-empty">No matches. Try another keyword or T1 line number.</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── Your budget (own column of the F8 schedule) ─── -->
    <section class="party-section">
      <h3>2. Your monthly expenses</h3>
      <p class="party-hint">
        Enter the monthly expenses you expect <strong>after separation</strong>,
        using your best estimate where you are not sure of the exact amount.
        The categories follow the court financial statement. Enter amounts at
        whatever frequency you know them; they convert to monthly automatically.
      </p>
      <div v-for="g in F8_BUDGET_GROUPS" :key="g.key" class="budget-group" v-show="g.key !== 'compulsory' && g.key !== 'supportOthers'">
        <div class="budget-group-head">
          <span class="budget-group-name">{{ g.label }}<span v-if="g.hint" class="budget-group-hint"> ({{ g.hint }})</span></span>
          <span class="budget-group-sub">${{ fmt(budgetGroupSubtotal(g.key, roleBudgetCol)) }}</span>
        </div>
        <div v-for="ln in g.lines" :key="g.key + '|' + ln" class="budget-row budget-row-party">
          <span class="budget-line-label">{{ ln }}</span>
          <input type="number" min="0" class="budget-amt" placeholder="0"
                 v-model.number="budgetLines[g.key + '|' + ln][roleBudgetCol]" />
          <select class="budget-freq" v-model="budgetLines[g.key + '|' + ln].freq">
            <option v-for="f in BUDGET_FREQS" :key="f.value" :value="f.value">{{ f.label }}</option>
          </select>
          <div v-if="budgetEcho(budgetLines[g.key + '|' + ln])" class="budget-echo budget-echo-party">
            {{ budgetEcho(budgetLines[g.key + '|' + ln]) }}
          </div>
        </div>
        <div v-for="(c, i) in (budgetCustom[g.key] || [])" :key="c.id" class="budget-row budget-row-party">
          <input type="text" class="budget-custom-label" placeholder="Describe this expense…" v-model="c.label" />
          <input type="number" min="0" class="budget-amt" placeholder="0" v-model.number="c[roleBudgetCol]" />
          <select class="budget-freq" v-model="c.freq">
            <option v-for="f in BUDGET_FREQS" :key="f.value" :value="f.value">{{ f.label }}</option>
          </select>
          <button type="button" class="budget-remove" @click="removeBudgetCustomRow(g.key, i)" title="Remove line">×</button>
        </div>
        <button type="button" class="budget-add" @click="addBudgetCustomRow(g.key)">+ Other [specify]</button>
      </div>
      <div class="budget-summary">
        <div class="budget-summary-row budget-summary-party">
          <span class="budget-summary-label">Your total monthly expenses</span>
          <span class="budget-summary-amt">${{ fmt(budgetLivingMonthly[roleBudgetCol]) }}</span>
        </div>
      </div>
      <p class="party-done-note">
        That's everything. Your information saves as you type; the professional
        on the file reviews it from here.
      </p>
    </section>
  </div>

  </div>
</template>

<script>
// ─────────────────────────────────────────────────────────────────────────────
// VUE 2 / VUE 3 DUAL-COMPATIBILITY CONTRACT  (verified 2026-06-30)
// This component currently runs on Vue 2.7 (see package.json / main.js) and
// embeds in the current Vue 2 LegalApp as-is. It is ALSO written to run
// unchanged on Vue 3 — the only differences between the two live in the HOST
// bootstrap + build tooling, which the embedding app owns, NOT in this file:
//   • bootstrap:  Vue 2 `new Vue({ render: h => h(App) }).$mount('#app')`
//                 Vue 3 `createApp(App).mount('#app')`
//   • build:      Vue 2 `@vitejs/plugin-vue2`   →   Vue 3 `@vitejs/plugin-vue`
// So when LegalApp upgrades to Vue 3, this same file drops in — no port.
//
// To KEEP it dual-compatible, do NOT introduce any of these (all Vue-2-only,
// removed/changed in Vue 3):
//   ✗ $on / $off / $once event bus      ✗ this.$set / this.$delete
//   ✗ template filters  {{ x | foo }}   ✗ .native / .sync modifiers
//   ✗ Vue.observable, global filters/mixins
// Stay in the Options API (data/computed/methods/watch); it works in both.
// (Composition API / <script setup> would ALSO work in both, but the whole
// file is Options API today — keep it consistent.)
// ─────────────────────────────────────────────────────────────────────────────
import {
  lookupCSG2025,
  T_BC, T_AB, T_MB, T_ON, T_NB, T_NL, T_NS, T_PE, T_SK, T_YT, T_NT, T_NU,
} from './tables-2025.js'
import {
  lookupCSG2017,
  T17_BC, T17_AB, T17_MB, T17_ON, T17_NB, T17_NL, T17_NS, T17_PE, T17_SK, T17_YT, T17_NT, T17_NU,
} from './tables-2017.js'
import SsagRangeAdvisor from './components/SsagRangeAdvisor.vue'
import { makeEmptyAnswers } from './ssagRangeAdvisor.js'

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────────────────────
const PROVINCES = {
  AB: 'Alberta', BC: 'British Columbia', MB: 'Manitoba',
  NB: 'New Brunswick', NL: 'Newfoundland & Labrador', NS: 'Nova Scotia',
  NT: 'Northwest Territories', NU: 'Nunavut', ON: 'Ontario',
  PE: 'Prince Edward Island', SK: 'Saskatchewan', YT: 'Yukon',
}

// Age of majority by province
const AOM = {
  AB: 18, MB: 18, ON: 18, PE: 18, SK: 18,
  BC: 19, NB: 19, NL: 19, NS: 19, NT: 19, NU: 19, YT: 19,
}

const S7_CATEGORIES = [
  { value: 'childcare', label: 'Child Care (employment/education)' },
  { value: 'medical_insurance', label: 'Medical/Dental Insurance Premium' },
  { value: 'health', label: 'Health Expenses (uninsured >$100/yr)' },
  { value: 'education', label: 'Extraordinary Education' },
  { value: 'postsecondary', label: 'Post-Secondary Education' },
  // Split like DM: NL's Physical Activity Tax Credit is fitness-only, while
  // SK's Active Families Benefit covers sport AND culture. The legacy
  // 'extracurricular' id stays (saved data) as the non-fitness line.
  { value: 'extracurricular_fitness', label: 'Extraordinary Extracurricular (fitness)' },
  { value: 'extracurricular', label: 'Extraordinary Extracurricular (non-fitness)' },
]

// Provincial refundable credits for children's activities (the "Claimed" box
// for extracurricular expenses in NL/SK — DM computes these automatically):
//   NL Physical Activity Tax Credit: 8.7% of up to $2,000 of eligible fitness
//   expenses PER FAMILY (max $174), refundable.
//   SK Active Families Benefit: 50% of eligible sport/culture/recreation fees
//   to $150 per child ($200 for a child with a disability), refundable,
//   family net income at or under $120,000.
const NL_PATC_RATE = 0.087
const NL_PATC_FAMILY_CAP = 2000
const SK_AFB_RATE = 0.5
const SK_AFB_CHILD_CAP = 150
const SK_AFB_DISABLED_CAP = 200
const SK_AFB_INCOME_CAP = 120000

// Combined federal + provincial top marginal rates (approximate, 2025)
// Used as fallback when MARGINAL_BRACKETS is unavailable
const MARGINAL_RATES = {
  AB: 0.48, BC: 0.535, MB: 0.502, NB: 0.523, NL: 0.518,
  NS: 0.540, NT: 0.447, NU: 0.445, ON: 0.535, PE: 0.514,
  SK: 0.475, YT: 0.483,
}

// Combined federal + provincial marginal rates by income bracket (2025)
// Each entry: [incomeThreshold, combinedRate] — ascending; use rate for income >= threshold
// Federal 2025: 14.5% to $57,375 (mid-year cut); 20.5% to $114,750; 26% to $158,520;
//               29% to $220,000; 33% above. Brackets below $57,375 use 14.5% federal portion.
// Provincial breakpoints reflect each province's actual 2025 brackets.
const MARGINAL_BRACKETS = {
  // 2026 indexed brackets: federal lowest 14% (was 14.5% in 2025), other
  // federal rates unchanged. BC bracket boundaries indexed +~2%.
  // Combined boundaries below = max(federal-cap, BC-cap) where they differ.
  // 2026 BC combined boundaries (BC indexed +2.0%, Federal indexed +2.7% from 2025):
  //   BC bracket boundaries (2026): 50265 / 100531 / 115421 / 140155 / 190032 / 265026
  //   Federal bracket boundaries (2026): 58523 / 117045 / 181440 / 258482
  //   Federal rates: 14% / 20.5% / 26% / 29% / 33% (Bill C-4 cut 14.5%→14% on first)
  //   BC rates: 5.06 / 7.7 / 10.5 / 12.29 / 14.7 / 16.8 / 20.5
  // BC 2026: Budget 2026 (Feb 17) raised the bottom rate 5.06% -> 5.60%
  // RETROACTIVE to Jan 1, 2026 (annual rate; payroll prorates 6.14% H2).
  // Thresholds indexed 2.2% per T4127 (50,363 / 100,728 / 115,648 / 140,430 /
  // 190,405 / 265,545). First combined band = 14% fed + 5.60% = 19.60%.
  BC: [[0,0.1960],[50363,0.2170],[58523,0.2820],[100728,0.3100],[115648,0.3279],[117045,0.3829],[140430,0.4070],[181440,0.4370],[190405,0.4580],[258482,0.4980],[265545,0.5350]],
  // Alberta 2026: NEW 8% first bracket on first $60,000 (announced in AB Budget 2024,
  // effective Jan 1, 2026). Combined: 14% fed + 8% AB = 22% on first $58,924,
  // then 14% fed + 8% AB = 22% to $60k, then 20.5% fed + 10% AB above.
  // Validated against DM: Michael @ $122,095 taxable → DM prov tax $10,986;
  // our calc with new brackets: 8% × $60k + 10% × ($122,095 - $60k) = $4,800 + $6,210 = $11,010 ✓
  // AB 2026 (T4127 Table 8.1): 8% to 61,200; 10% to 154,259; 12% to 185,111;
  // 13% to 246,813; 14% to 370,220; 15% over (2025 thresholds indexed 2.0%).
  // Pre-audit values were the unindexed 2025 thresholds (and 181,481/181,440
  // were out of order), costing +$24 flat at mid incomes up to +$242 at $500k.
  AB: [[0,0.22],[58523,0.285],[61200,0.305],[117045,0.36],[154259,0.38],[181440,0.41],[185111,0.42],[246813,0.43],[258482,0.47],[370220,0.48]],
  // Manitoba 2026: federal 14% / 20.5% / 26% / 29% / 33% × MB 10.80% / 12.75% / 17.40%.
  // MB provincial thresholds 2026: $47,564 / $101,200 (indexed).
  // Combined breakpoints: $47,564 / $58,924 / $101,200 / $117,848 / $182,750 / $260,256.
  // Verified against DM MB Hughes-Peslova: tax on Lucie's $56,098 → 47564×0.108 +
  // (56098-47564)×0.1275 = $6,225 prov; DM = $6,236 (within $11).
  // MB 2026 (T4127): 47,000 / 100,000 — bracket indexation PAUSED for 2025+
  // (pre-audit values 47,564/101,200 were incorrectly indexed upward).
  MB: [[0,0.2480],[47000,0.2675],[58523,0.3325],[100000,0.3790],[117045,0.4340],[181440,0.4640],[258482,0.5040]],
  // Ontario 2026: federal 14% / 20.5% / 26% / 29% / 33% × ON 5.05% / 9.15% /
  // 11.16% / 12.16% / 13.16%. ON provincial thresholds 2026: $52,886 /
  // $105,775 / $150,000 / $220,000 (first two indexed). Federal thresholds
  // 2026: $58,924 / $117,848 / $182,684 / $260,256. (Previously stale 2025:
  // 14.5% fed first bracket + old thresholds — over-taxed every ON case.)
  // ON surtax + Health Premium added separately via computeOntarioExtraTax.
  ON: [[0,0.1905],[53891,0.2315],[58523,0.2965],[107785,0.3166],[117045,0.3716],[150000,0.3816],[181440,0.4116],[220000,0.4216],[258482,0.4616]],
  // New Brunswick 2026: federal 14% / 20.5% / 26% / 29% / 33% × NB 9.40% / 14.00% / 16.00% / 19.50%.
  // NB provincial thresholds 2026: $51,306 / $102,614 / $190,060 (indexed).
  // Verified against DM NB Hughes-Peslova: tax on Lucie's $54,776 → 52333×0.094 +
  // (54776-52333)×0.14 = $5,309 prov before credits/reduction; DM = $5,261 (within $48).
  NB: [[0,0.2340],[52333,0.2800],[58523,0.3450],[104666,0.3650],[117045,0.4200],[181440,0.4500],[193861,0.4850],[258482,0.5250]],
  // Newfoundland & Labrador 2026: federal 14% / 20.5% / 26% / 29% / 33%
  // × NL 8.7% / 14.5% / 15.8% / 17.8% / 19.8% / 20.8% / 21.3% / 21.8%.
  // NL provincial thresholds 2026: $44,192 / $88,382 / $157,792 / $220,910 /
  // $282,214 / $564,429 / $1,128,858. Verified against DM Hughes-Peslova-NL:
  // tax on Lucie's $55,472 → 44678×0.087 + (55472-44678)×0.145 = $5,480 prov;
  // DM = $5,452 (within $28).
  NL: [[0,0.2270],[44678,0.2850],[58523,0.3500],[89354,0.3630],[117045,0.4180],[159528,0.4380],[181440,0.4680],[223340,0.4880],[258482,0.5280],[285319,0.5380],[570638,0.5430],[1141275,0.5480]],
  // Nova Scotia 2026: federal 14% / 20.5% / 26% / 29% / 33% × NS 8.79% / 14.95%
  // / 16.67% / 17.50% / 21.00%. NS thresholds 2026 (first indexed 2025):
  // $30,507 / $61,015 / $95,883 / $154,650. Verified against DM Mora-Primary-NS
  // Christal $101,512 → NS prov tax $14,041 calc; DM $13,981 (within $60).
  // NS 2026 (T4127, NS indexation 1.6%): 30,995 / 61,991 / 97,417 / 157,124
  // (pre-audit values were the 2025 thresholds).
  NS: [[0,0.2279],[30995,0.2895],[58523,0.3545],[61991,0.3717],[97417,0.3800],[117045,0.4350],[157124,0.4700],[181440,0.5000],[258482,0.5400]],
  // PEI 2026: federal 14% / 20.5% / 26% / 29% / 33% × PE 9.5% / 13.47% / 16.60% /
  // 17.62% / 19%. PE thresholds 2026: $33,328 / $66,656 / $105,000 / $140,000.
  // PE eliminated surtax in 2025. Combined federal(14/20.5/26/29/33 @ 58523/
  // 117045/181440/258482) + PE 2026 provincial (Budget 2026): 9.50% ≤33,928 /
  // 13.47% / 16.60% / 17.62% / 19.00% (142,250–200,000) / 20.00% over 200,000.
  // The 20%-over-$200k bracket is NEW for 2026 — the prior table topped out at
  // ~19% (combined 0.52), under-taxing high earners ~1% (fieber-PE was ~$3.6k/yr
  // low). Merged combined tax reconciles to DM within ~$176 at $507k taxable.
  PE: [[0,0.2350],[33928,0.2747],[58523,0.3397],[65820,0.3710],[106890,0.3812],[117045,0.4362],[142520,0.4500],[181440,0.4800],[200000,0.4900],[258482,0.5300]],
  // SK 2026: combined federal+provincial. Federal (post Bill C-4): 14% /
  // 20.5% / 26% / 29% / 33% at $58,924 / $117,848 / $182,684 / $260,256.
  // Sask: 10.5% / 12.5% / 14.5% at $53,407 / $152,750 (2026 indexed).
  // Combined breakpoints take max(fed,prov) at each tier.
  // Verified against DM SK Mora-Primary, Johnston-Smith: tax on Christal's
  // $101,282 = $28,525 ours vs $28,529 DM (within $4).
  // SK 2026 (T4127): 10.5% to 54,532; 12.5% to 155,805; 14.5% over (2.0%
  // indexation; pre-audit values were unindexed 2025 thresholds, +$22-84/yr).
  SK: [[0,0.245],[54532,0.265],[58523,0.330],[117045,0.385],[155805,0.405],[181440,0.435],[258482,0.475]],
  // Yukon 2026: federal 14% / 20.5% / 26% / 29% / 33% × YT 6.4% / 9% / 10.9% /
  // 12.8% / 15%. YT's first four rates use federal thresholds, BUT YT's top 15%
  // rate starts only at $500,000 (its own bracket) — so $260,256–$500,000 is
  // 33% fed + 12.8% YT = 45.8%, and 48% applies only above $500,000. (Prior
  // single [258482,0.48] over-taxed the $260k–$500k band by 2.2%, which
  // over-grossed non-taxable income at high income — campo-YT, above the SSAG
  // ceiling. DM gross-up: $252,304 on $624,054 ≈ true fed+YT tax.)
  // Verified Justin YT $53,863 → fed $7,541 + YT $3,447 = $10,988; DM $10,988 exact.
  YT: [[0,0.2040],[58523,0.2950],[117045,0.3690],[181440,0.4180],[258482,0.4580],[500000,0.4800]],
  // Northwest Territories 2026: federal 14% / 20.5% / 26% / 29% / 33% × NT 5.9% /
  // 8.6% / 12.2% / 14.05%. NT thresholds 2026: $52,011 / $104,022 / $169,094.
  // Verified Justin NT $53,662 → fed $7,513 + NT $3,211 = $10,724; DM $10,697 (within $27).
  NT: [[0,0.1990],[53003,0.2260],[58523,0.2910],[106009,0.3270],[117045,0.3820],[172346,0.4005],[181440,0.4305],[258482,0.4705]],  // 2026 T4127
  // Nunavut 2026: federal 14% / 20.5% / 26% / 29% / 33% × NU 4% / 7% / 9% / 11.5%.
  // NU thresholds 2026 (indexed): $54,707 / $109,413 / $177,881.
  // Combined breakpoints: $54,707 / $58,924 / $109,413 / $117,848 / $177,881 /
  // $182,684 / $260,256. Verified Justin NU $53,896: fed $7,545 + NU $2,156 = $9,701.
  NU: [[0,0.1800],[55801,0.2100],[58523,0.2750],[111602,0.2950],[117045,0.3500],[181439,0.3750],[181440,0.4050],[258482,0.4450]],  // 2026 T4127
}

// Phase 3 (tax-year threading): tax functions accept an optional `cfg` parameter
// that points at a TAX_YEARS[year] config block. When omitted, falls back to
// TAX_YEARS[2026] — which references the same module-level constants used
// historically, so 2026 behavior is byte-identical. Pass cfg from Vue via
// `this.taxYearCfg`. Older years currently spread from 2026, so math is
// unchanged across all years until 2025/24/23 data is populated.
function getMarginalRate(province, annualIncome, cfg) {
  cfg = cfg || TAX_YEARS[2026]
  const brackets = cfg.marginalBrackets[province]
  if (!brackets) return cfg.marginalRates[province] || 0.46
  let rate = brackets[0][1]
  for (const [threshold, r] of brackets) {
    if (annualIncome >= threshold) rate = r
    else break
  }
  return rate
}

// Provincial BPA (basic personal amount) and lowest-bracket rate, 2025
// Used by getTaxCredits to compute non-refundable credits per province
const PROV_TAX_DATA = {
  // 2026 indexed BPA values (calibrated against Divorcemate Tools Cloud 2026)
  BC: { bpa: 13216, rate: 0.056, edcAmount: 11317 },  // 2026: BPA 13,216 (T4127), credits at the new 5.60% bottom rate, EDC 11,317 (gov.bc.ca — NOT = BPA)
  // AB 2026: new 8% lowest bracket (Budget 2024 announcement, effective Jan 1, 2026).
  // BPA updated to KPMG 2026 value $22,769 (was $21,003).
  // Credits applied at the new 8% rate (lowest bracket).
  AB: { bpa: 22769, rate: 0.08   },
  // MB: the amount for an eligible dependant ($9,134) is LOWER than the BPA
  // ($15,780) — unlike most provinces where they're equal. Verified vs DM Tools
  // Cloud 2026 MB (johnston Jessica prov credit $3,042 = (15,780 + 9,134 + CPP +
  // EI) × 10.8%). edcAmount override drives the provincial eligible-dependant credit.
  // MB also claws back its BPA over net income $200,000–$400,000 (2024 Budget,
  // effective 2025+): fully eliminated at $400k. Without this, high-income MB
  // payors were over-credited ~$1,704 (fieber payor $565k → DM MB prov NRTC $501).
  MB: { bpa: 15780, rate: 0.108, edcAmount: 9134, bpaPhaseLo: 200000, bpaPhaseHi: 400000 },
  // NB: eligible-dependant amount ($11,371) < BPA ($13,220), same pattern as MB.
  // Verified vs DM Tools Cloud 2026 NB (johnston Jessica prov credit $2,596).
  NB: { bpa: 13664, rate: 0.094, edcAmount: 10709 },
  NL: { bpa: 13094, rate: 0.087, edcAmount: 9142 },  // 2026: BPA prorated 13,094 (Budget 2026 mid-year rise to 15,000; DM applies the full 15,000 — documented ~$166 delta); EDC 9,142 per TD1NL
  NS: { bpa: 11932, rate: 0.0879 },  // 2026 (T4127/TD1NS); old supplement structure abolished 2025
  NT: { bpa: 18198, rate: 0.059  },  // 2026 T4127/TD1NT
  NU: { bpa: 19659, rate: 0.04   },  // 2026 T4127/TD1NU (17,925 was the 2023 figure)
  // ON: eligible-dependant amount ($11,029, TD1ON line 6) < BPA ($12,989) —
  // same MB/NB/PE pattern. Verified vs T4127 2026 + DM ON johnston (Jessica
  // prov credit $1,377 = (12,989 + 11,029 + CPP + EI) × 5.05%).
  ON: { bpa: 12989, rate: 0.0505, edcAmount: 11029 },
  // PE: eligible-dependant amount ($12,740) < BPA ($15,000) — verified vs DM Tools
  // Cloud 2026 (angus Colin prov credit $3,053; johnston Jessica $2,944 both reconcile
  // to EDC ~$12,740). Brackets/BPA verified correct (Colin prov tax $9,600 vs DM $9,603).
  PE: { bpa: 15000, rate: 0.095, edcAmount: 12740 },
  // SK 2026 values (DM Tools Cloud 2026 uses the 2026 SK tax year, post-
  // Affordability-Act: +$500/yr to BPA/spousal/child amounts + 2% indexation).
  //   • BPA = eligible-dependant amount = $20,381 (edcAmount defaults to bpa)
  //   • childAmount = "amount for dependent children born in 2007+" $8,358/child
  // RECONCILED TO THE DOLLAR vs DM angus (Colin prov NRTC $5,620) and johnston
  // (Jessica $4,621, Tim $3,505). RULE: the dependent-children amount is claimed
  // for each assigned child EXCEPT the one claimed as the eligible dependant (no
  // double-dip) — see childAmountClaims (subtracts 1 for the EDC claimant).
  SK: { bpa: 20381, rate: 0.105, childAmount: 8358 },
  YT: { bpa: 16452, rate: 0.064, bpaPhaseLo: 181440, bpaPhaseHi: 258482, bpaFloor: 14829 },  // YT BPA = federal BPAF exactly incl. phase-out (T4127)
}

// 2026 CPP and EI parameters (CRA announced Nov 2025)
// CPP1: 5.95% on earnings $3,500–$74,600 (YMPE), max employee = $4,230.45
// CPP2: 4% on earnings $74,600–$85,000 (YAMPE), max employee = $416.00
// Combined max employee CPP = $4,646.45 (matches DM Tools Cloud 2026)
// Self-employed pays both halves (×2 contributions, no EI, but full amount deductible)
// EI 2026: max insurable earnings $68,900, rate 1.63%, max premium = $1,123.07
// (Matches DM Tools Cloud 2026 EI of $1,123)
const CPP_YMPE = 74600 // 2026 YMPE (CRA)
const CPP_YAMPE = 85000
const CPP_BASIC = 3500
const CPP1_RATE = 0.0595
const CPP2_RATE = 0.04
const EI_MIE = 68900
const EI_RATE = 0.0163
// Federal indexed values for 2026 tax year, per CRA T4127 (122nd ed.) and the
// CRA indexation table (2026 factor = 2.0%). Bottom-bracket rate is 14%
// (Bill C-4 progression from 15% → 14.5% → 14%).
const FED_BPA_2025 = 16452                    // 2026 federal BPA (T4127; was 16461 pre-audit)
const FED_LOWEST_RATE = 0.14                   // 2026 federal lowest bracket rate
const FED_AGE_AMOUNT_2025 = 9208              // 2026 federal age amount (TD1 2026 line 3; 2025: 9,028 x 1.02)
const FED_AGE_AMOUNT_THRESHOLD_2025 = 46432  // TD1 2026 (net-income phase start)    // 2026 federal age threshold (2025: $44,325 + 2.7%)
// Provincial Age Amount (65+) and net income threshold for 2026.
// BC/AB/ON values are calibrated against Divorcemate Tools Cloud 2026 (may
// differ slightly from KPMG/CRA public reference values). The remaining 9
// provinces/territories are populated from KPMG's 2026 Personal Tax Credits
// table and Nethris (both sources agree). These have NOT been calibrated
// against DM cases — they should be reviewed when DM fixtures from those
// provinces are available.
const PROV_AGE_AMOUNT = {
  BC: { amount: 5927, threshold: 44119 },     // 2026 official (TD1BC line 2)
  AB: { amount: 6345, threshold: 47234 },      // 2026 AB indexed per KPMG (was 5816/45522)
  ON: { amount: 6341, threshold: 47209 },  // 2026 = 2025 (6,223/46,330) x 1.019; reconciles DM retirement calc exactly
  // The following are public 2026 indexed values; uncalibrated.
  MB: { amount: 3728, threshold: 27749 },     // MB does not index credits
  SK: { amount: 5901, threshold: 43927 },  // TD1SK 2026 (retirement audit; was stale)
  NB: { amount: 6158, threshold: 45844 },
  NS: { amount: 5826, threshold: 30828 },     // NS partial indexation
  PE: { amount: 6510, threshold: 36600 },     // PE does not index credits
  NL: { amount: 7142, threshold: 39138 },
  YT: { amount: 9208, threshold: 46432 },     // YT mirrors federal age amount
  NT: { amount: 8902, threshold: 46432 },
  NU: { amount: 12550, threshold: 46432 },
}
// Disability Amount (federal line 31600 / provincial line 58440) — the base
// adult disability tax credit (DTC) amount. Flat, non-income-tested. The CREDIT
// = amount × the jurisdiction's lowest/NRTC rate (fedLowestRate federally,
// prov.rate provincially — same rate every other NRTC uses here). Sources:
// TaxTips.ca 2025/2026 non-refundable-credit base-amount tables; federal per
// CRA line 31600. YT mirrors the federal amount. MB/NS/PE do not index (flat).
// NOTE: base adult amount only — the under-18 supplement + caregiver amounts
// are separate and not modelled.
const FED_DISABILITY_AMOUNT_2026 = 10341
const FED_DISABILITY_AMOUNT_2025 = 10138
const PROV_DISABILITY_AMOUNT_2026 = {
  BC: 9913, AB: 17563, SK: 14266, MB: 6180, ON: 10494, NB: 10210,
  NS: 7341, PE: 6890, NL: 7549, NT: 14758, NU: 16733, YT: 10341,
}
const PROV_DISABILITY_AMOUNT_2025 = {
  BC: 9699, AB: 17219, SK: 13986, MB: 6180, ON: 10298, NB: 10010,
  NS: 7341, PE: 6890, NL: 7467, NT: 14469, NU: 16405, YT: 10138,
}
// Disability supplement for a dependant UNDER 18 (added to the base disability
// amount when the child's disability amount is transferred to a supporting
// parent — federal line 31800 / provincial line 58480). Max before the
// child-care/attendant-care clawback (reduced $1-for-$1 by such expenses above
// a threshold — not modelled; DM cases here have no such expenses). NT/NU use
// the FEDERAL supplement (not their own base); SK's supplement equals its base;
// YT mirrors federal. Source: TaxTips.ca 2025/2026 NRTC base-amount tables.
const FED_DISABILITY_SUPP_U18_2026 = 6032
const FED_DISABILITY_SUPP_U18_2025 = 5914
const PROV_DISABILITY_SUPP_U18_2026 = {
  BC: 5783, AB: 13180, SK: 14266, MB: 3605, ON: 6121, NB: 5955,
  NS: 3449, PE: 4019, NL: 3553, NT: 6032, NU: 6032, YT: 6032,
}
const PROV_DISABILITY_SUPP_U18_2025 = {
  BC: 5659, AB: 12922, SK: 13986, MB: 3605, ON: 6007, NB: 5839,
  NS: 3449, PE: 4019, NL: 3514, NT: 5914, NU: 5914, YT: 5914,
}
// Pension Income Amount — federal $2,000 / provincial varies. Capped at the
// actual amount of qualifying pension income (line 11500 — superannuation,
// annuities, RRIF). EXCLUDES CPP/OAS.
const FED_PENSION_AMOUNT_MAX = 2000
// Provincial Dividend Tax Credit rates (% of GROSSED-UP dividend amount).
// Eligible = T1 line 12000 dividends (38% gross-up, mature Canadian corps).
// Non-eligible = T1 line 12010 dividends (15% gross-up, CCPCs).
// Sources: provincial tax statutes + KPMG Canada 2026 tax tables.
// Calibrated against Divorcemate for BC ($5,299 on $44,160 grossed-up = 12.0%).
const PROV_DIVIDEND_CREDIT = {
  BC: { eligible: 0.12,    nonEligible: 0.0196 },
  AB: { eligible: 0.0812,  nonEligible: 0.02157 },
  ON: { eligible: 0.10,    nonEligible: 0.029863 },
  SK: { eligible: 0.11,    nonEligible: 0.02105 },
  MB: { eligible: 0.08,    nonEligible: 0.007835 },
  NB: { eligible: 0.14,    nonEligible: 0.0275 },
  NS: { eligible: 0.0885,  nonEligible: 0.015 },  // non-eligible cut 2.99->1.5% (Bill 68, 2025)
  PE: { eligible: 0.105,   nonEligible: 0.0196 },
  NL: { eligible: 0.054,   nonEligible: 0.032 },
  YT: { eligible: 0.1202,  nonEligible: 0.0067 },
  NT: { eligible: 0.115,   nonEligible: 0.06 },
  NU: { eligible: 0.0551,  nonEligible: 0.0261 },
}
// Pension Income Amount per province for 2026. BC/AB/ON values are calibrated
// against Divorcemate; the remaining 9 are from KPMG/Nethris 2026 reference
// tables (uncalibrated — review when DM cases from those provinces are added).
const PROV_PENSION_AMOUNT_MAX = {
  BC: 1000, AB: 1753, ON: 1819,  // ON 2026 = 1,785 x 1.019 (was stale 1,641); AB per KPMG
  // Public 2026 values (uncalibrated):
  MB: 1000, SK: 1000, NB: 1000, NS: 1173, PE: 1000, NL: 1000,
  YT: 2000, NT: 1000, NU: 2000,
}

// ─────────────────────────────────────────────────────────────────────────────
// TAX YEAR REGISTRY
// ─────────────────────────────────────────────────────────────────────────────
// Year-keyed view of all tax-year-specific constants. Currently 2026 is the
// only year with real data; 2023-2025 are placeholders that inherit 2026
// values until proper reference data is populated. Phase 3 of the tax-year
// selector work will refactor the math functions (getRawIncomeTax, partyTaxBundle,
// computeRefundableBenefits, etc.) to read constants from TAX_YEARS[taxYear]
// instead of the module-level constants above. For Phase 1, this registry
// exists primarily to support the UI selector and warning banner; the math
// layer continues to use the module-level 2026 constants.
const TAX_YEARS = {
  2026: {
    year: 2026,
    label: '2026',
    placeholder: false,
    // Federal
    fedBPA: FED_BPA_2025,
    fedLowestRate: FED_LOWEST_RATE,
    cea: 1501,  // Canada Employment Amount 2026 (CRA T4127 Table 8.2)
    fedAgeAmount: FED_AGE_AMOUNT_2025,
    fedAgeThreshold: FED_AGE_AMOUNT_THRESHOLD_2025,
    // OAS recovery tax ("clawback") threshold — 15% of net income above this,
    // capped at OAS received. 2026 figure estimated from 2025 ($93,454) at
    // ~2% indexation; replace with the CRA-published figure when available.
    oasClawbackThreshold: 95323,
    // Medical expense credit floor: claimed expenses over the LESSER of 3% of
    // net income or this cap earn the credit. 2026 estimated from 2025.
    medicalFloor: 2891,
    fedPensionAmountMax: FED_PENSION_AMOUNT_MAX,
    fedDisabilityAmount: FED_DISABILITY_AMOUNT_2026,
    fedDisabilitySupplementU18: FED_DISABILITY_SUPP_U18_2026,
    // Provincial (per-province lookups — these objects are already
    // year-2026 indexed; for older years we'll populate parallel objects
    // and key the whole structure by year)
    marginalBrackets: MARGINAL_BRACKETS,
    marginalRates: MARGINAL_RATES,
    provTaxData: PROV_TAX_DATA,
    provAgeAmount: PROV_AGE_AMOUNT,
    provDisabilityAmount: PROV_DISABILITY_AMOUNT_2026,
    provDisabilitySupplementU18: PROV_DISABILITY_SUPP_U18_2026,
    provPensionAmountMax: PROV_PENSION_AMOUNT_MAX,
    provDividendCredit: PROV_DIVIDEND_CREDIT,
    // CPP & EI
    cppYMPE: CPP_YMPE,
    cppYAMPE: CPP_YAMPE,
    cppBasic: CPP_BASIC,
    cpp1Rate: CPP1_RATE,
    cpp2Rate: CPP2_RATE,
    eiMIE: EI_MIE,
    eiRate: EI_RATE,
    // Refundable-benefit amounts (year-specific). CCB amounts here are the
    // July 2026–June 2027 benefit year; BC Family Benefit is the current
    // structure used by the 2026 BC fixtures. computeCCB / computeBCFamilyBenefit
    // read these via cfg, defaulting to these 2026 values when cfg is absent.
    benefits: {
      // 2026-27 benefit year = 2025-26 × 1.02 (CRA 2026 indexation 2.0%).
      // Previous values (8213/6930/37485) were over-indexed estimates; these
      // reconcile DM ON johnston/scott/angus/sylvie CCB rows to within $1.
      ccb: { under6: 8157, over6: 6883, threshold1: 38237, threshold2: 82847 },
      // BC Family Benefit 2026-27 BASE amounts (verified vs BC Min. Finance + CRA
      // provincial-programs page). The 25% "Family Benefit Bonus" (2,188/1,375/
      // 1,125) applied ONLY to July 2024–June 2025 and has EXPIRED; amounts returned
      // to base from July 2025 on. Phase-out 4% over $30,176 (2026-27 indexed band),
      // floors 775/750/725 (we approximate 2nd/additional as 725).
      bcfb: { max1: 1750, max2: 1100, maxRest: 900, single: 500,
              phaseStart: 30176, rate: 0.04, floor1: 775, floor2: 750, floorRest: 725,
              upperStart: 96562 }, // 2026-27 band; floor holds to here, then tails to 0
      // Child Disability Benefit (CDB) — refundable top-up to the CCB for each
      // DTC-eligible child under 18. 2025-26 benefit year: max $3,411/child,
      // phased out above AFNI $82,847 at 3.2% (1 eligible child) / 5.7% (2+).
      // Paid to whoever receives the CCB (shared → split via child weight).
      // 2026-27 CDB (CRA, 2026-06-15): max 3,480 (was 3,411 in 2025-26)
      cdb: { max: 3480, threshold: 82847, rate1: 0.032, rate2plus: 0.057 },
    },
  },
}
// ─── 2025 tax year ──────────────────────────────────────────────────────────
// Federal + BC values populated from CRA / BC public reference tables.
// Other provinces inherit 2026 combined brackets (i.e. 2026 federal + 2026
// provincial) — when running 2025 calcs for non-BC provinces, expect drift
// until each provincial 2025 schedule is populated.
//
// 2025 BC combined fed+prov brackets:
//   Federal 2025: 14.5% / 20.5% / 26% / 29% / 33%  (Bill C-4 first cut: 15% → 14.5%)
//   BC 2025:      5.06% / 7.7% / 10.5% / 12.29% / 14.7% / 16.8% / 20.5%
//   Federal steps: 57,375 / 114,750 / 177,882 / 253,414
//   BC steps:      49,279 / 98,560 / 113,158 / 137,407 / 186,306 / 259,829
const MARGINAL_BRACKETS_2025_BC = [
  [0,       0.1956], // 5.06 + 14.5
  [49279,   0.2220], // 7.7 + 14.5
  [57375,   0.2820], // 7.7 + 20.5
  [98560,   0.3100], // 10.5 + 20.5
  [113158,  0.3279], // 12.29 + 20.5
  [114750,  0.3829], // 12.29 + 26
  [137407,  0.4070], // 14.7 + 26
  [177882,  0.4370], // 14.7 + 29
  [186306,  0.4580], // 16.8 + 29
  [253414,  0.4980], // 16.8 + 33
  [259829,  0.5350], // 20.5 + 33
]
TAX_YEARS[2025] = {
  year: 2025,
  label: '2025',
  placeholder: false,
  // Federal (2025 indexed, post Bill C-4 first-step rate cut to 14.5%)
  fedBPA: 16129,
  fedLowestRate: 0.145,
  cea: 1471,  // Canada Employment Amount 2025
  fedAgeAmount: 8790,
  fedAgeThreshold: 44325,
  oasClawbackThreshold: 93454, // CRA-published 2025 OAS recovery threshold
  medicalFloor: 2834,           // CRA 2025 medical-expense floor cap
  fedPensionAmountMax: 2000,
  fedDisabilityAmount: FED_DISABILITY_AMOUNT_2025,
  fedDisabilitySupplementU18: FED_DISABILITY_SUPP_U18_2025,
  // Provincial — BC populated; other provinces still 2026-keyed (uncalibrated for 2025)
  marginalBrackets: { ...TAX_YEARS[2026].marginalBrackets, BC: MARGINAL_BRACKETS_2025_BC },
  marginalRates: TAX_YEARS[2026].marginalRates,
  provTaxData: {
    ...TAX_YEARS[2026].provTaxData,
    BC: { bpa: 12932, rate: 0.0506 },
  },
  provAgeAmount: {
    ...TAX_YEARS[2026].provAgeAmount,
    BC: { amount: 5506, threshold: 41032 },
  },
  provDisabilityAmount: PROV_DISABILITY_AMOUNT_2025,
  provDisabilitySupplementU18: PROV_DISABILITY_SUPP_U18_2025,
  provPensionAmountMax: TAX_YEARS[2026].provPensionAmountMax,
  // Provincial DTC rates don't change year-to-year (statutory). Inherit from 2026.
  provDividendCredit: TAX_YEARS[2026].provDividendCredit,
  // CPP & EI 2025
  cppYMPE: 71300,
  cppYAMPE: 81200,
  cppBasic: 3500,
  cpp1Rate: 0.0595,
  cpp2Rate: 0.04,
  eiMIE: 65700,
  eiRate: 0.0164,
  // Refundable benefits — July 2025–June 2026 benefit year.
  //   CCB: $7,997 (<6) / $6,748 (6–17); thresholds $37,487 / $81,222.
  //   BC Family Benefit: 25% bonus ended July 2025 → max $1,750 / $1,100 / $900
  //   (1st/2nd/each additional), single-parent supplement $500, phase-out 4%
  //   from $29,526. (Validated vs DM auchstaetter-2025: 3 kids @ ~$42.7k AFNI →
  //   CCB ~$20.5k, BCFB ~$3.72k.) GST credit values match 2026, so shared.
  benefits: {
    ccb: { under6: 7997, over6: 6748, threshold1: 37487, threshold2: 81222 },
    bcfb: { max1: 1750, max2: 1100, maxRest: 900, single: 500,
            phaseStart: 29526, rate: 0.04, floor1: 775, floor2: 750, floorRest: 725,
            upperStart: 94483 }, // 2025-26 band upper
  },
}
// 2024 and 2023 still placeholders — populate when DM cases from those years
// are encoded. `placeholder: true` triggers the warning banner.
TAX_YEARS[2024] = { ...TAX_YEARS[2026], year: 2024, label: '2024', placeholder: true }
TAX_YEARS[2023] = { ...TAX_YEARS[2026], year: 2023, label: '2023', placeholder: true }

// Years offered in the UI selector. Newest first.
// 2024/2023 hidden until their reference data is populated — they'd appear as
// "(data pending)" and silently compute with 2026 values. Uncomment to restore.
const TAX_YEAR_OPTIONS = [2026, 2025 /* , 2024, 2023 */]
const CURRENT_TAX_YEAR = 2026

// Compute CPP and EI contributions for a person
// Returns { cpp, ei, cppCreditable, cppDeductible }:
//   cpp           — total cash deduction (2× for self-employed)
//   cppCreditable — the BASE portion (4.95% of pensionable) eligible for non-refundable credit
//   cppDeductible — the enhancement portion deductible from taxable income (line 22215)
//                   For employees: enhancement of CPP1 (1/5.95 of CPP1) + all of CPP2
//                   For self-employed: 50% of contribution + enhancement of employee half
function getCppEi(gross, isSelfEmployed, cfg) {
  cfg = cfg || TAX_YEARS[2026]
  const cpp1 = Math.min(Math.max(0, Math.min(gross, cfg.cppYMPE) - cfg.cppBasic) * cfg.cpp1Rate,
                        (cfg.cppYMPE - cfg.cppBasic) * cfg.cpp1Rate)
  const cpp2 = Math.min(Math.max(0, Math.min(gross, cfg.cppYAMPE) - cfg.cppYMPE) * cfg.cpp2Rate,
                        (cfg.cppYAMPE - cfg.cppYMPE) * cfg.cpp2Rate)
  const ei = isSelfEmployed ? 0 : Math.min(gross * cfg.eiRate, cfg.eiMIE * cfg.eiRate)
  const cppEmployee = cpp1 + cpp2
  const cppTotal = isSelfEmployed ? cppEmployee * 2 : cppEmployee
  // Base portion of CPP1 (4.95% of pensionable) — credit-eligible
  const cppCreditable = cpp1 * (4.95 / 5.95)
  // Enhancement deduction (line 22215 on T1)
  let cppDeductible
  if (isSelfEmployed) {
    // Self-employed deduct: 50% of total contribution + enhancement portion of employee half
    cppDeductible = cppEmployee + (cpp1 * (1.0 / 5.95))
  } else {
    cppDeductible = (cpp1 * (1.0 / 5.95)) + cpp2
  }
  return { cpp: cppTotal, ei, cppCreditable, cppDeductible }
}

// Compute combined federal + provincial non-refundable tax credits
// Includes BPA, CPP/CPP2 contributions (employee portion), and EI premiums.
// Federal credit rate is 14.5% (2025 lowest bracket post mid-year rate cut).
function getTaxCredits(province, gross, isSelfEmployed, cfg) {
  cfg = cfg || TAX_YEARS[2026]
  const { ei, cppCreditable } = getCppEi(gross, isSelfEmployed, cfg)
  const fedCredits = (cfg.fedBPA + cppCreditable + ei) * cfg.fedLowestRate
  const prov = cfg.provTaxData[province] || { bpa: 12000, rate: 0.05 }
  const provCredits = (prov.bpa + cppCreditable + ei) * prov.rate
  return fedCredits + provCredits
}

// Compute combined federal + provincial income tax (gross of credits)
function getRawIncomeTax(province, grossIncome, cfg) {
  cfg = cfg || TAX_YEARS[2026]
  const brackets = cfg.marginalBrackets[province]
  if (!brackets) return grossIncome * 0.30
  let tax = 0
  for (let i = 0; i < brackets.length; i++) {
    const [lo, rate] = brackets[i]
    const hi = i + 1 < brackets.length ? brackets[i + 1][0] : grossIncome
    if (grossIncome <= lo) break
    tax += (Math.min(grossIncome, hi) - lo) * rate
  }
  return tax
}

// Net income after federal + provincial tax (after credits) AND CPP + EI cash deductions
// Matches Divorcemate's "Take-home" / "Net" income approximation
function getNetIncome(province, grossIncome, isSelfEmployed, cfg) {
  if (grossIncome <= 0) return 0
  cfg = cfg || TAX_YEARS[2026]
  const tax = getRawIncomeTax(province, grossIncome, cfg)
  const credit = getTaxCredits(province, grossIncome, isSelfEmployed, cfg)
  const taxPayable = Math.max(0, tax - credit)
  const { cpp, ei } = getCppEi(grossIncome, isSelfEmployed, cfg)
  return Math.max(0, grossIncome - taxPayable - cpp - ei)
}

// Backwards-compat wrapper for s.7 calculations and any pre-existing call sites
// (Treats income as employed by default)
function getAfterTaxIncome(province, grossIncome, cfg) {
  return getNetIncome(province, grossIncome, false, cfg)
}

// ─────────────────────────────────────────────────────────────────────────────
// REFUNDABLE BENEFITS — included in INDI for SSAG calculation
// 2025 figures. Approximations within ~$50 of CRA-precise amounts for typical cases.
// ─────────────────────────────────────────────────────────────────────────────

// Canada Child Benefit (CCB) — 2025
// Base: $7,997/yr per child <6, $6,748/yr per child 6-17
// Phase-out (AFNI = adjusted family net income):
//   $0-$36,502: full
//   $36,502-$79,087: reduced by phaseRate1 × (AFNI - 36502)
//   above $79,087: minus phase1 cap, then phaseRate2 × (AFNI - 79087)
// children: array of { age, weight } — weight is 1.0 for sole, 0.5 for shared
// CRA rule for shared custody: compute FULL-FAMILY CCB at full count/rates,
// then each parent gets half. We model this by computing the full CCB ignoring
// weights, then multiplying by the average weight of children claimed by this party.
function computeCCB(afni, children, cfg) {
  if (!children || children.length === 0) return 0
  // Year-specific CCB amounts/thresholds (default to 2026 benefit year).
  const ccb = (cfg && cfg.benefits && cfg.benefits.ccb) ||
    { under6: 8157, over6: 6883, threshold1: 38237, threshold2: 82847 }
  // CCB only applies to children under 18. Filter to eligibles first so the
  // phase-out tier (which uses the count of children) is consistent with
  // the base summed below.
  const eligible = children.filter(c => (c.age != null ? c.age : 12) < 18)
  if (eligible.length === 0) return 0
  const n = eligible.length
  // Full-family base — year-keyed CCB rates (under 6 / 6-17).
  let fullBase = 0
  for (const c of eligible) {
    const age = c.age != null ? c.age : 12
    fullBase += age < 6 ? ccb.under6 : ccb.over6
  }
  // Two-tier phase-out (year-keyed thresholds).
  let fullCCB = fullBase
  if (afni > ccb.threshold1) {
    const r1 = n === 1 ? 0.07 : n === 2 ? 0.135 : n === 3 ? 0.19 : 0.23
    if (afni <= ccb.threshold2) {
      fullCCB = Math.max(0, fullBase - r1 * (afni - ccb.threshold1))
    } else {
      const cap1 = r1 * (ccb.threshold2 - ccb.threshold1)
      const r2 = n === 1 ? 0.032 : n === 2 ? 0.057 : n === 3 ? 0.08 : 0.095
      fullCCB = Math.max(0, fullBase - cap1 - r2 * (afni - ccb.threshold2))
    }
  }
  // Apply this party's average custody weight (1.0 sole, 0.5 fully shared)
  const avgWeight = eligible.reduce((s, c) => s + (c.weight != null ? c.weight : 1), 0) / n
  return Math.round(fullCCB * avgWeight)
}

// Child Disability Benefit (CDB) — refundable CCB top-up for each DTC-eligible
// child under 18 (child.disabled === true). Its own phase-out above AFNI
// threshold: 3.2% for one eligible child, 5.7% for two or more. Split by custody
// weight in shared care (like the CCB). Year-keyed via cfg.benefits.cdb.
function computeCDB(afni, children, cfg) {
  const cdb = (cfg && cfg.benefits && cfg.benefits.cdb) ||
    { max: 3411, threshold: 82847, rate1: 0.032, rate2plus: 0.057 }
  const eligible = (children || []).filter(c => c.disabled && (c.age != null ? c.age : 12) < 18)
  if (eligible.length === 0) return 0
  const n = eligible.length
  let full = n * cdb.max
  if (afni > cdb.threshold) {
    const rate = n === 1 ? cdb.rate1 : cdb.rate2plus
    full = Math.max(0, full - rate * (afni - cdb.threshold))
  }
  const avgWeight = eligible.reduce((s, c) => s + (c.weight != null ? c.weight : 1), 0) / n
  return Math.round(full * avgWeight)
}

// GST/HST credit — 2025-2026 benefit year (annual)
// GST/HST credit — 2025-2026 benefit year (annual)
// Base: $349 single, $698 couple. Single parents with eligible kids get the couple base.
// Per child: $184. Single supplement (no kids) up to $184 phases in 2% above $11,337.
// Phase-out at 5% above $45,521 family net income (2025-2026 threshold).
//
// TASK #54 — CGEB REWORK ATTEMPTED & REVERTED (5th attempt): the CGEB model IS
// correct and fully reverse-engineered from DM PDFs:
//   base $945 + first child (equiv-to-spouse) $620 + $326 each additional;
//   child portion × shareFactor (½ shared, per CRA "50% of child amount");
//   phase-out ~7.7% over $46,432. Verified to the dollar on sole 2-kid/3-kid &
//   shared (sylvie/Lucie). BUT implementing it (even with the correct shared
//   split + dropping BC-Climate) STILL net-regressed the suite (710→702):
//   raising recipient benefits makes our SSAG amounts OVERSHOOT — our INDI
//   solver over-responds to recipient benefits vs DM, AND dropping BC-Climate
//   re-breaks lewis-shaw/campo-after-uni (calibration entangled with it). The
//   cluster is a SOLVER-mechanics + entangled-calibration issue, NOT a benefit-
//   amount one. CGEB model preserved in memory for a future solver-side rework.
// TASK #54 — CGEB model fully researched/verified (base 945 + first child 620 +
// 326 addl; child portion × shareFactor ½ in shared; phase-out 7.7% over 46432).
// Fixes the COMMON single-parent-recipient case (peter-sarah → exact) but breaks
// edge cases (non-taxable-payor lewis-shaw, adult-children campo) and nets the
// suite −8, due to INDI-solver interactions not yet reconciled. Held back from
// the shipped build; model is in memory for a solver-side follow-up.
function computeGSTCredit(afni, isSingle, numChildren) {
  const useCoupleBase = !isSingle || numChildren > 0
  let credit = useCoupleBase ? 698 : 349
  if (isSingle && numChildren === 0) {
    credit += Math.min(184, Math.max(0, (afni - 11337) * 0.02))
  }
  credit += numChildren * 184
  const threshold = 45521
  if (afni > threshold) credit = Math.max(0, credit - (afni - threshold) * 0.05)
  return Math.round(credit)
}

// ─────────────────────────────────────────────────────────────────────────────
// CGEB — Canada Groceries and Essentials Benefit (replaces GST/HST credit from
// July 2026; +25% for 5 years). Model fully reverse-engineered from DM Tools
// Cloud 2026 PDFs (Calc 53/54/55 sole, sylvie/Lucie shared), verified to the $:
//   base $945 (per adult, NOT shared)
//   + first child (equivalent-to-spouse) $620 + $326 each additional child
//   child portion (first+addl) × shareFactor (1.0 sole / 0.5 fully shared —
//     CRA "50% of the CHILD amount" rule, NOT halving the count)
//   phase-out 7.7% above $46,432 AFNI (empirical; published 5% does not match DM)
// Verified: sole 2-kid 1891, sole 3-kid 2217, shared 2-kid claimant 1418
// (sylvie), shared 1-kid 1036 (Lucie, after phase-out at AFNI≈49.2k).
//
// Childless CGEB is NOT separately calibrated against DM, so childless filers
// keep the legacy GST model (computeGSTCredit) — only with-children filers use
// CGEB. Toggle via USE_CGEB for isolated A/B calibration measurement.
// CGEB — calibrated to DM Tools Cloud 2026, which reports the recipient's FULL
// 2026-27 cash benefit: the recurring +25% boost PLUS the one-time +50% top-up paid
// June 2026 (Bill C-19; ≈ base × 1.75). We MATCH DM so the figure equals the
// recipient's actual 2026-27 receipts and lines up with the DivorceMate printout
// counsel cross-check against. The +50% top-up is one-time and won't recur after
// 2026 — surfaced in the UI methodology note. Amounts/phase back-fit to DM:
// sole 2-kid 1891, shared 2-kid 1418, shared 1-kid 1036, johnston shared-2-kid
// 805/510/305 (AFNI 56.5/62.1/67.6k); phase ~5.5% over $46,432 (DM's curve).
// CGEB POLICY STRUCTURE — PRIMARY-SOURCE VERIFIED (Bill C-19, SC 2026 c.1; ITA
// s.122.5; Finance Canada explanatory notes; deep-research wf_fa9f3aa5, 2026-06-29).
// DM's reported 2026-27 CGEB is the SUM of two real statutory layers, both received
// in 2026-27:
//   (1) RECURRING credit (s.122.5(3.005)): $445 self + $445 first child at the
//       equivalent-to-spouse (qualified-relation) rate + $234 per child (all kids).
//       Phase-out FLAT 5% of AFNI over $46,432. (Max single $679, couple $890.)
//   (2) ONE-TIME top-up (s.122.5(3.004), paid Jun 2026): published table by #kids —
//       single/single-parent $267/$441/$533/$625/$717 for 0/1/2/3/4 children
//       (≈50% of the 2025-26 GST/HST entitlement). Phase-out 15% over $45,521.
// SOLE-CUSTODY cases reconcile to the published amounts TO THE DOLLAR:
//   sole 2-kid = recurring (445+445+2×234 = 1358) + top-up 533 = 1891  ✓ (= Finance's
//   own $1,890 worked example); sole 3-kid = 1592 + 625 = 2217  ✓.
// The concave curve is just these two flat slopes: near the threshold the recurring
// 5% + top-up 15% SUM (~steep); above ~$48k the top-up is fully phased and only the
// 5% recurring remains (gentle). So the curve is POLICY-GROUNDED, not arbitrary.
// WHY A FITTED CURVE IS RETAINED (not the literal two-component formula): DM's
// SHARED-CUSTODY figures (sylvie 1418, Lucie 1036, johnston 805/510/305) do NOT
// reconcile to the published amounts under ANY consistent shared-split rule — the
// statute's "0.5×(A+B)" (s.122.5(3.06)) does not pin down whether the equiv-to-spouse
// uplift and the top-up halve, and DM uses an internal convention the text doesn't
// fix. A literal published-formula reimplementation reproduces SOLE cases but DIVERGES
// from DM on shared cases (johnston recurring-only 5% → ~513-853 vs DM 805). Per the
// standing decision to MATCH DM (counsel cross-check the DM printout), we keep the
// fitted curve below as a faithful approximation of the verified two-slope structure.
// Anchors: Justin/mora ($47,883), johnston (805/510/305 @ $56,537/$62,086/$67,579);
// `over` = AFNI − $46,432. See memory benefit-figures-2026-27 + calibration-method.
function cgebPhaseReduction(over) {
  if (over <= 0) return 0
  if (over <= 1451)  return over * 0.0903                       // → 131 @ 1451 (Justin)
  if (over <= 10105) return 131 + (over - 1451) * 0.0557        // → 613 @ 10105 (johnston low)
  if (over <= 15654) return 613 + (over - 10105) * 0.0532       // → 908 @ 15654 (johnston mid)
  return 908 + (over - 15654) * 0.0373                          // → 1113 @ 21147 (johnston high)
}
// isClaimant: the equivalent-to-spouse uplift ($620 vs the $326 additional rate) for
// the FIRST child goes ONLY to the parent who claims the eligible dependant (CRA's
// equivalent-to-spouse rule). The non-claiming parent (e.g. the payor in a shared-
// custody case where the recipient claims) treats every child at the additional rate.
// Verified vs DM lewis-shaw: Lawrence (payor, non-claimant, shared 1 kid, AFNI≈0) →
// 945 + 326×0.5 = 1108 ≈ DM's CGEB line, vs the 1255 the claimant rate would give.
function computeCGEB(afni, numChildren, shareFactor, isClaimant = true) {
  let childPortion = 0
  if (numChildren >= 1) {
    const firstChild = isClaimant ? 620 : 326
    childPortion = firstChild + Math.max(0, numChildren - 1) * 326
  }
  let credit = 945 + childPortion * shareFactor
  credit = Math.max(0, credit - cgebPhaseReduction(afni - 46432))
  return Math.round(credit)
}

// CGEB landing toggles (task #56). Flip + hard-reload to A/B-measure each change
// in isolation against the DM calibration suite before committing.
//   USE_CGEB        — with-children filers use CGEB instead of legacy GST credit
//   DROP_BC_CLIMATE — drop the BC Climate Action portion (DM Tools 2026 does not
//                     model it; its recipient benefit line = CCB+CGEB+BCFB only)
// CGEB is fully implemented + dollar-verified (sylvie shared 2-kid = 1418), but
// flipping USE_CGEB on net-regresses the suite −9 regardless of variant
// (full / +climate-drop / shared-only). Root cause is NOT the CGEB amounts: it
// is the INDI solver's response to recipient benefits — raising recipient
// benefits lowers our solved SS more than DM's does, which worsens the many
// fixtures that already slightly UNDERSHOOT. CGEB is held behind the flag until
// the solver response (m_r) is reconciled and/or the undershooting BC fixtures
// are refreshed against fresh DM PDFs showing the explicit CGEB benefit line.
// Baseline (both false) = clean 710 exact-match.
const USE_CGEB = false
const DROP_BC_CLIMATE = false
const CGEB_SHARED_ONLY = false

// ─────────────────────────────────────────────────────────────────────────────
// PROVINCIAL CHILD BENEFITS — annual amounts, dispatched by province
// ─────────────────────────────────────────────────────────────────────────────
// BC: calibrated against Divorcemate (Phase 1-4). All other provinces use
// public CRA + provincial reference values (2026 benefit year where available)
// — uncalibrated until DM test cases from those provinces are encoded.
// ─────────────────────────────────────────────────────────────────────────────

// BC Family Benefit — year-keyed (constants via cfg.benefits.bcfb).
function computeBCFamilyBenefit(afni, numChildren, isSingle, cfg) {
  // Two-tier formula:
  //   - Max amount: max1 (1st child) + max2 (2nd) + maxRest each additional
  //   - Single-parent supplement: +single
  //   - Phase-out from phaseStart at `rate`, BUT floor at minimum of
  //     floor1 (1st child) + floorRest each additional
  //   - Above the upper threshold (where minimum would equal phased-down full),
  //     benefit zeroes out completely (no minimum applies)
  //
  // 2026 defaults are the values the BC 2026 fixtures were calibrated against
  // ($2,188/$1,375/$1,125, phaseStart $27,397). The 2025 benefit year ended the
  // 25% bonus → $1,750/$1,100/$900, phaseStart $29,526 (via cfg).
  //
  // TWO-BAND phase-out (verified vs DM Tools Cloud 2026 PDFs):
  //   • AFNI ≤ phaseStart: full amount.
  //   • phaseStart..upperStart ($96,562 for 2026-27): full reduced 4%, but never
  //     below the FLOOR (floor1/floor2/floorRest). The floor HOLDS across this
  //     whole band — it does NOT cliff to zero.
  //   • above upperStart: the floor itself is reduced 4% of income over upperStart,
  //     down to zero.
  // DM data points confirming this: angus Colin (2 kids, AFNI $80,378) → $762
  // (= floor $1,525 × ½ shared); lewis-shaw Tannis (1 kid, AFNI $161,063) → $0
  // (floor $775 fully tailed off); johnston Tim (2 kids, $125,785) → ~$136 (tail).
  if (numChildren === 0) return 0
  const b = (cfg && cfg.benefits && cfg.benefits.bcfb) ||
    { max1: 1750, max2: 1100, maxRest: 900, single: 500,
      phaseStart: 30176, rate: 0.04, floor1: 775, floor2: 750, floorRest: 725, upperStart: 96562 }
  let fullMax = b.max1
  for (let i = 1; i < numChildren; i++) fullMax += (i === 1 ? b.max2 : b.maxRest)
  fullMax += (isSingle ? b.single : 0)
  let floor = b.floor1
  for (let i = 1; i < numChildren; i++) floor += (i === 1 ? (b.floor2 != null ? b.floor2 : b.floorRest) : b.floorRest)
  const upperStart = b.upperStart != null ? b.upperStart : (b.phaseStart + (fullMax - floor) / b.rate)
  let benefit
  if (afni <= b.phaseStart) {
    benefit = fullMax
  } else if (afni <= upperStart) {
    benefit = Math.max(floor, fullMax - (afni - b.phaseStart) * b.rate)
  } else {
    benefit = Math.max(0, floor - (afni - upperStart) * b.rate)
  }
  return Math.max(0, Math.round(benefit))
}

// Alberta Child and Family Benefit (ACFB) — 2026-27 benefit year (uncalibrated)
// Base + Working components, per-family amounts (NOT per-child).
// Base table by # of kids: 1=$1,529, 2=$2,293, 3=$3,057, 4+=$3,821
// Working table by # of kids: 1=$782, 2=$1,494, 3=$1,920, 4+=$2,061
// Base phase-out start: $28,116; ramp rate not published — use 8% as approx.
// Working phase-in: 15% above $2,760 employment income; phase-out starts $47,115.
function computeABFamilyBenefit(afni, numChildren, workingIncome) {
  if (numChildren === 0) return 0
  const baseTable    = [0, 1529, 2293, 3057, 3821]
  const workingTable = [0,  782, 1494, 1920, 2061]
  const idx = Math.min(numChildren, 4)
  let base = baseTable[idx]
  let working = workingTable[idx]
  // Statutory phase-out rates by child count (APITA RSA 2000 c A-30 s.30.2(2)):
  // base 8.05/12.07/16.09/20.11%, working 3.40/6.49/8.34/8.95%. The previous
  // flat 8%/5% were approximations (found in the AB line-by-line audit —
  // johnston/hughes recipients drifted +$61-146/yr, growing with income).
  const baseRates = [0, 0.0805, 0.1207, 0.1609, 0.2011]
  const workRates = [0, 0.034, 0.0649, 0.0834, 0.0895]
  // Base phase-out above $28,116 AFNI
  if (afni > 28116) base = Math.max(0, base - (afni - 28116) * baseRates[idx])
  // Working phase-in 15% above $2,760 employment income
  if (workingIncome <= 2760) working = 0
  else working = Math.min(working, (workingIncome - 2760) * 0.15)
  // Working phase-out above $47,115 AFNI (statutory rate by child count)
  if (afni > 47115) working = Math.max(0, working - (afni - 47115) * workRates[idx])
  return Math.max(0, Math.round(base + working))
}

// Ontario Child Benefit (OCB) — 2026-27 benefit year (uncalibrated)
// Max $1,760/yr per child. Phase-out 8% above $26,865 AFNI.
function computeONChildBenefit(afni, numChildren) {
  if (numChildren === 0) return 0
  let benefit = numChildren * 1760
  const threshold = 26865
  if (afni > threshold) benefit = Math.max(0, benefit - (afni - threshold) * 0.08)
  return Math.round(benefit)
}

// Saskatchewan Low-Income Tax Credit (SLITC) — 2025-26
// Calibrated against DM SK Johnston-Smith (Jessica $51,515 AFNI → DM $309)
// and Hughes-Peslova SK (Lucie $55,135 AFNI → DM $73):
//   - $410 per adult
//   - $161 per child (max 2 kids)
//   - Phase-out 2.75% above $36,360 AFNI
//   - NO spouse-equivalent base for single parents (different from BCFB)
// Per-DM back-solve: Jessica 2-kid match within $6; Lucie 1-kid match within $18.
// Previous formula used stale 2024 values ($460+$460 single-parent bonus +
// $181/kid) which gave ~3x the correct SLITC at typical mid-income.
// SLITC July 2026–June 2027 (CRA province-saskatchewan page, 2026-06-12):
// $460 adult + $460 spouse/eligible-dependant + $181 per child (max 2, family
// max $1,282); reduction from AFNI $39,345, nil at $81,668 (implied rate
// ≈3.03%). Pre-audit values (410/161/2.75%/36,360) were two benefit years
// stale. A single parent's eligible-dependant equivalent is included when
// there are children (matches the family-max structure).
function computeSKBenefit(afni, numChildren) {
  let credit = 460
  if (numChildren > 0) credit += 460
  credit += Math.min(numChildren, 2) * 181
  if (afni > 39345) credit = Math.max(0, credit - (afni - 39345) * 0.0303)
  return Math.round(credit)
}

// Manitoba Child Benefit — small. $35/mo per child for low-income families.
// $420/yr per child max. Phase-out steep around $15-20k AFNI.
function computeMBChildBenefit(afni, numChildren) {
  if (numChildren === 0) return 0
  let benefit = numChildren * 420
  if (afni > 15000) benefit = Math.max(0, benefit - (afni - 15000) * 0.084)
  return Math.round(benefit)
}

// Nova Scotia Child Benefit (NSCB) — 2026-27 (uncalibrated)
// $127.08/mo per child = $1,525/yr. AFNI partial $26,000-$34,000, nil above.
function computeNSChildBenefit(afni, numChildren) {
  if (numChildren === 0) return 0
  const perChild = 1525
  let benefit = numChildren * perChild
  if (afni > 26000) {
    if (afni >= 34000) benefit = 0
    else benefit = benefit * (1 - (afni - 26000) / 8000)
  }
  return Math.max(0, Math.round(benefit))
}

// Newfoundland & Labrador Child Benefit (NLCB) — 2026-27 (uncalibrated)
// Per month: 1st $155.66, 2nd $165.00, 3rd $177.25, 4th+ $190.41.
// Phase-out: full below $17,397; partial $17,397-$28,990; nil above.
// NL Child Benefit July 2026-27 (NLR 16/26, CRA NL programs page): per-child
// monthly 157.33 / 166.83 / 179.16 / 192.50 (1st/2nd/3rd/4th+); reduced by
// the statutory rate on AFNI over $20,397 (10.61% / 21.86% / 33.94% /
// +12.98% per child beyond 3).
function computeNLChildBenefit(afni, numChildren) {
  if (numChildren === 0) return 0
  const perMo = [0, 157.33, 166.83, 179.16, 192.50]
  let annual = 0
  for (let i = 1; i <= numChildren; i++) annual += perMo[Math.min(i, 4)] * 12
  const rates = [0, 0.1061, 0.2186, 0.3394]
  const rate = numChildren <= 3 ? rates[numChildren] : 0.3394 + 0.1298 * (numChildren - 3)
  if (afni > 20397) annual -= rate * (afni - 20397)
  return Math.max(0, Math.round(annual))
}

// New Brunswick Child Tax Benefit (NBCTB) — 2026-27 (uncalibrated)
// $20.83/mo per child = ~$250/yr per child. Phase-out $20,000 → $26,364.
function computeNBChildBenefit(afni, numChildren) {
  if (numChildren === 0) return 0
  let benefit = numChildren * 250
  if (afni > 20000) {
    if (afni >= 26364) benefit = 0
    else benefit = benefit * (1 - (afni - 20000) / 6364)
  }
  return Math.max(0, Math.round(benefit))
}

// PEI Sales Tax Credit — back-fit from DM 2026:
// $110 base, 0.5% phase-out above $50,000. Verified Gary PE Campo (afni $0) → $110;
// Justin PE Mora-Primary (afni $53,720) → ours $91, DM $91 (exact).
// PEI Child Benefit, July 2026-June 2027 (CRA province-prince-edward-island
// page + princeedwardisland.ca): stepped $410/child (AFNI < 45,000), $290
// (45,000-80,000), nil above. Cliffs, not a rate phase-out. Replaces the old
// PE sales-credit stub (the sales tax credit itself was replaced by the
// Island Essentials Benefit). Reconciles the PE audit recipients exactly
// (sylvie 410x2x0.5, johnston/angus 290x2x0.5, hughes 290x0.5).
function computePEBenefit(afni, numChildren, isSingle) {
  if (numChildren === 0) return 0
  const per = afni < 45000 ? 410 : afni <= 80000 ? 290 : 0
  return per * numChildren
}

// PEI Island Essentials Benefit (refundable) — the 2026 PEI benefit that DM Tools
// Cloud 2026 books (replaces the old PE Sales Tax Credit in DM's output). PER-ADULT
// (each separated parent gets their own based on their income + whether they have
// children in the household — NOT split by custody share). Reverse-engineered to
// the dollar from DM PE PDFs: full $338 (children in household) / $310 (no children),
// phased out 1.0% of AFNI over $95,000 (nil ~$128,800). Verified: Tim $116,524 → 123
// (DM 122), CJ $101,701 → 271 (270), Michael $118,342 → 105 (104), Colin $93,591 →
// 338, Julie $147k → 0.
function computePEIslandEssentials(afni, hasKidsInHousehold) {
  let amount = hasKidsInHousehold ? 338 : 310
  if (afni > 95000) amount -= (afni - 95000) * 0.01
  return Math.max(0, Math.round(amount))
}

// Manitoba Personal Tax Credit (refundable) — MB form MB479 line 61.
// $195 base + $113 per dependant + $113 spouse-equivalent, reduced by 1% of family income.
// Verified Gary MB Campo (afni $0) → $195; all other MB fixtures → $0.
function computeMBPersonalTaxCredit(afni, numChildren, hasEDC) {
  let base = 195
  base += numChildren * 113
  if (hasEDC) base += 113
  return Math.max(0, Math.round(base - afni * 0.01))
}

// New Brunswick HST Credit (refundable) — quarterly NB benefit.
// $300 base + $300 family/EDC + $100 per child, reduced 2% above $35,000.
// Verified Gary NB Campo (afni $0) → $300; Lucie/Justin/Christal → $0.
function computeNBHSTCredit(afni, numChildren, hasEDC) {
  let credit = 300
  if (hasEDC) credit += 300
  credit += numChildren * 100
  return Math.max(0, Math.round(credit - 0.02 * Math.max(0, afni - 35000)))
}

// NL Low Income Tax Reduction / Income Supplement (refundable) — Income Tax Act s.46.
// $520 base + $250 family + $200 per dependant, phased 16% above $20,000.
// Verified Gary NL Campo (afni $0) → $520; everyone else at our incomes → $0.
function computeNLIncomeSupplement(afni, numChildren, hasEDC) {
  // Base $520 + $250 (equivalent-to-spouse / EDC) + $200 per (effective) child,
  // phased out ~3.71% of adjusted net income over ~$22,549. Phase reverse-engineered
  // from DM Tools Cloud 2026 NL (moore Sylvie $751 @ afni $28,452; hughes Lucie $74
  // @ $46,688 — a single 3.71% line). The prior 16%/$20,000 phase zeroed it for
  // everyone (way too steep).
  // OFFICIAL structure (gov.nl.ca income-supplement page, unindexed): basic
  // $254 + additional $266 phased IN at 5.32% of income over $15,000 (max at
  // $20,000) + $69 spouse + $231 per child under 19; phased OUT at 9% of
  // family net income over $40,000. Replaces the old single-slope back-fit.
  let credit = 254 + Math.min(266, 0.0532 * Math.max(0, afni - 15000))
  credit += numChildren * 231
  return Math.max(0, Math.round(credit - 0.09 * Math.max(0, afni - 40000)))
}

// NS Affordable Living Tax Credit (refundable) — NS Income Tax Act.
// $255 base + $255 family + $60 per child, phased 5% above $30,000.
// Verified Gary NS Campo (afni $0) → $255; others → $0.
function computeNSAffordableLivingCredit(afni, numChildren, hasEDC) {
  let credit = 255
  if (hasEDC) credit += 255
  credit += numChildren * 60
  return Math.max(0, Math.round(credit - 0.05 * Math.max(0, afni - 30000)))
}

// Yukon Child Benefit — $80.50/mo = $966/yr per child. Phase-out ~$35k.
function computeYTChildBenefit(afni, numChildren) {
  if (numChildren === 0) return 0
  let benefit = numChildren * 966
  const ytRate = numChildren === 1 ? 0.025 : 0.05
  if (afni > 35000) benefit = Math.max(0, benefit - (afni - 35000) * ytRate)
  return Math.round(benefit)
}

// NWT Child Benefit — $67.91/mo first kid, decreasing per addl. Approximate ~$800/yr per kid.
// NWT Child Benefit (NWT ITA s.3.2(2)): AGE-TIERED cumulative monthly
// amounts — under-6: 67.91/122.25/166.41/203.75 (+30.58 each addl);
// 6-17: 54.33/97.83/133.08/163.00 (+24.41). Mixed families: under-6
// children take the u6 schedule positions first, 6-17 children add the
// 6-17 per-position increments after them. Full below $30,000 AFNI, then
// straight-line to zero at $80,000. When ages are unavailable (children
// omitted), the 6-17 schedule is used. NOTE the documented DM divergence:
// DM freezes ages as entered (its 2026 NT under-6 case pays the u6 rate
// for a child who is 6+ in the benefit year); engine uses tax-year ages
// per the CRA rule.
function computeNTChildBenefit(afni, numChildren, children) {
  if (numChildren === 0) return 0
  const cumU6  = [0, 67.91, 122.25, 166.41, 203.75]
  const cum617 = [0, 54.33, 97.83, 133.08, 163.00]
  const at = (cum, n, addl) => n <= 4 ? cum[n] : cum[4] + addl * (n - 4)
  let monthly
  if (children && children.length) {
    const u6 = children.filter(c => (c.age != null ? c.age : 12) < 6).length
    const o6 = children.length - u6
    monthly = at(cumU6, u6, 30.58)
      + (at(cum617, Math.min(u6 + o6, 99), 24.41) - at(cum617, u6, 24.41))
  } else {
    monthly = at(cum617, numChildren, 24.41)
  }
  let benefit = monthly * 12
  if (afni > 30000) benefit *= Math.max(0, 1 - (afni - 30000) / 50000)
  return Math.round(benefit)
}

// Nunavut Child Benefit — $348/yr base per child + worker supplement.
// NU Child Benefit from July 1, 2026 (S.Nu. 2026 c.8): $696/yr per child;
// the territorial workers' supplement was abolished; reduced 3% (one child)
// or 5% (2+) of adjusted income over $22,065; CPI-indexed going forward.
function computeNUChildBenefit(afni, numChildren, workingIncome) {
  if (numChildren === 0) return 0
  let benefit = numChildren * 696
  const rate = numChildren === 1 ? 0.03 : 0.05
  if (afni > 22065) benefit = Math.max(0, benefit - (afni - 22065) * rate)
  return Math.round(benefit)
}

// Dispatcher: returns the appropriate provincial child benefit / family benefit.
// BC is calibrated; everything else is public-formula approximation.
function computeProvincialChildBenefit(province, afni, numChildren, isSingle, workingIncome, cfg, children) {
  switch (province) {
    case 'BC': return computeBCFamilyBenefit(afni, numChildren, isSingle, cfg)
    case 'AB': return computeABFamilyBenefit(afni, numChildren, workingIncome)
    case 'ON': return computeONChildBenefit(afni, numChildren)
    case 'SK': return computeSKBenefit(afni, numChildren)
    case 'MB': return computeMBChildBenefit(afni, numChildren)
    case 'NS': return computeNSChildBenefit(afni, numChildren)
    case 'NL': return computeNLChildBenefit(afni, numChildren)
    case 'NB': return computeNBChildBenefit(afni, numChildren)
    // PEI Child Benefit (stepped 410/290 per child at 45k/80k, July 2026-27)
    // — shareFactor'd like other provincial child benefits. The per-adult
    // Island Essentials Benefit is separate (added below, not shared).
    case 'PE': return computePEBenefit(afni, numChildren, isSingle)
    case 'YT': return computeYTChildBenefit(afni, numChildren)
    case 'NT': return computeNTChildBenefit(afni, numChildren, children)
    case 'NU': return computeNUChildBenefit(afni, numChildren, workingIncome)
    default:   return 0
  }
}

// Canada Workers Benefit (CWB) — 2025 (annual)
// Phases in at 27% of working income above $3,000.
// For families with kids: max ~$2,447 federal + provincial top-up.
// For BC, max ≈ $2,813 (calibrated to Divorcemate).
// Phases out from family income $26,805 to $46,212 (with kids).
function computeCWB(province, workingIncome, afni, hasKids, cfg) {
  if (workingIncome < 3000) return 0
  // 2025 benefit year (arrears mode) — DM-fit from Moore PT Final (BC, tax
  // year 2025): FAMILY max $2,662, phase-out 15% from $31,648 (Sylvie 2,662 at
  // afni 31,647; 2,056 at 35,688; 1,359 at 40,333 — one exact 15% line).
  // Single scaled proportionally from the 2026 single/family ratio (no 2025
  // single-filer DM point). Provincial reconfigurations (AB/NU) keep their
  // 2026 shapes for 2025 — uncalibrated, flagged by the 2025 partial-data
  // banner.
  if (cfg && cfg.year <= 2025 && province !== 'AB' && province !== 'NU') {
    const max = hasKids ? 2662 : 1545
    const phaseStart = hasKids ? 31648 : 27742
    const phasedIn = Math.min(max, (workingIncome - 3000) * 0.27)
    return Math.round(Math.max(0, Math.min(phasedIn, max - Math.max(0, (afni - phaseStart) * 0.15))))
  }
  // Canada Workers Benefit — 2026 basic amount (federal). Reverse-engineered to
  // the dollar from DM Tools Cloud 2026 (benefit-year 2026, matching CCB/CGEB):
  //   FAMILY: max $2,869, phase-out 15% from $31,254 (nil ~$50,381). Verified
  //     across 5 DM points — sylvie-moore-ON Sylvie $2,869 plateau @ afni $28.5k
  //     → $2,710 @ $32,314; hughes-20k-ON Lucie $203 @ $49,023 → $0 @ $55,006,
  //     all on a single 15% line.
  //   SINGLE: max $1,665, phase-out 15% from $27,394 (indexed proportionally
  //     from the 2024 single/family ratio; no single-filer DM point in hand).
  // Phase-in is 27% of working income over $3,000, capped at max.
  // ALBERTA reconfigures the CWB under its 2019 federal agreement — different
  // amounts/thresholds, verified vs DM Tools Cloud 2026 AB: FAMILY max $2,672
  // (moore Sylvie plateau to afni $32,687), phase-out 15% from ~$33,425
  // (hughes Lucie $509 @ $47,845). Single AB estimated proportionally (no AB
  // single-filer DM point). QC/NU also reconfigure but calibrate within tol.
  let max, phaseStart, phaseRate = 0.15
  if (province === 'AB') {
    max        = hasKids ? 2672  : 1550
    phaseStart = hasKids ? 33425 : 27000
  } else if (province === 'NU') {
    // NUNAVUT reconfigures the CWB — LOWER max but a much SLOWER phase-out that
    // extends benefit well past the federal nil point. Reverse-engineered from DM
    // Tools Cloud 2026 NU: FAMILY max $2,170 (moore Sylvie plateau to afni $34.5k),
    // phase-out ~8% from ~$40,000 (johnston Jessica $839→967 region, hughes Lucie).
    // Single approximated (cunningham Tanya, ~4% slope). Mid-income fit ±~$250
    // (NU's exact two-tier phase not fully recovered — territory, low caseload).
    max        = hasKids ? 2170  : 1350
    phaseStart = 40000
    phaseRate  = hasKids ? 0.08  : 0.04
  } else {
    max        = hasKids ? 2869  : 1665
    phaseStart = hasKids ? 31254 : 27394
  }
  // Phase-in
  let benefit = Math.min(max, (workingIncome - 3000) * 0.27)
  // Phase-out above the threshold (15% federal; province-specific where reconfigured).
  if (afni > phaseStart) benefit -= (afni - phaseStart) * phaseRate
  return Math.max(0, Math.round(benefit))
}

// BC refundable tax offset. The BC Climate Action Tax Credit has ENDED — its final
// payment was April 2025, cancelled alongside BC's consumer carbon tax (verified vs
// primary sources: BC release 2025FIN0014-000280, CRA 5010-PC, BC Min. Finance
// climate-action page). So for all forward-looking (2025-26+) calculations only the
// BC Sales Tax Credit remains — which also matches DM Tools Cloud 2026 (its benefit
// line is CCB + CGEB + BC Family Benefit, no climate term). [dropClimate/
// DROP_BC_CLIMATE params retained for the historical (≤2024) path but unused now.]
function computeBCRefundableTaxOffset(afni, numChildren, isSingle, dropClimate = false) {
  if (afni > 50000) return 0
  const salesCredit = (1 + numChildren) * 75
  let total = salesCredit
  if (afni > 41000) total = Math.max(0, total - (afni - 41000) * 0.05)
  return Math.round(total)
}

// Ontario Sales Tax Credit (the OSTC component of the Ontario Trillium
// Benefit; we do not model the rent/property-tax OEPTC — DM doesn't either).
// 2026-27 (CRA province-ontario page): $378 per adult and per child under 19,
// reduced 4% of adjusted net income over $29,047 (single, no children) /
// $36,309 (single parent). Shared custody per the statute's averaging rule:
// the mean of the amount WITH the shared dependants and WITHOUT them.
// Reproduces DM ON rows within ~$2: johnston 165.76 vs 164; hughes 123.7 vs
// 122; sylvie 756 vs 754; all payor/high-income rows 0.
function computeONSalesTaxCredit(afni, soleKids, sharedKids) {
  const AMT = 378, T_SINGLE = 29047, T_PARENT = 36309, RATE = 0.04
  const calc = (kids) => {
    const units = 1 + kids
    const thr = kids > 0 ? T_PARENT : T_SINGLE
    return Math.max(0, units * AMT - RATE * Math.max(0, afni - thr))
  }
  const withShared = calc(soleKids + sharedKids)
  if (!sharedKids) return Math.round(withShared)
  return Math.round((withShared + calc(soleKids)) / 2)
}

// Generic small refundable tax credit for provinces without explicit programs.
// Returns ~$200 single, $400 family, phased out at $40k AFNI.
function computeGenericRefundableTaxOffset(afni, numChildren, isSingle) {
  if (afni > 40000) return 0
  let credit = (isSingle ? 200 : 400) + numChildren * 50
  if (afni > 25000) credit = Math.max(0, credit - (afni - 25000) * 0.03)
  return Math.round(credit)
}

// Dispatcher for province-specific refundable tax offsets (sales tax credits etc.)
function computeProvincialTaxOffset(province, afni, numChildren, isSingle) {
  switch (province) {
    case 'BC': return computeBCRefundableTaxOffset(afni, numChildren, isSingle)
    case 'ON': return computeONSalesTaxCredit(afni, numChildren, 0)
    // SK SLITC and PEI Sales Tax Credit are already rolled into their child
    // benefit functions above (they ARE the credit for those provinces).
    case 'SK': case 'PE': return 0
    default:   return computeGenericRefundableTaxOffset(afni, numChildren, isSingle)
  }
}

// Aggregate all refundable benefits for a person
// Returns annual total. children: array of { age, weight } where weight=1 for sole, 0.5 for shared
function computeRefundableBenefits(province, opts) {
  const {
    afni = 0,
    workingIncome = 0,
    children: rawChildren = [],
    isSingle = true,
    isPrimaryParent = true,
    // cfg: future use — benefit constants (CCB, GST, BC Family, CWB, BC tax
    // offset) are still hardcoded 2026 values. Phase 3B will year-key them
    // by promoting these helpers into TAX_YEARS[year].benefits.
    // eslint-disable-next-line no-unused-vars
    cfg,
  } = opts
  // Federal/provincial refundable child benefits (CCB, GST, BC FB, ACFB, OCB,
  // etc.) require the child to be under 18. Kids 18+ remain "children of the
  // marriage" for CS/SSAG purposes but drop out of these benefit calcs. (CCB
  // does its own filter; doing it once here keeps GST + provincial child
  // benefits consistent.) Calibrated against DM Little (2 kids on table, but
  // 18yo Levi doesn't qualify for BC FB or GST child supplement).
  const children = rawChildren.filter(c => (c.age != null ? c.age : 12) < 18)
  // BENEFIT YEAR FOLLOWS THE CALCULATION YEAR. Verified against fresh DM
  // v.2026.0701 PDFs run with the explicit Calculation/Tax Year 2025 option
  // (Hughes $20k + Moore PT Final, 2026-07-23): in true 2025 mode DM uses the
  // JULY 2025-JUNE 2026 benefit year — CCB 7,997/6,748 (Sylvie's 7,997 flat at
  // L/M and 7,805 at H reproduce exactly), the legacy GST/HST credit (CGEB
  // does not exist before July 2026), 2025 CWB, and the 2025-26 BC Family
  // Benefit WITH the $500 single-parent supplement (Sylvie 1,632 = half of
  // 3,350 - 4% x 2,121 exactly). The earlier forced-2026 rule (task #60) came
  // from PDFs that were actually 2026-product calcs with only the CS-table
  // year at 2025. This is also the correct arrears policy: a 2025 calculation
  // should reflect the benefits actually payable in that period.
  const benefitCfg = cfg || TAX_YEARS[CURRENT_TAX_YEAR]
  // CGEB replaces the legacy GST/HST credit from July 2026 (Bill C-19), so it
  // keys off the benefit year: 2026+ calcs use CGEB, 2025 arrears use GST.
  const useCGEB = !!(benefitCfg && benefitCfg.year >= 2026)
  let total = 0
  // Share factor for the primary parent's children: 1.0 sole, 0.5 fully shared.
  const totalKids = children.length
  const weightSum = children.reduce((s, c) => s + (c.weight != null ? c.weight : 1), 0)
  const shareFactor = (isPrimaryParent && totalKids > 0) ? weightSum / totalKids : 0
  if (isPrimaryParent && children.length > 0) {
    total += computeCCB(afni, children, benefitCfg) // handles weights internally
    // Child Disability Benefit — refundable CCB top-up for DTC-eligible kids
    // (child.disabled). Follows the CCB recipient; split by weight when shared.
    total += computeCDB(afni, children, benefitCfg)
    // Provincial child benefit (BC Family Benefit, Ontario Child Benefit, ACFB, etc.)
    // — dispatched by province. Full-family amount × shareFactor (CRA splits 50/50 in shared).
    // Uses the selected tax-year's amounts (benefitCfg = cfg).
    total += computeProvincialChildBenefit(province, afni, totalKids, isSingle, workingIncome, benefitCfg, children) * shareFactor
  }
  // GST credit: per-child portion uses effective (weighted) child count
  const effChildCount = isPrimaryParent
    ? Math.round(children.reduce((s, c) => s + (c.weight != null ? c.weight : 1), 0))
    : 0
  // Federal grocery credit: CGEB (2026+) replaces the legacy GST/HST credit for
  // with-children primary parents. The CGEB child portion is amount-scaled by
  // shareFactor (not by halving the count), so it uses totalKids + shareFactor.
  // Childless filers (and non-primary parents) keep the GST model (CGEB childless
  // not yet calibrated). Verified vs DM Tools Cloud 2026: sole 2-kid 1891, shared
  // 2-kid 1418, shared 1-kid 1036.
  if (useCGEB && isPrimaryParent && totalKids > 0) {
    // The equivalent-to-spouse uplift on the first child goes only to the parent who
    // claims the eligible dependant (opts.hasEDC); the non-claimant uses the additional
    // rate. For sole custody the primary/recipient parent is the claimant.
    total += computeCGEB(afni, totalKids, shareFactor, !!opts.hasEDC)
  } else if (useCGEB) {
    // Childless CGEB (2026-27 official parameters, CRA payment-amounts page):
    // $445 adult + single supplement (2% phase-in over $11,564, max $234),
    // reduced 5% of AFNI over $46,432; plus the one-time June 2026 top-up
    // ($267 single, phased 15% over $45,521). Reproduces DM cunningham
    // recipient exactly: 679 − 5% × (59,007 − 46,432) = $50.
    const supp = Math.min(234, 0.02 * Math.max(0, afni - 11564))
    const recurring = Math.max(0, 445 + supp - 0.05 * Math.max(0, afni - 46432))
    const topUp = Math.max(0, 267 - 0.15 * Math.max(0, afni - 45521))
    total += Math.round(recurring + topUp)
  } else {
    total += computeGSTCredit(afni, isSingle, effChildCount)
  }
  // CWB: based on individual income; "family with kids" rate if any custody share
  total += computeCWB(province, workingIncome, afni, children.length > 0 && isPrimaryParent, benefitCfg)
  // Nunavut Cost of Living Tax Credit (refundable) — back-fit from DM 2026:
  // ≈ 2% of taxable income, capped at $1,755 (cap calibrated against Christal
  // $101,401 and Tim $127,501 NU fixtures both showing $1,755). Approximated
  // here using AFNI as a proxy for taxable income.
  if (province === 'NU') {
    // NU Cost of Living Tax Credit (NU479 official): 2% of adjusted net
    // income capped at $1,500 ($75,000), plus the single-parent supplement
    // (2% of income over $60,000, max $255.12) when there are children.
    let colt = Math.min(1500, 0.02 * afni)
    if (isPrimaryParent && children.length > 0)
      colt += Math.min(255.12, 0.02 * Math.max(0, afni - 60000))
    total += Math.round(colt)
  }
  // NWT Cost of Living Tax Credit (refundable) — back-fit from DM 2026:
  // min($942, 282 + 0.01 × taxable). Verified across Lucie/Jessica/Justin
  // (uncapped) and Christal/Tim (capped at $942).
  if (province === 'NT') {
    // NT Cost of Living Tax Credit (NT479 official): 2.6% of first $12,000 +
    // 1.25% of $12,000-48,000 + 1% of $48,000-66,000, max $942.
    const colt = 0.026 * Math.min(afni, 12000)
      + 0.0125 * Math.max(0, Math.min(afni, 48000) - 12000)
      + 0.01 * Math.max(0, Math.min(afni, 66000) - 48000)
    total += Math.round(colt)
  }
  // Small province-specific refundable credits (back-fit from DM 2026):
  // Each is base + per-child supplement + EDC bonus, phased out above a threshold.
  // Primary effect at very low/zero taxable (e.g., Campo Gary with NT income).
  const hasEDCFlag = !!opts.hasEDC
  // SK SLITC is a low-income tax credit for ALL adults, not a child benefit —
  // but the parents-with-kids path routes it through computeProvincialChildBenefit
  // (shareFactor'd, calibrated). Add the adult base here for the CHILDLESS case
  // (e.g. Campo Gary: non-taxable income → $0 AFNI → full SLITC $410). High-income
  // childless payors phase out to $0, so this only affects genuinely low-AFNI
  // childless parties. Verified vs DM SK Campo (Gary benefits $965 incl SLITC).
  if (province === 'SK' && !(isPrimaryParent && children.length > 0)) {
    total += computeSKBenefit(afni, 0)
  }
  if (province === 'MB') total += computeMBPersonalTaxCredit(afni, effChildCount, hasEDCFlag)
  if (province === 'NB') total += computeNBHSTCredit(afni, effChildCount, hasEDCFlag)
  if (province === 'NL') total += computeNLIncomeSupplement(afni, effChildCount, hasEDCFlag)
  if (province === 'NS') total += computeNSAffordableLivingCredit(afni, effChildCount, hasEDCFlag)
  // PEI Island Essentials Benefit (2026) — per-adult, both parties, based on each
  // party's own AFNI + whether they have children in the household (shared counts).
  // Replaces the old PE Sales Tax Credit in DM Tools Cloud 2026's output.
  if (province === 'PE') {
    total += computePEIslandEssentials(afni, children.length > 0)
  }
  // Ontario Sales Tax Credit (the OTB row in DM; no OEPTC without rent data).
  // Shared custody = statutory averaging (with / without shared dependants).
  // `children` is already the under-18 benefit set, which matches DM's
  // qualified-dependant handling in the audited cases (johnston's 18-year-old
  // is excluded — he turns 19 during the 2026-27 payment year).
  if (province === 'ON') {
    const soleK = isPrimaryParent ? children.filter(c => (c.weight != null ? c.weight : 1) >= 1).length : 0
    const sharedK = isPrimaryParent ? children.filter(c => (c.weight != null ? c.weight : 1) < 1).length : 0
    total += computeONSalesTaxCredit(afni, soleK, sharedK)
  }
  // BC Sales Tax Credit + Climate Action Tax Credit (refundable).
  // Phases out fully by $50k AFNI. Calibrated against Divorcemate. In 2026/CGEB
  // mode DM does not model the Climate Action portion (its recipient benefit line
  // is CCB+CGEB+BCFB only), so drop it there.
  if (province === 'BC') {
    total += computeBCRefundableTaxOffset(afni, effChildCount, isSingle, useCGEB)
  }
  // NOTE: computeProvincialTaxOffset (BC Sales+Climate, ON OTB, etc.) is defined
  // above but NOT called from this aggregator. Existing BC calibration was
  // performed without this term, and adding it would require per-province
  // calibration to verify each province's numbers against DM. Wire it in
  // selectively once DM test cases for each province are encoded.
  return Math.round(total)
}

// s.7(3) tax treatment by expense category
// 'deduction' — childcare deduction (Line 21400); claimed by lower-income parent at their marginal rate
// 'credit'    — medical expense tax credit (non-refundable); ~20% combined federal + provincial
// 'none'      — no available tax benefit
const S7_TAX_TREATMENT = {
  childcare:        'deduction',
  medical_insurance: 'credit',
  health:           'credit',
  education:        'none',
  postsecondary:    'tuition',   // tuition transfer (line 32400, max 5,000/child)
  extracurricular_fitness: 'activity', // NL PATC (fitness) + SK AFB
  extracurricular:  'activity',        // SK AFB also covers non-fitness (culture)
}
const MEDICAL_CREDIT_RATE = 0.20 // federal 15% + provincial bottom rate ~5%, approximate

// ─────────────────────────────────────────────────────────────────────────────
// INCOME ITEM METADATA — Phase 1: 12 most-common categories
// Each entry describes how an income/deduction item is treated for:
//   • Guidelines Income (CSG): includes (sign +1) / excludes (0) / deducts (−1)
//   • Taxable Income (for tax engine): same convention but with grossUp factor
//   • Net cash flow (for SSAG INDI base): includes after applying tax/CPP/EI
//   • Whether CPP and EI premiums apply (employment vs. self-employment vs. passive)
// Phase 2/3/4 expand this table to ~80 entries.
// ─────────────────────────────────────────────────────────────────────────────
// DIVII PATCH: map the builder's Eligible Dependant Credit answer for a child
// ('0' = partner1 claims, '1' = partner2 claims) onto the calculator's slots
// ('A' = payor, 'B' = recipient). Only shared children carry a choice; a child
// primarily with one parent is claimed by that parent automatically. Slot A is
// partner1 unless the lawyer swapped payor and recipient (`swapped`).
function edcSlotFromBuilderClaim(partnerIndex, parenting, swapped) {
  if (parenting !== 'shared') return null
  const idx = partnerIndex === 0 || partnerIndex === '0' ? '0' : partnerIndex === 1 || partnerIndex === '1' ? '1' : null
  if (idx === null) return null
  const partner1Slot = swapped ? 'B' : 'A'
  const partner2Slot = swapped ? 'A' : 'B'
  return idx === '0' ? partner1Slot : partner2Slot
}

const INCOME_TYPES = {
  // ── Common ────────────────────────────────────────────────────────────
  employment_income: {
    label: 'Employment income',
    category: 'Common',
    t1Line: '10100',
    keywords: ['T4', 'salary', 'wages', 'job', 'employed', 'employee'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: true, eiApplies: true, isSelfEmployedCPP: false,
    indi: 'add',
    inputHint: 'Annual gross from T4',
  },
  employment_income_owner_employee: {
    label: 'Employment income (owner employee)',
    category: 'Income',
    t1Line: '10100',
    keywords: ['owner', 'corporate owner', 'T4', 'double CPP', 'no EI'],
    // DM-verified (2026-09-24): corporate owner-employees on a T4 pay no EI
    // and double CPP (both halves) — same flags as self-employment CPP.
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: true, eiApplies: false, isSelfEmployedCPP: true,
    indi: 'add',
    inputHint: 'T4 gross for a corporate owner (double CPP, no EI)',
  },
  employment_income_reserve_exempt: {
    label: 'Employment income on reserve (tax exempt, no CPP)',
    category: 'Income',
    t1Line: '10000',
    keywords: ['Indian Act', 'reserve', 'tax exempt', 'status'],
    // Tax-exempt employment income under the Indian Act: non-taxable, grossed
    // up at the marginal rate for guidelines income (s.19 treatment), full
    // amount as after-tax cash. DM also offers a CPP-pensionable variant;
    // enter that case as this type plus a manual CFA for the CPP if needed.
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    csgFactor: 1.0, taxFactor: 0,
    nontaxableGrossUp: true,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'Tax-exempt on-reserve employment income (grossed up for CSG)',
  },
  self_employment_income_net: {
    label: 'Self-employment income (net)',
    category: 'Common',
    t1Line: '13500',
    keywords: ['T2125', 'business', 'sole proprietor', 'contractor', 'self-employed'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: true, eiApplies: false, isSelfEmployedCPP: true,
    indi: 'add',
    inputHint: 'Net business income after expenses',
  },
  // CSG Schedule III s.19 — Non-Taxable Income with auto gross-up.
  // Common cases: tips, gratuities, gifts treated as income, certain workers'
  // compensation, foreign pension income exempt under tax treaty, federal
  // supplements, social assistance not paid back, allowances for board/lodging,
  // tax-exempt status under the Indian Act.
  //
  // The amount entered = the actual after-tax / non-taxable amount received.
  // For CSG/SSAG: gross-up is computed dynamically (iterative bisection) at
  // the marginal tax rate that would have applied if this income were taxable —
  // not a fixed factor. See `computeNontaxGrossUp()` for algorithm.
  // For tax: excluded (truly non-taxable, no tax payable).
  // For NDI: the full entered amount adds to net cash (already after-tax).
  other_non_taxable_grossup: {
    label: 'Other non-taxable income (auto gross up)',
    category: 'Common',
    t1Line: '—',
    keywords: ['non-taxable', 'tips', 'gratuities', 'gross up', 'workers comp',
               'social assistance', 'tax-exempt', 'foreign pension exempt',
               'allowance', 'board and lodging', 'native', 'Indian Act'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    csgFactor: 1.0,                           // base amount; gross-up added separately
    taxFactor: 0,                              // not taxable
    cppApplies: false, eiApplies: false,
    nontaxableGrossUp: true,                   // flag triggers dynamic gross-up (CSG s.19)
    indi: 'add_after_tax',                     // full amount adds to NDI as cash
    indiFactor: 1.0,
    inputHint: 'Actual non-taxable amount received per year',
  },
  imputed_underemployed: {
    label: 'Intentionally under-employed/unemployed (s.19)',
    category: 'Common',
    t1Line: 's.19',
    keywords: ['imputed', 'underemployed', 'capacity', 'section 19'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,    // imputed as employment-equivalent
    cppApplies: true, eiApplies: true, isSelfEmployedCPP: false,
    indi: 'add',
    inputHint: 'Imputed income amount under s.19',
  },
  expenses_unreasonably_deducted: {
    label: 'Expenses unreasonably deducted (s.19)',
    category: 'Common',
    t1Line: 's.19',
    keywords: ['add-back', 'unreasonable', 'business expenses', 'section 19'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,    // add back as taxable
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Amount to add back to income',
  },

  // ── Income (taxable additions) ────────────────────────────────────────
  ei_benefits: {
    label: 'Employment insurance benefits (EI)',
    category: 'Income',
    t1Line: '11900',
    keywords: ['EI received', 'unemployment', 'parental leave', 'maternity'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual EI benefits received',
  },
  oas_pension: {
    label: 'Old age security pension (OAS)',
    category: 'Income',
    t1Line: '11300',
    keywords: ['OAS', 'old age security', 'senior'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual OAS pension',
  },
  cpp_retirement: {
    label: 'CPP benefits (retirement)',
    category: 'Income',
    t1Line: '11400',
    keywords: ['CPP', 'retirement', 'pension'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual CPP retirement benefits',
  },
  other_pension: {
    label: 'Other pensions or superannuation',
    category: 'Income',
    t1Line: '11500',
    keywords: ['pension', 'superannuation', 'annuity', 'RIF', 'RIFF'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    qualifiesForPensionAmount: true,  // Line 11500 income qualifies for the
                                       // Pension Income Amount credit (CRA).
                                       // CPP & OAS do NOT qualify.
    indi: 'add',
    inputHint: 'Annual pension income',
  },
  foreign_pension: {
    label: 'Foreign pension income',
    category: 'Income',
    t1Line: '11500',
    keywords: ['foreign', 'overseas pension', 'UK pension', 'international'],
    // Foreign pensions report on line 11500 in Canadian dollars and are fully
    // taxable unless a treaty exempts part (use US Social Security below for
    // the common treaty case). STATUTE-BASED, NOT YET DM-VERIFIED.
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    qualifiesForPensionAmount: true,
    indi: 'add',
    inputHint: 'Annual foreign pension (Canadian dollars, fully taxable)',
  },
  us_social_security: {
    label: 'US Social Security benefits',
    category: 'Income',
    t1Line: '11500',
    keywords: ['USA', 'social security', 'SSA', 'treaty', 'american pension'],
    // Canada-US treaty: the full benefit reports on line 11500, and 15% is
    // deducted at line 25600 — so Guidelines income takes the FULL amount
    // (line 15000) while only 85% is taxed. taxFactor 0.85 models the
    // deduction. STATUTE-BASED (ITA 110(1)(f)(i)), NOT YET DM-VERIFIED.
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    taxFactor: 0.85,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual US Social Security (full amount; 15% treaty deduction applied automatically)',
  },
  // (See `other_non_taxable_grossup` in the Common category above.)
  // ── Dividends — four variants matching Divorcemate's data-entry options ──
  // Both "Actual" and "Taxable (T1)" forms are offered so the user can enter
  // whichever number they have:
  //   • Actual:  dividend received in cash. Tax engine grosses up internally.
  //   • Taxable: grossed-up T1 line 12000/12010 amount. Tax engine uses as-is.
  // For CSG Guideline Income, both variants land on the ACTUAL amount per
  // Schedule III s.5 (no gross-up).
  dividends_eligible: {
    label: 'Actual Cdn dividends (eligible)',
    category: 'Income',
    t1Line: '12000',
    keywords: ['dividends', 'eligible', 'Canadian corp', 'public company', 'actual'],
    csgFactor: 1.0,                            // CSG: actual amount (Sched III s.5)
    csgSign: 1, taxSign: 1, grossUp: 1.38,     // tax: 38% gross-up
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    dividendCredit: 0.150198,                  // federal DTC ~15.0198% of grossed-up
    inputHint: 'Actual dividends received. CSG uses actual; tax uses 38% gross-up.',
  },
  taxable_dividends_eligible: {
    label: 'Taxable Cdn dividends (eligible)',
    category: 'Income',
    t1Line: '12000',
    keywords: ['dividends', 'eligible', 'taxable', 'grossed up', 'T1', '12000'],
    // User enters the GROSSED-UP T1 line 12000 amount. Tax uses it as-is.
    // CSG must back out the gross-up to land on the actual amount per s.5:
    //   csgFactor = 1 / 1.38 ≈ 0.7246
    csgFactor: 1 / 1.38,
    csgSign: 1, taxSign: 1, grossUp: 1.0,      // tax: no further gross-up
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    // DTC = amount × grossUp × dividendCredit. Since grossUp=1.0 here and the
    // amount is already grossed-up, DTC math still produces the right credit.
    dividendCredit: 0.150198,
    inputHint: 'Grossed-up T1 line 12000 amount. CSG backs out the 38% gross-up to actual (Sched III s.5).',
  },
  taxable_dividends_all: {
    label: 'Taxable Cdn dividends (all)',
    category: 'Income',
    t1Line: '12000',
    keywords: ['dividends', 'taxable', 'combined', 'T1', '12000'],
    // Convenience line matching DM's "Taxable Cdn dividends (all)": the full
    // line 12000 amount when the eligible/other split is unknown. Treated as
    // eligible (the usual case for public-company dividends). When the split
    // IS known, use the two separate labeled lines for exact credits.
    csgFactor: 1 / 1.38,
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    dividendCredit: 0.150198,
    inputHint: 'Full T1 line 12000 when the eligible/other split is unknown (treated as eligible)',
  },
  interest_investment: {
    label: 'Interest and other investment income',
    category: 'Income',
    t1Line: '12100',
    keywords: ['interest', 'investment', 'GIC', 'bond', 'savings'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual interest/investment income',
  },
  rental_income_net: {
    label: 'Rental income (net)',
    category: 'Income',
    t1Line: '12600',
    keywords: ['rental', 'property', 'rent', 'real estate', 'landlord'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Net rental income after expenses',
  },

  // ── Phase 2: Additional Income types ─────────────────────────────────
  rrsp_withdrawal: {
    label: 'RRSP withdrawal',
    category: 'Income',
    t1Line: '12900',
    keywords: ['RRSP income', 'registered retirement savings', 'T4RSP', 'withdrawal'],
    // Fully taxable, no CPP/EI, and does NOT qualify for the Pension Income
    // Amount (unlike RRIF income at 65+ — use "Other pensions" for RRIFs).
    // DM has no dedicated row (entered there as "Other taxable income" —
    // same treatment, verified vs DM "Shared (6)" BC); this labeled type
    // exists so users don't have to guess the mapping.
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual RRSP withdrawals (T1 line 12900, fully taxable)',
  },
  cpp_disability: {
    label: 'CPP benefits (disability)',
    category: 'Income',
    t1Line: '11410',
    keywords: ['CPP-D', 'disability'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual CPP disability benefits',
  },
  gis: {
    label: 'Guaranteed Income Supplement (GIS)',
    category: 'Income',
    t1Line: '14600',
    keywords: ['senior', 'low income', 'OAS supplement'],
    // DM-verified (2026-09-24): GIS is non-taxable and grossed up at the
    // marginal rate for guidelines income, like other s.19 non-taxable income.
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    csgFactor: 1.0, taxFactor: 0,
    nontaxableGrossUp: true,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'Annual GIS (non-taxable, grossed up for CSG)',
  },
  elected_split_pension: {
    label: 'Elected split-pension amount (received)',
    category: 'Income',
    t1Line: '11600',
    keywords: ['pension splitting', 'transfer'],
    // Sch III s.14 + DM-verified (2026-09-24): the received split-pension
    // amount is DEDUCTED from the receiver's guidelines income and NDI. It is
    // a notional tax allocation, not real cash (the pension stays with the
    // transferor); it remains taxable to the receiver (line 11600). Was
    // wrongly counted as guidelines income + cash before this date.
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    csgFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Split-pension amount received (taxed here; guidelines income stays with the transferor)',
  },
  dividends_other_than_eligible: {
    label: 'Actual Cdn dividends (other than eligible)',
    category: 'Income',
    t1Line: '12010',
    keywords: ['dividends', 'CCPC', 'small business', 'private corp', 'actual', 'non-eligible'],
    csgFactor: 1.0,                            // CSG: actual amount (Sched III s.5)
    csgSign: 1, taxSign: 1, grossUp: 1.15,     // tax: 15% gross-up
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    dividendCredit: 0.090301,
    inputHint: 'Actual non-eligible (CCPC) dividends received. CSG uses actual; tax uses 15% gross-up.',
  },
  taxable_dividends_other_than_eligible: {
    label: 'Taxable Cdn dividends (other than eligible)',
    category: 'Income',
    t1Line: '12010',
    keywords: ['dividends', 'CCPC', 'private corp', 'taxable', 'grossed up', 'T1', '12010', 'non-eligible'],
    // User enters the GROSSED-UP T1 line 12010 amount. Tax uses as-is.
    // CSG backs out the 15% gross-up: csgFactor = 1 / 1.15 ≈ 0.8696
    csgFactor: 1 / 1.15,
    csgSign: 1, taxSign: 1, grossUp: 1.0,      // tax: no further gross-up
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    dividendCredit: 0.090301,
    inputHint: 'Grossed-up T1 line 12010 amount. CSG backs out the 15% gross-up to actual (Sched III s.5).',
  },
  partnership_income_net: {
    label: 'Net partnership income',
    category: 'Income',
    t1Line: '12200',
    keywords: ['partnership', 'partner', 'business'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: true, eiApplies: false, isSelfEmployedCPP: true,
    indi: 'add',
    inputHint: 'Net partnership income (treated as self-employment for CPP)',
  },
  actual_capital_gains: {
    label: 'Actual capital gains',
    category: 'Income',
    t1Line: '12700',
    keywords: ['capital gain', 'investment', 'sale of property'],
    csgFactor: 1.0,    // Full gain in CSG (Sched III s.6 — use full amount, not taxable portion)
    taxFactor: 0.50,   // 50% inclusion for tax
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Actual realized capital gains; CSG uses full amount, tax uses 50%',
  },
  taxable_capital_gains: {
    label: 'Taxable capital gains',
    category: 'Income',
    t1Line: '12700',
    keywords: ['capital gain', 'taxable portion'],
    csgFactor: 2.0,    // Sched III s.6: gross up taxable amount to actual gain (=2× taxable)
    taxFactor: 1.0,    // Already the taxable portion
    indiFactor: 2.0,   // Full actual gain hit the bank account (not just the 50% taxable)
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Taxable portion (line 12700); CSG and INDI use the full actual gain',
  },
  other_taxable_income: {
    label: 'Other taxable income',
    category: 'Income',
    t1Line: '13000',
    keywords: ['taxable', 'other', 'miscellaneous'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Other taxable income items',
  },
  // Per-T1-line labeled variants of the plain taxable-add treatment (no
  // CPP/EI at source) — so users can pick the return line they see instead
  // of guessing that "Other taxable income" is the right bucket. Verified
  // treatment vs DM "Shared (7)" BC (DM books all of these as Other
  // taxable income; guidelines + taxable at face value).
  other_employment_income: {
    label: 'Other employment income',
    category: 'Income',
    t1Line: '10400',
    keywords: ['tips', 'gratuities', 'casual', 'royalties from work', 'net research grants'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Tips, casual earnings, other T1 line 10400 income (no source deductions)',
  },
  rdsp_income: {
    label: 'RDSP payments (taxable portion)',
    category: 'Income',
    t1Line: '12500',
    keywords: ['registered disability savings plan', 'RDSP', 'disability savings'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Taxable RDSP payments (T4A box 131 amount reported on line 12500)',
  },
  scholarship_taxable: {
    label: 'Scholarships and bursaries (taxable)',
    category: 'Income',
    t1Line: '13010',
    keywords: ['scholarship', 'bursary', 'fellowship', 'student', 'grant'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Taxable portion of scholarships/bursaries (above the exemption)',
  },
  self_employment_ei_optin: {
    label: 'Self-employment income (net) (EI opt-in)',
    category: 'Income',
    t1Line: '13500',
    keywords: ['self-employed', 'EI premium', 'opt-in'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: true, eiApplies: true, isSelfEmployedCPP: true,
    indi: 'add',
    inputHint: 'Net business income; EI premium applied (opted-in)',
  },
  workers_comp: {
    label: "Workers' compensation benefits",
    category: 'Income',
    t1Line: '14400',
    keywords: ['WCB', 'WSIB', 'workers comp', 'workplace injury'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    // CSG s.19(1)(b): non-taxable income is grossed up to its pre-tax
    // equivalent for guidelines income (DM retirement calc: Rick's 4,800 WC
    // -> +1,568 gross-up in BC). Flag was missing — without it the SSAG
    // gross income difference overstated by the full gross-up.
    nontaxableGrossUp: true,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: "Workers' compensation benefits",
  },
  social_assistance_no_clawback: {
    label: 'Social assistance (no clawback)',
    category: 'Income',
    t1Line: '14500',
    keywords: ['welfare', 'income assistance', 'social assistance'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'Social assistance counted for CSG',
  },
  social_assistance_clawback_spousal: {
    label: 'Social assistance (clawback spousal only)',
    category: 'Income',
    t1Line: '14500',
    keywords: ['welfare', 'income assistance'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'Counted for CSG; excluded from SSAG due to spousal clawback',
  },
  social_assistance_clawback_both: {
    label: 'Social assistance (clawback child and spousal)',
    category: 'Income',
    t1Line: '14500',
    keywords: ['welfare', 'income assistance'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only', // excluded from both CSG and INDI; full clawback consumes it
    inputHint: 'Excluded from CSG and SSAG (full clawback)',
  },
  disability_non_taxable: {
    label: 'Disability benefits (non-taxable)',
    category: 'Income',
    t1Line: '—',
    keywords: ['disability', 'non-taxable', 'private insurance'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'Non-taxable disability insurance benefits',
  },
  other_non_taxable_no_grossup: {
    label: 'Other non-taxable income (no gross up)',
    category: 'Income',
    t1Line: '—',
    keywords: ['non-taxable'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'Other non-taxable income (CSG includes at face value)',
  },

  // ── Other Support — obligations to a PRIOR spouse/partner ────────────
  // DM "Other Support Paid/Received". Verified vs DM Tools Cloud 2026
  // (PriorSpSupEI / PriorC&SSupEI, NT): Guidelines Income is UNCHANGED by
  // prior-relationship support (Sched III ss.2-3 only adjust for support
  // between THESE parties), so csgFactor = 0 for both. The SSAG treats prior
  // support as a first charge on income (SSAG Ch. 15.3.1): under the
  // with-child formula it reduces the payor's INDI (indi: 'subtract' flows
  // into partyNetCashFlow / aggregateCashIn); under the without-child
  // formula the gross-income basis is adjusted in ssagRange (nominal for
  // deductible spousal, grossed-up for post-tax child support).
  prior_spousal_support_received: {
    label: 'Spousal support received from prior spouse/partner',
    category: 'Other Support',
    t1Line: '12800',
    keywords: ['prior', 'support received', 'alimony', 'maintenance', 'first family'],
    // Taxable income to the recipient (T1 line 12800) and counted in
    // Guidelines income. Verified vs DM Tools Cloud 2026 "Shared (6)" (BC):
    // DM lists it in the SSAG Guidelines Income section at face value.
    // (Support received from THE OTHER PARTY in this case is never entered
    // as income — the calculator models it through the support scenarios.)
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Annual spousal support received from a prior spouse (taxable)',
  },
  prior_child_support_received: {
    label: 'Child support received from prior spouse/partner',
    category: 'Other Support',
    t1Line: '12799',
    keywords: ['prior', 'child support received', 'first family', 'other relationship'],
    // Post-1997 child support is tax-neutral: NOT taxable to the recipient
    // (reported at line 12799 for information only, never in income) and NOT
    // part of Guidelines income. It is real cash in the household, so it
    // enters NDI, but it is earmarked for those children.
    // STATUTE-BASED, NOT YET DM-VERIFIED: treatment follows the ITA and CSG
    // directly; verify against a DivorceMate calc when one is available.
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'Annual child support received for children of a prior relationship (tax free)',
  },
  prior_child_support_paid: {
    label: 'Child support paid to prior spouse/partner',
    category: 'Other Support',
    t1Line: '—',
    keywords: ['prior', 'first family', 'child support paid', 'other relationship'],
    // NOT tax-deductible (post-1997 child support is tax-neutral) and NOT a
    // Guidelines-income deduction — it only reduces disposable income.
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'subtract',
    inputHint: 'Annual child support paid for children of a prior relationship',
  },
  prior_spousal_support_paid: {
    label: 'Spousal support paid to prior spouse/partner',
    category: 'Other Support',
    t1Line: '22000',
    keywords: ['prior', 'first family', 'spousal support paid', 'alimony', 'maintenance'],
    // Tax-deductible (T1 line 22000) — lowers taxable/net income (and so the
    // payor's benefits phase-outs) — but Guidelines income is unchanged.
    csgSign: 0, taxSign: -1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'subtract',
    inputHint: 'Annual spousal support paid to a prior spouse (tax deductible)',
  },

  // ── CSG Adjustments — Sched. III ss.2-14 ─────────────────────────────
  cca_real_property_grossup: {
    label: 'CCA for real property (auto gross up)',
    category: 'CSG Adjustments (Sched. III)',
    t1Line: 'Sched III s.11',
    keywords: ['CCA', 'depreciation', 'real estate', 'rental'],
    // DM-verified (Tools Cloud 2026 help, 2026-09-24): the CCA add-back is
    // treated as NON-TAXABLE income — grossed up at the party's marginal rate
    // (s.19 mechanism, not a flat factor) for guidelines income, while only
    // the base amount flows into the with-child SSAG formula and NDI as cash
    // (depreciation is a non-cash expense, so the cash is real).
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    csgFactor: 1.0, taxFactor: 0,
    nontaxableGrossUp: true,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: 'CCA claimed (added back for CSG, grossed up at marginal rate)',
  },
  cca_real_property_no_grossup: {
    label: 'CCA for real property (no gross up)',
    category: 'CSG Adjustments (Sched. III)',
    t1Line: 'Sched III s.11',
    keywords: ['CCA', 'depreciation'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'CCA claimed (added back for CSG, no gross-up)',
  },
  partnership_capitalize: {
    label: 'Capitalize (partnership/sole proprietorship)',
    category: 'CSG Adjustments (Sched. III)',
    t1Line: 'Sched III s.12',
    keywords: ['partnership', 'capital', 'sole prop'],
    // Sch III s.12 is a DEDUCTION: income properly required to capitalize the
    // partnership/sole proprietorship is not available for support. DM-verified
    // (Tools Cloud 2026 help): deducted from guidelines income AND from NDI in
    // the scenarios (the cash stays in the business); still taxed personally.
    // Was mis-set as an add-back before 2026-09-24.
    csgSign: -1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'subtract',
    inputHint: 'Income required to capitalize the business (deducted)',
  },
  ccpc_stock_options: {
    label: 'CCPC employee stock options (security options deduction)',
    category: 'CSG Adjustments (Sched. III)',
    t1Line: 'Sched III s.13',
    keywords: ['stock options', 'CCPC', 'security options deduction'],
    // DM-verified workflow (Tools Cloud 2026 help): the s.13(1) option benefit
    // is ALSO entered under Employment income (which carries it into guidelines
    // income and tax); this entry only grants the 50% security options
    // deduction against taxable income. Was mis-set as a guidelines add-back
    // before 2026-09-24 (would have double-counted alongside employment income).
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: -0.5,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Option benefit (also add it to Employment income); grants the 50% deduction',
  },
  social_assistance_not_attributed: {
    label: 'Social assistance not attributed to party (for children)',
    category: 'CSG Adjustments (Sched. III)',
    t1Line: 'Sched III s.4',
    keywords: ['welfare', 'not attributable', 'children portion'],
    // Self-contained entry, VERIFIED vs DM Tools Cloud 2026 "Calculation 1
    // (78)" (BC, Jane employment 35,000 + this entry 3,000): enter the
    // children's/other members' portion of social assistance HERE, not as
    // social assistance income. DM: excluded from CS guidelines income
    // (Jane stays 35,000, Sch III s.4) but ADDED BACK into SSAG income
    // ("Social Assistance for child(ren) (added back)" -> 38,000), flowing
    // into the with-child INDI as untaxed cash. csg 0 + add_after_tax
    // reproduces both. Documented divergences kept: our scenario NDI shows
    // the cash (DM's Support Scenarios omit it by design) and our benefit
    // phase-outs use true CRA line 23600 net income including this amount
    // (DM omits SA from 23600).
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: "Children's portion of social assistance (enter here, not as income)",
  },
  business_investment_loss: {
    label: 'Business investment loss (actual)',
    category: 'CSG Adjustments (Sched. III)',
    t1Line: 'Sched III s.7',
    keywords: ['BIL', 'ABIL', 'allowable business investment loss', '21700'],
    // Sched III s.7: deduct the ACTUAL business investment loss for guidelines
    // income. The tax return only deducts the allowable half (line 21700), so
    // enter the actual loss: CSG takes it in full, tax takes 50%.
    csgFactor: -1.0,
    taxFactor: -0.5,
    csgSign: -1, taxSign: -1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Actual business investment loss suffered in the year',
  },
  income_to_non_arms_length: {
    label: "Income paid to non-arm's length person",
    category: 'CSG Adjustments (Sched. III)',
    t1Line: 'Sched III s.9',
    keywords: ['family', 'spouse', 'related party', 'salary paid'],
    // DM-verified (Tools Cloud 2026 help): the added-back compensation is
    // non-taxable to the party, so DM grosses it up at the marginal rate for
    // guidelines income (s.19 mechanism); only the base amount flows into the
    // with-child SSAG formula and scenarios as cash.
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    csgFactor: 1.0, taxFactor: 0,
    nontaxableGrossUp: true,
    cppApplies: false, eiApplies: false,
    indi: 'add_after_tax',
    inputHint: "Unreasonable salary/fees paid to non-arm's length person (added back, grossed up)",
  },

  // ── CSG Adjustments — s.17 Pattern of Income ─────────────────────────
  pattern_employment: {
    label: 'Pattern of income (employment)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.17',
    keywords: ['averaging', '3-year', 'pattern', 'imputed'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Net adjustment to employment income via 3-year averaging',
  },
  pattern_self_employment: {
    label: 'Pattern of income (self-employment)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.17',
    keywords: ['averaging', '3-year', 'pattern', 'imputed'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Net adjustment to self-employment income via 3-year averaging',
  },
  pattern_owner_employee: {
    label: 'Pattern of income (owner employee)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.17',
    keywords: ['averaging', 'owner employee', 'pattern'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Pattern adjustment for owner-employee income',
  },
  non_recurring_loss: {
    label: 'Non-recurring loss',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.17',
    keywords: ['loss', 'one-time', 'non-recurring'],
    csgSign: 1, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Non-recurring business loss to add back',
  },

  // ── CSG Adjustments — s.18 Corporate Income Imputation ───────────────
  corporate_income_employment: {
    label: 'Corporate income (impute as employment)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.18',
    keywords: ['corporation', 'CCPC', 'shareholder', 'imputed', 'retained earnings'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Corporate retained earnings imputed as employment income',
  },
  corporate_income_dividend: {
    label: 'Corporate income (impute as dividend)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.18',
    keywords: ['corporation', 'CCPC', 'shareholder', 'dividend', 'imputed', 'non-eligible'],
    // s.18(1) imputed corporate income paid out as a dividend follows the
    // corporation's income character. For a private corporation's active /
    // small-business income (the standard s.18 case) that's a NON-ELIGIBLE
    // dividend: 15% gross-up + non-eligible dividend tax credit. VERIFIED vs
    // DM Tools Cloud 2026 (Neil $500k s.18 → "Imputed dividend income (s.18(1))
    // 575,000" = ×1.15, guidelines income backs out to the actual $500k →
    // $624,800; taxable T1 $699,800). Was previously mis-set to eligible (38%),
    // which also over-stated GUIDELINES income (no csgFactor → the grossed-up
    // $690k flowed into guidelines instead of the actual $500k). csgFactor 1.0
    // makes CSG use the actual amount; grossUp 1.15 grosses only for tax.
    csgFactor: 1.0,                            // CSG: actual amount (Sched III s.5)
    csgSign: 1, taxSign: 1, grossUp: 1.15,     // tax: 15% gross-up (non-eligible)
    cppApplies: false, eiApplies: false,
    dividendCredit: 0.090301,
    indi: 'add_after_tax',
    inputHint: 'Corporate retained earnings imputed as a (non-eligible) dividend. CSG uses the actual amount; tax uses the 15% gross-up.',
  },

  // ── CSG Adjustments — s.19 Additional Imputation Categories ─────────
  diverted_income: {
    label: 'Diverted income (s.19)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.19',
    keywords: ['diverted', 'hidden', 'imputed', 'income shifting'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: "Income diverted to non-arm's-length parties",
  },
  property_not_utilized: {
    label: 'Property not utilized to generate income (s.19)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.19',
    keywords: ['property', 'unused', 'imputed', 'unproductive'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Imputed income from unproductive property',
  },
  failure_to_provide_income_info: {
    label: 'Failure to provide income information (s.19)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.19',
    keywords: ['adverse inference', 'imputed', 'non-disclosure'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Income imputed for failure to disclose',
  },
  beneficiary_of_trust: {
    label: 'Beneficiary of a trust (s.19)',
    category: 'CSG Adjustments (s.17–19)',
    t1Line: 's.19',
    keywords: ['trust', 'beneficiary', 'imputed'],
    csgSign: 1, taxSign: 1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'add',
    inputHint: 'Income imputed from trust benefits',
  },

  // ── Tax Deductions ───────────────────────────────────────────────────
  // Reduce taxable income (and where noted, Guidelines Income too).
  // Stored as positive amounts; aggregators apply the negative sign.
  // INDI effect is via reduced tax payable, NOT direct cash subtraction.
  // ── Household members + Undue Hardship (CSG s.10 / Sch II) ────────────
  // These capture the party's POST-SEPARATION household. None of them enter
  // the party's own guidelines/taxable income. Effects:
  //   income_of_spouse — new partner's gross annual income (enter 0 for a
  //     partner with no income). Presence: suppresses the party's EDC and
  //     single-parent benefit supplements; income joins family net income for
  //     every benefit phase-out; low income earns the spouse-amount credit;
  //     joins household income for the Sch II standards-of-living test.
  //   income_of_additional_adult — one item per other adult; Sch II only.
  //   additional_children_* — amount = NUMBER of children; Sch II household
  //     size only (their benefits are not computed).
  //   uh_* — annual dollar amounts for the s.10 undue hardship factors; they
  //     feed the Sch II comparison, nothing else.
  income_of_spouse: {
    label: 'Income of spouse / new partner',
    category: 'Household / Undue Hardship',
    t1Line: 's.10 / Sch II',
    keywords: ['new partner', 'repartnered', 'spouse income', 'common law'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: "New partner's gross annual income (0 if none) — affects benefits, credits, Sch II",
  },
  income_of_additional_adult: {
    label: 'Income of additional adult in household',
    category: 'Household / Undue Hardship',
    t1Line: 'Sch II',
    keywords: ['roommate', 'parent', 'adult child', 'household'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'One item per additional adult; their gross annual income (Sch II only)',
  },
  additional_children_prior: {
    label: 'Additional children from prior relationship (count)',
    category: 'Household / Undue Hardship',
    t1Line: 'Sch II',
    keywords: ['stepchildren', 'other children', 'household size'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'NUMBER of prior-relationship children living in this household',
  },
  additional_children_new: {
    label: 'Additional children from new relationship (count)',
    category: 'Household / Undue Hardship',
    t1Line: 'Sch II',
    keywords: ['new baby', 'new family', 'household size'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'NUMBER of new-relationship children living in this household',
  },
  uh_high_debts: {
    label: 'Undue hardship: unusually high debts (annual payments)',
    category: 'Household / Undue Hardship',
    t1Line: 's.10(2)(a)',
    keywords: ['debts', 'hardship', 'loan payments'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Annual payments on debts reasonably incurred for the family or to earn a living',
  },
  uh_high_access_costs: {
    label: 'Undue hardship: unusually high access expenses',
    category: 'Household / Undue Hardship',
    t1Line: 's.10(2)(b)',
    keywords: ['access', 'travel', 'parenting time', 'hardship'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Annual cost of exercising parenting time (travel etc.)',
  },
  uh_support_legal_duty: {
    label: 'Undue hardship: support for another person (legal duty)',
    category: 'Household / Undue Hardship',
    t1Line: 's.10(2)(c)-(e)',
    keywords: ['support obligation', 'dependent', 'hardship'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Annual support paid under a legal duty for a person other than the children of this case',
  },
  uh_other: {
    label: 'Undue hardship: other circumstances (annual cost)',
    category: 'Household / Undue Hardship',
    t1Line: 's.10(2)',
    keywords: ['hardship', 'other'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    csgFactor: 0, taxFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Annual cost of any other circumstance causing undue hardship',
  },

  rpp_deduction: {
    label: 'RPP deduction',
    category: 'Tax Deductions',
    t1Line: '20700',
    keywords: ['registered pension plan', 'pension contribution'],
    csgSign: -1, taxSign: -1, grossUp: 1.0,         // CSG-deductible per Sched III s.10
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Annual RPP contributions',
  },
  rrsp_deduction: {
    label: 'RRSP deduction',
    category: 'Tax Deductions',
    t1Line: '20800',
    keywords: ['retirement savings', 'RRSP contribution'],
    csgSign: 0, taxSign: -1, grossUp: 1.0,          // NOT CSG-deductible; tax-only
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Annual RRSP contributions (reduces tax only, not Guidelines Income)',
  },
  split_pension_deduction: {
    label: 'Deduction for elected split-pension amount',
    category: 'Tax Deductions',
    t1Line: '21000',
    keywords: ['pension splitting', 'transferor', 'T1032'],
    // DM-verified (2026-09-24): the transferor's line 21000 deduction does NOT
    // reduce guidelines income (the full pension stays theirs for support),
    // but the tax saving flows through to SSAG/NDI.
    csgSign: 0, taxSign: -1, grossUp: 1.0,
    csgFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Split-pension amount transferred to spouse (tax deduction only)',
  },
  union_dues: {
    label: 'Union, professional, or like dues',
    category: 'Tax Deductions',
    t1Line: '21200',
    keywords: ['professional fees', 'membership', 'union'],
    csgSign: -1, taxSign: -1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    // Union dues are deducted at source from gross employment income — they
    // reduce both taxable income (via line 21200) AND actual take-home cash.
    // Net NDI effect: −amount + tax_savings = −amount × (1 − marginal_rate).
    // Divorcemate displays this as a "Cash Flow - Decrease NDI" row, but per
    // CSG Sched III s.1(g) the deduction also applies to Guideline Income.
    indi: 'subtract',
    inputHint: 'Annual union/professional dues',
  },
  carrying_charges: {
    label: 'Carrying charges and interest expenses',
    category: 'Tax Deductions',
    t1Line: '22100',
    keywords: ['investment management fees', 'interest expense', 'investment loan', 'carrying'],
    // Sched III s.8: deduct carrying charges and interest expenses that are
    // paid and deductible under the ITA (line 22100) — reduces both taxable
    // income and guidelines income. Real cash outflow, so NDI drops by the
    // amount net of the tax savings (same pattern as union dues).
    csgSign: -1, taxSign: -1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'subtract',
    inputHint: 'Annual deductible carrying charges/interest (line 22100)',
  },
  carrying_charges_no_csg: {
    label: 'Carrying charges (tax deduction only, no CSG effect)',
    category: 'Tax Deductions',
    t1Line: '22100',
    keywords: ['carrying', 'legal fees support', 'not impacting guidelines'],
    // DM's "Carrying charges not impacting Guidelines Income": tax deduction
    // only. Used e.g. where a court declines the Sch III s.8 guidelines
    // deduction (legal fees to obtain support are line 22100 deductible but
    // the case law splits on whether they reduce guidelines income).
    csgSign: 0, taxSign: -1, grossUp: 1.0,
    csgFactor: 0,
    cppApplies: false, eiApplies: false,
    indi: 'subtract',
    inputHint: 'Deductible carrying charges the court keeps out of guidelines income',
  },
  employment_expenses_sched_iii: {
    label: 'Employment expenses (Sched. III, s.1)',
    category: 'Tax Deductions',
    t1Line: '22900',
    keywords: ['T2200', 'work expenses', 'business expenses'],
    csgSign: -1, taxSign: -1, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    indi: 'tax_only',
    inputHint: 'Employment expenses (deductible for both CSG and tax)',
  },

  // ── Tax Credits ───────────────────────────────────────────────────────
  // Non-refundable credits computed via creditCalc dispatcher in partyTaxBundle.
  medical_expenses: {
    label: 'Medical expenses',
    category: 'Tax Credits',
    t1Line: '33099',
    keywords: ['health', 'doctor', 'dental', 'prescription', 'physio'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    creditCalc: 'medical',
    indi: 'tax_only',
    inputHint: 'Total eligible medical expenses (threshold auto-applied)',
  },
  donations: {
    label: 'Donations and gifts',
    category: 'Tax Credits',
    t1Line: '34900',
    keywords: ['charity', 'charitable', 'gift'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    creditCalc: 'donations',
    indi: 'tax_only',
    inputHint: 'Total charitable donations',
  },
  tuition: {
    label: 'Tuition amount (self)',
    category: 'Tax Credits',
    t1Line: '32300',
    keywords: ['school', 'university', 'college', 'education'],
    csgSign: 0, taxSign: 0, grossUp: 1.0,
    cppApplies: false, eiApplies: false,
    creditCalc: 'tuition',
    indi: 'tax_only',
    inputHint: 'Eligible tuition fees',
  },
}

// Categories in display order (for the picker dropdown)
const INCOME_CATEGORIES = [
  'Common',
  'Income',
  'Other Support',
  'CSG Adjustments (Sched. III)',
  'CSG Adjustments (s.17–19)',
  'Tax Deductions',
  'Tax Credits',
  'Household / Undue Hardship',
]

// Helper: filter type IDs by category
function typesInCategory(cat) {
  return Object.entries(INCOME_TYPES)
    .filter(([_, m]) => m.category === cat)
    .map(([id, m]) => ({ id, ...m }))
}

// ─────────────────────────────────────────────────────────────────────────────
// NEEDS TAB — party budgets, aligned line-for-line with BC Supreme Court Form
// F8 (Financial Statement, SCFR 5-1) Part 2 "Expenses", so entries can later
// populate an F8 directly. Entry UX follows the FCAC Budget Planner: grouped
// lines, flexible entry frequency (normalized to monthly, which is what F8
// reports), live group subtotals.
//
// Two groups are AUTO-FILLED from the calculation rather than typed:
//   • Compulsory deductions — income tax, CPP, EI, employee pension (RPP),
//     union dues: the engine already computes these exactly.
//   • Support payments to others — the prior-relationship support items.
// Auto rows count toward the F8 total but are EXCLUDED from the "living
// expenses" figure compared against net disposable income, because NDI is
// already net of them (avoids double counting).
const F8_BUDGET_GROUPS = [
  { key: 'compulsory', label: 'Compulsory deductions', auto: true, lines: [] },
  { key: 'housing', label: 'Housing', lines: [
    'Rent or mortgage', 'Property taxes', 'Property insurance',
    'Water, sewer, garbage', 'Strata fees', 'House repairs and maintenance'] },
  { key: 'utilities', label: 'Utilities', lines: [
    'Heat and electricity', 'Telephone', 'Cellular telephone', 'Cable TV',
    'Internet service'] },
  { key: 'household', label: 'Household expenses', lines: [
    'Food', 'Household supplies', 'Meals outside the home',
    'Furnishings and equipment'] },
  { key: 'transportation', label: 'Transportation', lines: [
    'Public transit, taxis', 'Gas and oil', 'Car insurance and licence',
    'Parking', 'Repairs and maintenance', 'Lease payments'] },
  { key: 'health', label: 'Health', lines: [
    'Extended health premiums', 'Dental plan premiums',
    'Health care (net of coverage)', 'Drugs (net of coverage)',
    'Dental care (net of coverage)'] },
  { key: 'personal', label: 'Personal', lines: [
    'Clothing', 'Hair care', 'Toiletries, cosmetics', 'Education',
    'Life insurance', 'Dry cleaning/laundry', 'Entertainment/recreation',
    'Gifts'] },
  { key: 'children', label: 'Children', lines: [
    'Child care', 'Clothing', 'Hair care', 'School fees and supplies',
    'Entertainment/recreation', 'Activities and lessons', 'Gifts',
    'Insurance'] },
  { key: 'other', label: 'Other', lines: [
    'Charitable donations', 'Vacation', 'Pet care', 'Newspapers, publications'] },
  { key: 'savings', label: 'Savings', lines: ['RRSP', 'RESP'] },
  { key: 'supportOthers', label: 'Support payments to others', auto: true, lines: [] },
  { key: 'debt', label: 'Debt payments', lines: [], hint: 'minimum monthly payments' },
]

// ─────────────────────────────────────────────────────────────────────────────
// ROLE LEVEL PER AGREEMENT — Divii role → access profile inside the Support
// feature (Rebecca's mapping, 2026-07):
//   full             everything, editable
//   contribute-view  enter OWN income + expenses; view the rest read-only
//   contribute       enter OWN income + expenses only (party mode)
//   view             the whole calculation, read-only
// LegalApp passes matterContext.role (string below) or, minimally,
// matterContext.roleLevel (1 → full, 2 → contribute).
const ROLE_PROFILES = {
  'lawyer-level-1':      'full',
  'client-level-1':      'full',
  'lawyer-level-2':      'contribute-view',
  'client-level-2':      'contribute-view',
  'client-sharing-user': 'contribute',
  'client-level-3':      'view',
  'client-level-4':      'view',
}

// Entry frequency → monthly factor (FCAC-style flexible entry; F8 is monthly).
const BUDGET_FREQS = [
  { value: 'wk',  label: 'Weekly',        factor: 52 / 12 },
  { value: '2wk', label: 'Every 2 weeks', factor: 26 / 12 },
  { value: 'mo',  label: 'Monthly',       factor: 1 },
  { value: 'yr',  label: 'Yearly',        factor: 1 / 12 },
]
function budgetFreqFactor(freq) {
  const f = BUDGET_FREQS.find(x => x.value === freq)
  return f ? f.factor : 1
}

// All standard F8 lines pre-created (Vue 2 reactivity needs the keys up
// front). Keyed "group|line"; `a`/`b` are the two parties' entered amounts.
function makeBudgetLines() {
  const lines = {}
  for (const g of F8_BUDGET_GROUPS) {
    for (const ln of g.lines) lines[g.key + '|' + ln] = { a: null, b: null, freq: 'mo' }
  }
  return lines
}
// Custom "Other [specify]" rows per group (every group allows them on F8,
// except the fully-auto compulsory deductions).
function makeBudgetCustom() {
  const custom = {}
  for (const g of F8_BUDGET_GROUPS) { if (g.key !== 'compulsory') custom[g.key] = [] }
  return custom
}

// Helper: search across labels, keywords, and T1 line numbers
function searchIncomeTypes(query) {
  const q = (query || '').toLowerCase().trim()
  if (!q) return null // null = show all
  return Object.entries(INCOME_TYPES)
    .filter(([_, m]) => {
      if (m.label.toLowerCase().includes(q)) return true
      if (m.t1Line && m.t1Line.toLowerCase().includes(q)) return true
      if (m.keywords && m.keywords.some(k => k.toLowerCase().includes(q))) return true
      return false
    })
    .map(([id, m]) => ({ id, ...m }))
}

// Generate a short unique id for new items
function newItemId() {
  return Math.random().toString(36).slice(2, 9)
}

// ─────────────────────────────────────────────────────────────────────────────
// INCOME AGGREGATORS — pure functions of an items array
// Phase 1: covers the 12 starter line items. Phase 2 will extend with
// Schedule III adjustments, deductions, credits, and other-support items.
// ─────────────────────────────────────────────────────────────────────────────

// CRA NET income (line 23600) includes workers' compensation (line 14400) and
// social assistance (line 14500); they are deducted only at line 25000 en
// route to TAXABLE income. Refundable benefits (CCB, CGEB, CWB, provincial
// credits) phase out on NET income, so wherever the engine uses its taxable
// figure as the AFNI proxy for benefits, this addback must be applied. Same
// principle as the age-amount phase-out fix verified vs DM in the retirement
// audit (Rick's WC costing exactly 15% x 4,800 of age amount).
function netIncomeAddback(items) {
  let add = 0
  for (const item of items || []) {
    const id = item.typeId || ''
    if (id === 'workers_comp' || id.startsWith('social_assistance')) add += (item.amount || 0)
    // Family net income: benefit phase-outs (CCB, CGEB/GST, provincial child
    // benefits) run on the FAMILY's line 23600 when the party has a new
    // partner, so the partner's income joins the AFNI proxy at every benefit
    // computation. (This function feeds benefit AFNI only — the tax legs and
    // the age-amount block use their own item loops and stay per-party.)
    if (id === 'income_of_spouse') add += (item.amount || 0)
  }
  return add
}

// New-partner income for a party: null = no partner recorded; a number
// (0 allowed) = partner present with that gross annual income.
function partnerIncomeOf(items) {
  let found = null
  for (const it of items || []) {
    if (it.typeId === 'income_of_spouse') found = (found || 0) + (it.amount || 0)
  }
  return found
}

// ── CSG s.10 Undue Hardship — Schedule II comparison of household standards
// of living. Ratio = adjusted household income ÷ the Schedule's low-income
// measure for the household size. The regulation's LIM table follows
// base 10,382 + 4,153 for the second member + 3,114 for each additional.
const SCHII_LIM_BASE = 10382
const SCHII_LIM_SECOND = 4153
const SCHII_LIM_ADDITIONAL = 3114
function schIILowIncomeMeasure(size) {
  if (size <= 1) return SCHII_LIM_BASE
  return SCHII_LIM_BASE + SCHII_LIM_SECOND + Math.max(0, size - 2) * SCHII_LIM_ADDITIONAL
}
function sumTypeAmounts(items, ids) {
  return (items || []).reduce((s, it) => s + (ids.includes(it.typeId) ? (it.amount || 0) : 0), 0)
}

// OAS recovery tax ("clawback"): 15% of net income over the year's threshold,
// capped at the OAS received. Per DM's guidelines interpretation, guidelines
// income includes OAS AS ADJUSTED by the clawback, and the repaid amount also
// leaves taxable income and cash. Implemented by scaling the party's OAS items
// down before any aggregation, so every downstream leg (CSG, tax, benefits,
// NDI) sees the effective amount. NOTE: computed on entered income, before
// spousal support — DM recomputes per scenario (its "OAS clawback" cash-flow
// row); at typical clawback incomes the difference is small. Documented
// divergence.
function adjustOASClawback(items, cfg) {
  const oasTotal = (items || []).reduce(
    (s, it) => s + (it.typeId === 'oas_pension' ? (it.amount || 0) : 0), 0)
  if (!oasTotal) return items
  const c = cfg || TAX_YEARS[CURRENT_TAX_YEAR]
  const threshold = c.oasClawbackThreshold || TAX_YEARS[CURRENT_TAX_YEAR].oasClawbackThreshold
  // Net income before adjustments: every item's taxable leg (full OAS in)
  // plus the WC/SA net-income addback.
  let net = 0
  for (const it of items || []) {
    const t = INCOME_TYPES[it.typeId]
    if (t) net += (it.amount || 0) * taxFactor(t)
  }
  net += netIncomeAddback(items)
  const clawback = Math.min(oasTotal, Math.max(0, (net - threshold) * 0.15))
  if (clawback <= 0) return items
  const f = (oasTotal - clawback) / oasTotal
  return items.map(it => it.typeId === 'oas_pension'
    ? { ...it, amount: Math.round((it.amount || 0) * f), oasClawbackApplied: true }
    : it)
}

// Per-item factor resolvers — items can specify csgFactor/taxFactor/indiFactor
// explicitly; otherwise CSG and tax fall back to {sign × grossUp}, and indi
// defaults to 1.0 (the entered amount is the cash that flows). This separation
// lets a single item have different CSG, tax, and cash-flow treatments.
// Example — `taxable_capital_gains`: csgFactor 2.0 (Sched III s.6 grosses up to
// actual gain), taxFactor 1.0 (entered amount is the taxable portion), indiFactor
// 2.0 (full actual gain is what hit the bank account).
function csgFactor(t) { return t.csgFactor != null ? t.csgFactor : t.csgSign * t.grossUp }
function taxFactor(t) { return t.taxFactor != null ? t.taxFactor : t.taxSign * t.grossUp }
function indiFactor(t) { return t.indiFactor != null ? t.indiFactor : 1.0 }

// CSG Guidelines Income — sum of (amount × csgFactor) for each item.
// If `province` is provided AND any items have `nontaxableGrossUp: true`, the
// non-taxable income is grossed up per CSG Schedule III s.19 — adding the
// before-tax equivalent of each non-taxable dollar to the guidelines income.
// Pre-tax cash flow: sum of all items at their indiFactor that hit the bank
// account (excludes deductions like RRSP/RPP, includes non-taxable amounts at
// face value — NOT grossed up). Used in NDI calculations where we need the
// actual dollar inflow regardless of the income's tax treatment.
function aggregateCashIn(items) {
  let total = 0
  for (const item of items || []) {
    const t = INCOME_TYPES[item.typeId]
    if (!t) continue
    const f = indiFactor(t)
    if (t.indi === 'add' || t.indi === 'add_after_tax') total += (item.amount || 0) * f
    else if (t.indi === 'subtract') total -= (item.amount || 0) * f
  }
  return Math.round(total)
}

function aggregateGuidelinesIncome(items, province, cfg) {
  let total = 0
  for (const item of items || []) {
    const t = INCOME_TYPES[item.typeId]
    if (!t) continue
    total += (item.amount || 0) * csgFactor(t)
  }
  if (province) total += computeNontaxGrossUp(items, province, cfg)
  return Math.round(total)
}

// CSG Schedule III s.19 — Non-Taxable Income Gross-Up.
// Solves for the grossed-up TAXABLE-equivalent amount that would, after tax,
// leave the same after-tax cash as the non-taxable income. Algorithm:
//
//   Find X such that aftertax(baseTaxable + X) = aftertax(baseTaxable) + nonTaxable
//   then grossUp = X − nonTaxable    (the increment over the base NT amount)
//
// Bisection is used (not Newton) because the piecewise-linear tax function
// can produce instability with marginal-rate iteration when the gross-up
// crosses bracket boundaries. Calibration against Divorcemate Tools Cloud
// 2026 confirms this matches DM's algorithm to within ~$200/yr across the
// test bank (Stephen Beugeling $7,941, Lawrence Lewis Shaw $233,505, Gary
// Campo $316,056). The remaining drift is from DM using slightly different
// 2025 vs 2026 brackets.
function computeNontaxGrossUp(items, province, cfg) {
  cfg = cfg || TAX_YEARS[2026]
  let nonTaxable = 0
  for (const item of items || []) {
    const t = INCOME_TYPES[item.typeId]
    if (!t || !t.nontaxableGrossUp) continue
    nonTaxable += (item.amount || 0)
  }
  if (nonTaxable <= 0) return 0
  const baseTaxable = aggregateTaxableIncome(items, cfg)
  // Use raw tax MINUS federal BPA credit only, per CSG Schedule III s.19:
  // "amount the parent would have to receive to have an equivalent amount
  // of after-tax income." Tax payable is post-credit, not raw bracket tax.
  //
  // Back-solved from DM PDFs:
  //   Lewis-Shaw BC (\$300k NT): DM effective tax at \$533,505 = \$233,505.
  //     Our raw tax = \$235,840. Implied DM credit subtraction = \$2,335,
  //     which matches federal BPA (\$16,461 × 14% = \$2,305) within rounding.
  //   Campo BC (\$372k NT): DM effective tax at \$687,806 = \$315,694.
  //     Our raw = \$318,391. Implied DM credit ≈ \$2,697, ≈ federal BPA.
  //
  // Using federal BPA only (not provincial) is the empirical match. Provincial
  // BPA credit appears to be already captured elsewhere in DM's pipeline or
  // omitted from gross-up by design.
  const bpaCredit = cfg.fedBPA * cfg.fedLowestRate
  // CPP2 burden — only when the party has employment income. Above the CPP
  // YMPE ($71,300 in 2026) up to YAMPE ($81,200), employees pay an additional
  // 4% CPP. This "payroll burden" reduces effective after-tax income on the
  // grossed-up portion. Beugeling-Afewerk back-solve: DM's effective rate
  // over Stephen's gross-up range was ~30.6% vs our base 28.2% — accounting
  // for ~half the gap. For NT-only parties (Lewis-Shaw, Campo) no employment
  // means no CPP2, so this is a no-op.
  const hasEmp = (items || []).some(it => {
    const t = INCOME_TYPES[it.typeId]
    return t && t.cppApplies && !t.isSelfEmployedCPP && (it.amount || 0) > 0
  })
  const cpp2Burden = (X) => {
    if (!hasEmp) return 0
    const inCpp2Range = Math.max(0, Math.min(X, cfg.cppYAMPE) - cfg.cppYMPE)
    return inCpp2Range * cfg.cpp2Rate
  }
  const aftertax = (X) => X - Math.max(0, getRawIncomeTax(province, X, cfg) - bpaCredit) - cpp2Burden(X)
  const baseAfter = aftertax(baseTaxable)
  const target = baseAfter + nonTaxable
  // Bisection bounds: grossed total is at least baseTaxable + nonTaxable
  // (no tax) and at most baseTaxable + 3 × nonTaxable (bracket-extreme).
  let lo = baseTaxable, hi = baseTaxable + nonTaxable * 4
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2
    if (aftertax(mid) < target) lo = mid
    else hi = mid
    if (hi - lo < 1) break
  }
  const grossedTotal = (lo + hi) / 2
  // The gross-up is the increment OVER both the base taxable and the NT itself
  // (NT is already added once via csgFactor=1.0 on the income type).
  return Math.max(0, grossedTotal - baseTaxable - nonTaxable)
}

// Taxable Income — sum of (amount × taxFactor), minus CPP enhancement deduction.
function aggregateTaxableIncome(items, cfg) {
  let total = 0
  for (const item of items || []) {
    const t = INCOME_TYPES[item.typeId]
    if (!t) continue
    total += (item.amount || 0) * taxFactor(t)
  }
  const cppEi = aggregateCppEi(items, { cfg })
  total -= cppEi.cppDeductible || 0
  return Math.round(Math.max(0, total))
}

// Working income (employment + self-employment) for CWB phase-in.
function aggregateWorkingIncome(items) {
  let total = 0
  for (const item of items || []) {
    const t = INCOME_TYPES[item.typeId]
    if (!t || !t.cppApplies) continue
    total += (item.amount || 0)
  }
  return Math.round(total)
}

// CPP and EI premiums across all items.
// Approximation: compute employment and self-employment portions independently.
// Mixed-income parties may slightly over-collect since YMPE caps are shared per CRA;
// acceptable for Phase 1 (real cases are usually all-employment or all-SE).
function aggregateCppEi(items, opts) {
  opts = opts || {}
  const cfg = opts.cfg || TAX_YEARS[2026]
  let empGross = 0, seGross = 0
  for (const item of items || []) {
    const t = INCOME_TYPES[item.typeId]
    if (!t || !t.cppApplies) continue
    if (t.isSelfEmployedCPP) seGross += (item.amount || 0)
    else empGross += (item.amount || 0)
  }
  const emp = empGross > 0 ? getCppEi(empGross, false, cfg) : { cpp: 0, ei: 0, cppCreditable: 0, cppDeductible: 0 }
  const se  = seGross  > 0 ? getCppEi(seGross,  true,  cfg) : { cpp: 0, ei: 0, cppCreditable: 0, cppDeductible: 0 }
  // CPP contributions cease when the contributor reaches age 70 (or earlier
  // if they elect to stop at age 65+). EI continues to apply on employment
  // income at any age. Calibration against Divorcemate confirms: their
  // calculation drops CPP for parties at 70+ but keeps EI capped at the max.
  const stopCpp = opts.partyAge != null && opts.partyAge >= 70
  return {
    cpp: stopCpp ? 0 : (emp.cpp + se.cpp),
    ei:  emp.ei,                     // SE doesn't pay EI
    cppCreditable: stopCpp ? 0 : (emp.cppCreditable + se.cppCreditable),
    // Line 22215: CPP enhancement deduction. Reduces Line 23600 (Net Income)
    // and thus the tax base. Without this, every party is over-taxed by
    // ~$1k (employed at high income) to ~$2.5k (self-employed at $200k+).
    cppDeductible: stopCpp ? 0 : (emp.cppDeductible + se.cppDeductible),
  }
}

// Compute the credit value for a user-input credit item (medical, donations, tuition).
// Returns annual dollar credit (federal + provincial combined).
function computeUserCredit(item, province, taxableIncome, cfg) {
  cfg = cfg || TAX_YEARS[2026]
  const t = INCOME_TYPES[item.typeId]
  if (!t || !t.creditCalc) return 0
  const amount = item.amount || 0
  if (amount <= 0) return 0
  const provRate = (cfg.provTaxData[province] || { rate: 0.05 }).rate
  switch (t.creditCalc) {
    case 'medical': {
      // Threshold: lesser of 3% of net income or $2,759 (2025)
      const threshold = Math.min(2759, taxableIncome * 0.03)
      const eligible = Math.max(0, amount - threshold)
      return eligible * (cfg.fedLowestRate + provRate)
    }
    case 'donations': {
      // Federal: 14.5% on first $200, 29% on excess (33% if income > $253,414, ignored here)
      // Provincial: lowest rate on first $200, top rate on excess (approx with 2× provRate)
      const fedFirst = Math.min(amount, 200) * cfg.fedLowestRate
      const fedRest = Math.max(0, amount - 200) * 0.29
      const provFirst = Math.min(amount, 200) * provRate
      const provRest = Math.max(0, amount - 200) * Math.min(provRate * 2.5, 0.21)
      return fedFirst + fedRest + provFirst + provRest
    }
    case 'tuition': {
      return amount * (cfg.fedLowestRate + provRate)
    }
    default:
      return 0
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// ONTARIO-specific tax adjustments — surtaxes + Health Premium
// Applied AFTER base combined fed+ON tax + credits. These don't apply outside ON.
// ─────────────────────────────────────────────────────────────────────────────

// Ontario provincial-only brackets for 2026 (used to compute ON-only tax for surtax)
const ON_PROV_BRACKETS_2026 = [
  [0,        0.0505],
  [53891,    0.0915],
  [107785,   0.1116],
  [150000,   0.1216],
  [220000,   0.1316],
]

// Compute ON provincial-only tax (used as base for surtax calculation)
function _onProvRawTax(taxable) {
  let tax = 0
  for (let i = 0; i < ON_PROV_BRACKETS_2026.length; i++) {
    const [lo, rate] = ON_PROV_BRACKETS_2026[i]
    const hi = i + 1 < ON_PROV_BRACKETS_2026.length ? ON_PROV_BRACKETS_2026[i + 1][0] : taxable
    if (taxable <= lo) break
    tax += (Math.min(taxable, hi) - lo) * rate
  }
  return tax
}

// Ontario Health Premium 2026 — stepped function by taxable income.
// Returns annual amount. Calibrated from observed DM PDF values.
function _onHealthPremium(taxable) {
  if (taxable <= 20000) return 0
  if (taxable <= 36000) return Math.min(300, (taxable - 20000) * 0.06)
  if (taxable <= 48000) return 300 + Math.min(150, (taxable - 36000) * 0.06)
  if (taxable <= 72600) return 450 + Math.min(150, (taxable - 48000) * 0.25)
  if (taxable <= 200000) return 600 + Math.min(150, (taxable - 72600) * 0.25)
  if (taxable <= 200600) return 750 + Math.min(150, (taxable - 200000) * 0.25)
  return 900
}

// Ontario surtaxes + Health Premium for a party. Returns the ADDITIONAL tax
// owed beyond the federal + ON base tax (already in combined brackets).
// onTaxAfterCredits = the ON-only tax after ON non-refundable credits
function computeOntarioExtraTax(taxable, cppCreditable, ei, cfg) {
  // ON provincial-only raw tax
  const onRaw = _onProvRawTax(taxable)
  // ON credits (BPA × 5.05% + CPP creditable + EI all at lowest ON rate)
  const onBPA = (cfg && cfg.provTaxData && cfg.provTaxData.ON && cfg.provTaxData.ON.bpa) || 12989
  const onRate = 0.0505
  const onCredits = (onBPA + cppCreditable + ei) * onRate
  // ON base tax after credits (this is what surtaxes apply to)
  const onTaxBase = Math.max(0, onRaw - onCredits)
  // Surtax 1: 20% on ON tax > $5,710 (2026)
  const surtax1 = Math.max(0, (onTaxBase - 5710) * 0.20)
  // Surtax 2: 36% on ON tax > $7,307 (2026)
  const surtax2 = Math.max(0, (onTaxBase - 7307) * 0.36)
  // Ontario Health Premium
  const healthPremium = _onHealthPremium(taxable)
  return surtax1 + surtax2 + healthPremium
}

// Ontario LIFT Credit (Low-Income Individuals and Families Tax) — ON428 line 71.
// 2026 single max $875 + $875 family supplement. Phase-out 5% above $33,000.
// Reduces Ontario tax payable (not refundable).
function computeONLIFTCredit(taxable) {
  const max = 875
  return Math.max(0, Math.round(max - 0.05 * Math.max(0, taxable - 33000)))
}

// Ontario CARE credit (Childcare Access and Relief from Expenses) — ON479-A,
// REFUNDABLE. On top of the federal/provincial child-care expense DEDUCTION, ON
// gives a refundable credit = rate × eligible child-care expenses, where the rate
// is a stepped function of ADJUSTED family income (line 23600 net income + the
// child-care expenses added back). Rate schedule (Ontario.ca / ON479-A):
//   ≤$20,000: 75%
//   $20,000–40,000: 75% − 2pp per $2,500 (or part) over $20,000  → 59% at $40k
//   $40,000–60,000: 59% − 2pp per $5,000 (or part) over $40,000  → 51% at $60k
//   $60,000–150,000: 51% − 2pp per $3,600 (or part) over $60,000 → tails to 0
//   >$150,000: 0%
// Verified to the dollar vs DM Tools Cloud 2026 (sylvie-moore-ON, Dylan claiming
// $2,000 childcare): adj income $72,800 → 43% → $860; $68,938 → 45% → $900.
function computeONCARERate(adjFamilyIncome) {
  const afi = Math.max(0, adjFamilyIncome)
  if (afi <= 20000) return 0.75
  if (afi <= 40000) return Math.max(0, 0.75 - 0.02 * Math.ceil((afi - 20000) / 2500))
  if (afi <= 60000) return Math.max(0, 0.59 - 0.02 * Math.ceil((afi - 40000) / 5000))
  if (afi <= 150000) return Math.max(0, 0.51 - 0.02 * Math.ceil((afi - 60000) / 3600))
  return 0
}
// claimedChildcare = the child-care expense claimed as a deduction by this party
// (T1 line 21400). netIncome = this party's line 23600 (childcare already net of
// the deduction in DM's figure; we add it back to form adjusted family income).
function computeONCARECredit(netIncome, claimedChildcare) {
  const claimed = Math.max(0, claimedChildcare || 0)
  if (claimed <= 0) return 0
  const adjFamilyIncome = Math.max(0, netIncome) + claimed
  return Math.round(computeONCARERate(adjFamilyIncome) * claimed)
}

// New Brunswick Low-Income Tax Reduction (NB428 line 67). Reverse-engineered
// from DM 2026 fixtures (Hughes-Peslova-NB, Johnston-Smith-NB) to ~$5/yr:
//   • With EDC claim:  max(0, 2306 - 0.03 × taxable)
//   • Without EDC:     max(0, 1389 - 0.05 × max(0, taxable - 21000))
// Verified: Lucie $54,776 + EDC → DM $663, ours $663.
//           Justin $53,598 no EDC → DM $0, ours $0.
// NB low-income tax reduction (Form NB428 lines 77-86; not in T4127): basic
// $802 + $802 spouse/eligible-dependant (family max $1,604), reduced by 3%
// of adjusted family income over $21,920. 2025 legislated figures — the 2026
// NB428 publishes Jan 2027 (audit note: DM's rows arbitrate whether its 2026
// build indexes these; re-check when the 2026 form lands). Replaces an old
// curve-fit that used a wrong 5% rate on the single path.
function computeNBTaxReduction(taxable, hasEDC) {
  // 2026 = 2025 legislated (802/1,604 over 21,920) × 1.02 — matches DM's 2026
  // build to the dollar (johnston/hughes recipients were +$45-46 at the 2025
  // figures). Confirm against the official 2026 NB428 when published.
  const base = hasEDC ? 1636 : 818
  return Math.max(0, base - 0.03 * Math.max(0, taxable - 22358))
}

// Compute taxes for a party's items.
// opts: { hasEligibleDependant: boolean, partyAge: number }
//   - hasEligibleDependant controls Eligible Dependant Credit (EDC)
//   - partyAge gates CPP contribution cessation at 70+
// Federal-only raw bracket tax, for per-level credit clamping. Year-keyed:
// 2026 = 14/20.5/26/29/33 at 58,523/117,045/181,440/258,482 (T4127);
// 2025 = 14.5% bottom (Bill C-4 blend) at the 2025 thresholds.
function getFedRawTax(t, cfg) {
  const fed = (cfg && cfg.year === 2025)
    ? [[0,0.145],[57375,0.205],[114750,0.26],[177882,0.29],[253414,0.33]]
    : [[0,0.14],[58523,0.205],[117045,0.26],[181440,0.29],[258482,0.33]]
  let tax = 0
  for (let i = 0; i < fed.length; i++) {
    const [lo, rate] = fed[i]
    const hi = i + 1 < fed.length ? fed[i + 1][0] : t
    if (t <= lo) break
    tax += (Math.min(t, hi) - lo) * rate
  }
  return tax
}

function partyTaxBundle(province, items, opts) {
  opts = opts || {}
  const cfg = opts.cfg || TAX_YEARS[2026]
  const taxable = aggregateTaxableIncome(items, cfg)
  const cppEi = aggregateCppEi(items, { partyAge: opts.partyAge, cfg })
  const rawTax = getRawIncomeTax(province, taxable, cfg)
  const prov = cfg.provTaxData[province] || { bpa: 12000, rate: 0.05 }

  // Compute non-refundable credits AT A GIVEN TAXABLE INCOME. Some credits
  // (Age Amount) phase out as income rises, so we expose this as a function:
  // base-income tax uses computeCreditsAt(taxable); SSAG scenarios can call
  // it again with the SS-adjusted taxable to get the correct credit total
  // for that scenario (matches Divorcemate's per-scenario credit calc).
  function computeCreditsSplitAt(t) {
    // Federal BPA phase-out: the enhanced BPA reduces from the max ($16,452 in
    // 2026) to the floor ($14,829) linearly for net income $181,440–$258,482
    // (Bill C-30 high-income clawback). Above the top threshold only the floor
    // remains. DM applies this — without it high earners are over-credited by
    // up to ~$1,623 × 14% ≈ $227 (e.g. fieber payor at $565k). The federal
    // eligible-dependant amount tracks the BPA, so it phases out identically.
    const fedBpaFloor = cfg.fedBPAFloor || 14829
    const fedBpaPhaseLo = cfg.fedBPAPhaseLo || 181440
    const fedBpaPhaseHi = cfg.fedBPAPhaseHi || 258482
    let effFedBPA = cfg.fedBPA
    if (t > fedBpaPhaseLo) {
      const frac = Math.min(1, (t - fedBpaPhaseLo) / (fedBpaPhaseHi - fedBpaPhaseLo))
      effFedBPA = cfg.fedBPA - (cfg.fedBPA - fedBpaFloor) * frac
    }
    // Provincial BPA phase-out (currently MB only): MB claws back its $15,780
    // BPA linearly over net income $200,000–$400,000 (fully gone at $400k),
    // per prov.bpaPhaseLo/Hi. Without it, high earners in MB are over-credited
    // by up to ~$1,704 (fieber payor at $565k → DM MB prov NRTC is just CPP/EI).
    let effProvBPA = prov.bpa
    if (prov.bpaPhaseLo && t > prov.bpaPhaseLo) {
      const floor = prov.bpaFloor || 0
      const frac = Math.min(1, (t - prov.bpaPhaseLo) / (prov.bpaPhaseHi - prov.bpaPhaseLo))
      effProvBPA = prov.bpa - (prov.bpa - floor) * frac
    }
    // BPA + CPP creditable + EI (universal)
    const fedBase  = (effFedBPA  + cppEi.cppCreditable + cppEi.ei) * cfg.fedLowestRate
    const provBase = (effProvBPA + cppEi.cppCreditable + cppEi.ei) * prov.rate

    // Age Amount (parties 65+) — phases out at 15% above threshold
    let ageFed = 0, ageProv = 0
    if (opts.partyAge != null && opts.partyAge >= 65) {
      // The age amount phases out on NET income (line 23600), which INCLUDES
      // workers' compensation and social assistance (they're only deducted at
      // line 25000 en route to taxable). Verified vs DM retirement calc:
      // Rick's $4,800 WC costs exactly 15% x 4,800 = $720 of age amount.
      let netAddback = 0
      for (const item of items || []) {
        const it = INCOME_TYPES[item.typeId]
        if (it && (item.typeId === 'workers_comp' || item.typeId.startsWith('social_assistance')))
          netAddback += (item.amount || 0)
      }
      const netInc = t + netAddback
      ageFed = Math.max(0, cfg.fedAgeAmount - Math.max(0, (netInc - cfg.fedAgeThreshold) * 0.15))
      const pa = cfg.provAgeAmount[province]
      if (pa) {
        ageProv = Math.max(0, pa.amount - Math.max(0, (netInc - pa.threshold) * 0.15))
      }
      // SK senior supplementary amount (line 58220, TD1SK 2026): flat $2,569
      // for every 65+, NOT income-tested. Found in the retirement audit
      // (constant +$270 tax on both SK seniors = 2,569 x 10.5%).
      if (province === 'SK') ageProv += 2569
    }
    const autoFedAge  = ageFed  * cfg.fedLowestRate
    const autoProvAge = ageProv * prov.rate

    // Disability Amount (federal line 31600 + provincial equivalent) — the
    // non-refundable credit for a person certified to have a severe and
    // prolonged impairment (DTC). Flat amount, NOT income-tested (unlike the
    // age amount), claimed for oneself. Gated by opts.disabled. DM applies this
    // when a party is flagged "Disabled" — it lowers that party's tax, which
    // (for a disabled payor) raises after-tax income and shifts SSAG up.
    // Note: this is the base adult amount only — the under-18 supplement and
    // caregiver/infirm-dependant amounts are separate and not modelled here.
    const disFed  = opts.disabled ? (cfg.fedDisabilityAmount || 0) : 0
    const disProv = opts.disabled
      ? ((cfg.provDisabilityAmount && cfg.provDisabilityAmount[province]) || 0) : 0
    const autoFedDis  = disFed  * cfg.fedLowestRate
    const autoProvDis = disProv * prov.rate

    // Disability amount TRANSFERRED from a dependent child (federal line 31800
    // / provincial line 58480 = child's base disability amount + under-18
    // supplement) is INTENTIONALLY NOT applied here. Verified against DM Tools
    // Cloud 2026 (Owen/Jamie, Brynn disabled): DM's recipient "Taxes and
    // Deductions" line is identical with and without the transfer — DM books the
    // child's disability only as the refundable Child Disability Benefit (see
    // computeCDB), and does NOT reduce the claiming parent's tax by the
    // transferred non-refundable amount in its SSAG/NDI figures. We match DM.
    // The fed/prov under-18 supplement constants remain in cfg (researched +
    // documented) for a future "true tax" mode if ever needed.

    // Pension Income Amount — capped at qualifying pension income (line 11500)
    let qualPension = 0
    for (const item of items || []) {
      const it = INCOME_TYPES[item.typeId]
      if (it && it.qualifiesForPensionAmount) qualPension += (item.amount || 0)
    }
    const autoFedPension  = Math.min(qualPension, cfg.fedPensionAmountMax) * cfg.fedLowestRate
    const autoProvPension = Math.min(qualPension, cfg.provPensionAmountMax[province] || 0) * prov.rate

    // Canada Employment Amount + Eligible Dependant Credit
    let cea = 0
    for (const item of items || []) {
      const it = INCOME_TYPES[item.typeId]
      if (it && it.cppApplies && !it.isSelfEmployedCPP && (item.amount || 0) > 0) { cea = cfg.cea || 1501; break }
    }
    // Eligible Dependant Credit: federal uses federal BPA ($16,461 in 2026),
    // provincial uses the PROVINCIAL BPA (BC: $13,200). DM Lewis-Shaw PDF
    // page 3 confirms: Tannis's BC EDC credit at the prov rate uses the BC
    // BPA, not the fed BPA. Using fed BPA at the prov rate over-credited
    // provincial taxes by ~$165/yr for every EDC claimant.
    const edcFed  = opts.hasEligibleDependant ? effFedBPA : 0
    // Most provinces set the eligible-dependant amount = provincial BPA; MB is the
    // exception (edcAmount < bpa). Fall back to bpa when no override is present.
    const edcProv = opts.hasEligibleDependant ? (prov.edcAmount != null ? prov.edcAmount : prov.bpa) : 0
    // Spouse or common-law partner amount (line 30300): same base amounts as
    // the EDC, reduced dollar-for-dollar by the partner's net income. Applies
    // when a new partner is recorded (income_of_spouse item; 0 income = full
    // credit). Mutually exclusive with the EDC, which is suppressed upstream
    // for a cohabiting party.
    const spouseFed  = opts.partnerIncome != null ? Math.max(0, effFedBPA - opts.partnerIncome) : 0
    const spouseProv = opts.partnerIncome != null
      ? Math.max(0, (prov.edcAmount != null ? prov.edcAmount : prov.bpa) - opts.partnerIncome) : 0
    // Provincial "amount for dependent children" (SK line 58210). Provincial-only
    // (no federal equivalent since 2015). Per CHILD, claimed by one parent — in
    // shared custody DM distributes children between the two parents (angus: both
    // to recipient; johnston/scott/fieber: split). opts.numChildAmountClaims is
    // the count of children THIS party claims (see childAmountClaims computed).
    const provChildAmt = prov.childAmount
      ? prov.childAmount * (opts.numChildAmountClaims || 0) : 0
    // The Canada Employment Amount is a FEDERAL credit only. Yukon is the one
    // jurisdiction that mirrors it provincially (YT tracks federal credits).
    // Crediting it at every province's rate over-credited ~$70-150/yr per
    // employed party (found in the ON line-by-line audit vs DM: Tim's ON
    // credits engine 4,118 vs DM 4,053).
    const provCea = province === 'YT' ? cea : 0
    const autoFed  = (cea + edcFed + spouseFed)  * cfg.fedLowestRate
    const autoProv = (provCea + edcProv + spouseProv + provChildAmt) * prov.rate

    // Dividend tax credit (federal + provincial). DM shows these as two
    // separate lines ("Federal dividend tax credit" / "BC dividend tax credit"
    // etc). Both apply to the GROSSED-UP dividend amount.
    // Federal DTC: encoded per income type (it.dividendCredit) — 15.0198% for
    // eligible, 9.0301% for non-eligible.
    // Provincial DTC: looked up from cfg.provDividendCredit[province] by
    // category (eligible vs non-eligible, distinguished by it.dividendCredit > 0.12).
    let dtcFed = 0, dtcProv = 0
    const provDtcRates = (cfg.provDividendCredit && cfg.provDividendCredit[province]) || null
    for (const item of items || []) {
      const it = INCOME_TYPES[item.typeId]
      if (!it || !it.dividendCredit) continue
      const grossed = (item.amount || 0) * it.grossUp
      // Federal DTC
      dtcFed += grossed * it.dividendCredit
      // Provincial DTC
      if (provDtcRates) {
        const isEligible = it.dividendCredit > 0.12  // eligible ~0.15, non-eligible ~0.09
        const provRate = isEligible ? provDtcRates.eligible : provDtcRates.nonEligible
        dtcProv += grossed * (provRate || 0)
      }
    }
    const dtc = dtcFed + dtcProv

    // Manitoba Family Tax Benefit (Schedule MB428-A, unindexed statute):
    // basic $2,065 + $2,065 eligible dependant + $2,065 age 65+ + $2,752 per
    // dependent child under 19 + $2,752 disability, LESS 9% of net income,
    // floored at 0; the result joins the MB non-refundable pool at 10.8%.
    // The child claimed as the eligible dependant is excluded from the
    // per-child count (no-double-dip, same convention as the SK child
    // amount; reproduces DM sylvie-MB: (2,065+2,065+2,752 − 9% × 28,452)
    // × 10.8% = 467). Found in the MB line-by-line audit.
    let mbFtb = 0
    if (province === 'MB') {
      const ftbKids = Math.max(0, (opts.dependantsUnder19 || 0) - (opts.hasEligibleDependant ? 1 : 0))
      let comps = 2065
        + (opts.hasEligibleDependant ? 2065 : 0)
        + (opts.partyAge != null && opts.partyAge >= 65 ? 2065 : 0)
        + (opts.disabled ? 2752 : 0)
        + 2752 * ftbKids
      mbFtb = Math.max(0, comps - 0.09 * t) * prov.rate
    }

    // User-input credits (medical/donations/tuition) — these depend on `t`
    // since medical has a 3% income-based threshold.
    let userCred = 0
    for (const item of items || []) {
      const it = INCOME_TYPES[item.typeId]
      if (!it || !it.creditCalc) continue
      userCred += computeUserCredit(item, province, t, cfg)
    }

    // Per-LEVEL split: non-refundable credits cannot cross levels (unused
    // provincial credits never offset federal tax and vice versa). userCred
    // (medical/donations, computed at a combined rate) is apportioned by the
    // two rates. Found material in the income-type audit: an eligible-dividend
    // payor's unused BC DTC was wrongly offsetting $5,652 of federal tax.
    const fedShare = cfg.fedLowestRate / (cfg.fedLowestRate + prov.rate)
    const fedC = fedBase + autoFedAge + autoFedDis + autoFedPension + autoFed
      + dtcFed + userCred * fedShare
    const provC = provBase + autoProvAge + autoProvDis + autoProvPension
      + autoProv + mbFtb + dtcProv + userCred * (1 - fedShare)
    return { fed: fedC, prov: provC }
  }
  function computeCreditsAt(t) {
    const c = computeCreditsSplitAt(t)
    return c.fed + c.prov
  }

  const totalCred = computeCreditsAt(taxable)
  // Ontario-specific add-ons: surtaxes + Health Premium. Applied AFTER base
  // fed+ON tax + credits. For non-ON provinces, this returns 0.
  const onExtra = province === 'ON'
    ? computeOntarioExtraTax(taxable, cppEi.cppCreditable, cppEi.ei, cfg)
    : 0
  // ─── Ontario tax pieces at a given taxable income (T4127 ON formulas) ───
  // Surtax base = ON basic tax AFTER ON non-refundable credits (incl. the ON
  // eligible-dependant amount — omitting it overstated surtax for EDC
  // claimants, found in the ON line-by-line audit vs DM scott/johnston).
  // The ON tax reduction (S factor: 2 × ($300 + $554 × dependants under 19)
  // less ON tax incl. surtax) and the LIFT credit (min($875, 5.05% of
  // employment income), phased out 5% above $32,500 adjusted net income,
  // statutory/non-indexed) are NON-REFUNDABLE against ONTARIO tax only —
  // both are capped so they can never reduce federal tax.
  function onPiecesAt(t) {
    if (province !== 'ON') return { extra: 0, reduction: 0 }
    const onEdc = opts.hasEligibleDependant ? (prov.edcAmount != null ? prov.edcAmount : prov.bpa) : 0
    const onCred = (prov.bpa + cppEi.cppCreditable + cppEi.ei + onEdc) * prov.rate
    const onBasic = Math.max(0, _onProvRawTax(t) - onCred)
    const s1 = Math.max(0, (onBasic - 5818) * 0.20)   // T4127 2026
    const s2 = Math.max(0, (onBasic - 7446) * 0.36)   // T4127 2026
    const hp = _onHealthPremium(t)
    const onTaxInclSurtax = onBasic + s1 + s2
    const deps = opts.dependantsUnder19 || 0
    const red = Math.max(0, Math.min(onTaxInclSurtax,
      2 * (300 + 554 * deps) - onTaxInclSurtax))
    let empIncome = 0
    for (const item of items || []) {
      const it = INCOME_TYPES[item.typeId]
      if (it && it.cppApplies && !it.isSelfEmployedCPP) empIncome += (item.amount || 0)
    }
    const liftRaw = Math.max(0, Math.min(875, 0.0505 * empIncome) - 0.05 * Math.max(0, t - 32500))
    const lift = Math.min(liftRaw, Math.max(0, onTaxInclSurtax - red))
    return { extra: s1 + s2 + hp, reduction: red + lift }
  }
  // Provincial tax reductions (applied against provincial tax payable).
  function computeProvReductionAt(t) {
    if (province === 'NB') return computeNBTaxReduction(t, !!opts.hasEligibleDependant)
    // BC tax reduction (T4127 S factor; Budget 2026 raised the max to $690
    // for 2026-2030): $690 less 3.56% of income over $25,570, nil at $44,952.
    if (province === 'BC') {
      return Math.max(0, 690 - 0.0356 * Math.max(0, t - 25570))
    }
    // NL low-income tax reduction (NL428 / ITA 2000 s.21.1): basic $1,008 +
    // $563 eligible dependant (2026 ×1.011 projections; 2025 = 997/557),
    // reduced 16% of income over $24,191 individual / $40,905 family (single
    // parents use the family threshold). Was missing entirely — the NL
    // low-income fixture's NDI red traced here.
    if (province === 'NL') {
      const base = 1008 + (opts.hasEligibleDependant ? 563 : 0)
      const thr = opts.hasEligibleDependant ? 40905 : 24191
      return Math.max(0, base - 0.16 * Math.max(0, t - thr))
    }
    // PE low-income tax reduction (Form PE428; threshold $23,000 for 2026 per
    // Budget 2025-26, components unindexed): $350 basic + $350 eligible
    // dependant + $300 per dependent child, less 5% of AFI over $23,000.
    if (province === 'PE') {
      const peKids = Math.max(0, (opts.dependantsUnder19 || 0) - (opts.hasEligibleDependant ? 1 : 0))
      const base = 350 + (opts.hasEligibleDependant ? 350 : 0) + 300 * peKids
        + (opts.partyAge != null && opts.partyAge >= 65 ? 250 : 0)
      return Math.max(0, base - 0.05 * Math.max(0, t - 23000))
    }
    // NS low-income tax reduction (Form NS428, unindexed 2025 figures carried
    // to 2026): $300 basic + $300 eligible dependant + $165 per dependent
    // child (excluding the ED child), less 5% of income over $15,000.
    if (province === 'NS') {
      const nsKids = Math.max(0, (opts.dependantsUnder19 || 0) - (opts.hasEligibleDependant ? 1 : 0))
      const base = 300 + (opts.hasEligibleDependant ? 300 : 0) + 165 * nsKids
      return Math.max(0, base - 0.05 * Math.max(0, t - 15000))
    }
    if (province === 'ON') return onPiecesAt(t).reduction
    return 0
  }
  const provReduction = computeProvReductionAt(taxable)
  // Per-LEVEL clamping: federal and provincial tax each floor at zero with
  // their OWN credits (reductions are provincial). Unused credits from one
  // level never offset the other (income-type audit: eligible dividends).
  function taxPayableAt(t) {
    const split = computeCreditsSplitAt(t)
    const fedRaw = getFedRawTax(t, cfg)
    const provRaw = getRawIncomeTax(province, t, cfg) - fedRaw
    const red = computeProvReductionAt(t)
    return Math.max(0, fedRaw - split.fed)
      + Math.max(0, provRaw - split.prov - red)
      + (province === 'ON' ? onPiecesAt(t).extra : 0)
  }
  const taxPayable = taxPayableAt(taxable)
  // Expose an Ontario-aware extra-tax function for SSAG scenario recomputation
  function computeOntarioExtraAt(t) {
    return province === 'ON' ? onPiecesAt(t).extra : 0
  }
  return {
    taxable, taxPayable, cpp: cppEi.cpp, ei: cppEi.ei, totalCred, rawTax,
    onExtra, provReduction,
    // Expose the credit calculator so SSAG scenario code can recompute at
    // the SS-adjusted taxable income (Age Amount phases out, etc).
    computeCreditsAt,
    computeCreditsSplitAt,
    taxPayableAt,
    computeOntarioExtraAt,
    computeProvReductionAt,
  }
}

// Effective marginal tax rate for a party — numerical derivative of tax payable
// with respect to a small income increment. Captures credit absorption (when
// non-refundable credits exceed tax owed, EMR = 0) which the bracket rate misses.
// This is what should be used in the SSAG iterative-tax adjustment instead of
// the raw bracket rate. Returns a value in [0, 0.95].
function partyEffectiveMarginalRate(province, items, opts) {
  opts = opts || {}
  const cfg = opts.cfg || TAX_YEARS[2026]
  const delta = 1000
  const tax0 = partyTaxBundle(province, items, opts)
  // Synthesize a +$1000 employment-income item to probe the marginal effect
  const probedItems = (items || []).slice()
  probedItems.push({ id: '__probe', typeId: '__probe_marginal__', amount: delta })
  // Use a synthetic taxable-only delta — bypasses needing INCOME_TYPES lookup
  const taxableDelta = aggregateTaxableIncome(items, cfg) + delta
  const cppEi = aggregateCppEi(items, { cfg })
  const rawTax1 = getRawIncomeTax(province, taxableDelta, cfg)
  const prov = cfg.provTaxData[province] || { bpa: 12000, rate: 0.05 }
  // Recompute credits at unchanged level (they don't grow with income)
  const totalCred1 = tax0.totalCred
  const taxPayable1 = Math.max(0, rawTax1 - totalCred1)
  return Math.max(0, Math.min(0.95, (taxPayable1 - tax0.taxPayable) / delta))
}

// Net cash flow for a party — what hits their bank account after tax/CPP/EI.
// Sums all "indi: add" / "add_after_tax" amounts using the item's indiFactor
// (which differs from csgFactor/taxFactor for items like capital gains, where
// the actual gain hit the bank but only 50% is taxable).
function partyNetCashFlow(province, items, opts) {
  let cashIn = 0
  for (const item of items || []) {
    const t = INCOME_TYPES[item.typeId]
    if (!t) continue
    const f = indiFactor(t)
    if (t.indi === 'add' || t.indi === 'add_after_tax') cashIn += (item.amount || 0) * f
    else if (t.indi === 'subtract') cashIn -= (item.amount || 0) * f
  }
  const tax = partyTaxBundle(province, items, opts)
  return Math.max(0, cashIn - tax.taxPayable - tax.cpp - tax.ei)
}

// ─── Dev-only audit hook — REMOVED IN DIVII ───────────────────────────────
// Upstream exposes the module-private tax/benefit primitives on
// window.__diviiAudit for the standalone calibration audit, behind a
// Vite-only dev-env guard. Divii builds on webpack 4 (vue-cli-service 4.x),
// which cannot even parse that guard's meta-property syntax, so upstream's
// block is a hard build error here rather than dead code. The calibration
// harness doesn't ship in Divii, so the whole block is dropped.
// DIVII-ONLY DELTA — re-drop on every upstream sync until upstream gates it
// on something webpack understands.

export default {
  name: 'SupportCalculator',

  components: { SsagRangeAdvisor },

  props: {
    // Parent app provides these from the case file
    province: { type: String, default: null },       // e.g. 'BC'
    payorNameProp: { type: String, default: null },
    recipientNameProp: { type: String, default: null },
    cohabitationDateProp: { type: String, default: null }, // ISO yyyy-mm-dd
    separationDateProp: { type: String, default: null },   // ISO yyyy-mm-dd
    // DOBs feed two SSAG inputs:
    //  1. Recipient age at separation → SSAG "Rule of 65" (indefinite duration
    //     if recipientAge + yearsMarried ≥ 65 and yearsMarried ≥ 5).
    //  2. Both ages displayed on the worksheet for context.
    payorDOBProp:     { type: String, default: null },
    recipientDOBProp: { type: String, default: null },
    // Children passed from the parent app (Divii). Each item:
    //   { name: string, dob: string (ISO yyyy-mm-dd), parenting?: 'primaryA' | 'primaryB' | 'shared' }
    // The parent app typically knows name + DOB; parenting may be set in
    // Divii or default to "primarily with recipient" until edited here.
    // User can still add/remove children in the calculator if needed.
    childrenProp: { type: Array, default: () => [] },

    // LegalApp integration — opaque JSON state previously emitted via
    // `update-state` and stored on the Matter record. When the calculator
    // panel opens for an existing Matter, LegalApp passes the saved state
    // back here, and the calculator restores incomes, children, scenarios,
    // SSAG choices, etc. exactly as the lawyer left them.
    //
    // Shape matches computeFullState() return value. schemaVersion lets
    // future versions migrate older saved states. Pass null/undefined for
    // a fresh calculator (no saved state yet).
    initialState: { type: Object, default: null },

    // LegalApp integration — bundled Matter Details context. Used on FIRST
    // open (when initialState is null) to pre-fill identifying inputs so
    // the lawyer doesn't re-type what's already in Divii. Once the calc
    // has its own saved state, initialState takes precedence and matterContext
    // is ignored (the calc owns these fields).
    //
    // Shape (all fields optional; calculator fills gaps with safe defaults):
    //   {
    //     province: 'BC' | 'AB' | …       // → effectiveProvince
    //     taxYear: 2026 | 2025 | …        // from "Year of Agreement"
    //     party1Name: 'Daniel',
    //     party2Name: 'Jane',
    //     party1DOB: 'YYYY-MM-DD',
    //     party2DOB: 'YYYY-MM-DD',
    //     cohabitationDate: 'YYYY-MM-DD',
    //     separationDate: 'YYYY-MM-DD',
    //     children: [{ name, dob, parenting?, isDependent? }, …],
    //   }
    //
    // NOTE: party1/party2 here are *unordered* — Matter Details doesn't
    // know payor vs. recipient. The auto-payor detection (step 5) decides
    // direction from parenting + income. Until then, party1 → A (payor
    // slot) and party2 → B (recipient slot) as a placeholder; the lawyer
    // can flip with the "Switch payor" link.
    //
    // NOTE: income is NOT in matterContext — calculator is the source of
    // truth for income, and pushes it OUT to LegalApp on Apply.
    matterContext: { type: Object, default: null },

    // DIVII PATCH: host-side record gating and the host's record of what was
    // last written. canRecordProp=false disables every "Record in agreement"
    // control (read-only roles). recordedProp is
    //   { scenarioId, scenarioLabel, mode: 'periodic'|'lumpSum'|null, at }
    // or null when nothing has been recorded yet. Its default is deliberately
    // undefined (NOT null): a host that passes the prop, even as null, owns the
    // "In agreement" badge, because it confirms with the lawyer before writing
    // and may cancel; a host that never passes it keeps the local badge.
    canRecordProp: { type: Boolean, default: true },
    recordedProp: { type: Object, default: undefined },
  },

  data() {
    // Helper: pull a field from matterContext, falling back to a per-prop
    // value, then a literal default. matterContext beats individual props
    // because LegalApp's bundled context is the canonical first-open seed.
    const mc = this.matterContext || {}
    const fromCtx = (key, propVal, fallback) => {
      if (mc[key] !== undefined && mc[key] !== null && mc[key] !== '') return mc[key]
      if (propVal !== undefined && propVal !== null && propVal !== '') return propVal
      return fallback
    }
    return {
      step: 1,
      // Phase B: 'wizard' = step-by-step entry; 'workspace' = single-page Divorcemate-style view
      viewMode: 'wizard',
      // Active workspace tab — the s.15.2(4) frame: needs (budgets), means
      // (the calculation), circumstances (advisor questions), and the
      // recommendation that draws on all three.
      workspaceTab: 'means',
      // Party budgets (Needs tab) — F8 Part 2 aligned; see F8_BUDGET_GROUPS.
      budgetLines: makeBudgetLines(),
      budgetCustom: makeBudgetCustom(),
      // Advisor answers lifted here so the wizard panel, Circumstances tab,
      // and Recommendation tab share one set of answers (and so they persist
      // in case scenarios).
      advisorAnswers: makeEmptyAnswers(),
      // Party self-identification claims ("I am Smith"). Keyed by party id;
      // each claim = { name, userKey }. Once a party is claimed, only the
      // claiming user (matched by userKey) gets that party's entry surface.
      // Full-access roles can reset a wrong claim. Persisted in update-state.
      partyClaims: { A: null, B: null },
      claimPartyPick: '',
      PROVINCES,
      S7_CATEGORIES,
      F8_BUDGET_GROUPS,
      BUDGET_FREQS,
      // Tax year for calculations. Defaults to the current tax year (2026).
      // Pulled from matterContext.taxYear when LegalApp passes the Matter's
      // "Year of Agreement". 2023-2025 selectable for arrears calculations
      // but currently use 2026 data with a warning until reference values
      // are populated.
      taxYear: fromCtx('taxYear', null, CURRENT_TAX_YEAR),
      // Override for which party claims the Eligible Dependant Credit (EDC).
      // Most useful for shared-custody cases where both parents are technically
      // eligible but only one can claim — and sometimes (per Divorcemate's
      // "Dependant credit not claimed" annotation) neither claims it.
      //   null   — auto: recipient claims when there's a shared kid or kid
      //            primary with recipient; payor claims when kid primary with payor
      //   'A'    — payor claims (force)
      //   'B'    — recipient claims (force)
      //   'none' — neither claims (matches DM "not claimed" annotation)
      edcClaim: null,
      TAX_YEAR_OPTIONS,
      // Map of year → placeholder flag for the selector "(data pending)" suffix.
      // Only shows the suffix when the year is a FULL placeholder (no real data
      // at all). Partial-coverage years like 2025 (BC + federal only) get a
      // separate per-province warning via isPartialTaxYearForProvince.
      TAX_YEARS_PLACEHOLDER_FLAG: Object.fromEntries(
        TAX_YEAR_OPTIONS.map(y => [y, !!TAX_YEARS[y].placeholder])
      ),

      // Step 1 — Matter identity (matterContext > individual props > default)
      cohabitationDate: fromCtx('cohabitationDate', this.cohabitationDateProp, ''),
      separationDate:   fromCtx('separationDate',   this.separationDateProp,   ''),
      // Party DOBs — matterContext uses party1DOB/party2DOB (unordered);
      // map party1 → payor slot, party2 → recipient slot as a placeholder.
      // Auto-payor detection (step 5) will adjust direction when ready.
      payorDOB:     fromCtx('party1DOB', this.payorDOBProp,     ''),
      recipientDOB: fromCtx('party2DOB', this.recipientDOBProp, ''),
      // Disability Tax Credit eligibility per party (line 31600). When true,
      // the party's tax bundle claims the federal + provincial disability
      // amount. Follows the payor/recipient slot like DOB — swapped by
      // switchPayor(). Persisted in state; not currently sourced from
      // matterContext (LegalApp can add a prop later without breaking).
      payorDisabled:     false,
      recipientDisabled: false,
      // provinceLocal is the in-calc fallback when no `province` prop is set.
      // Pulled from matterContext.province on first open. Once initialState
      // hydrates, the saved provinceLocal wins.
      provinceLocal: fromCtx('province', null, 'BC'),

      // Step 2 — Income items (Phase 1 picker model)
      // party1Name → payor slot, party2Name → recipient slot (placeholder
      // direction; see auto-payor note above).
      payorName:     fromCtx('party1Name', this.payorNameProp,     'Payor'),
      recipientName: fromCtx('party2Name', this.recipientNameProp, 'Recipient'),
      // incomeItems: array of { id, partyId: 'A'|'B', typeId, amount, label?, meta? }
      // Aggregators (payorIncome, recipientGuidelinesIncome, etc.) derive from this.
      incomeItems: [],
      // Picker UI state
      openPickerFor: null,    // null | 'A' | 'B'
      pickerSearch: '',       // current search query in the picker
      amountFocusedId: null,  // id of currently-focused amount input (suppresses comma-format while typing)

      // LegalApp integration — id of scenario most recently sent via Apply to
      // Agreement. Drives the "Currently in agreement" badge. Persisted as
      // part of update-state so it survives panel close/reopen.
      lastAppliedScenarioId: null,
      // Which form the last-applied scenario was sent in: 'periodic' | 'lumpSum'.
      lastAppliedMode: null,
      // The scenario whose Apply confirm dialog is open (null = closed), and the
      // mode currently selected in that dialog.
      applyDialogScenario: null,
      applyDialogMode: 'periodic',
      // Term for monthly support: indefinite (open-ended) vs a fixed # of years.
      applyDialogIndefinite: false,
      applyDialogYears: 0,
      // Gate for the "Record in agreement" button + confirm dialog.
      // DIVII PATCH: ON. The host (SupportCalculatorTab) handles the `apply`
      // event: it confirms the field-by-field changes with the lawyer and
      // writes them into the agreement builder.
      applyToAgreementEnabled: true,
      // DIVII PATCH: which lump-sum figure the dialog records:
      // 'midpoint' (split-the-difference) or 'payorCost' (payor's after-tax cost).
      applyDialogLumpBasis: 'midpoint',

      // Case scenarios — named snapshots of the full input set. Different from
      // `scenarios` (which holds SS-amount/duration columns within a single
      // case). Use case: husband's income position vs wife's, mediator's
      // blended assumption, with/without imputed income, etc. The active
      // scenario auto-updates as the lawyer edits inputs.
      //
      // Shape: { id, name, notes, createdAt, updatedAt, state: {...inputs...} }
      // The `state` object mirrors a subset of computeFullState — everything
      // a "what-if" should override when loaded. Excludes meta fields like
      // caseScenarios itself, activeCaseScenarioId, viewMode, etc.
      //
      // Persisted via the existing update-state event → LegalApp saves to the
      // Matter record. localStorage is a no-op fallback only when the calc
      // runs without a parent listener.
      caseScenarios: [],
      activeCaseScenarioId: null,
      // Naming-prompt UI state (modal dialog data)
      scenarioPromptOpen: false,
      scenarioPromptMode: 'new', // 'new' | 'rename'
      scenarioPromptName: '',
      scenarioPromptTargetId: null,

      // Auto-payor detection — once the lawyer clicks "Switch payor"
      // (or accepts/dismisses the suggestion banner), this flips true and
      // the auto-detector stops suggesting swaps. Reset when the user
      // changes inputs that meaningfully alter the calculation.
      payorManuallyChosen: false,

      // Builder→calculator sync (Divii). After hydration, fresh props are
      // diffed against the saved state for identity fields (names, DOBs,
      // dates, children). Blank state values fill silently; genuine conflicts
      // raise the sync banner. syncDeclinedFingerprint persists the last
      // dismissed diff so the same offer doesn't re-banner every open.
      syncDeclinedFingerprint: null,
      // Transient: { fingerprint, changes: [{key,label,apply}] } | null.
      // Truthy = banner visible. Not persisted.
      syncConflicts: null,
      // Payor/recipient orientation relative to the builder's party1/party2.
      // Starts false (A=party1=payor, B=party2=recipient); every explicit
      // swap ("Swap payor & recipient" or "Switch payor") toggles it. The
      // sync engine reads this to re-orient incoming builder identity fields
      // before diffing, so a swap isn't offered back as a rename — even when
      // a party was also renamed in the builder (which defeats name-matching).
      builderOrientationSwapped: false,

      // Step 3 — children. Seeded from matterContext.children when LegalApp
      // passes the Matter's Child Information section, else from the legacy
      // childrenProp, else empty. With no children, SSAG falls back to the
      // without-child formula.
      children: (
        (mc.children && mc.children.length) ? mc.children :
        (this.childrenProp || [])
      ).map(c => ({
        // Stable id from the parent app's data (Divii builder child id) —
        // used by the builder-sync to match children exactly. Null for
        // children that predate ids; heuristics cover those.
        sourceId: c.id != null ? c.id : (c.sourceId != null ? c.sourceId : null),
        name: c.name || '',
        dob: c.dob || '',
        // Default to "primarily with recipient" if the parent app doesn't
        // specify parenting — the most common arrangement when SS is owed.
        parenting: c.parenting || 'primaryB',
        // Honor caller-supplied dependency status for AOM children. When the
        // parent app or a test fixture flags a 19-year-old as still dependent
        // ("child of the marriage"), the calculator should include them in
        // SSAG/CSG calcs without forcing the user to re-confirm in the UI.
        isDependent: c.isDependent != null ? c.isDependent : null,
        // Optional override: forces FCSG table to apply (true) or not apply
        // (false) regardless of AOM. Default behavior: under-AOM applies,
        // AOM doesn't. See `csgChildren` computed for full logic.
        subjectToTable: c.subjectToTable != null ? c.subjectToTable : null,
        // Per-child claim assignments (shared custody): who claims the eligible
        // dependant credit and the provincial per-child amount (e.g. SK). Must
        // be preserved from the caller/fixture — matches the childrenProp watcher.
        // DIVII PATCH: the builder's own dependant credit answer
        // (edcClaimedByPartner '0'|'1' = partner1|partner2) seeds a shared
        // child that has no calculator choice yet. Slot A is partner1 on a
        // first open (nothing has been swapped yet).
        edcClaimedBy: c.edcClaimedBy != null
          ? c.edcClaimedBy
          : edcSlotFromBuilderClaim(c.edcClaimedByPartner, c.parenting || 'primaryB', false),
        childAmountBy: c.childAmountBy != null ? c.childAmountBy : null,
        disabled: c.disabled != null ? c.disabled : false,
        s7expenses: c.s7expenses || [],
      })),

      // Step 5 — SSAG
      ssagApplicable: false,
      // yearsMarried is computed from cohabitationDate → separationDate
      ssagPayor: 'A',
      ssagSelected: 0,
      ssagSelectedDuration: 0,

      // ── Restructuring (SSAG Ch. 10) — opt-in lump-sum + monthly split ──
      // Hidden until the lawyer opens the worksheet disclosure. When open,
      // the lawyer enters a lump sum (paid up front, tax-free) and a stream
      // duration; the calculator back-solves the monthly that keeps the
      // package's after-tax global value equal to the selected periodic award
      // (the lump-sum equivalent — DM's "Midpoint" of payor after-tax cost and
      // recipient after-tax benefit, present-valued at the discount rate).
      showRestructure: false,
      restructureScenarioId: null,    // which scenario column to restructure; null → SSAG Mid / first
      restructureLumpSum: 0,
      restructureDurationYears: null, // null → defaults to the basis scenario's duration
      restructureDiscountRate: 1.81,  // % — DM's real-return-bond rate

      // Phase B+ — Scenarios for comparison. Each is a column in the support-
      // scenarios table; bound to a SSAG tier or has an explicit SS override.
      scenarios: [],

      // Cash Flow Adjustments (SSAG Ch. 6.6) — per-party post-tax outflows
      // that decrease NDI but don't affect taxable income. Examples:
      //   • Mandatory employer pension contributions beyond Schedule III
      //   • After-tax disability/extended health premiums
      //   • Court-ordered support to other dependants
      // Each item: { id, partyId: 'A'|'B', label, amount } — amount is annual.
      cfaItems: [],
    }
  },

  computed: {
    // ── Province ──────────────────────────────────────────────────
    // Resolution order:
    //   1. `province` prop — explicit override from a test harness or
    //      legacy caller; pins the calculator to one province
    //   2. matterContext.province — bundled Matter Details context from
    //      LegalApp; used when no explicit prop is set
    //   3. provinceLocal (data) — in-calc state, default 'BC', survives
    //      via the saved state round-trip
    effectiveProvince() {
      return this.province
        || (this.matterContext && this.matterContext.province)
        || this.provinceLocal
    },

    // ── Auto-payor detection ──────────────────────────────────────
    // Based on:
    //   • Income (primary signal): higher earner is typically the payor
    //     for both child support and spousal support.
    //   • Parenting (sanity check): if all kids are primarily with one
    //     party, the OTHER party pays CS. Combined with income, this
    //     usually agrees with the income signal.
    //
    // Returns the *slot* that should be the payor: 'A' or 'B', or
    // null when there's not enough info to decide (e.g. both incomes 0).
    //
    // Note: the calc's current arrangement always puts 'A' in the payor
    // slot. So suggestedPayor==='B' means "the auto-detector thinks the
    // current arrangement is backwards." See suggestedSwap below for the
    // boolean form used by the banner.
    suggestedPayor() {
      const a = this.payorIncome || 0
      const b = this.recipientIncome || 0
      // Need at least one party with income to suggest anything.
      if (a <= 0 && b <= 0) return null
      // Only one income on file → that party is the only candidate to pay.
      if (a > 0 && b <= 0) return 'A'
      if (b > 0 && a <= 0) return 'B'
      // Both incomes on file → higher earner. Tie → no suggestion.
      if (a > b) return 'A'
      if (b > a) return 'B'
      return null
    },
    // Boolean form: true when the auto-detector wants to flip the payor.
    // Drives the "Switch payor — suggested" badge in the UI.
    suggestedSwap() {
      if (this.payorManuallyChosen) return false
      return this.suggestedPayor === 'B'
    },
    provinceName() {
      return PROVINCES[this.effectiveProvince] || this.effectiveProvince
    },
    aom() {
      return AOM[this.effectiveProvince] || 18
    },

    // ── Tax year config ────────────────────────────────────────────
    // Returns the year-keyed config block for the currently selected tax year.
    // Math functions read from this via opts.cfg (Phase 3 threading).
    taxYearCfg() {
      return TAX_YEARS[this.taxYear] || TAX_YEARS[CURRENT_TAX_YEAR]
    },
    // CGEB (the post-July-2026 grocery credit) is in play when the selected year is
    // 2026+ and there are children driving a refundable child benefit. Used to show
    // the recurring-vs-one-time-top-up methodology note.
    cgebActive() {
      return this.taxYear >= 2026 && this.csgChildren && this.csgChildren.length > 0
    },
    // True when the selected year has no real reference data populated yet
    // (i.e. it inherits 2026 values across the board). Triggers the calibration warning.
    isPlaceholderTaxYear() {
      return !!this.taxYearCfg.placeholder
    },
    // True when the selected year has data for federal but the current province
    // still inherits 2026 values (partial coverage). 2025 has BC populated; other
    // provinces inherit 2026. When the user picks 2025 + non-BC, this fires.
    isPartialTaxYearForProvince() {
      if (this.isPlaceholderTaxYear) return false   // already showing stronger warning
      if (this.taxYear === CURRENT_TAX_YEAR) return false
      // 2025 has BC populated; any other province is partial
      if (this.taxYear === 2025 && this.effectiveProvince !== 'BC') return true
      return false
    },
    // True when the selected province has provincial child benefit + refundable
    // tax credit formulas implemented from public CRA/provincial reference data
    // but NOT yet calibrated against Divorcemate test cases. Only BC is
    // calibrated; all other non-Quebec provinces produce approximation numbers.
    isUncalibratedProvince() {
      return this.effectiveProvince && this.effectiveProvince !== 'BC' && this.effectiveProvince !== 'QC'
    },

    // ── Table year selection ───────────────────────────────────────
    tableYear() {
      // Federal Child Support Guidelines tables apply based on the date of
      // calculation (when the order is made), not the date of separation.
      // The 2025 amendments came into force October 1, 2025; calculations
      // performed on/after that date use the new tables regardless of when
      // the parties separated. Calibration against Divorcemate confirms.
      const today = new Date()
      return today >= new Date('2025-10-01') ? 2025 : 2017
    },

    // Length of relationship in years — auto-computed from cohabitation → separation.
    // SSAG uses this for the without-child formula and for duration ranges.
    yearsMarried() {
      if (!this.cohabitationDate || !this.separationDate) return 0
      const start = new Date(this.cohabitationDate)
      const end = new Date(this.separationDate)
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0
      const ms = end - start
      if (ms <= 0) return 0
      const years = ms / (1000 * 60 * 60 * 24 * 365.25)
      // Divorcemate rounds marriage length to the nearest half-year (e.g., a
      // 23.25-year marriage rounds to 23.5; 16.4 rounds to 16.5; 23.3 rounds
      // to 23.5). Calibration against 7 DM cases (Tim/Jess 16.5, Malkin 23,
      // Moore 7, Angus 17, Romagnoli 16.5, Little 24.5, Emms 23.5) confirms
      // this convention. The 0.5-step rounding cascades into the without-child
      // formula's 1.5%–2% × yrs × gap calculation.
      return Math.round(years * 2) / 2
    },
    relationshipLengthLabel() {
      if (!this.yearsMarried) return ''
      const y = this.yearsMarried
      return `${y} year${y === 1 ? '' : 's'}`
    },
    formattedSeparationDate() {
      if (!this.separationDate) return ''
      const d = new Date(this.separationDate)
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    formattedCohabitationDate() {
      if (!this.cohabitationDate) return ''
      const d = new Date(this.cohabitationDate)
      return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    // Today's date in the same format — used on the printable worksheet
    // header so the lawyer knows when the figures were calculated.
    formattedTodayDate() {
      const d = new Date()
      return d.toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' })
    },
    // Party ages calculated at the date of separation (SSAG anchor point) —
    // not "today", since support entitlement and the Rule of 65 are tested at
    // the moment the marriage ends.
    // Plain-language notes for the audited, documented divergences from other
    // support software (each grounded in a CRA/statute citation — see
    // AUDIT_CALC_DETAILS.md). Only notes whose condition applies are shown.
    // Style rule: no em-dashes in user-facing note text.
    calcDivergenceNotes() {
      const notes = []
      const prov = this.effectiveProvince
      const yr = (this.taxYearCfg && this.taxYearCfg.year) || 2026
      const hasEmployment = (items) => (items || []).some(i =>
        i.typeId === 'employment_income' && (i.amount || 0) > 0)
      // Working seniors: employment credit (CRA line 31260 has no age limit;
      // some software denies it at 65+).
      for (const [name, age, items] of [
        [this.payorName || 'the payor', this.payorAgeAtSeparation, this.payorItems],
        [this.recipientName || 'the recipient', this.recipientAgeAtSeparation, this.recipientItems],
      ]) {
        if (age != null && age >= 65 && hasEmployment(items)) {
          notes.push(`This calculation includes the Canada Employment Amount for ${name}. ` +
            'CRA applies no age limit to this credit; some support software denies it to seniors.')
        }
      }
      // CCB: a child turning 6 in the tax year moves to the 6-17 rate at the
      // birthday (CRA rule); some software keeps the under-6 rate all year.
      const turning6 = this.includedChildren.filter(c => {
        if (!c.dob) return false
        const by = parseInt(String(c.dob).slice(0, 4), 10)
        return by && (yr - by) === 6
      })
      if (turning6.length > 0) {
        notes.push('The Canada Child Benefit here uses the 6-17 rate for a child who turns 6 ' +
          'in the tax year, per the CRA rule (the under-6 rate ends at the sixth birthday). ' +
          'Some software keeps the under-6 rate for the full year.')
      }
      // ON Child Benefit in shared custody: CRA administration pays 50% of the
      // full-custody amount; some software floors it out at middle incomes.
      if (prov === 'ON' && this.includedChildren.some(c => c.parenting === 'shared')) {
        notes.push('The Ontario Child Benefit in shared custody follows CRA administration ' +
          '(each parent receives 50% of the full-custody amount). Some software computes ' +
          '$0 at middle incomes; the statutory averaging supports the CRA approach.')
      }
      // NL: prorated 2026 BPA (Budget 2026 raised it to 15,000 effective July 1).
      if (prov === 'NL' && yr === 2026) {
        notes.push('Newfoundland taxes use the official prorated 2026 basic personal amount ' +
          'of $13,094 (the Budget 2026 increase to $15,000 applies from July 1, giving a ' +
          'blended figure per gov.nl.ca). Some software applies the full $15,000 for 2026.')
      }
      // SK: current SLITC year + senior supplementary credit.
      if (prov === 'SK') {
        notes.push('Saskatchewan amounts use the July 2026 to June 2027 Low-Income Tax Credit ' +
          'from the 2026-27 provincial budget; software built earlier in the year may still ' +
          'pay the prior amounts.')
        if ((this.payorAgeAtSeparation >= 65) || (this.recipientAgeAtSeparation >= 65)) {
          notes.push('This calculation includes the Saskatchewan senior supplementary amount ' +
            '($2,569 for every taxpayer 65 and over, line 58220).')
        }
      }
      // NU: the July 2026 child benefit statute.
      if (prov === 'NU' && this.includedChildren.length > 0 && yr >= 2026) {
        notes.push('The Nunavut Child Benefit uses the new statutory amount of $696 per child ' +
          'effective July 1, 2026 (S.Nu. 2026, c.8). Software built before the amendment ' +
          'pays the old amounts.')
      }
      return notes
    },
    payorAgeAtSeparation() {
      return this.ageAtDate(this.payorDOB, this.separationDate)
    },
    recipientAgeAtSeparation() {
      return this.ageAtDate(this.recipientDOB, this.separationDate)
    },
    // The recipient is the SSAG support recipient, which is the OPPOSITE
    // party from ssagPayor.
    ssagRecipientAgeAtSeparation() {
      const recipientIsB = this.ssagPayor === 'A'
      return recipientIsB ? this.recipientAgeAtSeparation : this.payorAgeAtSeparation
    },
    // SSAG Rule of 65: without-child formula, marriages ≥ 5 years AND
    // recipient age at separation + length of marriage ≥ 65 → indefinite.
    // (Subject to review.) Only applies under the without-child formula.
    rule65Triggered() {
      // SSAG Rule of 65: indefinite duration when recipient_age + years_married ≥ 65
      // and marriage ≥ 5 years. Calibration against Divorcemate confirms this
      // applies under BOTH formulas (with-child AND without-child) — the strict
      // SSAG paper limits it to without-child but practitioners and Divorcemate
      // apply it more broadly.
      if (!this.yearsMarried || this.yearsMarried < 5) return false
      const age = this.ssagRecipientAgeAtSeparation
      if (age == null) return false
      return age + this.yearsMarried >= 65
    },
    // SSAG age-of-children test: support continues until the last child is
    // no longer a child of the marriage. Calibration against Divorcemate
    // shows they use the province's age of majority (19 in BC, 18 in ON/AB)
    // — not the strict "finishes high school" reading. Years remaining =
    // (AOM − age at separation), floored at 0, max'd across all kids.
    ageOfChildrenDuration() {
      if (!this.includedChildren.length) return 0
      const refDate = this.separationDate
      const aom = this.aom
      let maxRemaining = 0
      for (const c of this.includedChildren) {
        if (!c.dob) continue
        const age = this.ageAtDate(c.dob, refDate)
        if (age == null) continue
        const remaining = Math.max(0, aom - age)
        if (remaining > maxRemaining) maxRemaining = remaining
      }
      return maxRemaining
    },
    // SSAG with-child LOW-end duration test (§8.5.2): the lower bound is the
    // LONGER of the length-of-marriage low (0.5 × years) and the time until the
    // youngest child commences full-time school. DM calibration (Peter & Sarah,
    // 3.5-yr marriage, youngest ~age 1 at separation → DM low = 5 yrs) confirms
    // full-time school is modelled at age 6, measured from separation. Years =
    // max over kids of (6 − age) → driven by the youngest. 0 when all kids are
    // already in school (then the length-of-marriage low governs).
    schoolStartDuration() {
      if (!this.includedChildren.length) return 0
      const refDate = this.separationDate
      const SCHOOL_START_AGE = 6
      let maxRemaining = 0
      for (const c of this.includedChildren) {
        if (!c.dob) continue
        const age = this.ageAtDate(c.dob, refDate)
        if (age == null) continue
        const remaining = Math.max(0, SCHOOL_START_AGE - age)
        if (remaining > maxRemaining) maxRemaining = remaining
      }
      return maxRemaining
    },
    // Tells UI which test produced the longer duration. Useful for explaining
    // to the user why the range looks the way it does.
    durationTestApplied() {
      const lengthHigh = this.yearsMarried * 1.0
      const ageHigh = this.includedChildren.length > 0 ? this.ageOfChildrenDuration : 0
      if (ageHigh > lengthHigh) return 'age-of-children'
      return 'length-of-marriage'
    },
    // SSAG income ceilings/floors. These don't change the formulas — they flag
    // that we're outside the range where SSAG produces reliable advisory output.
    //   • Ceiling: payor gross > $350,000 → ranges become guidelines only
    //   • Floor:   payor gross < $20,000 → typically no SS owed
    //   • Warning band: $20,000–$30,000 → reduced/discretionary
    ssagPayorGross() {
      return this.ssagPayor === 'A' ? this.payorIncome : this.recipientIncome
    },
    ssagAboveCeiling() {
      return this.ssagPayorGross > 350000
    },
    ssagBelowFloor() {
      return this.ssagPayorGross > 0 && this.ssagPayorGross < 20000
    },
    ssagInLowIncomeBand() {
      return this.ssagPayorGross >= 20000 && this.ssagPayorGross < 30000
    },
    // Read-only snapshot handed to the SSAG location advisor panel. Purely a
    // view of existing computeds — does not affect any calculation.
    advisorContext() {
      const r = this.ssagRange
      if (!r) return { applicable: false }
      return {
        applicable: true,
        formula: (r._debug && r._debug.formula) || 'without-child',
        payorIncome: this.ssagPayor === 'A' ? this.payorIncome : this.recipientIncome,
        recipientIncome: this.ssagPayor === 'A' ? this.recipientIncome : this.payorIncome,
        low: r.low, mid: r.mid, high: r.high,
        durationLow: r.durationLow, durationHigh: r.durationHigh,
        indefinite: r.indefinite,
        durationTestApplied: this.durationTestApplied,
        yearsMarried: this.yearsMarried,
        recipientAge: this.ssagRecipientAgeAtSeparation != null
          ? this.ssagRecipientAgeAtSeparation
          : this.recipientAgeAtSeparation,
        childrenCount: (this.includedChildren || []).length,
        parentingArrangement: (() => {
          const kids = this.includedChildren || []
          if (kids.length === 0) return 'none'
          const sharedCount = kids.filter(c => c.parenting === 'shared').length
          if (sharedCount === kids.length) return 'shared'      // all children shared
          if (sharedCount > 0) return 'hybrid'                  // mix of shared + primary
          const hasA = kids.some(c => c.parenting === 'primaryA')
          const hasB = kids.some(c => c.parenting === 'primaryB')
          if (hasA && hasB) return 'split'                      // one+ primary with each parent
          return 'primary'
        })(),
        aboveCeiling: this.ssagAboveCeiling,
        belowFloor: this.ssagBelowFloor,
        inLowIncomeBand: this.ssagInLowIncomeBand,
        payorName: this.payorName,
        recipientName: this.recipientName,
        // Party disability flags (the DTC checkboxes), mapped to SSAG roles.
        recipientDisabled: this.ssagPayor === 'A' ? !!this.recipientDisabled : !!this.payorDisabled,
        payorDisabled:     this.ssagPayor === 'A' ? !!this.payorDisabled : !!this.recipientDisabled,
        // ── Circumstances the entered data already shows (SSAG Ch. 9 / 12) ──
        // Income types are facts about each party's situation: prior-family
        // support = a prior obligation (Ch. 12.3); disability/EI/social
        // assistance income = illness or interrupted employment (Ch. 12.4);
        // pension/OAS/CPP-retirement income = retirement already underway.
        // The advisor folds these in WITHOUT asking, and questions they answer
        // are skipped.
        incomeSignals: (() => {
          const pItems = this.ssagPayor === 'A' ? this.payorItems : this.recipientItems
          const rItems = this.ssagPayor === 'A' ? this.recipientItems : this.payorItems
          const sum = (items, ids) => (items || []).reduce(
            (s, it) => s + (ids.includes(it.typeId) ? (it.amount || 0) : 0), 0)
          const DISABILITY = ['cpp_disability', 'disability_non_taxable', 'workers_comp']
          const SOCIAL = ['social_assistance_no_clawback', 'social_assistance_clawback_spousal', 'social_assistance_clawback_both']
          const RETIRE = ['oas_pension', 'cpp_retirement', 'other_pension', 'gis', 'elected_split_pension']
          return {
            payorPriorSupport: sum(pItems, ['prior_child_support_paid', 'prior_spousal_support_paid']),
            recipientPriorSupportReceived: sum(rItems, ['prior_spousal_support_received']),
            recipientEI: sum(rItems, ['ei_benefits']),
            recipientSocialAssistance: sum(rItems, SOCIAL),
            recipientDisabilityIncome: sum(rItems, DISABILITY),
            payorDisabilityIncome: sum(pItems, DISABILITY),
            payorRetirementIncome: sum(pItems, RETIRE),
            recipientRetirementIncome: sum(rItems, RETIRE),
          }
        })(),
        childSignals: {
          anyChildDisabled: (this.includedChildren || []).some(c => !!c.disabled),
          s7TotalMonthly: Math.round(((this.s7PaidByAAnnual || 0) + (this.s7PaidByBAnnual || 0)) / 12),
        },
        // Payor's share of family NDI at the TOP of the range — the work-
        // incentive check (SSAG Ch. 9): an award should leave the payor a
        // meaningful share of the family's disposable income.
        payorNdiShareHigh: (() => {
          const b = this.computeNDIBreakdown(r.high || 0)
          const payorCol = this.ssagPayor === 'A' ? 'a' : 'b'
          const total = b.a.ndiM + b.b.ndiM
          return total > 0 ? b[payorCol].ndiM / total : null
        })(),
        // Budget signals from the Needs tab (null until budgets are entered).
        // Shortfall = monthly living expenses minus NDI at that range point;
        // positive means the budget is not met. Columns follow the SSAG roles
        // (recipient may be party A or B), while needsComparison rows stay in
        // party order for display.
        needs: (() => {
          const nc = this.needsComparison
          if (!nc || !nc.hasBudgets || !nc.rows.length) return null
          const recipCol = this.ssagPayor === 'A' ? 'b' : 'a'
          const payorCol = this.ssagPayor === 'A' ? 'a' : 'b'
          const at = (k) => nc.rows.find(row => row.k === k)
          const sf = (k, col) => { const row = at(k); return row ? -(row[col].delta) : 0 }
          return {
            hasBudgets: true,
            recipientBudget: recipCol === 'a' ? nc.livingA : nc.livingB,
            payorBudget: payorCol === 'a' ? nc.livingA : nc.livingB,
            recipientShortfallLow:  sf('low',  recipCol),
            recipientShortfallMid:  sf('mid',  recipCol),
            recipientShortfallHigh: sf('high', recipCol),
            payorShortfallLow:      sf('low',  payorCol),
            payorShortfallMid:      sf('mid',  payorCol),
            payorShortfallHigh:     sf('high', payorCol),
          }
        })(),
      }
    },
    // ── Needs tab (budgets) ────────────────────────────────────────────────
    // Auto-filled rows per group, from the calculation. Monthly figures.
    budgetAutoRows() {
      const pt = this.payorTaxBundle
      const rt = this.recipientTaxBundle
      const m = (x) => Math.round(((x || 0) / 12) * 100) / 100
      const itemsOf = (col) => (col === 'a' ? this.payorItems : this.recipientItems) || []
      const sumType = (col, id) => itemsOf(col).reduce(
        (s, it) => s + (it.typeId === id ? (it.amount || 0) : 0), 0)
      const compulsory = [
        { label: 'Income taxes', a: m(pt.taxPayable), b: m(rt.taxPayable) },
        { label: 'CPP contributions', a: m(pt.cpp), b: m(rt.cpp) },
        { label: 'EI premiums', a: m(pt.ei), b: m(rt.ei) },
        { label: 'Employee pension contributions',
          a: m(sumType('a', 'rpp_deduction')), b: m(sumType('b', 'rpp_deduction')) },
        { label: 'Union and professional dues',
          a: m(sumType('a', 'union_dues')), b: m(sumType('b', 'union_dues')) },
      ].filter(r => r.a || r.b)
      const supportOthers = [
        { label: 'Child support (prior relationship)',
          a: m(sumType('a', 'prior_child_support_paid')), b: m(sumType('b', 'prior_child_support_paid')) },
        { label: 'Spousal support (prior relationship)',
          a: m(sumType('a', 'prior_spousal_support_paid')), b: m(sumType('b', 'prior_spousal_support_paid')) },
      ].filter(r => r.a || r.b)
      return { compulsory, supportOthers }
    },
    // Monthly living expenses per party: everything the party TYPED (standard
    // lines + custom rows), normalized to monthly. Excludes the auto rows —
    // NDI is already net of those (see F8_BUDGET_GROUPS note).
    budgetLivingMonthly() {
      const sum = { a: 0, b: 0 }
      for (const key of Object.keys(this.budgetLines)) {
        const e = this.budgetLines[key]
        const f = budgetFreqFactor(e.freq)
        sum.a += (Number(e.a) || 0) * f
        sum.b += (Number(e.b) || 0) * f
      }
      for (const g of Object.keys(this.budgetCustom)) {
        for (const c of this.budgetCustom[g]) {
          const f = budgetFreqFactor(c.freq)
          sum.a += (Number(c.a) || 0) * f
          sum.b += (Number(c.b) || 0) * f
        }
      }
      return { a: Math.round(sum.a), b: Math.round(sum.b) }
    },
    // F8-style total monthly expenses per party (auto rows + entered rows).
    budgetTotalMonthly() {
      const auto = this.budgetAutoRows
      const t = { a: this.budgetLivingMonthly.a, b: this.budgetLivingMonthly.b }
      for (const r of auto.compulsory.concat(auto.supportOthers)) {
        t.a += r.a || 0
        t.b += r.b || 0
      }
      return { a: Math.round(t.a), b: Math.round(t.b) }
    },
    // Budget vs NDI at each range point. NDI comes from the same breakdown
    // that drives the scenario table, so the comparison is exactly consistent
    // with the displayed numbers.
    needsComparison() {
      const living = this.budgetLivingMonthly
      const hasBudgets = living.a > 0 || living.b > 0
      const r = this.ssagRange
      if (!r) return { hasBudgets, livingA: living.a, livingB: living.b, rows: [] }
      const rows = ['low', 'mid', 'high'].map((k) => {
        const ss = r[k] || 0
        const b = this.computeNDIBreakdown(ss)
        return {
          k, ss,
          a: { ndi: b.a.ndiM, budget: living.a, delta: Math.round(b.a.ndiM - living.a) },
          b: { ndi: b.b.ndiM, budget: living.b, delta: Math.round(b.b.ndiM - living.b) },
        }
      })
      return { hasBudgets, livingA: living.a, livingB: living.b, rows }
    },
    // CUSTOMARY-CASE DETECTION
    // Two configurations need flagging because SSAG handles them differently
    // from the standard with-child formula we currently apply:
    //
    //   1. Custodial payor — the SS payor is also the parent with primary care.
    //      SSAG (Ch. 8) uses a different formula structure (Boyd v. Boyd
    //      modifications). Calculator currently applies the standard with-child
    //      formula with parties flipped, which produces approximate results.
    //
    //   2. Split custody — at least one child primary with each parent.
    //      The SSAG with-child formula handles this through notional CS sums
    //      (which our INDI math already does), but the configuration is
    //      worth flagging to the user since it's an unusual case.
    custodialPayorScenario() {
      if (this.includedChildren.length === 0) return false
      // Split custody (kids with each parent) is NOT custodial-payor — it's
      // its own scenario, handled by the with-child formula via notional sums
      // at the TOTAL children count (per CSG s.8). DM Fieber PDF confirms
      // split routes through with-child, not without-child. Returning true
      // here would push split into the without-child branch and collapse
      // the SSAG range against ss5050.
      if (this.splitCustodyScenario) return false
      const payorKey = this.ssagPayor === 'A' ? 'primaryA' : 'primaryB'
      const payorPrimaryCount = this.includedChildren.filter(c => c.parenting === payorKey).length
      const recipKey = this.ssagPayor === 'A' ? 'primaryB' : 'primaryA'
      const recipPrimaryCount = this.includedChildren.filter(c => c.parenting === recipKey).length
      // Custodial-payor = SS payor has more kids primarily than recipient does.
      return payorPrimaryCount > 0 && payorPrimaryCount >= recipPrimaryCount
    },
    // SSAG Ch. 9 — Adult Children. When ALL included children are at/over the
    // age of majority (i.e., no minor "child of the marriage" remains), SSAG
    // recommends the WITHOUT-CHILD formula even though kids are still being
    // supported. Calibration against Divorcemate confirms this — the with-child
    // INDI solver doesn't fit because there's no FCSG table amount to anchor
    // the INDI deduction. Practitioners may still negotiate based on the adult
    // kid's budget contribution, but the headline range comes from the
    // without-child formula (1.5%–2% × yrs × gross gap, capped for ≥25yr).
    allChildrenAreAdult() {
      if (this.includedChildren.length === 0) return false
      return this.includedChildren.every(c => c.dob && this.isAtAOM(c.dob))
    },
    splitCustodyScenario() {
      if (this.includedChildren.length < 2) return false
      const hasA = this.includedChildren.some(c => c.parenting === 'primaryA')
      const hasB = this.includedChildren.some(c => c.parenting === 'primaryB')
      return hasA && hasB
    },
    // The without-child formula is naturally capped because SSAG specifies a
    // ceiling at 37.5% (low) / 50% (high) of the gross income gap for marriages
    // ≥ 25 years. We apply this cap explicitly so the formula doesn't produce
    // values exceeding 50% of gap for very long marriages.
    withoutChildHitsCap() {
      // Cap applies whenever the without-child formula is in use (no kids,
      // adult-only, OR custodial-payor) and marriage length is ≥ 25 years.
      const useWithoutChild = this.includedChildren.length === 0
        || this.custodialPayorScenario
        || this.allChildrenAreAdult
      return useWithoutChild && this.yearsMarried >= 25
    },
    // Human-readable label for which SSAG formula is currently driving the range.
    formulaLabel() {
      if (this.includedChildren.length === 0) return 'without-child formula'
      if (this.allChildrenAreAdult) return 'without-child formula · adult children (Ch. 9)'
      if (this.custodialPayorScenario) return 'without-child formula · custodial payor'
      return 'with-child formula'
    },
    lookup() {
      return this.tableYear === 2025 ? lookupCSG2025 : lookupCSG2017
    },

    // ── Income items (Phase 1) ─────────────────────────────────────
    // Raw entered items — the editing UI binds to these so inputs always show
    // what the user typed.
    payorItemsRaw() {
      return this.incomeItems.filter(i => i.partyId === 'A')
    },
    recipientItemsRaw() {
      return this.incomeItems.filter(i => i.partyId === 'B')
    },
    // Calculation views — OAS items arrive pre-adjusted for the recovery-tax
    // clawback so every downstream aggregation (CSG, tax, benefits, NDI) sees
    // effective OAS.
    payorItems() {
      return adjustOASClawback(this.payorItemsRaw, this.taxYearCfg)
    },
    recipientItems() {
      return adjustOASClawback(this.recipientItemsRaw, this.taxYearCfg)
    },
    // New-partner income per party (null = no partner recorded).
    payorPartnerIncome() { return partnerIncomeOf(this.payorItemsRaw) },
    recipientPartnerIncome() { return partnerIncomeOf(this.recipientItemsRaw) },
    // ── CSG s.10 / Schedule II — comparison of household standards of living.
    // Renders once any household or undue-hardship input exists. Ratio =
    // adjusted household income ÷ Schedule II low-income measure. Adjustments
    // implemented: + partner/adult incomes, ± the child support in issue,
    // − support paid to others, − claimed undue-hardship amounts. (Spousal
    // support in issue is not folded in; run scenarios for that.)
    schIIComparison() {
      const HH = ['income_of_spouse', 'income_of_additional_adult',
                  'additional_children_prior', 'additional_children_new']
      const UH = ['uh_high_debts', 'uh_high_access_costs', 'uh_support_legal_duty', 'uh_other']
      const any = (this.incomeItems || []).some(it => HH.includes(it.typeId) || UH.includes(it.typeId))
      if (!any) return null
      const csAnnual = (this.netCS || 0) * 12
      const payerIsA = !!this.netCSPayer && this.netCSPayer === this.payorName
      const build = party => {
        const raw = party === 'A' ? this.payorItemsRaw : this.recipientItemsRaw
        const guidelines = party === 'A' ? this.payorIncome : this.recipientIncome
        const partner = partnerIncomeOf(raw)
        const adults = raw.filter(it => it.typeId === 'income_of_additional_adult')
        const extraKids = sumTypeAmounts(raw, ['additional_children_prior', 'additional_children_new'])
        const caseKids = this.includedChildren.filter(c =>
          c.parenting === 'shared' || c.parenting === (party === 'A' ? 'primaryA' : 'primaryB')).length
        const size = 1 + (partner != null ? 1 : 0) + adults.length + extraKids + caseKids
        const uh = sumTypeAmounts(raw, UH)
        const otherSupport = sumTypeAmounts(raw, ['prior_child_support_paid', 'prior_spousal_support_paid'])
        const csAdj = csAnnual === 0 ? 0 : ((party === 'A') === payerIsA ? -csAnnual : +csAnnual)
        const income = Math.max(0,
          guidelines + (partner || 0) +
          adults.reduce((s, a) => s + (a.amount || 0), 0) +
          csAdj - uh - otherSupport)
        const lim = schIILowIncomeMeasure(size)
        return { size, lim, income, uh, otherSupport, csAdj, ratio: lim > 0 ? income / lim : 0 }
      }
      const A = build('A')
      const B = build('B')
      return { A, B, lower: A.ratio === B.ratio ? null : (A.ratio < B.ratio ? 'A' : 'B') }
    },
    // Guidelines Income — for CSG table lookups; preserves the existing
    // payorIncome / recipientIncome interface used throughout the file.
    // Province is passed so non-taxable gross-up (CSG Sched III s.19) can be
    // applied at the correct marginal rate.
    payorIncome() {
      return aggregateGuidelinesIncome(this.payorItems, this.effectiveProvince, this.taxYearCfg)
    },
    recipientIncome() {
      return aggregateGuidelinesIncome(this.recipientItems, this.effectiveProvince, this.taxYearCfg)
    },
    // EDC-eligible children — those for which the user can pick a claimant.
    //   • Sole-with-payor kid: only the payor can claim (one-sided UI)
    //   • Sole-with-recipient kid: only the recipient can claim
    //   • Shared kids: either parent. With 3+ shared kids, only the youngest
    //     two are eligible — the oldest will age out first, so EDC is most
    //     valuable claimed on the youngest. (User can still override.)
    edcEligibleChildren() {
      // EDC only applies to children under 18 (see edcAgeEligible).
      const kids = this.includedChildren.filter(c => c.dob && this.edcAgeEligible(c))
      const shared = kids.filter(c => c.parenting === 'shared')
      let eligibleShared = shared
      if (shared.length >= 3) {
        // Sort by DOB descending (youngest first), take youngest 2
        const sorted = [...shared].sort((a, b) => (b.dob || '').localeCompare(a.dob || ''))
        eligibleShared = sorted.slice(0, 2)
      }
      const eligibleSet = new Set([
        ...kids.filter(c => c.parenting === 'primaryA' || c.parenting === 'primaryB'),
        ...eligibleShared,
      ])
      // Preserve original ordering
      return this.includedChildren.filter(c => eligibleSet.has(c))
    },
    // Eligible Dependant Credit (EDC) — only one parent can claim per year
    // (CRA rule). Two resolution layers:
    //   1. Per-child override (new model): if any child has edcClaimedBy set,
    //      it wins. Each parent's claim = any child where edcClaimedBy = 'A'/'B'.
    //   2. Legacy `edcClaim` field ('A'/'B'/'none') — fixture syntax. Applies
    //      when no per-child claim is set.
    //   3. Auto-default: payor claims if a kid is primary with them; recipient
    //      claims for shared kids or kids primary with recipient.
    // DIVII PATCH: the Eligible Dependant Credit claim for every eligible child,
    // resolved to a definite answer so the host can record it in the builder.
    // Mirrors how payorHasEligibleDependant / recipientHasEligibleDependant read
    // the inputs today: a child primarily with a parent is claimed by that
    // parent; an explicitly assigned shared child by the assigned parent;
    // shared children with no choice yet are handed out
    // youngest first, to the recipient and then to the payor, each only if that
    // parent has no other claim (CRA: one eligible dependant per parent per
    // year), so two shared children give one claim each and a hybrid case gives
    // the shared child to the parent without a primary child. There is no
    // "Auto" or "Neither" option any more: materializeEDCClaims writes these
    // defaults into the children as soon as they apply, so a null (or legacy
    // 'none') edcClaimedBy on a shared child is transient and the lawyer always
    // sees one parent selected. The tax
    // computation (payorHasEligibleDependant / recipientHasEligibleDependant)
    // and the agreement's "one child each" clause use the same list. The legacy
    // per-case edcClaim 'none' switches every claim off. Each entry: { sourceId,
    // name, dob, parenting, claimedBy: 'A' | 'B' | 'none' }.
    edcResolvedClaims() {
      const claims = this.edcEligibleChildren.map(c => {
        let claimedBy = null
        if (c.edcClaimedBy === 'A' || c.edcClaimedBy === 'B' || c.edcClaimedBy === 'none') claimedBy = c.edcClaimedBy
        else if (c.parenting === 'primaryA') claimedBy = 'A'
        else if (c.parenting === 'primaryB') claimedBy = 'B'
        return {
          sourceId: c.sourceId != null ? c.sourceId : null,
          name: c.name || '',
          dob: c.dob || '',
          parenting: c.parenting,
          claimedBy,
        }
      })
      const auto = claims.filter(c => c.claimedBy === null)
      if (auto.length) {
        let recipientTaken = claims.some(c => c.claimedBy === 'B')
        let payorTaken = claims.some(c => c.claimedBy === 'A')
        const youngestFirst = [...auto].sort((a, b) => String(b.dob).localeCompare(String(a.dob)))
        youngestFirst.forEach(c => {
          if (!recipientTaken) { c.claimedBy = 'B'; recipientTaken = true }
          else if (!payorTaken) { c.claimedBy = 'A'; payorTaken = true }
          else c.claimedBy = 'none'
        })
      }
      if (this.edcClaim === 'none') claims.forEach(c => { c.claimedBy = 'none' })
      return claims
    },
    payorHasEligibleDependant() {
      // Party A (payor) claims an EDC if ANY of these hold (checked
      // independently so a primary-custody child isn't masked when a shared
      // sibling has an explicit claim):
      //  1. A shared child is explicitly assigned to them via the UI.
      //  2. The legacy per-case edcClaim field names them.
      //  3. Auto: a child is primary with them (unless that child is 'none').
      // A party cohabiting with a new partner cannot claim the EDC (they may
      // claim the spouse amount instead — see partyTaxBundle).
      if (this.payorPartnerIncome != null) return false
      if (this.includedChildren.some(c => c.edcClaimedBy === 'A')) return true
      if (this.edcClaim === 'A') return true
      if (this.edcClaim === 'B' || this.edcClaim === 'none') return false
      // Auto: only children under 18 attract the EDC (federal line 30400).
      // DIVII PATCH: a shared child on Auto can also fall to the payor when the
      // recipient already has a claim (see edcResolvedClaims).
      return this.includedChildren.some(c =>
        c.parenting === (this.ssagPayor === 'A' ? 'primaryA' : 'primaryB') &&
        c.edcClaimedBy !== 'none' && this.edcAgeEligible(c)
      ) || this.edcResolvedClaims.some(c => c.parenting === 'shared' && c.claimedBy === 'A')
    },
    recipientHasEligibleDependant() {
      // Cohabiting with a new partner bars the EDC (spouse amount instead).
      if (this.recipientPartnerIncome != null) return false
      // Party B (recipient) claims an EDC if ANY of these hold:
      //  1. A shared child is explicitly assigned to them via the UI.
      //  2. The legacy per-case edcClaim field names them.
      //  3. Auto: shared kids (convention — lower-income party) or a child
      //     primary with them, unless assigned to the payor or set to 'none'.
      if (this.includedChildren.some(c => c.edcClaimedBy === 'B')) return true
      if (this.edcClaim === 'B') return true
      if (this.edcClaim === 'A' || this.edcClaim === 'none') return false
      // Auto: only children under 18 attract the EDC (federal line 30400).
      // DIVII PATCH: shared children on Auto follow edcResolvedClaims (youngest
      // to the recipient, the next to the payor) instead of always counting for
      // the recipient.
      return this.includedChildren.some(c =>
        c.parenting === (this.ssagPayor === 'A' ? 'primaryB' : 'primaryA') &&
        c.edcClaimedBy !== 'A' && c.edcClaimedBy !== 'none' && this.edcAgeEligible(c)
      ) || this.edcResolvedClaims.some(c => c.parenting === 'shared' && c.claimedBy === 'B')
    },
    payorTaxBundle() {
      return partyTaxBundle(this.effectiveProvince, this.payorItems, {
        hasEligibleDependant: this.payorHasEligibleDependant,
        partnerIncome: this.payorPartnerIncome,
        numChildAmountClaims: this.childAmountClaims.A,
        dependantsUnder19: this.dependantsUnder19.A,
        partyAge: this.payorAgeAtSeparation,
        disabled: this.payorDisabled,
        cfg: this.taxYearCfg,
      })
    },
    recipientTaxBundle() {
      return partyTaxBundle(this.effectiveProvince, this.recipientItems, {
        hasEligibleDependant: this.recipientHasEligibleDependant,
        partnerIncome: this.recipientPartnerIncome,
        numChildAmountClaims: this.childAmountClaims.B,
        dependantsUnder19: this.dependantsUnder19.B,
        partyAge: this.recipientAgeAtSeparation,
        disabled: this.recipientDisabled,
        cfg: this.taxYearCfg,
      })
    },
    payorNetCashFlow() {
      const base = partyNetCashFlow(this.effectiveProvince, this.payorItems, {
        hasEligibleDependant: this.payorHasEligibleDependant,
        partnerIncome: this.payorPartnerIncome,
        numChildAmountClaims: this.childAmountClaims.A,
        dependantsUnder19: this.dependantsUnder19.A,
        partyAge: this.payorAgeAtSeparation,
        disabled: this.payorDisabled,
        cfg: this.taxYearCfg,
      })
      // Deduct each party's static share of total s.7 GROSS paid so
      // the SSAG INDI solver works with the post-s.7 cash pool.
      // Matches computeNDIBreakdown — apportioning gross (not net)
      // keeps the settlement row mathematically symmetric.
      const totalPaid = (this.s7PaidByAAnnual || 0) + (this.s7PaidByBAnnual || 0)
      const s7Share = totalPaid * this.payorPct / 100
      return Math.max(0, base - this.payorCFAAnnual - s7Share)
    },
    recipientNetCashFlow() {
      const base = partyNetCashFlow(this.effectiveProvince, this.recipientItems, {
        hasEligibleDependant: this.recipientHasEligibleDependant,
        partnerIncome: this.recipientPartnerIncome,
        numChildAmountClaims: this.childAmountClaims.B,
        dependantsUnder19: this.dependantsUnder19.B,
        partyAge: this.recipientAgeAtSeparation,
        disabled: this.recipientDisabled,
        cfg: this.taxYearCfg,
      })
      const totalPaid = (this.s7PaidByAAnnual || 0) + (this.s7PaidByBAnnual || 0)
      const s7Share = totalPaid * this.recipientPct / 100
      return Math.max(0, base - this.recipientCFAAnnual - s7Share)
    },
    // Annual CFA totals per party — sum of cfaItems amounts, filtered by partyId
    payorCFAAnnual() {
      return (this.cfaItems || [])
        .filter(c => c.partyId === 'A')
        .reduce((s, c) => s + (Number(c.amount) || 0), 0)
    },
    recipientCFAAnnual() {
      return (this.cfaItems || [])
        .filter(c => c.partyId === 'B')
        .reduce((s, c) => s + (Number(c.amount) || 0), 0)
    },
    // Working income for CWB
    payorWorkingIncome() {
      return aggregateWorkingIncome(this.payorItems)
    },
    recipientWorkingIncome() {
      return aggregateWorkingIncome(this.recipientItems)
    },
    // INCOME_TYPES exposed to template (for picker rendering)
    incomeTypes() { return INCOME_TYPES },
    incomeCategories() { return INCOME_CATEGORIES },
    // ── Role Level Per Agreement (Divii) ─────────────────────────────────
    // Read from matterContext so LegalApp can pass the signed-in user's role
    // without any change to the component's plug:
    //   matterContext.roleLevel     1 (full) | 2 (limited)   default 1
    //   matterContext.userPartySlot 'party1' | 'party2'      (level 2 only)
    // party1/party2 follow the same convention as party1Name/party1DOB and
    // map to internal party ids A/B at load.
    // Access profile: full | contribute-view | contribute | view.
    // matterContext.role (Divii role string) wins; matterContext.roleLevel
    // (1|2) is the minimal fallback.
    accessProfile() {
      const mc = this.matterContext || {}
      // DIVII FIX — Divii's User Role page can withhold Edit on this tool.
      if (mc.readOnly) return 'view'
      if (mc.role && ROLE_PROFILES[mc.role]) return ROLE_PROFILES[mc.role]
      const v = mc.roleLevel
      return (v === 2 || v === '2') ? 'contribute' : 'full'
    },
    // Sees the case-wide views (tabs, calculation, recommendation)?
    canViewCase() { return this.accessProfile !== 'contribute' },
    // Sees them but cannot change anything?
    readOnlyCase() { return this.accessProfile === 'view' || this.accessProfile === 'contribute-view' },
    // Has a personal entry surface (own income + own budget)?
    canContribute() { return this.accessProfile === 'contribute' || this.accessProfile === 'contribute-view' },
    // ── Wizard vs Worksheet visibility ───────────────────────────────────
    // With spousal entitlement: the Calculator tab IS the wizard and the
    // breakdown workspace IS tab 5 (Worksheet). Without entitlement (no
    // tabs): the classic viewMode toggle (wizard ↔ workspace) still governs.
    showWizardView() {
      if (this.workspaceTab !== 'means') return false
      return this.ssagApplicable ? true : this.viewMode === 'wizard'
    },
    showWorksheetView() {
      if (this.ssagApplicable) return this.workspaceTab === 'worksheet'
      return this.workspaceTab === 'means' && this.viewMode === 'workspace'
    },
    // Who sees the Recommendation tab (Rebecca 2026-07): full and
    // contribute-view profiles (lawyer/client level 1 and 2) yes;
    // view-only (client-level-3/4) no; contribute (client-sharing-user)
    // never reaches the tabs at all.
    canViewRecommendation() { return this.accessProfile === 'full' || this.accessProfile === 'contribute-view' },
    // The party entry panel is on screen: always for contribute-only, and on
    // the "Your info" tab for contribute-view.
    showPartyEntry() {
      if (!this.rolePartyId) return false // no confident party link — never guess
      if (this.accessProfile === 'contribute') return true
      return this.accessProfile === 'contribute-view' && this.workspaceTab === 'myinfo'
    },
    roleLevel() { return this.accessProfile === 'full' ? 1 : 2 },
    rolePartyId() {
      // 1. A confident link from LegalApp always wins (suppresses the picker).
      const s = (this.matterContext || {}).userPartySlot
      if (s === 'party2') return 'B'
      if (s === 'party1') return 'A'
      // 2. A self-identification claim OWNED BY THIS USER (party picker).
      const key = this.currentUserKey
      if (this.partyClaims.A && this.partyClaims.A.userKey === key) return 'A'
      if (this.partyClaims.B && this.partyClaims.B.userKey === key) return 'B'
      // 3. FAIL CLOSED: never guess which party the user is. A wrong guess
      // would let one party edit the other's income and budget. With no link
      // and no owned claim, the picker (or a notice) shows instead.
      return null
    },
    // Non-full role whose party link LegalApp could not determine — editing
    // is disabled and a notice explains why.
    partyLinkMissing() { return this.canContribute && !this.rolePartyId },
    // The self-identification picker: shown to a contributor with no party
    // link and no owned claim. Contribute-only users see it as their whole
    // page; contribute-view users see it on the Your info tab.
    showPartyPicker() {
      if (!this.partyLinkMissing) return false
      if (this.accessProfile === 'contribute') return true
      return this.accessProfile === 'contribute-view' && this.workspaceTab === 'myinfo'
    },
    // Display name for the signed-in user: Divii's userName, else the name
    // they typed when claiming their party.
    roleDisplayName() {
      if (this.roleUserName) return this.roleUserName
      const claim = this.rolePartyId ? this.partyClaims[this.rolePartyId] : null
      return (claim && claim.userKey === this.currentUserKey && claim.name) || null
    },
    // Both parties already claimed by other users — nothing to pick.
    allPartiesClaimed() {
      const key = this.currentUserKey
      const taken = p => this.partyClaims[p] && this.partyClaims[p].userKey !== key
      return taken('A') && taken('B')
    },
    // Signed-in user's identity (optional, from matterContext.userName /
    // userId — read live like role). Lets the UI say who is signed in and
    // stamp self-reported entries with who typed them, which matters when an
    // agreement has more than one user in the same role (e.g. two
    // client-sharing-users, or a sharing user AND a level-2 client on the
    // same party).
    roleUserName() {
      const n = (this.matterContext || {}).userName
      return (typeof n === 'string' && n.trim()) ? n.trim() : null
    },
    roleUserId() {
      const v = (this.matterContext || {}).userId
      return v == null || v === '' ? null : String(v)
    },
    // Stable key identifying THIS user for party-claim ownership. Divii's
    // userId (account-level, works across devices) when passed; otherwise a
    // per-browser token — the lock then holds per device, and a wrong/lost
    // claim is reset by a full-access user.
    currentUserKey() {
      if (this.roleUserId) return 'u:' + this.roleUserId
      try {
        let t = localStorage.getItem('divii-support-user-key')
        if (!t) {
          t = 'd:' + Math.random().toString(36).slice(2) + Date.now().toString(36)
          localStorage.setItem('divii-support-user-key', t)
        }
        return t
      } catch (e) {
        // Storage unavailable (private mode) — session-scoped fallback.
        if (!this._sessionUserKey) this._sessionUserKey = 's:' + Math.random().toString(36).slice(2)
        return this._sessionUserKey
      }
    },
    roleBudgetCol() { return this.rolePartyId === 'A' ? 'a' : 'b' },
    rolePartyName() {
      return this.rolePartyId === 'A' ? this.payorName : this.recipientName
    },
    rolePartyItems() {
      return (this.incomeItems || []).filter(i => i.partyId === this.rolePartyId)
    },
    // THE decision table: what each role level can see and do inside the
    // Support feature. Rebecca tunes access here (one place), not scattered
    // through the templates. Currently only fullWorkspace gates rendering
    // (level 2 = party mode); the finer capabilities are declared so the
    // host and future UI can consume them without another schema change.
    roleCaps() {
      if (this.roleLevel === 1) {
        return {
          fullWorkspace: true,      // tabs, wizard, workspace, scenarios
          editOwnIncome: true, editOtherIncome: true,
          editOwnBudget: true, editOtherBudget: true,
          viewResults: true,        // ranges, NDI, recommendation
          answerCircumstances: true,
          manageScenarios: true,
        }
      }
      return {
        fullWorkspace: false,       // party mode only
        editOwnIncome: true, editOtherIncome: false,
        editOwnBudget: true, editOtherBudget: false,
        viewResults: false,         // no ranges/recommendation for parties
        answerCircumstances: false,
        manageScenarios: false,
      }
    },
    // Search results: when no search → all types. When searching → filtered list.
    filteredTypesAll() {
      const filtered = searchIncomeTypes(this.pickerSearch)
      if (filtered === null) {
        // Show everything, in declaration order
        return Object.entries(INCOME_TYPES).map(([id, m]) => ({ id, ...m }))
      }
      return filtered
    },
    totalFilteredCount() {
      return this.filteredTypesAll.length
    },
    // Each scenario column resolves its SS amount from one of two sources:
    //   1. ssOverridden = true  →  use scenario.ssMonthly verbatim (locked override)
    //   2. bind = 'low'|'mid'|'high'  →  track the live ssagRange tier
    // Bound scenarios re-compute as inputs change. Overridden scenarios stay put,
    // letting the user pin what-ifs like "$0 spousal" or "$3,500 cap".
    // DIVII PATCH: source of the "In agreement" badge. A host that passes
    // recordedProp (even as null) owns it; otherwise the local tracker is used.
    hostTracksRecord() {
      return this.recordedProp !== undefined
    },
    appliedScenarioId() {
      if (this.hostTracksRecord) return this.recordedProp ? this.recordedProp.scenarioId || null : null
      return this.lastAppliedScenarioId
    },
    appliedMode() {
      if (this.hostTracksRecord) return this.recordedProp ? this.recordedProp.mode || null : null
      return this.lastAppliedMode
    },
    // DIVII PATCH: the lump-sum figure currently selected in the record dialog.
    applyDialogLumpAmount() {
      const s = this.applyDialogScenario
      if (!s || !s.b) return 0
      return this.applyDialogLumpBasis === 'payorCost'
        ? this.lumpSumPayorCost(s)
        : this.lumpSumMidpoint(s)
    },
    displayedScenarios() {
      if (!this.ssagApplicable || !this.ssagRange) return []
      const r = this.ssagRange
      // Default duration for all bound scenarios is the slider value
      // (ssagSelectedDuration). The lawyer can override per column —
      // useful for "what if SS for 5 years vs. 10 years" what-ifs —
      // but the columns no longer split into low/mid/high duration
      // ranges by default. Falls back to the SSAG mid duration when
      // the slider hasn't been touched yet (ssagSelectedDuration === 0).
      const defaultDur = this.ssagSelectedDuration > 0
        ? this.ssagSelectedDuration
        : (r.durationLow + r.durationHigh) / 2
      return this.scenarios.map(s => {
        const ssM = s.ssOverridden
          ? s.ssMonthly
          : (s.bind && r[s.bind] != null ? r[s.bind] : s.ssMonthly)
        const dur = s.durationOverridden
          ? s.durationYears
          : (s.bind ? defaultDur : s.durationYears)
        return {
          id: s.id,
          name: s.name,
          bind: s.bind,
          ssMonthly: ssM,
          ssOverridden: s.ssOverridden,
          durationYears: dur,
          durationOverridden: s.durationOverridden,
          b: this.computeNDIBreakdown(ssM),
        }
      })
    },
    // Chunk scenarios greedily, 3 per table. Adding a 4th scenario starts a
    // new table below the first. Each scenario column is sized to 1/3 of the
    // available width via container-query CSS, so single-column tables still
    // render their column at the same width with a spacer absorbing the slack.
    displayedScenarioChunks() {
      const MAX_PER_TABLE = 3
      const all = this.displayedScenarios
      const chunks = []
      for (let i = 0; i < all.length; i += MAX_PER_TABLE) {
        chunks.push(all.slice(i, i + MAX_PER_TABLE))
      }
      return chunks
    },

    // ── Step validation ───────────────────────────────────────────
    canProceed1() {
      return this.separationDate && this.effectiveProvince
    },

    // ── Child helpers ─────────────────────────────────────────────
    includedChildren() {
      return this.children.filter(c => this.childIsIncluded(c))
    },
    // Per-party count of children claimed for provincial per-child amounts
    // (e.g. SK "amount for dependent children", line 58210). Each under-18 child
    // is claimed by exactly one parent. Resolution: explicit child.childAmountBy
    // ('A'/'B'/'none') wins; else primaryA/primaryB parenting; else shared
    // children default to the recipient (the custodial claimant DM assigns them
    // to by convention). Fixtures override per child where DM splits siblings.
    childAmountClaims() {
      const recipientParty = this.ssagPayor === 'B' ? 'A' : 'B'
      let A = 0, B = 0
      for (const c of this.includedChildren) {
        if (!this.edcAgeEligible(c)) continue
        let who = c.childAmountBy
        if (who !== 'A' && who !== 'B' && who !== 'none') {
          who = c.parenting === 'primaryA' ? 'A'
              : c.parenting === 'primaryB' ? 'B'
              : recipientParty
        }
        if (who === 'A') A++
        else if (who === 'B') B++
      }
      // The child claimed as the eligible dependant is claimed under the
      // eligible-dependant amount, NOT the dependent-children amount (no
      // double-dip). Subtract one assigned child from whichever party claims
      // the eligible dependant. Verified vs DM SK (angus/johnston).
      if (this.payorHasEligibleDependant) A = Math.max(0, A - 1)
      if (this.recipientHasEligibleDependant) B = Math.max(0, B - 1)
      return { A, B }
    },
    // Dependants under 19 per party, for the Ontario tax reduction (T4127 S
    // factor: $554 per dependent child under 19). Same child→party assignment
    // as childAmountClaims but WITHOUT the EDC subtraction (the ON reduction
    // counts the EDC child too — DM johnston Jessica: 1 dependant → $137
    // reduction reconciles exactly). Whole-year ages, consistent with fixtures.
    dependantsUnder19() {
      const recipientParty = this.ssagPayor === 'B' ? 'A' : 'B'
      const taxYear = (this.taxYearCfg && this.taxYearCfg.year) || 2026
      let A = 0, B = 0
      for (const c of this.includedChildren) {
        if (!c.dob) continue
        const birthYear = parseInt(String(c.dob).slice(0, 4), 10)
        if (!birthYear || taxYear - birthYear >= 19) continue
        let who = c.childAmountBy
        if (who !== 'A' && who !== 'B' && who !== 'none') {
          who = c.parenting === 'primaryA' ? 'A'
              : c.parenting === 'primaryB' ? 'B'
              : recipientParty
        }
        if (who === 'A') A++
        else if (who === 'B') B++
      }
      return { A, B }
    },
    // CSG-eligible children: those subject to the FCSG table.
    // Default behavior:
    //   • Under-AOM child → table applies (subjectToTable defaults true)
    //   • AOM child → table doesn't apply (Adult Children, s.7 budget contribution)
    // Per-child override via `subjectToTable` boolean — useful for:
    //   • AOM dependent child still living with parent (Campo Agreement) → set true
    //   • Under-AOM child in post-secondary, treated as independent (Lewis Shaw
    //     Kate, age 18 in BC) → set false
    csgChildren() {
      return this.includedChildren.filter(c => {
        if (c.subjectToTable === false) return false
        if (c.subjectToTable === true) return true
        return !c.dob || !this.isAtAOM(c.dob)
      })
    },
    soleAChildren() {
      return this.csgChildren.filter(c => c.parenting === 'primaryA')
    },
    soleBChildren() {
      return this.csgChildren.filter(c => c.parenting === 'primaryB')
    },
    sharedChildren() {
      return this.csgChildren.filter(c => c.parenting === 'shared')
    },

    // ── Child support table amounts ───────────────────────────────
    // Split custody detection: each parent has primary care of ≥ 1 child.
    // Per CSG s.8, each parent's "obligation" is the table amount at THEIR
    // income for the TOTAL number of sole-custody children (not just the
    // subset in the other parent's care). The set-off is the difference.
    // (Fieber DM PDF confirms: 2 split kids → both lookups use 2-child rate.)
    //
    // Pure sole custody (all kids with one parent): only that direction's
    // count is non-zero, so the total equals the active count and the
    // formula reduces to the standard table lookup.
    isSplitCustody() {
      return this.soleAChildren.length > 0 && this.soleBChildren.length > 0
    },
    splitTotalChildren() {
      // Total children subject to split-custody set-off — used as the
      // table-lookup child count for BOTH parties in split custody.
      return this.soleAChildren.length + this.soleBChildren.length
    },
    // Payor → Recipient: children living primarily with Recipient
    csPayorToRecipient() {
      if (this.soleBChildren.length === 0) return 0
      const n = this.isSplitCustody ? this.splitTotalChildren : this.soleBChildren.length
      return this.lookup(this.effectiveProvince, n, this.payorIncome) || 0
    },
    // Recipient → Payor: children living primarily with Payor
    csRecipientToPayor() {
      if (this.soleAChildren.length === 0) return 0
      const n = this.isSplitCustody ? this.splitTotalChildren : this.soleAChildren.length
      return this.lookup(this.effectiveProvince, n, this.recipientIncome) || 0
    },

    // Shared: s.9 set-off
    // Each party's table as if ALL shared children were with the other
    sharedTableA() {
      if (this.sharedChildren.length === 0) return 0
      return this.lookup(this.effectiveProvince, this.sharedChildren.length, this.payorIncome) || 0
    },
    sharedTableB() {
      if (this.sharedChildren.length === 0) return 0
      return this.lookup(this.effectiveProvince, this.sharedChildren.length, this.recipientIncome) || 0
    },
    sharedNet() {
      return Math.abs(this.sharedTableA - this.sharedTableB)
    },
    sharedNetPayer() {
      return this.sharedTableA >= this.sharedTableB ? this.payorName : this.recipientName
    },

    // Net CS combining sole + shared.
    //
    // SPLIT custody (each parent has ≥1 sole child): unchanged — each parent's
    // obligation is the table at their income for the TOTAL sole-child count,
    // then set off (CSG s.8 "total count" convention, calibrated to DM Fieber).
    //
    // HYBRID / non-split (a child in one parent's sole care AND/OR shared kids):
    // each parent's obligation = their table for (children in the OTHER parent's
    // sole care) + (shared children), then set off. This is the single hybrid
    // set-off DM uses — e.g. 1 child with recipient + 1 shared → payor pays the
    // 2-child table, recipient pays the 1-child (shared-only) table. The old
    // code summed "sole 1-child table + shared set-off", which over-counted the
    // mixed case (DM $1,140 vs old $1,648). Pure-sole and pure-shared are
    // unchanged (one term is zero), so only the mixed case moves.
    netCSPayorOwes() {
      if (this.isSplitCustody) {
        let owes = this.csPayorToRecipient
        if (this.sharedTableA >= this.sharedTableB) owes += this.sharedNet
        return owes
      }
      const n = this.soleBChildren.length + this.sharedChildren.length
      return n > 0 ? (this.lookup(this.effectiveProvince, n, this.payorIncome) || 0) : 0
    },
    netCSRecipientOwes() {
      if (this.isSplitCustody) {
        let owes = this.csRecipientToPayor
        if (this.sharedTableB > this.sharedTableA) owes += this.sharedNet
        return owes
      }
      const n = this.soleAChildren.length + this.sharedChildren.length
      return n > 0 ? (this.lookup(this.effectiveProvince, n, this.recipientIncome) || 0) : 0
    },
    netCS() {
      return Math.abs(this.netCSPayorOwes - this.netCSRecipientOwes)
    },
    netCSPayer() {
      return this.netCSPayorOwes >= this.netCSRecipientOwes ? this.payorName : this.recipientName
    },

    // ── Section 7 ─────────────────────────────────────────────────
    hasS7() {
      return this.includedChildren.some(c => c.s7expenses && c.s7expenses.length > 0)
    },
    totalIncome() {
      return this.payorIncome + this.recipientIncome
    },
    payorPct() {
      if (!this.totalIncome) return 50
      return Math.round(this.payorIncome / this.totalIncome * 100)
    },
    recipientPct() {
      return 100 - this.payorPct
    },
    s7Results() {
      const results = []
      // Per-party marginal rates — childcare deduction reduces the
      // CLAIMING party's tax bill at their own marginal rate.
      const partyAMargRate = getMarginalRate(this.effectiveProvince, this.payorIncome, this.taxYearCfg)
      const partyBMargRate = getMarginalRate(this.effectiveProvince, this.recipientIncome, this.taxYearCfg)
      // Legacy fallback: when neither party has an explicit "claimed"
      // amount, attribute the full gross to the lower-income parent at
      // their marginal rate (matches the pre-2026.2 behaviour so old
      // fixtures and existing data continue to work).
      const lowerIncomeRate = Math.min(partyAMargRate, partyBMargRate)

      // Provincial activity credits (NL PATC / SK AFB) — running caps: NL is
      // a family-wide $2,000 expense cap, SK a per-child credit cap. The SK
      // income gate uses each party's post-separation FAMILY income (party +
      // new partner).
      const prov = this.effectiveProvince
      const famIncA = this.payorIncome + (this.payorPartnerIncome || 0)
      const famIncB = this.recipientIncome + (this.recipientPartnerIncome || 0)
      const skGateA = famIncA <= SK_AFB_INCOME_CAP
      const skGateB = famIncB <= SK_AFB_INCOME_CAP
      let nlCapLeft = NL_PATC_FAMILY_CAP

      for (const child of this.includedChildren) {
        let skCapLeft = child.disabled ? SK_AFB_DISABLED_CAP : SK_AFB_CHILD_CAP
        for (const exp of (child.s7expenses || [])) {
          const paidByA  = exp.paidByA  || 0
          const paidByB  = exp.paidByB  || 0
          // Gross = sum of what each party actually paid out-of-pocket.
          // Falls back to legacy annualAmount when paidByA/B are both 0.
          const gross   = (paidByA + paidByB) > 0
            ? paidByA + paidByB
            : (exp.annualAmount || 0)
          const subsidy = exp.subsidy || 0

          // Claimed amounts per party (T1 line 21400 for childcare,
          // medical-expense credit for medical). Zero for both falls
          // back to "lower-income parent claims the full gross" so
          // pre-existing data without claimedBy* still has tax benefit.
          let claimedA = exp.claimedByA || 0
          let claimedB = exp.claimedByB || 0
          let usedLegacyFallback = false
          if (claimedA === 0 && claimedB === 0 && gross > 0) {
            usedLegacyFallback = true
          }

          const treatment = S7_TAX_TREATMENT[exp.category] || 'none'
          let taxBenefitA = 0
          let taxBenefitB = 0
          if (treatment === 'deduction') {
            // Childcare: each party's claimed × their marginal rate
            taxBenefitA = claimedA * partyAMargRate
            taxBenefitB = claimedB * partyBMargRate
            if (usedLegacyFallback) {
              // Attribute the full deduction to whichever party has the
              // lower marginal rate (CRA rule: lower-income parent claims).
              if (partyAMargRate <= partyBMargRate) taxBenefitA = gross * lowerIncomeRate
              else                                  taxBenefitB = gross * lowerIncomeRate
            }
          } else if (treatment === 'credit') {
            // Medical credit net of the CRA floor: only expenses over the
            // lesser of 3% of the party's net income or the year cap earn
            // the ~20% credit (DM applies the same floor).
            const cap = (this.taxYearCfg && this.taxYearCfg.medicalFloor) || 2891
            const credOf = (claimed, inc) => Math.max(0,
              claimed - Math.min(0.03 * Math.max(0, inc), cap)) * MEDICAL_CREDIT_RATE
            taxBenefitA = credOf(claimedA, this.payorIncome)
            taxBenefitB = credOf(claimedB, this.recipientIncome)
            if (usedLegacyFallback) {
              if (partyAMargRate <= partyBMargRate) taxBenefitA = credOf(gross, this.payorIncome)
              else                                  taxBenefitB = credOf(gross, this.recipientIncome)
            }
          } else if (treatment === 'tuition') {
            // Post-secondary: tuition transferred to the parent (line 32400)
            // credits at the lowest fed+prov rates. No legacy fallback — a
            // transfer only exists when explicitly claimed.
            const lr = ((this.taxYearCfg && this.taxYearCfg.fedLowestRate) || 0.14)
              + ((((this.taxYearCfg || {}).provTaxData || {})[this.effectiveProvince] || {}).rate || 0.05)
            taxBenefitA = claimedA * lr
            taxBenefitB = claimedB * lr
          } else if (treatment === 'activity') {
            // NL Physical Activity Tax Credit — fitness expenses only, 8.7%
            // of up to $2,000 per FAMILY (running cap across all children).
            if (prov === 'NL' && exp.category === 'extracurricular_fitness') {
              const useA = Math.min(claimedA, nlCapLeft); nlCapLeft -= useA
              const useB = Math.min(claimedB, nlCapLeft); nlCapLeft -= useB
              taxBenefitA = useA * NL_PATC_RATE
              taxBenefitB = useB * NL_PATC_RATE
            }
            // SK Active Families Benefit — sport AND culture, 50% of fees to
            // $150/child ($200 disabled), family income <= $120,000. The cap
            // runs per child across both categories and parties.
            if (prov === 'SK') {
              const credA = Math.min(SK_AFB_RATE * claimedA, skCapLeft)
              skCapLeft -= credA
              const credB = Math.min(SK_AFB_RATE * claimedB, skCapLeft)
              skCapLeft -= credB
              taxBenefitA = skGateA ? credA : 0
              taxBenefitB = skGateB ? credB : 0
            }
          }

          const totalTaxBenefit = taxBenefitA + taxBenefitB
          const netCost = Math.max(0, gross - totalTaxBenefit - subsidy)
          // Legacy field kept for the s7-detail summary table — payor's
          // CONCEPTUAL share of net cost (income-only apportionment).
          const payorShare = Math.round(netCost * this.payorPct / 100)
          const payorNet = Math.round(payorShare - paidByA)

          results.push({
            child:        child.name || 'Child',
            rawCategory:  exp.category,
            category:     S7_CATEGORIES.find(c => c.value === exp.category)?.label || exp.category,
            grossAnnual:  gross,
            paidByA,
            paidByB,
            claimedByA:   exp.claimedByA || 0,
            claimedByB:   exp.claimedByB || 0,
            subsidy,
            taxBenefitA:  Math.round(taxBenefitA),
            taxBenefitB:  Math.round(taxBenefitB),
            netCost:      Math.round(netCost),
            payorShare,
            payorNet,
          })
        }
      }
      return results
    },
    s7PayorNetAnnual() {
      return this.s7Results.reduce((sum, r) => sum + r.payorNet, 0)
    },
    // Child-care expenses CLAIMED as a deduction per party (T1 line 21400) —
    // the base for the Ontario CARE refundable credit. Attribution priority:
    //   (1) explicit claimedByA/B if set;
    //   (2) else the party who PAID it (paidByA/B) — DM ties CARE to the payer,
    //       verified vs sylvie-moore-ON (Dylan paid → Dylan's $860) and
    //       nicolson-stewart-ON (Austin paid $5,110 → CARE at HIS income, ~$1,175);
    //   (3) else (only a lump annualAmount) fall back to the lower-income parent.
    onCareChildcareClaimed() {
      let a = 0, b = 0
      const partyAMargRate = getMarginalRate(this.effectiveProvince, this.payorIncome, this.taxYearCfg)
      const partyBMargRate = getMarginalRate(this.effectiveProvince, this.recipientIncome, this.taxYearCfg)
      for (const child of this.includedChildren) {
        for (const exp of (child.s7expenses || [])) {
          if (exp.category !== 'childcare') continue
          const claimedA = exp.claimedByA || 0
          const claimedB = exp.claimedByB || 0
          const paidA = exp.paidByA || 0
          const paidB = exp.paidByB || 0
          if (claimedA !== 0 || claimedB !== 0) {
            a += claimedA; b += claimedB
          } else if (paidA !== 0 || paidB !== 0) {
            a += paidA; b += paidB
          } else {
            const gross = exp.annualAmount || 0
            if (partyAMargRate <= partyBMargRate) a += gross
            else b += gross
          }
        }
      }
      return { a, b }
    },
    // Annual tax benefit per party from claiming s.7 deductions.
    // Deducted from each party's effective tax line in computeNDIBreakdown.
    // NL/SK activity credits per party — scenario-invariant, summed from the
    // s7Results rows so the NDI layer can fold them into the s.7 offsets.
    s7ActivityCreditsAnnual() {
      let a = 0, b = 0
      for (const r of this.s7Results) {
        if (r.rawCategory === 'extracurricular' || r.rawCategory === 'extracurricular_fitness') {
          a += r.taxBenefitA || 0
          b += r.taxBenefitB || 0
        }
      }
      return { a, b }
    },
    s7TaxBenefitAAnnual() {
      return this.s7Results.reduce((sum, r) => sum + (r.taxBenefitA || 0), 0)
    },
    s7TaxBenefitBAnnual() {
      return this.s7Results.reduce((sum, r) => sum + (r.taxBenefitB || 0), 0)
    },
    s7PayorNetMonthly() {
      return Math.round(this.s7PayorNetAnnual / 12)
    },
    // Sum of net costs (gross − tax benefit − subsidy) across all s.7
    // expenses, regardless of who paid out-of-pocket. Used as the base
    // for the SS-adjusted apportionment in computeNDIBreakdown — both
    // parties' NDI is reduced by their proportional share of this total.
    s7TotalNetAnnual() {
      return this.s7Results.reduce((sum, r) => sum + r.netCost, 0)
    },
    // Annual gross out-of-pocket paid by each party (across all s.7
    // expenses). Used by the DM-style two-row breakdown: "Special
    // Expenses (s.7)" shows -paidBy (cash outflow), "Child Support
    // (s.7 Payment)" shows the settlement between parties.
    s7PaidByAAnnual() {
      const total = (this.includedChildren || []).reduce((sum, child) => {
        return sum + (child.s7expenses || []).reduce((s, exp) => s + (exp.paidByA || 0), 0)
      }, 0)
      return total
    },
    s7PaidByBAnnual() {
      const total = (this.includedChildren || []).reduce((sum, child) => {
        return sum + (child.s7expenses || []).reduce((s, exp) => s + (exp.paidByB || 0), 0)
      }, 0)
      return total
    },
    // Sum of payorShare (conceptual annual obligation) across all s.7
    // expenses — what the payor "owes" each year for their portion of
    // the net cost, before any cash settlement for amounts already paid.
    // This is the number that flows into NDI; the recipient's share is
    // simply the complement (s7TotalNetAnnual − payor's share).
    s7PayorConceptualAnnual() {
      return this.s7Results.reduce((sum, r) => sum + r.payorShare, 0)
    },
    s7PayorConceptualMonthly() {
      return Math.round(this.s7PayorConceptualAnnual / 12)
    },
    s7RecipientConceptualAnnual() {
      return this.s7TotalNetAnnual - this.s7PayorConceptualAnnual
    },
    s7RecipientConceptualMonthly() {
      return Math.round(this.s7RecipientConceptualAnnual / 12)
    },
    // Sum of tax benefits applied across all s.7 expenses (childcare
    // deduction at the lower-earner's marginal rate, medical credit at
    // the flat rate). Used to reconstruct gross total from net total
    // on the footer row.
    s7TaxBenefitsAnnual() {
      return this.s7Results.reduce((sum, r) => sum + (r.grossAnnual - r.netCost), 0)
    },

    // ── SSAG ──────────────────────────────────────────────────────
    // Children eligible for SSAG-recipient's CCB/benefits, with custody weights:
    //   primaryB (with CSG-recipient): weight 1.0
    //   shared:                         weight 0.5
    //   primaryA (with CSG-payor):      weight 0 (excluded)
    // Assumes SSAG-recipient = CSG-recipient (typical case).
    ssagRecipientChildren() {
      const isRecipientSSAG = this.ssagPayor === 'A' // SSAG payor=A means recipient gets SS
      return this.includedChildren.map(c => {
        let w = 0
        if (c.parenting === 'shared') w = 0.5
        else if (c.parenting === (isRecipientSSAG ? 'primaryB' : 'primaryA')) w = 1
        // TAX-YEAR age — used only for refundable-benefit eligibility (CCB
        // under-18 cutoff + under-6/6-17 bracket). DM keys benefits to the
        // child's age in the benefit/tax year, NOT age-at-separation:
        // Roberton-Kirby separated in 2022 but Owen is 18 by tax year 2025,
        // so DM gives him $0 CCB even though he was 15 at separation. Using
        // sep-date age paid ~$944 phantom CCB. Anchored to the tax year (not
        // the live clock) so calibration stays deterministic.
        const age = this.ageAtDate(c.dob, `${this.taxYear}-12-31`)
        return { age, weight: w, disabled: !!c.disabled }
      }).filter(c => c.weight > 0)
    },
    // Mirror of ssagRecipientChildren for the SSAG-payor's own benefits claim.
    // In shared custody, the payor also gets half the CCB/BCFB/GST credit and that
    // counts toward their INDI under the SSAG with-child formula (Divorcemate matches).
    ssagPayorChildren() {
      const isRecipientSSAG = this.ssagPayor === 'A'
      return this.includedChildren.map(c => {
        let w = 0
        if (c.parenting === 'shared') w = 0.5
        else if (c.parenting === (isRecipientSSAG ? 'primaryA' : 'primaryB')) w = 1
        // TAX-YEAR age — benefit eligibility only (see ssagRecipientChildren).
        const age = this.ageAtDate(c.dob, `${this.taxYear}-12-31`)
        return { age, weight: w, disabled: !!c.disabled }
      }).filter(c => c.weight > 0)
    },

    // Children for whom support is being calculated (SSAG-payor pays support for these).
    //   - 'shared' kids: both parents share, payor "supports" through shared payments
    //   - kids with the OTHER party (recipient): payor supports them
    //   - kids with the payor: not in this list (payor is caring for them already)
    ssagPaidForKidCount() {
      // Split custody: per CSG s.8 each parent's "table obligation" is
      // computed at their income for the TOTAL number of sole-custody
      // children (both parents' kids together), then offset. Mirror that
      // here so the SSAG notional lookups use the same count as the CSG
      // table lookups. DM Fieber PDF: Cameron's notional at 2 children
      // (his $665k for 2 kids = $8,807); Jamie's notional at 2 children
      // (her $200k for 2 kids = $2,855).
      if (this.splitCustodyScenario) {
        return this.soleAChildren.length + this.soleBChildren.length
      }
      // IMPORTANT: filter against csgChildren, not includedChildren, so that
      // post-secondary / AOM children with subjectToTable=false are excluded
      // from the notional table lookup. DM Lewis-Shaw PDF: Kate (18, post-
      // secondary independent) is excluded → 1 child for table, not 2.
      const otherSidePrimary = this.ssagPayor === 'A' ? 'primaryB' : 'primaryA'
      return this.csgChildren.filter(c =>
        c.parenting === 'shared' || c.parenting === otherSidePrimary
      ).length
    },
    // Full table CS at SSAG-payor's income for the supported kids.
    // SSAG with-child formula: payor INDI subtracts this (full table — for shared
    // custody this is more than the actual cash set-off, matching Divorcemate's
    // "notional sum of tables" approach).
    payorNotionalTableCS() {
      const n = this.ssagPaidForKidCount
      if (n === 0) return 0
      const payorGross = this.ssagPayor === 'A' ? this.payorIncome : this.recipientIncome
      return this.lookup(this.effectiveProvince, n, payorGross) || 0
    },
    // Full table CS at SSAG-recipient's income for same supported kids.
    recipientNotionalTableCS() {
      const n = this.ssagPaidForKidCount
      if (n === 0) return 0
      const recipGross = this.ssagPayor === 'A' ? this.recipientIncome : this.payorIncome
      return this.lookup(this.effectiveProvince, n, recipGross) || 0
    },

    // Where the user's selected duration sits inside the SSAG-recommended range.
    // Returns { pct, label } where pct = 0..100 and label is a human-readable
    // bucket ("Low end", "Lower-mid", "Mid-range", "Upper-mid", "High end").
    // Name of the currently-active case scenario (for hint text). Null when
    // no scenario is active.
    activeCaseScenarioName() {
      if (!this.activeCaseScenarioId) return null
      const s = this.caseScenarios.find(x => x.id === this.activeCaseScenarioId)
      return s ? s.name : null
    },
    // True when the live inputs have diverged from the active scenario's
    // saved snapshot. Scenarios are frozen — edits do NOT auto-save — so this
    // drives the "Save changes" button and the discard-warning on switch.
    // Compares a fresh capture against the stored snapshot; reactive because
    // captureScenarioState() reads every input field.
    activeScenarioDirty() {
      if (!this.activeCaseScenarioId) return false
      const s = this.caseScenarios.find(x => x.id === this.activeCaseScenarioId)
      if (!s || !s.state) return false
      return JSON.stringify(this.captureScenarioState()) !== JSON.stringify(s.state)
    },
    // SS amount that produces a 50/50 NDI split. Reactive so the "Equalize
    // NDI" button label updates as inputs change. Returns null when
    // unreachable (recipient already ≥50% at SS=0, or no valid bisection).
    equalizeNDIAmount() {
      if (!this.ssagApplicable || !this.ssagRange) return null
      return this.findSs5050()
    },
    // ── Restructuring (SSAG Ch. 10) ───────────────────────────────────
    // Back-solve a lump-sum + monthly split that preserves the after-tax
    // global value of the currently-selected periodic award. Only evaluated
    // when the worksheet disclosure is open (gated by v-if in the template),
    // so it adds no cost to the normal flow.
    // Which scenario column the restructuring is based on. Defaults to the
    // SSAG Mid column (or the first scenario), falling back to the slider
    // amount if no scenarios exist. The lawyer picks via the panel dropdown.
    restructureBasis() {
      const scs = this.displayedScenarios || []
      let sc = null
      if (this.restructureScenarioId != null) sc = scs.find(s => s.id === this.restructureScenarioId)
      if (!sc) sc = scs.find(s => s.bind === 'mid') || scs[0] || null
      if (sc) {
        return { amount: sc.ssMonthly, duration: sc.durationYears, label: sc.name, id: sc.id }
      }
      const dur = this.ssagSelectedDuration > 0 ? this.ssagSelectedDuration
        : (this.ssagRange && this.ssagRange.durationHigh > 0 ? this.ssagRange.durationHigh : 10)
      return { amount: this.ssagSelected, duration: dur, label: 'Selected amount', id: null }
    },
    restructurePackage() {
      if (!this.ssagApplicable || !this.ssagRange) return null
      const basis = this.restructureBasis
      if (!basis.amount) return null
      // Base duration = the chosen scenario's duration (fallback to range high
      // or 10y for indefinite cases, which the lawyer can override).
      const baseDur = basis.duration > 0
        ? basis.duration
        : (this.ssagRange.durationHigh > 0 ? this.ssagRange.durationHigh : 10)
      // Stream duration = lawyer's restructure duration, else base.
      const streamDur = (this.restructureDurationYears != null && this.restructureDurationYears > 0)
        ? this.restructureDurationYears : baseDur
      // Target = after-tax lump-sum equivalent of the chosen scenario's award.
      const target = this._restructureLumpValueOf(basis.amount, baseDur)
      const lump = Math.max(0, this.restructureLumpSum || 0)
      const remaining = Math.max(0, target - lump)
      // Back-solve the monthly whose after-tax lump value (over streamDur)
      // equals the remaining value after the lump. Monotonic in monthly.
      let monthly = 0
      if (remaining > 0) {
        let lo = 0, hi = Math.max(this.ssagRange.high * 4, 1000)
        for (let i = 0; i < 44; i++) {
          const mid = (lo + hi) / 2
          const v = this._restructureLumpValueOf(mid, streamDur)
          if (v < remaining) lo = mid; else hi = mid
          if (hi - lo < 0.5) break
        }
        monthly = Math.round((lo + hi) / 2)
      }
      const withinRange = monthly >= this.ssagRange.low - 1 && monthly <= this.ssagRange.high + 1
      return {
        target: Math.round(target),
        lump: Math.round(lump),
        monthly,
        streamDur,
        baseDur,
        withinRange,
        lumpExceedsAward: lump > target + 1,
        basisLabel: basis.label,
        basisAmount: Math.round(basis.amount),
      }
    },
    ssagDurationPosition() {
      const r = this.ssagRange
      if (!r || r.durationHigh <= r.durationLow) return null
      const sel = this.ssagSelectedDuration
      const raw = ((sel - r.durationLow) / (r.durationHigh - r.durationLow)) * 100
      const pct = Math.max(0, Math.min(100, raw))
      let label
      if (pct <= 15) label = 'Low end'
      else if (pct < 40) label = 'Lower-mid'
      else if (pct <= 60) label = 'Mid-range'
      else if (pct < 85) label = 'Upper-mid'
      else label = 'High end'
      return { pct, label, sel, low: r.durationLow, high: r.durationHigh }
    },
    ssagRange() {
      if (!this.ssagApplicable || !this.yearsMarried) return null
      const hasChildren = this.includedChildren.length > 0
      const prov = this.effectiveProvince

      const payorGross = this.ssagPayor === 'A' ? this.payorIncome : this.recipientIncome
      const recipGross = this.ssagPayor === 'A' ? this.recipientIncome : this.payorIncome
      const payorWorking = this.ssagPayor === 'A' ? this.payorWorkingIncome : this.recipientWorkingIncome
      const recipWorking = this.ssagPayor === 'A' ? this.recipientWorkingIncome : this.payorWorkingIncome
      // Tax bundles — needed by both with-child INDI math AND the without-child
      // / custodial-payor / adult-children CS-grossup probe.
      const payorBundle = this.ssagPayor === 'A' ? this.payorTaxBundle : this.recipientTaxBundle
      const recipBundle = this.ssagPayor === 'A' ? this.recipientTaxBundle : this.payorTaxBundle
      // Net cash flow comes directly from per-party tax-aware aggregators.
      // ADD CFA back: payorNetCashFlow / recipientNetCashFlow subtract Cash
      // Flow Adjustments (SSAG Ch. 6.6), but DM's SSAG INDI math does NOT
      // include CFA — CFA is an NDI-only deduction. DM Mora-Primary PDF
      // confirms: Justin's INDI = Guidelines − tax+source − notional + SS,
      // with no CFA line. For cases without CFA this is a no-op; for Little
      // (Aaron \$12k + Robin \$4.6k CFA) it's the difference between
      // ssagHigh=0 (wrongly) and \$323 (DM expected).
      const payorCFAAnnual = this.ssagPayor === 'A' ? this.payorCFAAnnual : this.recipientCFAAnnual
      const recipCFAAnnual = this.ssagPayor === 'A' ? this.recipientCFAAnnual : this.payorCFAAnnual
      // Party items in SSAG roles — for the social-assistance and prior-
      // support adjustments below.
      const ssagPayorItems = this.ssagPayor === 'A' ? this.payorItems : this.recipientItems
      const ssagRecipItems = this.ssagPayor === 'A' ? this.recipientItems : this.payorItems
      const sumType = (items, id) => (items || []).reduce(
        (s, it) => s + (it.typeId === id ? (it.amount || 0) : 0), 0)
      // Social assistance with a spousal clawback is EXCLUDED from SSAG income
      // (DM row "Social Assistance for party (excluded)"): every spousal dollar
      // awarded displaces an assistance dollar, so the formulas must treat the
      // recipient as if the assistance were not there. It STAYS in CSG income
      // (table amounts) and in s.7 apportioning income, and in displayed NDI the
      // clawback is min(SS received, SA) — see computeNDIBreakdown. Verified vs
      // DM Tools Cloud 2026 SocialAssistCPPDis (NT: CPP-D $9,600 + SA $12,000).
      const recipSAClawback = sumType(ssagRecipItems, 'social_assistance_clawback_spousal')
      const payorNetCash = (this.ssagPayor === 'A' ? this.payorNetCashFlow : this.recipientNetCashFlow) + payorCFAAnnual
      const recipNetCash = (this.ssagPayor === 'A' ? this.recipientNetCashFlow : this.payorNetCashFlow) + recipCFAAnnual
        - recipSAClawback

      // Three scenarios trigger the WITHOUT-CHILD formula structure:
      //   1. No children
      //   2. Custodial-payor (SSAG Ch. 8) — the SS payor has primary care of
      //      the kids, so the with-child formula's INDI math is wrong (it
      //      assumes the payor pays CS; here the payor receives CS).
      //   3. All-adult children (SSAG Ch. 9) — every included child is at or
      //      over the age of majority. There's no FCSG table amount to anchor
      //      the with-child INDI deduction, so the without-child formula is
      //      the recommended methodology. Practitioners may still negotiate
      //      based on the adult child's budget, but the headline range comes
      //      from the without-child gross-income-gap formula.
      // Duration in the custodial-payor case still respects the age-of-children
      // test, since kids are still in the household needing support.
      // The "all-adult" trigger uses csgChildren (table-subject children) so
      // that AOM-but-dependent kids living with a parent (Campo Agreement)
      // still route to the with-child formula when subjectToTable=true is set.
      const useWithoutChild = !hasChildren
        || this.custodialPayorScenario
        || this.csgChildren.length === 0

      // Indefinite duration flag — strict rule for BOTH formulas:
      //   true only when marriage ≥ 20 years OR Rule of 65 triggers.
      //
      // Earlier behaviour auto-flagged with-child as always indefinite (per
      // DM's "indefinite (unspecified)" display). Reverted because labelling
      // a 17-year with-child case as "Indefinite (≥ 20 year marriage)" is
      // inaccurate on its face — the marriage doesn't satisfy that
      // condition. The min/max range is still computed and displayed below
      // as the duration anchor; that's enough information without a
      // misleading "indefinite" headline.
      const indefinite = this.yearsMarried >= 20 || this.rule65Triggered
      // Duration is the LONGER of (a) length-of-marriage test [0.5x – 1x years]
      // and (b) age-of-children test [until last kid finishes HS, ~18].
      // Age-of-children only applies under the with-child formula.
      const lengthLow  = this.yearsMarried * 0.5
      const lengthHigh = this.yearsMarried * 1.0
      const ageOfKids  = hasChildren ? this.ageOfChildrenDuration : 0
      // SSAG with-child duration is the LONGER of two tests on BOTH ends
      // (§8.5.2), confirmed against DM:
      //   • HIGH end = longer of (1 × years) and time until the LAST child
      //     finishes high school (age-of-children test, ~age of majority).
      //   • LOW  end = longer of (0.5 × years) and time until the YOUNGEST
      //     child starts full-time school (school-start test, age 6).
      // For a short marriage with young kids the age tests govern both ends —
      // e.g. DM's Peter & Sarah (3.5-yr marriage, youngest ~1) → 5 to 18 yrs,
      // not the 1.75-to-3.5 the length test alone would give.
      const schoolStart  = hasChildren ? this.schoolStartDuration : 0
      const durationLow  = Math.max(lengthLow, schoolStart)
      const durationHigh = Math.max(lengthHigh, ageOfKids)

      if (hasChildren && !useWithoutChild) {
        // SSAG WITH-CHILD FORMULA (matches Divorcemate's INDI methodology):
        //   payor INDI = net cash + payor's share of refundable benefits − actual CS paid
        //   recip INDI = net cash + recip's share of refundable benefits − notional table CS
        //   (CS received is NOT added to recip INDI; refundable benefits ARE.)
        //   In SHARED custody both parents get half the CCB/BCFB/GST per CRA rules.
        // Range: SS such that recip share of combined INDI = 40% (low) and 46% (high).
        const recipChildren = this.ssagRecipientChildren
        const payorChildren = this.ssagPayorChildren
        // AFNI for CRA refundable benefits is Line 23600 (Net Income), which is
        // close to our aggregateTaxableIncome (handles CPP enhancement deduction)
        // PLUS the workers'-comp / social-assistance addback (deducted only at
        // line 25000, so part of NET income and of every benefit phase-out).
        const payorAFNI = this.ssagPayor === 'A' ? this.payorTaxBundle.taxable : this.recipientTaxBundle.taxable
        const recipAFNI = this.ssagPayor === 'A' ? this.recipientTaxBundle.taxable : this.payorTaxBundle.taxable
        const payorNetAdd = netIncomeAddback(ssagPayorItems)
        const recipNetAdd = netIncomeAddback(ssagRecipItems)
        const recipBenefits = computeRefundableBenefits(prov, {
          afni: recipAFNI + recipNetAdd,
          workingIncome: recipWorking,
          children: recipChildren,
          isSingle: this.recipientPartnerIncome == null,
          isPrimaryParent: recipChildren.length > 0,
          hasEDC: this.recipientHasEligibleDependant,
          cfg: this.taxYearCfg,
        })
        const payorBenefits = computeRefundableBenefits(prov, {
          afni: payorAFNI + payorNetAdd,
          workingIncome: payorWorking,
          children: payorChildren,
          isSingle: this.payorPartnerIncome == null,
          isPrimaryParent: payorChildren.length > 0,
          hasEDC: this.payorHasEligibleDependant,
          cfg: this.taxYearCfg,
        })
        // SSAG INDI deduction = each parent's FULL TABLE at their own income for the
        // supported kids (notional-sum-of-tables approach, matches Divorcemate).
        // For sole custody this equals actual cash CS; for shared custody it's
        // more than the set-off (the difference is each parent's notional contribution).
        const csAnnual = this.payorNotionalTableCS * 12
        const notionalAnnual = this.recipientNotionalTableCS * 12

        // NOTE: the Ontario CARE refundable childcare credit is applied at the
        // displayed-NDI layer (computeNDIBreakdown), NOT here in the INDI solver.
        // Adding it to payor/recip INDI over-drives the SSAG range upward (our
        // bisection responds more strongly to payor-benefit changes than DM's),
        // measured net-negative on the suite. DM's published SSAG range for these
        // low-income childcare cases doesn't move by the full CARE amount, so we
        // keep CARE out of the INDI basis and let it flow only into the NDI/50-50.
        const P = payorNetCash + payorBenefits - csAnnual              // payor INDI (no SS)
        const R = recipNetCash + recipBenefits - notionalAnnual        // recip INDI (no SS)

        if (P <= 0 || P + R <= 0) {
          return { low: 0, mid: 0, high: 0, indefinite, durationLow, durationHigh,
            _debug: { formula: 'with-child', payorGross, recipGross, payorNetCash, recipNetCash,
              payorBenefits, recipBenefits, csAnnual, notionalAnnual, P, R, m_p: null, m_r: null, reason: 'P<=0 or P+R<=0' } }
        }

        // Effective marginal rates for SS transfer effects — INCLUDING refundable
        // benefit phase-out response.
        //
        // DM's SSAG bisection accounts for the fact that paying SS reduces the
        // payor's taxable income (lifting their CCB / provincial child benefits)
        // and receiving SS raises the recipient's taxable income (clawing back
        // the same benefits). Tax-only marginal rates miss this — for a
        // low-/mid-income recipient with active CCB, ~18¢ of every $1 SS
        // evaporates as lost benefits before tax even applies. Without the
        // benefit term, m_r is severely understated and the bisection lands
        // at an SS too low (calibration showed this across every with-child
        // shared-parenting case).
        //
        // Confirmed via johnston-smith DM page 2 "SSAG Calculation Details":
        //   Jessica's benefits go $11,925 → $10,291 as SS goes 0 → $8,932/yr,
        //   so ΔB/ΔAFNI ≈ 0.183. Adding this to her 0.231 tax marginal gives
        //   0.414, matching DM's effective transfer rate.
        //
        // Probe: shift taxable income by ±$1000, recompute tax (credits + ON
        // surtax/health premium) AND refundable benefits, return the combined
        // derivative. The probe doesn't perturb CPP/EI because SS isn't
        // employment income — we use the bundle's exposed computeCreditsAt /
        // computeOntarioExtraAt helpers instead of re-running partyTaxBundle.
        const probeRate = (bundle, benefitOpts, direction, netAdd = 0) => {
          const delta = 1000
          const baseAfni = bundle.taxable
          // bundle.taxPayable already includes onExtra (see partyTaxBundle line ~3819:
          // `taxPayable = Math.max(0, rawTax - totalCred) + onExtra`). Do NOT add
          // onExtra a second time — doing so inflates the baseline by the surtax
          // amount, which made probed dTax artificially large and m_p clamp to 0.95
          // for every ON case in the calibration.
          const baseTaxTotal = bundle.taxPayable
          const baseBenefits = computeRefundableBenefits(prov, { ...benefitOpts, afni: baseAfni + netAdd })
          const newAfni = Math.max(0, baseAfni + direction * delta)
          const newRawTax = getRawIncomeTax(prov, newAfni, this.taxYearCfg)
          const newCred = bundle.computeCreditsAt
            ? bundle.computeCreditsAt(newAfni)
            : bundle.totalCred
          const newOnExtra = bundle.computeOntarioExtraAt
            ? bundle.computeOntarioExtraAt(newAfni)
            : 0
          const newProvRed = bundle.computeProvReductionAt
            ? bundle.computeProvReductionAt(newAfni)
            : 0
          const newTaxTotal = Math.max(0, newRawTax - newCred - newProvRed) + newOnExtra
          const newBenefits = computeRefundableBenefits(prov, { ...benefitOpts, afni: newAfni + netAdd })
          const dTax = newTaxTotal - baseTaxTotal       // direction=+1: positive
          const dBenefits = newBenefits - baseBenefits  // direction=+1: negative (clawback)
          // Recipient receiving SS (direction = +1):
          //   m_r = fraction NOT reaching pocket = (tax paid + benefits lost) / delta
          //       = (dTax - dBenefits) / delta
          // Payor paying SS (direction = -1):
          //   m_p = fraction NOT costing payor  = (tax saved + benefits gained) / delta
          //       = (-dTax + dBenefits) / delta
          const rate = direction === 1
            ? (dTax - dBenefits) / delta
            : (-dTax + dBenefits) / delta
          return Math.max(0, Math.min(0.95, rate))
        }
        const payorBenefitOpts = {
          workingIncome: payorWorking,
          children: payorChildren,
          isSingle: this.payorPartnerIncome == null,
          isPrimaryParent: payorChildren.length > 0,
          hasEDC: this.payorHasEligibleDependant,
          cfg: this.taxYearCfg,
        }
        const recipBenefitOpts = {
          workingIncome: recipWorking,
          children: recipChildren,
          isSingle: this.recipientPartnerIncome == null,
          isPrimaryParent: recipChildren.length > 0,
          hasEDC: this.recipientHasEligibleDependant,
          cfg: this.taxYearCfg,
        }
        // m_p / m_r still computed for the debug payload — useful for comparing
        // against the non-linear bisection below.
        const m_p = probeRate(payorBundle, payorBenefitOpts, -1, payorNetAdd)
        const m_r = probeRate(recipBundle, recipBenefitOpts, +1, recipNetAdd)

        // NON-LINEAR SSAG bisection.
        //
        // The previous linear solver assumed tax + benefit responses to an SS
        // transfer are perfectly linear in SS — captured by single m_p / m_r
        // derivatives probed at baseline. That breaks whenever a refundable
        // benefit threshold (e.g. CCB phase-out at $36,502 / $81,222) sits
        // inside the SS range: the recipient's effective transfer rate is
        // ~0% below the threshold and 7-32% above, so a single linear m_r
        // cannot fit both endpoints. Calibration evidence:
        //   • Hughes-Peslova family — recipient baseline $20k well below
        //     CCB threshold, but a real SS of ~$28k pushes her to ~$48k,
        //     crossing the threshold. Linear solver under-corrects by
        //     ~$300-500/mo.
        //   • Fieber — back-solving DM showed m_r implied by ssagLow (0.4225)
        //     differs from m_r implied by ssagHigh (0.4598). One linear m_r
        //     cannot fit both endpoints.
        //
        // True solver: at each candidate annual SS, shift AFNI (−SS for payor,
        // +SS for recipient), recompute tax (raw - credits + ON surtax/health)
        // and refundable benefits, then directly evaluate each party's INDI.
        // Binary-search SS so recipient share of combined INDI hits the target.
        //
        // Spousal support remains taxable/deductible under the Income Tax Act
        // (only child support became tax-neutral in 1997), so this AFNI shift
        // is correct.
        // S.7 dynamic apportioning. Our baseline payorNetCashFlow / recipientNetCashFlow
        // already subtract each party's STATIC share of total s.7 paid, where the
        // share is payorPct = payorIncome / (payorIncome + recipientIncome).
        //
        // DM's actual SSAG INDI math apportions s.7 by APPORTIONING INCOME =
        // Guidelines − SS_paid for the payor (+ SS_received for the recipient),
        // recomputed at each candidate SS. This makes the bisection genuinely
        // depend on SS through the s.7 channel: as SS rises, the recipient's
        // apportioning income grows, their s.7 share grows, and the payor's
        // s.7 burden drops — effectively giving the payor a ~10% "discount" on
        // each $1 of SS through the s.7 channel.
        //
        // DM Lewis-Shaw PDF Apportioning Income table: at SSAG_low (SS=$57,190),
        // Lawrence's apportioning income = $476,315 (=$533,505 − $57,190),
        // Tannis's = $162,190 (=$105,000 + $57,190). Tannis's share = 25.4%,
        // she reimburses Lawrence 25.4% × $93,400 = $23,724.
        const totalS7Paid = (this.s7PaidByAAnnual || 0) + (this.s7PaidByBAnnual || 0)
        const totalIncomeForApp = payorGross + recipGross
        // Baseline static share (the one already baked into P and R via netCashFlow)
        const baselinePayorS7Share = totalIncomeForApp > 0 ? (payorGross / totalIncomeForApp) : 0.5
        const baselineRecipS7Share = 1 - baselinePayorS7Share
        const baselinePayorS7Cost = totalS7Paid * baselinePayorS7Share
        const baselineRecipS7Cost = totalS7Paid * baselineRecipS7Share

        const indiAt = (annualSS) => {
          const newPayorAfni = Math.max(0, payorAFNI - annualSS)
          const newRecipAfni = recipAFNI + annualSS
          const newPayorRawTax = getRawIncomeTax(prov, newPayorAfni, this.taxYearCfg)
          const newPayorCred = payorBundle.computeCreditsAt
            ? payorBundle.computeCreditsAt(newPayorAfni)
            : payorBundle.totalCred
          const newPayorOnExtra = payorBundle.computeOntarioExtraAt
            ? payorBundle.computeOntarioExtraAt(newPayorAfni)
            : 0
          const newPayorProvRed = payorBundle.computeProvReductionAt
            ? payorBundle.computeProvReductionAt(newPayorAfni)
            : 0
          const newPayorTax = Math.max(0, newPayorRawTax - newPayorCred - newPayorProvRed) + newPayorOnExtra
          const newRecipRawTax = getRawIncomeTax(prov, newRecipAfni, this.taxYearCfg)
          const newRecipCred = recipBundle.computeCreditsAt
            ? recipBundle.computeCreditsAt(newRecipAfni)
            : recipBundle.totalCred
          const newRecipOnExtra = recipBundle.computeOntarioExtraAt
            ? recipBundle.computeOntarioExtraAt(newRecipAfni)
            : 0
          const newRecipProvRed = recipBundle.computeProvReductionAt
            ? recipBundle.computeProvReductionAt(newRecipAfni)
            : 0
          const newRecipTax = Math.max(0, newRecipRawTax - newRecipCred - newRecipProvRed) + newRecipOnExtra
          const newPayorBenefits = computeRefundableBenefits(prov, { ...payorBenefitOpts, afni: newPayorAfni + payorNetAdd })
          const newRecipBenefits = computeRefundableBenefits(prov, { ...recipBenefitOpts, afni: newRecipAfni + recipNetAdd })

          // S.7 dynamic apportioning: recompute each party's share at this SS.
          let s7DeltaPayor = 0, s7DeltaRecip = 0
          if (totalS7Paid > 0) {
            const payorAppInc = Math.max(0, payorGross - annualSS)
            const recipAppInc = recipGross + annualSS
            const totalApp = payorAppInc + recipAppInc
            if (totalApp > 0) {
              const newPayorShare = payorAppInc / totalApp
              const newRecipShare = recipAppInc / totalApp
              // Baseline already deducted baselineXS7Cost; new cost = newShare × total.
              // Delta we ADD to P_eff/R_eff = (baseline cost − new cost) — positive
              // when new cost is lower (party benefits).
              s7DeltaPayor = baselinePayorS7Cost - totalS7Paid * newPayorShare
              s7DeltaRecip = baselineRecipS7Cost - totalS7Paid * newRecipShare
            }
          }

          // Baseline P/R already use baseline tax + benefits + s.7 (netCash is
          // post-tax-and-s.7). Shift each side by: gross SS flow + tax delta +
          // benefit delta + s.7 reapportionment delta.
          const P_eff = P - annualSS
            + (payorBundle.taxPayable - newPayorTax)
            + (newPayorBenefits - payorBenefits)
            + s7DeltaPayor
          const R_eff = R + annualSS
            + (recipBundle.taxPayable - newRecipTax)
            + (newRecipBenefits - recipBenefits)
            + s7DeltaRecip
          return { P_eff, R_eff }
        }
        const ssAt = (target) => {
          // f(SS) = R_eff / (P_eff + R_eff) − target. Monotonic increasing in SS
          // (more SS → R up, P down). Find SS where f = 0 via bisection.
          const base = indiAt(0)
          if (base.P_eff + base.R_eff <= 0) return 0
          if (base.R_eff / (base.P_eff + base.R_eff) >= target) return 0
          let lo = 0
          let hi = Math.max(1000, P) // initial guess: at most payor's INDI
          for (let i = 0; i < 30; i++) {
            const { P_eff, R_eff } = indiAt(hi)
            if (P_eff + R_eff <= 0) break
            if (R_eff / (P_eff + R_eff) >= target) break
            hi *= 2
            if (hi > 1e8) break
          }
          for (let i = 0; i < 60; i++) {
            const mid = (lo + hi) / 2
            const { P_eff, R_eff } = indiAt(mid)
            if (P_eff + R_eff <= 0) { hi = mid; continue }
            const ratio = R_eff / (P_eff + R_eff)
            if (ratio < target) lo = mid
            else hi = mid
            if (hi - lo < 0.5) break
          }
          return (lo + hi) / 2
        }
        const lowAnnual  = ssAt(0.40)
        const midAnnual  = ssAt(0.43)
        const highAnnual = ssAt(0.46)
        const formulaLow  = Math.round(lowAnnual / 12)
        const formulaHigh = Math.round(highAnnual / 12)
        // Mid is at the 43% target (not the SS midpoint between low and high).
        // DM PDFs (Lewis-Shaw, Roberton-Kirby) confirm: SSAG mid corresponds to
        // recipient share = 43% of combined INDI, not the SS midpoint. When
        // ssAt(0.40) clamps to 0 (recipient already > 40% at SS=0), the SS-
        // midpoint formula was off by a factor of 2 (Roberton-Kirby mid was
        // $576 ours vs $284 DM).
        const mid  = Math.round(midAnnual / 12)
        const ss5050 = this.findSs5050()
        // SSAG 8.6.3 "Adjusting the limits of the range" (shared-custody 50/50-NDI
        // inclusion). PRIMARY-SOURCE VERIFIED (DOJ Revised User's Guide Ch.8 / 2008
        // SSAG 8.6.3): the 50/50-NDI inclusion is grounded ONLY in shared custody —
        // NOT the basic with-child formula (which deliberately stops at 46% INDI;
        // the authors considered and REJECTED a 50/50 upper limit), NOT split
        // custody, NOT hybrid. The "50/50 split" is the broader FAMILY net
        // disposable income (after BOTH child and spousal support), not INDI; our
        // findSs5050() targets that NDI-equalizing amount. The range is broadened
        // only when that amount falls OUTSIDE the 40–46% INDI range:
        //   • LOW down to ss5050 — but ONLY in the specific case the Guidelines
        //     describe: "the recipient parent has little or no income and there are
        //     two or more children subject to shared custody ... the lower end of
        //     the range has been adjusted downwards ... automatically by the
        //     software" (SSAG 8.6.3, verbatim). The `ss5050 < formulaLow`
        //     inequality below only arises when the recipient is sufficiently
        //     low-income / CS-heavy, which IS the income half of that condition;
        //     the ≥2-shared-table-children test is the count half.
        //   • HIGH up to ss5050 when the 50/50 amount exceeds the formula high
        //     (shared custody generally; no 2+-children requirement at the high
        //     end). e.g. Frewing, Roberton-Kirby, Nicolson-Stewart.
        // VERIFIED vs DM Tools Cloud 2026: Scott-Laurel (2 shared table kids,
        // recipient $42k) → DM extends low to 50/50 (3,881*). Lewis-Shaw (1 shared
        // table kid — Kate 18 is post-secondary, subjectToTable:false — recipient
        // $105k) → DM does NOT extend (low = pure 40% INDI 4,766). Adult-children
        // (Ch. 9) cases like Campo route here via subjectToTable but have 0 shared
        // table kids, so neither gate fires.
        const sharedTableKids = this.sharedChildren.length
        // The 50/50-NDI broadening (SSAG 8.6.3) is SHARED-custody-specific, and
        // only for PURE shared custody — every dependent child shared. If any
        // dependent child is in one parent's PRIMARY care, the arrangement is
        // hybrid, not shared, and DM does NOT broaden to 50/50 (it stops at the
        // 46%-INDI high). This bites most often with an off-table adult
        // dependent (s.3(2)) living with one parent alongside shared minors.
        // VERIFIED vs DM Tools Cloud 2026 (Shawn/Gini BC, 2 shared kids): WITHOUT
        // the 19-yo dependent DM extends the high to 50/50 ($596); ADDING him
        // (primary with the recipient) drops the high to the 46% figure ($290).
        // DM's INDI detail is byte-identical either way — the adult child does
        // NOT enter the INDI; he only removes the 50/50 broadening by making the
        // arrangement non-shared. All calibrated extension cases (scott-laurel,
        // frewing, roberton-kirby, nicolson-stewart) are pure-shared, so
        // unaffected.
        const hasPrimaryDependent = this.includedChildren.some(c =>
          (c.parenting === 'primaryA' || c.parenting === 'primaryB') && c.isDependent !== false)
        const pureShared = sharedTableKids >= 1 && !hasPrimaryDependent
        let low = formulaLow
        // High-end broadening: pure shared custody only (SSAG 8.6.3).
        let high = (ss5050 != null && pureShared)
          ? Math.max(formulaHigh, ss5050) : formulaHigh
        // Low-end downward adjustment: pure shared custody with TWO OR MORE
        // children subject to shared custody (SSAG 8.6.3).
        if (ss5050 != null && pureShared && sharedTableKids >= 2) {
          low = Math.min(formulaLow, ss5050)
        }
        return { low, mid, high, indefinite, durationLow, durationHigh,
          _debug: { formula: 'with-child', payorGross, recipGross, payorNetCash, recipNetCash,
            payorBenefits, recipBenefits, csAnnual, notionalAnnual, P, R, m_p, m_r,
            lowAnnual, midAnnual, highAnnual, formulaHigh, ss5050 } }
      } else {
        // WITHOUT-CHILD formula (also applied for custodial-payor per SSAG Ch. 8
        // and adult-children per SSAG Ch. 9):
        //   amount = 1.5–2% per year of marriage × ADJUSTED gross income diff,
        //   capped at 37.5% / 50% of the gap for marriages ≥ 25 years.
        //
        // For custodial-payor / adult-children scenarios with kids still in the
        // calc, DM's "Adjusted Guidelines Income" reduces each party's
        // Guidelines income by THREE things (per Mora-Primary DM PDF):
        //   1. Table or Notional CS at their income for the kids
        //   2. Their static-apportioned share of total NET s.7
        //   3. CS gross-up = CS × m/(1-m), where m is the party's effective
        //      marginal rate (tax + refundable benefit phase-out). This captures
        //      the tax disadvantage of CS being non-deductible.
        //
        // When kids are not in the calc (true no-children case), all three
        // adjustments are zero and the diff is just payorGross − recipGross.
        let adjustedPayor = payorGross
        let adjustedRecip = recipGross
        // Social assistance with a spousal clawback is excluded from the SSAG
        // gross-income basis (see recipSAClawback note above).
        adjustedRecip -= recipSAClawback
        // Prior support obligations — SSAG Ch. 15.3.1 treats support payable to
        // a prior spouse/family as a FIRST CHARGE on income: deduct it from the
        // party's gross-income basis before the income-gap formula. Prior
        // SPOUSAL support is tax-deductible, so deduct at face value; prior
        // CHILD support is paid from after-tax dollars, so gross it up to the
        // pre-tax income that funds it (amount / (1 − marginal rate)).
        const priorSupportAdj = (items, bundle) => {
          const ssPrior = sumType(items, 'prior_spousal_support_paid')
          let csPrior = sumType(items, 'prior_child_support_paid')
          if (csPrior > 0) {
            const m = Math.min(0.95, Math.max(0,
              getMarginalRate(prov, bundle.taxable, this.taxYearCfg)))
            csPrior = csPrior / (1 - m)
          }
          return ssPrior + csPrior
        }
        adjustedPayor -= priorSupportAdj(ssagPayorItems, payorBundle)
        adjustedRecip -= priorSupportAdj(ssagRecipItems, recipBundle)
        const nKids = this.csgChildren.length
        if (nKids > 0) {
          // 1. Table or Notional CS at each party's income for the kids
          const payorTableMo = this.lookup(this.effectiveProvince, nKids, payorGross) || 0
          const recipTableMo = this.lookup(this.effectiveProvince, nKids, recipGross) || 0
          const payorTableAnnual = payorTableMo * 12
          const recipTableAnnual = recipTableMo * 12
          adjustedPayor -= payorTableAnnual
          adjustedRecip -= recipTableAnnual

          // 2. Special expenses apportioned (static, NET cost)
          const totalS7 = (this.s7PaidByAAnnual || 0) + (this.s7PaidByBAnnual || 0)
          const totalNetS7 = Math.max(0, totalS7
            - (this.s7TaxBenefitAAnnual || 0)
            - (this.s7TaxBenefitBAnnual || 0))
          const totalInc = payorGross + recipGross
          if (totalNetS7 > 0 && totalInc > 0) {
            adjustedPayor -= totalNetS7 * (payorGross / totalInc)
            adjustedRecip -= totalNetS7 * (recipGross / totalInc)
          }

          // 3. CS gross-up — probe each party's TAX-ONLY marginal rate at
          //    (income + notional CS). DM uses the marginal tax rate that
          //    would apply to the parent's income plus the CS amount — but
          //    excludes refundable benefit clawback from this specific rate
          //    (back-solved from Mora-Primary: m ≈ 0.392 for Christal, which
          //    matches BC combined tax marginal 0.383 at $130k plus a small
          //    adjustment, NOT 0.44 = tax 0.383 + CCB clawback 0.057).
          const grossupRate = (bundle, notionalCS) => {
            const delta = 1000
            const probeAfni = bundle.taxable + notionalCS
            const baseRawTax = getRawIncomeTax(prov, probeAfni, this.taxYearCfg)
            const baseCred = bundle.computeCreditsAt ? bundle.computeCreditsAt(probeAfni) : bundle.totalCred
            const baseOnExtra = bundle.computeOntarioExtraAt ? bundle.computeOntarioExtraAt(probeAfni) : 0
            const baseTax = Math.max(0, baseRawTax - baseCred) + baseOnExtra
            const newAfni = probeAfni + delta
            const newRawTax = getRawIncomeTax(prov, newAfni, this.taxYearCfg)
            const newCred = bundle.computeCreditsAt ? bundle.computeCreditsAt(newAfni) : bundle.totalCred
            const newOnExtra = bundle.computeOntarioExtraAt ? bundle.computeOntarioExtraAt(newAfni) : 0
            const newProvRed = bundle.computeProvReductionAt ? bundle.computeProvReductionAt(newAfni) : 0
            const newTax = Math.max(0, newRawTax - newCred - newProvRed) + newOnExtra
            const rate = (newTax - baseTax) / delta
            return Math.max(0, Math.min(0.95, rate))
          }
          const m_p = grossupRate(payorBundle, payorTableAnnual)
          const m_r = grossupRate(recipBundle, recipTableAnnual)
          adjustedPayor -= payorTableAnnual * m_p / (1 - m_p)
          adjustedRecip -= recipTableAnnual * m_r / (1 - m_r)
        }

        const diff = adjustedPayor - adjustedRecip
        if (diff <= 0) return { low: 0, mid: 0, high: 0, indefinite, durationLow, durationHigh,
          _debug: { formula: 'without-child', payorGross, recipGross, adjustedPayor, adjustedRecip,
            diff, reason: 'diff<=0' } }
        const lowPct  = Math.min(0.015 * this.yearsMarried, 0.375)
        const highPct = Math.min(0.02  * this.yearsMarried, 0.50)
        const formulaLow  = Math.round(diff * lowPct  / 12)
        const formulaHigh = Math.round(diff * highPct / 12)
        // Without-child: BOTH low and high are capped at ss5050 if the formula
        // exceeds it. When ss5050 < formulaLow the entire range collapses to
        // ss5050 (Campo After Uni: 17380/.../23174 → all 14644).
        const ss5050 = this.findSs5050()
        const low  = ss5050 != null ? Math.min(formulaLow,  ss5050) : formulaLow
        const high = ss5050 != null ? Math.min(formulaHigh, ss5050) : formulaHigh
        // Mid stays at the formula midpoint per DM convention.
        const mid  = Math.round((formulaLow + formulaHigh) / 2)
        const cappedMid = ss5050 != null ? Math.min(mid, ss5050) : mid
        return { low, mid: cappedMid, high, indefinite, durationLow, durationHigh,
          _debug: { formula: 'without-child', payorGross, recipGross, payorNetCash, recipNetCash,
            adjustedPayor, adjustedRecip, diff, lowPct, highPct,
            formulaLow, formulaHigh, ss5050 } }
      }
    },
  },

  watch: {
    // ─── Prop → local-state sync ──────────────────────────────────────
    // The component copies props into local data once at created(). That
    // means changes the PARENT makes to a prop after mount don't propagate
    // on their own. These watchers re-seed local state when a prop changes,
    // so the dev "Test props" panel (and any live parent update) carries
    // through — most visibly child info. In production LegalApp passes props
    // once and uses state hydration, so these only fire when content actually
    // changes. Editing inside the calculator (step 3, names, etc.) does NOT
    // touch the props, so user edits aren't clobbered.
    childrenProp: {
      deep: true,
      handler(arr) {
        this.children = (arr || []).map(c => ({
          sourceId: c.id != null ? c.id : (c.sourceId != null ? c.sourceId : null),
          name: c.name || '',
          dob: c.dob || '',
          parenting: c.parenting || 'primaryB',
          isDependent: c.isDependent != null ? c.isDependent : null,
          subjectToTable: c.subjectToTable != null ? c.subjectToTable : null,
          // DIVII PATCH: see data() — builder claim seeds an Auto shared child.
          edcClaimedBy: c.edcClaimedBy != null
            ? c.edcClaimedBy
            : edcSlotFromBuilderClaim(c.edcClaimedByPartner, c.parenting || 'primaryB', this.builderOrientationSwapped),
          childAmountBy: c.childAmountBy != null ? c.childAmountBy : null,
          disabled: c.disabled != null ? c.disabled : false,
          s7expenses: c.s7expenses || [],
        }))
      },
    },
    province(v)             { if (v != null) this.provinceLocal = v },
    payorNameProp(v)        { if (v != null) this.payorName = v },
    recipientNameProp(v)    { if (v != null) this.recipientName = v },
    cohabitationDateProp(v) { if (v != null) this.cohabitationDate = v },
    separationDateProp(v)   { if (v != null) this.separationDate = v },
    payorDOBProp(v)         { if (v != null) this.payorDOB = v },
    recipientDOBProp(v)     { if (v != null) this.recipientDOB = v },

    ssagRange(val) {
      if (val && !this.ssagSelected) {
        this.ssagSelected = val.mid
      }
      if (val && !this.ssagSelectedDuration) {
        // Snap initial value to a 0.25-year grid so it lines up with the slider
        // step (which floors min/ceils max to whole years and steps by 0.25,
        // i.e. lands on 0.00, 0.25, 0.50, 0.75, 1.00, …).
        const mid = (val.durationLow + val.durationHigh) / 2
        this.ssagSelectedDuration = Math.round(mid * 4) / 4
      }
    },

    // ─── State persistence — fires `update-state` whenever any
    //     calculator-owned field changes. LegalApp's parent listens and
    //     saves to the Matter record (debounced internally — see
    //     emitStateUpdate). Watchers below cover every serializable
    //     field returned by computeFullState(). ───────────────────────
    taxYear()              { this.emitStateUpdate() },
    edcClaim()             { this.emitStateUpdate() },
    cohabitationDate()     { this.emitStateUpdate() },
    separationDate()       { this.emitStateUpdate() },
    payorDOB()             { this.emitStateUpdate() },
    recipientDOB()         { this.emitStateUpdate() },
    payorDisabled()        { this.emitStateUpdate() },
    recipientDisabled()    { this.emitStateUpdate() },
    provinceLocal()        { this.emitStateUpdate() },
    payorName()            { this.emitStateUpdate() },
    recipientName()        { this.emitStateUpdate() },
    ssagApplicable()       { this.emitStateUpdate() },
    ssagPayor()            { this.emitStateUpdate() },
    ssagSelected()         { this.emitStateUpdate() },
    ssagSelectedDuration() { this.emitStateUpdate() },
    lastAppliedScenarioId(){ this.emitStateUpdate() },
    lastAppliedMode(){ this.emitStateUpdate() },
    payorManuallyChosen()  { this.emitStateUpdate() },
    syncDeclinedFingerprint() { this.emitStateUpdate() },
    builderOrientationSwapped() { this.emitStateUpdate() },
    // viewMode persists (lawyer's chosen workflow) AND resets scroll — a
    // wizard↔workspace switch should land the user at the top of the page.
    viewMode()             { this.emitStateUpdate(); this.scrollPageToTop() },
    // Wizard step changes only reset scroll; step itself is not persisted.
    step()                 { this.scrollPageToTop() },
    workspaceTab()         { this.emitStateUpdate() },
    // The spousal-support tabs (Budget / SSAG Questions / Recommendation) only
    // exist while ssagApplicable is Yes. If it flips to No while one of them
    // is open, fall back to the Calculator tab.
    ssagApplicable(val) {
      if (!val && ['needs', 'circumstances', 'recommendation'].includes(this.workspaceTab)) {
        this.workspaceTab = 'means'
      }
      // Worksheet tab disappears with entitlement — keep the user on the
      // breakdown via the classic viewMode path.
      if (!val && this.workspaceTab === 'worksheet') {
        this.workspaceTab = 'means'
        this.viewMode = 'workspace'
      }
    },
    budgetLines:    { deep: true, handler() { this.emitStateUpdate() } },
    budgetCustom:   { deep: true, handler() { this.emitStateUpdate() } },
    advisorAnswers: { deep: true, handler() { this.emitStateUpdate() } },
    showRestructure()          { this.emitStateUpdate() },
    restructureScenarioId()    { this.emitStateUpdate() },
    restructureLumpSum()       { this.emitStateUpdate() },
    restructureDurationYears() { this.emitStateUpdate() },
    restructureDiscountRate()  { this.emitStateUpdate() },
    incomeItems: { deep: true, handler() { this.emitStateUpdate() } },
    children:    { deep: true, handler() { this.materializeEDCClaims(); this.emitStateUpdate() } },
    scenarios:   { deep: true, handler() { this.emitStateUpdate() } },
    cfaItems:    { deep: true, handler() { this.emitStateUpdate() } },
  },

  created() {
    // Hydrate from saved state FIRST — before default scenario seeding —
    // so that a restored Matter shows the lawyer's previous scenarios
    // (including custom columns) rather than freshly-defaulted ones.
    if (this.initialState) {
      this.loadFromState(this.initialState)
      // DIVII PATCH: states saved when "Auto" existed may hold shared children
      // with no claim; give them the default so the UI shows a choice.
      this.materializeEDCClaims()
    }
    // Seed three default scenarios. Each is bound to a SSAG range tier
    // (low/mid/high) and re-resolves live as inputs change, UNLESS the user
    // overrides its SS amount, at which point ssOverridden flips and the row
    // becomes a frozen what-if (e.g. "$0 spousal support").
    if (this.scenarios.length === 0) {
      this.scenarios = this.makeDefaultScenarios()
    }
    // Role-based landing view. Read-only profiles start in the workspace (the
    // wizard is an entry flow, pointless when nothing is editable);
    // contribute-view users land on their own entry tab.
    if (this.readOnlyCase) this.viewMode = 'workspace'
    if (this.accessProfile === 'contribute-view') this.workspaceTab = 'myinfo'
    // A restored tab this role may not see (e.g. host state saved by a
    // full-access user on the Recommendation tab) falls back to Calculator.
    if (this.workspaceTab === 'recommendation' && !this.canViewRecommendation) this.workspaceTab = 'means'
    // DIVII FIX — restored tab must be RENDERABLE by this profile, not merely
    // permitted. The tab bar is a fixed set of buttons, but 'myinfo' is the only
    // tab whose content section is gated on the profile itself
    // (showPartyEntry === contribute-view only). So a state persisted by a
    // level-2 session — whose landing tab IS 'myinfo' (line above) — reopens
    // BLANK for every other role: tab bar visible, no content, until the user
    // clicks a tab. Whitelist rather than a one-off 'myinfo' check so any
    // unknown or future value degrades to the Calculator instead of a blank
    // screen. Reported upstream; drop this once upstream validates the tab.
    const renderableTabs =
      this.accessProfile === 'contribute-view'
        ? ['myinfo', 'means', 'needs', 'circumstances', 'recommendation', 'worksheet']
        : ['means', 'needs', 'circumstances', 'recommendation', 'worksheet']
    if (!renderableTabs.includes(this.workspaceTab)) this.workspaceTab = 'means'
    // Spousal tabs only exist once entitlement is answered Yes.
    if (!this.ssagApplicable && ['needs', 'circumstances', 'recommendation'].includes(this.workspaceTab)) {
      this.workspaceTab = 'means'
    }
    if (!this.ssagApplicable && this.workspaceTab === 'worksheet') {
      this.workspaceTab = 'means'
      this.viewMode = 'workspace'
    }
    // Builder→calculator sync. Only meaningful when a saved state was
    // hydrated (it overwrote the prop-seeded identity fields above); the
    // standalone dev harness passes no initialState and keeps its live
    // prop watchers instead. Deferred a tick so it runs AFTER loadFromState's
    // own $nextTick has released _isHydrating — silent fills then flow
    // through the normal watchers → emitStateUpdate → parent persist.
    // MUST stay last: the role-based assignments above are synchronous, so
    // running them first keeps runPropSync's post-hydration contract intact.
    if (this.initialState) {
      this.$nextTick(() => this.runPropSync())
    }
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocClick)
  },
  beforeDestroy() {
    // Vue 2 lifecycle hook name (Vue 3 uses beforeUnmount). Cleans up
    // the global mousedown listener registered in mounted().
    document.removeEventListener('mousedown', this.onDocClick)
    // Cancel any pending debounced state emission so the parent doesn't
    // receive a stale event after the calculator has unmounted.
    if (this._stateUpdateTimer) clearTimeout(this._stateUpdateTimer)
  },

  methods: {
    // ── Income items (Phase 1) ────────────────────────────────────
    addIncomeItem(partyId, typeId) {
      if (!INCOME_TYPES[typeId]) return
      const item = {
        id: newItemId(),
        partyId,
        typeId,
        amount: 0,
      }
      // Entries made by a level-2 user (party mode) are stamped so the
      // level-1 view can flag them for review — the party's own assertion,
      // like a financial-statement disclosure, until the professional
      // verifies it.
      if (this.roleLevel === 2) {
        item.selfReported = true
        // Attribute the entry to the signed-in user — Divii's userName when
        // passed, else the name they typed when claiming their party.
        const claim = this.rolePartyId ? this.partyClaims[this.rolePartyId] : null
        const who = this.roleUserName || (claim && claim.name) || null
        if (who) item.enteredBy = who
        if (this.roleUserId) item.enteredById = this.roleUserId
      }
      this.incomeItems.push(item)
    },
    removeIncomeItem(id) {
      const idx = this.incomeItems.findIndex(i => i.id === id)
      if (idx >= 0) this.incomeItems.splice(idx, 1)
    },
    // ── Party self-identification ─────────────────────────────────────────
    // "I am Smith/Jones" — locks that party's entry surface to this user.
    claimParty() {
      const pid = this.claimPartyPick
      if (pid !== 'A' && pid !== 'B') return
      const existing = this.partyClaims[pid]
      if (existing && existing.userKey !== this.currentUserKey) return // taken
      // The claim is simply userKey ↔ party (like the payor/recipient
      // assignment). name is only stored when Divii identifies the account —
      // no self-typed names.
      this.$set(this.partyClaims, pid, { name: this.roleUserName || null, userKey: this.currentUserKey })
      this.claimPartyPick = ''
      this.emitStateUpdate()
    },
    // Full-access roles can clear a wrong claim (frees the party for
    // re-identification; already-entered items keep their enteredBy stamp).
    resetPartyClaim(pid) {
      if (this.accessProfile !== 'full') return
      this.$set(this.partyClaims, pid, null)
      this.emitStateUpdate()
    },
    updateIncomeAmount(id, amount) {
      const item = this.incomeItems.find(i => i.id === id)
      if (item) item.amount = Number(amount) || 0
    },
    // Parse a user-typed dollar string into an ANNUAL number.
    // Handles commas, $, spaces — and a single trailing pay-period
    // suffix that auto-multiplies the figure into an annual equivalent:
    //   1000m → $12,000/yr  (monthly,   ×12)
    //   1000w → $52,000/yr  (weekly,    ×52)
    //   1000b → $26,000/yr  (biweekly,  ×26 — 26 pay periods/yr)
    //   1000t → $24,000/yr  (twice-monthly / semi-monthly, ×24)
    //   1000a or no suffix → $1,000/yr (annual, ×1)
    // Suffix is case-insensitive. Works on any field that uses
    // parseDollarInput — income line items, scenario SS, s.7 amounts.
    parseDollarInput(str) {
      if (str == null || str === '') return 0
      const raw = String(str).trim()
      // Detect a trailing period suffix BEFORE stripping non-digits.
      const sufMatch = raw.match(/([mwbta])\s*$/i)
      let multiplier = 1
      if (sufMatch) {
        switch (sufMatch[1].toLowerCase()) {
          case 'm': multiplier = 12; break
          case 'w': multiplier = 52; break
          case 'b': multiplier = 26; break
          case 't': multiplier = 24; break
          case 'a': multiplier = 1;  break
        }
      }
      const cleaned = raw.replace(/[^\d.-]/g, '')
      const n = parseFloat(cleaned)
      if (isNaN(n)) return 0
      return Math.round(n * multiplier)
    },
    // Display value for an amount input — comma-formatted when idle, raw digits while focused
    displayAmount(item) {
      if (item.amount === 0 && this.amountFocusedId !== item.id) return ''
      if (this.amountFocusedId === item.id) return String(item.amount || '')
      return this.fmt(item.amount)
    },
    onAmountFocus(item) {
      this.amountFocusedId = item.id
    },
    onAmountBlur() {
      this.amountFocusedId = null
    },
    incomeTypeMeta(typeId) {
      return INCOME_TYPES[typeId] || null
    },

    // Format a signed dollar amount with parens for negatives (accounting
    // convention). Used throughout the printable worksheet to keep the
    // scenarios table consistent with DivorceMate's output style — never
    // a leading minus sign, always parens for outflows / deductions.
    fmtSigned(v) {
      if (v == null || isNaN(v)) return '—'
      if (v < 0) return '($' + this.fmt(-v) + ')'
      return '$' + this.fmt(v)
    },
    // For the SS and CS rows in the worksheet: payor column always shows
    // the amount as a cost (parens) when > 0; recipient column shows it
    // as a benefit. Mirrors DM's column-direction convention.
    fmtCostPayor(v) {
      if (!v || v <= 0) return '$0'
      return '($' + this.fmt(v) + ')'
    },

    // ═══════════════════════════════════════════════════════════════════════
    // LegalApp integration — Apply to Agreement
    // ═══════════════════════════════════════════════════════════════════════
    // Build the payload sent to LegalApp when the lawyer clicks "Apply to
    // Agreement" on a scenario column. Shape is stable across all scenarios
    // — LegalApp's parent component listens for @apply and routes payload
    // fields into matching form sections (Child Support Details, Special
    // Expenses, Spousal Support Details, Client Details income).
    //
    // The calculator is the source of truth for income — Apply overwrites
    // whatever Divii has for each party's income. Other Divii fields
    // (parties, province, dates, children, etc.) flow IN via matterContext
    // but are not modified by the calculator.
    //
    // See INTEGRATION.md for full schema and field-mapping notes.
    buildApplyPayload(scenario, mode = 'periodic', options = {}) {
      // DIVII PATCH: `scenario` may be null for a child-support-only record
      // (no SSAG claim, so no scenario columns exist). `options` carries the
      // confirm dialog's term and lump-sum choices, which upstream collected
      // but never wrote into the payload. Every addition below is a new field;
      // nothing documented in INTEGRATION.md §7 is renamed or removed.
      const csOnly = !scenario
      const r = this.ssagRange || null
      const useSsag = !csOnly && !!(this.ssagApplicable && r)

      // Income breakdown per party — line items the lawyer entered, with
      // T1 references so Divii's UI can show provenance if it supports it.
      const breakdownFor = (partyId) => {
        return (this.incomeItems || [])
          .filter(it => it.partyId === partyId)
          .map(it => {
            const meta = this.incomeTypeMeta(it.typeId) || {}
            return {
              typeId: it.typeId,
              label: meta.label || it.typeId,
              amount: it.amount || 0,
              t1Line: meta.t1Line || null,
              category: meta.category || null,
              grossUp: meta.grossUp || 1,
              isDeduction: !!meta.isDeduction,
            }
          })
      }

      // Spousal support — always emit BOTH periodic and lump representations,
      // plus `mode` flagging which the parties chose (set via the Apply split
      // button). LegalApp writes the SS section per `mode`; the other
      // representation is still available as the alternative.
      const ssAfterTaxPayor = (scenario && scenario.b && scenario.b.a && scenario.b.a.ssAfterTaxM) || 0
      const periodicMonthly = useSsag ? (scenario.ssMonthly || 0) : 0
      const durationYears = useSsag ? (scenario.durationYears || 0) : 0
      // DIVII PATCH: the dialog's term choice wins over the SSAG default.
      const termChosen = options.indefinite !== undefined && options.indefinite !== null
      const indefinite = useSsag
        ? (termChosen ? !!options.indefinite : !!(r && r.indefinite))
        : false
      const termYears = useSsag && termChosen && !indefinite && Number(options.years) > 0
        ? Number(options.years)
        : durationYears
      const lumpSum = useSsag
        ? this.lumpSumValue(ssAfterTaxPayor, durationYears)
        : 0
      const lumpSumMid = useSsag ? this.lumpSumMidpoint(scenario) : 0
      const lumpBasis = options.lumpBasis === 'payorCost' ? 'payorCost' : 'midpoint'
      // DIVII PATCH: netPayer below is a display NAME (not the documented
      // 'payor'|'recipient'); netPayerSlot is the slot form hosts can rely on.
      const netPayerSlot = !this.netCS
        ? null
        : (this.netCSPayorOwes >= this.netCSRecipientOwes ? 'payor' : 'recipient')

      return {
        // ─── Identifying meta ───────────────────────────────────────────
        meta: {
          scenarioId: csOnly ? 'child-support-only' : scenario.id,
          scenarioLabel: csOnly ? 'Child support only' : (scenario.name || scenario.bind || 'Custom'),
          scenarioBind: csOnly ? null : (scenario.bind || null), // 'low'|'mid'|'high'|null(custom)
          province: this.effectiveProvince,
          taxYear: this.taxYear,
          calculatorVersion: '2026.1',
          // DIVII PATCH: which builder party sits in the payor slot (A). It is
          // partner1 until the lawyer swaps payor and recipient.
          payorIsPartner1: !this.builderOrientationSwapped,
        },

        // ─── Per-party data (income overrides Divii's Client Details) ──
        payor: {
          name: this.payorName,
          guidelinesIncome: this.payorIncome,
          incomeBreakdown: breakdownFor('A'),
        },
        recipient: {
          name: this.recipientName,
          guidelinesIncome: this.recipientIncome,
          incomeBreakdown: breakdownFor('B'),
        },

        // ─── Child support ─────────────────────────────────────────────
        childSupport: {
          payorTable: this.csPayorToRecipient || this.sharedTableA || 0,
          recipientTable: this.csRecipientToPayor || this.sharedTableB || 0,
          net: this.netCS || 0,
          netPayer: this.netCSPayer || null, // display name of the net payer
          netPayerSlot, // DIVII PATCH: 'payor' | 'recipient' | null
          // DIVII PATCH: each parent's full obligation before set-off. For pure
          // sole or pure shared custody these equal the tables above; for a
          // split or hybrid case (a shared child plus a child primarily with
          // one parent) they are the set-off inputs the builder's Shared
          // arrangement needs, which the tables above are not.
          payorOwes: this.netCSPayorOwes || 0,
          recipientOwes: this.netCSRecipientOwes || 0,
          // DIVII PATCH: which builder arrangement the custody pattern needs.
          // 'shared' whenever a set-off applies (any shared child, or split
          // custody with a child primarily with each parent); 'primary' when
          // every child lives primarily with the same parent.
          arrangement: this.sharedChildren.length > 0 || this.isSplitCustody ? 'shared' : 'primary',
          custody: {
            shared: this.sharedChildren.length,
            withPayor: this.soleAChildren.length,
            withRecipient: this.soleBChildren.length,
          },
          // DIVII PATCH: who claims the Eligible Dependant Credit for each
          // eligible child (under 18 in the tax year), as slots. `id` is the
          // builder's child id when the child came from the builder.
          dependantClaims: this.edcResolvedClaims.map(c => ({
            id: c.sourceId,
            name: c.name,
            residence: c.parenting === 'shared' ? 'shared' : (c.parenting === 'primaryA' ? 'payor' : 'recipient'),
            claimedBy: c.claimedBy === 'A' ? 'payor' : (c.claimedBy === 'B' ? 'recipient' : 'none'),
          })),
        },

        // ─── Section 7 special expenses (apportioning %) ───────────────
        // DIVII PATCH: with no scenario column the income shares are used.
        specialExpenses: {
          payorPct: csOnly ? this.payorPct : ((scenario.b && scenario.b.a && scenario.b.a.s7Pct) || 0),
          recipientPct: csOnly ? this.recipientPct : ((scenario.b && scenario.b.b && scenario.b.b.s7Pct) || 0),
        },

        // ─── Spousal support — both representations ────────────────────
        spousalSupport: {
          applicable: useSsag,
          // 'periodic' (monthly award) | 'lumpSum' (single NPV payment) — the
          // parties' chosen form, picked at apply-time. Defaults to periodic.
          mode: mode,
          // DIVII PATCH: who pays spousal support, as a slot. 'payor' is the
          // child support payor's slot (A), 'recipient' is slot B.
          payor: this.ssagPayor === 'B' ? 'recipient' : 'payor',
          periodic: {
            monthlyAmount: periodicMonthly,
            durationYears: termYears,
            indefinite: indefinite,
          },
          lumpSum: {
            amount: lumpSum,
            midpoint: lumpSumMid,
            discountRate: 0.0181,
            // DIVII PATCH: the figure the lawyer chose to record, always a
            // positive dollar amount (`amount` above keeps upstream's sign,
            // negative for the payor's cost).
            basis: lumpBasis,
            chosen: lumpBasis === 'payorCost' ? Math.abs(lumpSum) : lumpSumMid,
          },
        },
      }
    },

    // Click handler for the "Record in agreement" button at the bottom of
    // each scenario column. Builds the payload and emits — LegalApp's parent
    // is responsible for confirmation, form writes, and re-render.
    // Open the record confirm dialog for a scenario. Defaults the mode to the
    // last choice (or periodic) so recording the same way is one extra click.
    openApplyDialog(scenario) {
      if (!this.canRecordProp) return
      this.applyDialogScenario = scenario
      const lastMode = this.hostTracksRecord ? (this.recordedProp && this.recordedProp.mode) : this.lastAppliedMode
      this.applyDialogMode = lastMode === 'lumpSum' ? 'lumpSum' : 'periodic'
      // DIVII PATCH: seed the term from the SSAG range and the column's
      // duration so the dialog describes the scenario being recorded (upstream
      // left the previous dialog's values in place).
      const r = this.ssagRange
      this.applyDialogIndefinite = !!(r && r.indefinite)
      this.applyDialogYears = Number(scenario && scenario.durationYears) || 0
      this.applyDialogLumpBasis = 'midpoint'
    },
    closeApplyDialog() {
      this.applyDialogScenario = null
    },
    // "Record in agreement": commit the dialog's selection.
    confirmApplyDialog() {
      if (!this.applyDialogScenario) return
      this.onApplyToAgreement(this.applyDialogScenario, this.applyDialogMode, {
        indefinite: this.applyDialogIndefinite,
        years: this.applyDialogYears,
        lumpBasis: this.applyDialogLumpBasis,
      })
      this.applyDialogScenario = null
    },
    onApplyToAgreement(scenario, mode = 'periodic', options = {}) {
      if (!this.canRecordProp) return
      const payload = this.buildApplyPayload(scenario, mode, options)
      if (!payload) return
      this.$emit('apply', payload)
      // Track which scenario + form was last applied so we can show the
      // "In agreement · monthly/lump sum" badge on this and future opens.
      // DIVII PATCH: only when no host owns the badge (see recordedProp): the
      // host confirms before writing, so a cancelled record must not flip it.
      if (!this.hostTracksRecord) {
        this.lastAppliedScenarioId = scenario ? scenario.id : 'child-support-only'
        this.lastAppliedMode = scenario ? mode : null
      }
    },
    // DIVII PATCH: record child support (and Section 7 shares, incomes) when
    // there is no spousal support claim and therefore no scenario table.
    onApplyChildSupportOnly() {
      this.onApplyToAgreement(null, 'periodic', {})
    },

    // Open the browser's print dialog with the printable worksheet
    // template. To guarantee the dev harness chrome (mode switcher,
    // test props) and the live calculator UI don't bleed into the
    // PDF, we clone the .print-worksheet element directly under
    // <body> and hide everything else via a body-level CSS class
    // for the duration of the print. On afterprint, we tear down
    // the clone and remove the class. This is more reliable than
    // pure visibility:hidden because clones live outside the hidden
    // ancestor tree, so they take their own space and start at the
    // top of the print area with no preceding empty layout boxes.
    printWorksheet() {
      const worksheet = this.$el && this.$el.querySelector('.print-worksheet')
      if (!worksheet) return

      // Clone the worksheet into a host node attached directly to body.
      // Cloning preserves the rendered Vue output (interpolated text,
      // SVG paths from piePath, etc.) without needing reactivity.
      const host = document.createElement('div')
      host.className = 'divii-print-host'
      const clone = worksheet.cloneNode(true)
      clone.classList.add('print-worksheet--active')
      host.appendChild(clone)
      document.body.appendChild(host)
      document.body.classList.add('divii-printing')

      const cleanup = () => {
        document.body.classList.remove('divii-printing')
        if (host.parentNode) host.parentNode.removeChild(host)
        window.removeEventListener('afterprint', cleanup)
      }
      window.addEventListener('afterprint', cleanup)

      // Brief tick gives the browser time to apply the body class
      // and lay out the clone before the print dialog opens.
      this.$nextTick(() => window.print())
    },

    // ═══════════════════════════════════════════════════════════════════════
    // LegalApp integration — State persistence (update-state event)
    // ═══════════════════════════════════════════════════════════════════════
    // Snapshot every calculator-owned field that affects computed outputs OR
    // that the lawyer would expect to come back after closing & reopening.
    // Excludes transient UI state (open picker, focused input) and constants.
    //
    // Shape is stable — LegalApp stores it as opaque JSON on the Matter
    // record. The hydrator (loadFromState) reads the same shape on open.
    computeFullState() {
      return {
        schemaVersion: 1,
        // Identity / context
        taxYear: this.taxYear,
        provinceLocal: this.provinceLocal,
        payorName: this.payorName,
        recipientName: this.recipientName,
        cohabitationDate: this.cohabitationDate,
        separationDate: this.separationDate,
        payorDOB: this.payorDOB,
        recipientDOB: this.recipientDOB,
        payorDisabled: this.payorDisabled,
        recipientDisabled: this.recipientDisabled,
        // Inputs
        incomeItems: JSON.parse(JSON.stringify(this.incomeItems || [])),
        children:    JSON.parse(JSON.stringify(this.children    || [])),
        cfaItems:    JSON.parse(JSON.stringify(this.cfaItems    || [])),
        // SSAG
        ssagApplicable: this.ssagApplicable,
        ssagPayor: this.ssagPayor,
        ssagSelected: this.ssagSelected,
        ssagSelectedDuration: this.ssagSelectedDuration,
        edcClaim: this.edcClaim,
        // Scenarios (user-overridden SS amounts / durations / custom columns)
        scenarios: JSON.parse(JSON.stringify(this.scenarios || [])),
        // UI preferences worth persisting (lawyer's chosen workflow)
        viewMode: this.viewMode,
        workspaceTab: this.workspaceTab,
        // Needs tab budgets + advisor answers (tabbed workspace)
        budgetLines: JSON.parse(JSON.stringify(this.budgetLines || {})),
        budgetCustom: JSON.parse(JSON.stringify(this.budgetCustom || {})),
        advisorAnswers: JSON.parse(JSON.stringify(this.advisorAnswers || {})),
        // Restructuring (SSAG Ch. 10) — persist so a modelled lump/monthly
        // split survives reopen; only meaningful once the lawyer opens it.
        showRestructure: this.showRestructure,
        restructureScenarioId: this.restructureScenarioId,
        restructureLumpSum: this.restructureLumpSum,
        restructureDurationYears: this.restructureDurationYears,
        restructureDiscountRate: this.restructureDiscountRate,
        // LegalApp integration metadata
        lastAppliedScenarioId: this.lastAppliedScenarioId,
        lastAppliedMode: this.lastAppliedMode,
        // Auto-payor — preserve the lawyer's explicit choice across reopens
        payorManuallyChosen: this.payorManuallyChosen,
        // Builder→calculator sync — remember the last dismissed diff so the
        // same offer isn't re-bannered every open (see runPropSync).
        syncDeclinedFingerprint: this.syncDeclinedFingerprint,
        // Persist payor/recipient orientation vs. the builder so a swap is
        // still respected by the sync engine after reopen (see runPropSync).
        builderOrientationSwapped: this.builderOrientationSwapped,
        // Case scenarios (named full-input snapshots). Lives alongside live
        // state; Divii's Matter record stores both so reopening restores the
        // last-active scenario along with the full list.
        caseScenarios: JSON.parse(JSON.stringify(this.caseScenarios || [])),
        activeCaseScenarioId: this.activeCaseScenarioId,
        // Party self-identification claims (who each party's entry surface
        // is locked to). NOT part of case scenarios — a claim is about the
        // agreement's users, not a what-if.
        partyClaims: JSON.parse(JSON.stringify(this.partyClaims || { A: null, B: null })),
      }
    },

    // Debounced emission of `update-state`. Watchers call this on every
    // change; the actual emit waits 400ms of quiet so we don't fire dozens
    // of events while the lawyer is typing a dollar amount. LegalApp's
    // listener writes the payload to the Matter record on each emit.
    emitStateUpdate() {
      // Skip during hydration to avoid echoing the parent's own initialState
      // back at it. _isHydrating flips true while loadFromState runs.
      if (this._isHydrating) return
      // NOTE: saved case scenarios are frozen snapshots — edits do NOT
      // auto-save into the active scenario (that silently clobbered the saved
      // copy). The user explicitly clicks "Save changes" (saveActiveScenario)
      // to update it, or "Save as new…" to fork. The live inputs still persist
      // on their own via computeFullState() below.
      if (this._stateUpdateTimer) clearTimeout(this._stateUpdateTimer)
      this._stateUpdateTimer = setTimeout(() => {
        this._stateUpdateTimer = null
        this.$emit('update-state', this.computeFullState())
      }, 400)
    },

    // Reset scroll after a wizard-step or wizard↔workspace switch so the new
    // page starts at the top. The calculator doesn't own its scroll container
    // (standalone dev scrolls the window; embedded in Divii's agreement view
    // an ancestor panel scrolls), so walk up from the root element and reset
    // every scrolled ancestor, then the window itself.
    scrollPageToTop() {
      this.$nextTick(() => {
        let node = this.$el && this.$el.parentElement
        while (node) {
          if (node.scrollTop) node.scrollTop = 0
          node = node.parentElement
        }
        if (typeof window !== 'undefined') window.scrollTo(0, 0)
      })
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Auto-payor — swap A ↔ B
    // ═══════════════════════════════════════════════════════════════════════
    // Swap everything keyed by 'A' / 'B' so the lawyer's calculator state
    // flips direction cleanly: payor becomes recipient, recipient becomes
    // payor. Used by the "Switch payor" link and (eventually) by an
    // accept-suggestion button on the auto-detection banner.
    //
    // Once invoked, payorManuallyChosen → true so the auto-detector stops
    // suggesting further swaps. The lawyer's choice is canonical from here
    // on (until they edit incomes/parenting again and explicitly re-run).
    switchPayor() {
      // Identity fields
      const tmpName = this.payorName
      this.payorName = this.recipientName
      this.recipientName = tmpName

      const tmpDOB = this.payorDOB
      this.payorDOB = this.recipientDOB
      this.recipientDOB = tmpDOB

      const tmpDis = this.payorDisabled
      this.payorDisabled = this.recipientDisabled
      this.recipientDisabled = tmpDis

      // Income items — flip partyId on every row
      this.incomeItems = this.incomeItems.map(it => ({
        ...it,
        partyId: it.partyId === 'A' ? 'B' : (it.partyId === 'B' ? 'A' : it.partyId),
      }))

      // Cash flow adjustments — same flip
      this.cfaItems = this.cfaItems.map(it => ({
        ...it,
        partyId: it.partyId === 'A' ? 'B' : (it.partyId === 'B' ? 'A' : it.partyId),
      }))

      // Children parenting — primaryA ↔ primaryB; shared stays as-is
      this.children = this.children.map(c => ({
        ...c,
        parenting: c.parenting === 'primaryA' ? 'primaryB'
                 : c.parenting === 'primaryB' ? 'primaryA'
                 : c.parenting,
      }))

      // SSAG payor designation. The data swap above moves the (new) payor into
      // slot A — slot A IS the payor slot — so spousal support is payable by A.
      // Set (don't flip) so the SS-page "Spousal Support Payable by" toggle stays
      // in sync with the income-page payor after a switch. (Flipping it here was a
      // bug: it left the SS direction pointing at the OLD payor, forcing the user
      // to re-toggle it on the Spousal Support page.)
      this.ssagPayor = 'A'

      // EDC claim override (only flips the explicit-slot values; 'none'
      // and null pass through)
      if (this.edcClaim === 'A') this.edcClaim = 'B'
      else if (this.edcClaim === 'B') this.edcClaim = 'A'

      // Party claims are keyed by slot (A/B), so they travel with the swap.
      const tmpClaim = this.partyClaims.A
      this.$set(this.partyClaims, 'A', this.partyClaims.B)
      this.$set(this.partyClaims, 'B', tmpClaim)

      // Mark that the lawyer has explicitly chosen — suppresses further
      // auto-detection suggestions.
      this.payorManuallyChosen = true

      // Orientation vs. builder party1/party2 is now reversed — toggle so the
      // sync engine re-orients incoming builder fields instead of offering the
      // swap back as a rename.
      this.builderOrientationSwapped = !this.builderOrientationSwapped
    },

    // Dismiss the auto-payor suggestion without swapping. Used when the
    // lawyer reviewed the banner and decided the current arrangement is
    // correct after all (e.g. imputed-income scenarios).
    dismissPayorSuggestion() {
      this.payorManuallyChosen = true
    },

    // Hydrate the calculator from a previously-saved state object. Inverse
    // of computeFullState — same shape, restored verbatim. The _isHydrating
    // flag prevents the watcher chain from echoing this back to LegalApp
    // as an update-state event during restore.
    //
    // Defensive: every field is checked for presence before assignment, so
    // partial states (e.g. from an earlier schemaVersion that lacked some
    // fields) still load cleanly with defaults filling the gaps.
    loadFromState(state) {
      if (!state || typeof state !== 'object') return
      this._isHydrating = true
      try {
        // Identity / context
        if (state.taxYear != null)        this.taxYear = state.taxYear
        if (state.provinceLocal != null)  this.provinceLocal = state.provinceLocal
        if (state.payorName != null)      this.payorName = state.payorName
        if (state.recipientName != null)  this.recipientName = state.recipientName
        if (state.cohabitationDate != null) this.cohabitationDate = state.cohabitationDate
        if (state.separationDate != null) this.separationDate = state.separationDate
        if (state.payorDOB != null)       this.payorDOB = state.payorDOB
        if (state.recipientDOB != null)   this.recipientDOB = state.recipientDOB
        if (state.payorDisabled != null)     this.payorDisabled = !!state.payorDisabled
        if (state.recipientDisabled != null) this.recipientDisabled = !!state.recipientDisabled
        // Inputs — deep-clone in case the parent reuses the object reference
        if (Array.isArray(state.incomeItems)) this.incomeItems = JSON.parse(JSON.stringify(state.incomeItems))
        if (Array.isArray(state.children))    this.children    = JSON.parse(JSON.stringify(state.children))
        if (Array.isArray(state.cfaItems))    this.cfaItems    = JSON.parse(JSON.stringify(state.cfaItems))
        // SSAG
        if (state.ssagApplicable != null) this.ssagApplicable = !!state.ssagApplicable
        if (state.ssagPayor != null)      this.ssagPayor = state.ssagPayor
        if (state.ssagSelected != null)   this.ssagSelected = state.ssagSelected
        if (state.ssagSelectedDuration != null) this.ssagSelectedDuration = state.ssagSelectedDuration
        if (state.edcClaim !== undefined) this.edcClaim = state.edcClaim
        // Scenarios (custom columns + overrides)
        if (Array.isArray(state.scenarios)) this.scenarios = JSON.parse(JSON.stringify(state.scenarios))
        // UI preferences
        if (state.viewMode != null)       this.viewMode = state.viewMode
        if (state.workspaceTab != null)   this.workspaceTab = state.workspaceTab
        // Budgets + advisor answers — same merge-onto-fresh strategy as
        // applyScenarioState (tolerates schedule/question changes between
        // save and reload).
        if (state.budgetLines && typeof state.budgetLines === 'object') {
          const fresh = makeBudgetLines()
          for (const k of Object.keys(fresh)) {
            if (state.budgetLines[k]) fresh[k] = { ...fresh[k], ...state.budgetLines[k] }
          }
          this.budgetLines = fresh
        }
        if (state.budgetCustom && typeof state.budgetCustom === 'object') {
          const fresh = makeBudgetCustom()
          for (const k of Object.keys(fresh)) {
            if (Array.isArray(state.budgetCustom[k])) fresh[k] = JSON.parse(JSON.stringify(state.budgetCustom[k]))
          }
          this.budgetCustom = fresh
        }
        if (state.advisorAnswers && typeof state.advisorAnswers === 'object') {
          const fresh = makeEmptyAnswers()
          for (const k of Object.keys(fresh)) {
            if (state.advisorAnswers[k] !== undefined) fresh[k] = state.advisorAnswers[k]
          }
          this.advisorAnswers = fresh
        }
        // Restructuring (SSAG Ch. 10)
        if (state.showRestructure != null)         this.showRestructure = state.showRestructure
        if (state.restructureScenarioId !== undefined) this.restructureScenarioId = state.restructureScenarioId
        if (state.restructureLumpSum != null)      this.restructureLumpSum = state.restructureLumpSum
        if (state.restructureDurationYears !== undefined) this.restructureDurationYears = state.restructureDurationYears
        if (state.restructureDiscountRate != null) this.restructureDiscountRate = state.restructureDiscountRate
        // Integration metadata
        if (state.lastAppliedScenarioId !== undefined) {
          this.lastAppliedScenarioId = state.lastAppliedScenarioId
        }
        if (state.lastAppliedMode !== undefined) {
          this.lastAppliedMode = state.lastAppliedMode
        }
        // Auto-payor — restore the lawyer's explicit choice
        if (state.payorManuallyChosen !== undefined) {
          this.payorManuallyChosen = !!state.payorManuallyChosen
        }
        // Builder→calculator sync — restore the dismissed-diff fingerprint.
        // Old blobs lack the key and simply keep the null default.
        if (state.syncDeclinedFingerprint !== undefined) {
          this.syncDeclinedFingerprint = state.syncDeclinedFingerprint
        }
        // Restore payor/recipient orientation vs. the builder. Old blobs lack
        // the key and keep the false default (the exact-name cross-match in
        // computePropSyncPlan still covers those pre-flag swapped states).
        if (state.builderOrientationSwapped !== undefined) {
          this.builderOrientationSwapped = !!state.builderOrientationSwapped
        }
        // Party self-identification claims
        if (state.partyClaims && typeof state.partyClaims === 'object') {
          this.partyClaims = {
            A: state.partyClaims.A || null,
            B: state.partyClaims.B || null,
          }
        }
        // Case scenarios — restore the full list + active selection.
        if (Array.isArray(state.caseScenarios)) {
          this.caseScenarios = JSON.parse(JSON.stringify(state.caseScenarios))
        }
        if (state.activeCaseScenarioId !== undefined) {
          this.activeCaseScenarioId = state.activeCaseScenarioId
        }
      } finally {
        // Release the guard on next tick so any reactivity-triggered watchers
        // queued by the assignments above all see _isHydrating=true and skip
        // their emit. Anything the lawyer changes AFTER hydration emits normally.
        this.$nextTick(() => { this._isHydrating = false })
      }
    },
    // ═══════════════════════════════════════════════════════════════════════
    // Builder→calculator sync (Divii) — silent fill + conflict banner
    // ═══════════════════════════════════════════════════════════════════════
    // NOTE(sync-reconciliation): implemented directly in this Divii copy while
    // the source repo (D:\aot\support-calculator) carries an unshipped
    // disability/DTC version. Before the next verbatim drop from the source
    // repo, port over: scrollPageToTop + step/viewMode scroll watchers, the
    // restructure empty-state panel, "Federal Tables" wording, and this sync
    // engine (syncDeclinedFingerprint/syncConflicts data fields, the
    // computeFullState/loadFromState lines, the created() hook, these methods,
    // the .sync-banner template + CSS, and the `sourceId` passthrough in the
    // two children whitelist mappings — data() seeding + childrenProp watcher).
    //
    // On open, the hydrated saved state has overwritten the prop-seeded
    // identity fields. These methods diff the FRESH props against that state:
    //   • blank state value + present prop value → applied silently
    //   • differing non-blank values             → offered via the sync banner
    // Only identity fields participate: payorName, recipientName, payorDOB,
    // recipientDOB, cohabitationDate, separationDate, children name/dob.
    // Incomes, scenarios, parenting, s7 expenses and SSAG choices are never
    // touched.
    syncBlank(v) {
      return v == null || String(v).trim() === ''
    },
    syncNorm(v) {
      return String(v == null ? '' : v).trim().replace(/\s+/g, ' ').toLowerCase()
    },

    computePropSyncPlan() {
      const silent = []
      const conflicts = []
      const mc = this.matterContext || {}
      // Same precedence as data() seeding: matterContext > individual prop.
      const pick = (mcKey, propVal) => {
        if (mc[mcKey] !== undefined && mc[mcKey] !== null && mc[mcKey] !== '') return mc[mcKey]
        if (propVal !== undefined && propVal !== null && propVal !== '') return propVal
        return null
      }
      const incoming = {
        payorName: pick('party1Name', this.payorNameProp),
        recipientName: pick('party2Name', this.recipientNameProp),
        payorDOB: pick('party1DOB', this.payorDOBProp),
        recipientDOB: pick('party2DOB', this.recipientDOBProp),
        cohabitationDate: pick('cohabitationDate', this.cohabitationDateProp),
        separationDate: pick('separationDate', this.separationDateProp),
        children: (mc.children && mc.children.length) ? mc.children : (this.childrenProp || []),
      }

      // Swap awareness: after "Switch payor" / "Swap payor & recipient" the
      // state's payor/recipient are legitimately reversed relative to the
      // builder's party1/party2. Re-orient the incoming payor/recipient fields
      // before diffing so the swap is respected rather than offered back as a
      // rename. Two independent signals trigger the re-orientation:
      //   1. builderOrientationSwapped — the persisted orientation flag,
      //      toggled by every explicit swap. Authoritative and robust even
      //      when a party was ALSO renamed in the builder (which defeats the
      //      name-matching below). This is the primary signal.
      //   2. Exact cross-wise name match — a stateless fallback that also
      //      covers states saved before the flag existed (flag defaults false).
      const crossNameMatch =
          !this.syncBlank(this.payorName) && !this.syncBlank(this.recipientName) &&
          !this.syncBlank(incoming.payorName) && !this.syncBlank(incoming.recipientName) &&
          this.syncNorm(this.payorName) === this.syncNorm(incoming.recipientName) &&
          this.syncNorm(this.recipientName) === this.syncNorm(incoming.payorName)
      if (this.builderOrientationSwapped || crossNameMatch) {
        const tmpName = incoming.payorName
        incoming.payorName = incoming.recipientName
        incoming.recipientName = tmpName
        const tmpDOB = incoming.payorDOB
        incoming.payorDOB = incoming.recipientDOB
        incoming.recipientDOB = tmpDOB
      }

      const scalar = (key, label, current, incomingVal, assign, normed) => {
        if (this.syncBlank(incomingVal)) return
        const cur = String(current == null ? '' : current).trim()
        const inc = String(incomingVal).trim()
        if (this.syncBlank(cur)) {
          silent.push({ key, label: `${label}: '${inc}'`, apply: () => assign(inc) })
        } else if (normed ? this.syncNorm(cur) !== this.syncNorm(inc) : cur !== inc) {
          conflicts.push({ key, label: `${label}: '${cur}' → '${inc}'`, apply: () => assign(inc) })
        }
      }
      scalar('payorName', 'Payor name', this.payorName, incoming.payorName, v => { this.payorName = v }, true)
      scalar('recipientName', 'Recipient name', this.recipientName, incoming.recipientName, v => { this.recipientName = v }, true)
      scalar('payorDOB', 'Payor date of birth', this.payorDOB, incoming.payorDOB, v => { this.payorDOB = v }, false)
      scalar('recipientDOB', 'Recipient date of birth', this.recipientDOB, incoming.recipientDOB, v => { this.recipientDOB = v }, false)
      scalar('cohabitationDate', 'Cohabitation date', this.cohabitationDate, incoming.cohabitationDate, v => { this.cohabitationDate = v }, false)
      scalar('separationDate', 'Separation date', this.separationDate, incoming.separationDate, v => { this.separationDate = v }, false)

      // Children — matched by stable builder id ONLY (inc.id ↔ st.sourceId).
      // Ids are generated/backfilled by ChildInformation in the builder and
      // persisted by its 1s debounced autosave, so children carry ids before
      // the calculator ever opens. Renames and DOB edits (even both at once)
      // therefore always land on the right child — no fuzzy matching.
      //   • Incoming children WITHOUT an id are skipped entirely — only
      //     possible when the matter hasn't been opened in the builder since
      //     ids were introduced; the next builder visit self-heals it.
      //   • One exact-identity bootstrap: an unmatched incoming child whose
      //     name AND dob are identical to an unclaimed sourceId-less state
      //     child is that same child, seeded before its id existed — stamp
      //     the id silently rather than duplicating the row.
      //   • Otherwise unmatched incoming children are ADDED — silently when
      //     every unclaimed state child already carries a sourceId (no
      //     duplication risk), via the banner when sourceId-less state
      //     children exist (the add could be a disguised duplicate; the
      //     lawyer decides).
      //   • Unmatched STATE children are never deleted and never bannered —
      //     they may be manual what-if entries.
      const stateKids = this.children || []
      const claimed = new Set()
      // Diff one matched (state child, incoming child) pair — blank state
      // fields fill silently, differing non-blank fields become conflicts.
      const diffChildFields = (st, inc, idx) => {
        const incName = String(inc.name || '').trim()
        const incDob = String(inc.dob || '').trim()
        const ckey = incDob || this.syncNorm(incName) || String(idx)
        if (incName) {
          const cur = String(st.name || '').trim()
          if (this.syncBlank(cur)) {
            silent.push({ key: `child:${ckey}:name`, label: `Child name: '${incName}'`, apply: () => { st.name = incName } })
          } else if (this.syncNorm(cur) !== this.syncNorm(incName)) {
            conflicts.push({ key: `child:${ckey}:name`, label: `Child name: '${cur}' → '${incName}'`, apply: () => { st.name = incName } })
          }
        }
        if (incDob) {
          const cur = String(st.dob || '').trim()
          if (this.syncBlank(cur)) {
            silent.push({ key: `child:${ckey}:dob`, label: `Child '${incName}' date of birth: '${incDob}'`, apply: () => { st.dob = incDob } })
          } else if (cur !== incDob) {
            conflicts.push({ key: `child:${ckey}:dob`, label: `Child '${st.name || incName}' date of birth: '${cur}' → '${incDob}'`, apply: () => { st.dob = incDob } })
          }
        }
        // DIVII PATCH: the builder's Eligible Dependant Credit answer fills a
        // shared child still on Auto, silently. An explicit calculator choice
        // (a parent or Neither) always wins, so this never raises a conflict.
        const incClaim = edcSlotFromBuilderClaim(inc.edcClaimedByPartner, st.parenting, this.builderOrientationSwapped)
        if (incClaim && st.edcClaimedBy == null) {
          silent.push({
            key: `child:${ckey}:edc`,
            label: `Child '${st.name || incName}' dependant credit claim from the builder`,
            apply: () => { st.edcClaimedBy = incClaim },
          })
        }
      }

      // Match by id (with the exact-identity bootstrap for pre-id states).
      const unmatchedInc = []
      ;(incoming.children || []).forEach((inc, idx) => {
        const incName = String(inc.name || '').trim()
        const incDob = String(inc.dob || '').trim()
        if (!incName && !incDob) return
        const incId = inc.id != null ? String(inc.id) : ''
        if (!incId) return // pre-id builder child — no reliable identity, skip
        let mi = -1
        for (let i = 0; i < stateKids.length; i++) {
          if (claimed.has(i)) continue
          if (stateKids[i].sourceId != null && String(stateKids[i].sourceId) === incId) { mi = i; break }
        }
        if (mi === -1) {
          // Exact-identity bootstrap: identical name AND dob on an unclaimed
          // sourceId-less state child → same child seeded pre-id; stamp it.
          for (let i = 0; i < stateKids.length; i++) {
            if (claimed.has(i)) continue
            if (stateKids[i].sourceId != null) continue
            if (this.syncNorm(stateKids[i].name) !== this.syncNorm(incName)) continue
            if (String(stateKids[i].dob || '').trim() !== incDob) continue
            mi = i
            const st = stateKids[i]
            silent.push({
              key: `child:${incId}:stamp`,
              label: 'link child id',
              apply: () => { this.$set(st, 'sourceId', inc.id) },
            })
            break
          }
        }
        if (mi !== -1) {
          claimed.add(mi)
          diffChildFields(stateKids[mi], inc, idx)
        } else {
          unmatchedInc.push({ inc, idx })
        }
      })

      // Unmatched incoming children are genuinely new — unless sourceId-less
      // state children linger (pre-id seeding), in which case an add could be
      // a disguised duplicate and needs the lawyer's consent via the banner.
      const hasUnstampedLeftovers = stateKids.some((c, i) => !claimed.has(i) && c.sourceId == null)
      unmatchedInc.forEach(({ inc, idx }) => {
        const incName = String(inc.name || '').trim()
        const incDob = String(inc.dob || '').trim()
        const ckey = incDob || this.syncNorm(incName) || String(idx)
        const entry = {
          key: `child:${ckey}:add`,
          label: `Add child '${incName || incDob}'`,
          apply: () => {
            // Same field whitelist data()/the childrenProp watcher use.
            this.children.push({
              sourceId: inc.id != null ? inc.id : null,
              name: incName,
              dob: incDob,
              parenting: inc.parenting || 'primaryB',
              isDependent: inc.isDependent != null ? inc.isDependent : null,
              subjectToTable: null,
              // DIVII PATCH: builder claim seeds the new shared child.
              edcClaimedBy: edcSlotFromBuilderClaim(inc.edcClaimedByPartner, inc.parenting || 'primaryB', this.builderOrientationSwapped),
              childAmountBy: null,
              disabled: false,
              s7expenses: [],
            })
          },
        }
        if (hasUnstampedLeftovers) conflicts.push(entry)
        else silent.push(entry)
      })

      return { silent, conflicts }
    },

    // Stable across opens: same conflicting fields + values → same string.
    // Labels embed both the current and incoming values, so any change on
    // either side produces a new fingerprint (and re-banners after a dismiss).
    syncFingerprintFor(changes) {
      return JSON.stringify(changes.map(c => c.key + '=' + c.label).sort())
    },

    runPropSync() {
      const plan = this.computePropSyncPlan()
      // Silent fills: nothing user-entered can be overwritten, so no consent
      // needed. The field watchers persist them via emitStateUpdate.
      plan.silent.forEach(ch => ch.apply())
      if (!plan.conflicts.length) return
      const fp = this.syncFingerprintFor(plan.conflicts)
      if (fp === this.syncDeclinedFingerprint) return
      this.syncConflicts = { fingerprint: fp, changes: plan.conflicts }
    },
    applySyncChanges() {
      if (!this.syncConflicts) return
      this.syncConflicts.changes.forEach(ch => ch.apply())
      this.syncConflicts = null
      this.syncDeclinedFingerprint = null
    },
    dismissSyncBanner() {
      if (!this.syncConflicts) return
      this.syncDeclinedFingerprint = this.syncConflicts.fingerprint
      this.syncConflicts = null
    },

    filteredTypesByCategory(cat) {
      return this.filteredTypesAll.filter(t => t.category === cat)
    },
    onDocClick(e) {
      if (!this.openPickerFor) return
      // Close if click is not inside any picker-related element
      const el = e.target.closest('.picker-popover, .btn-add-income')
      if (!el) this.openPickerFor = null
    },

    // ── SSAG range "50/50 NDI" rule ───────────────────────────────
    // SSAG Ch. 12 — the SSAG range is bounded by the SS amount that produces
    // a 50/50 NDI split. The rule applies asymmetrically:
    //   • Without-child: BOTH low and high are CAPPED at ss5050 — the entire
    //     range collapses toward 50/50 if the formula would push past it.
    //   • With-child: only the HIGH end is EXTENDED up to ss5050 if the
    //     formula gives less; the low end is left at the formula's value.
    //
    // SSAG's "NDI" for this rule EXCLUDES Cash Flow Adjustments — DM applies
    // the cap on a CFA-restored basis. We add CFA back to each party's NDI
    // before computing the share, then bisect over SS for the 50/50 point.
    //
    // Calibrated against DM Tools Cloud 2026:
    //   • Cunningham (no kids, CFA $14,400): 1958/2285/2611 → 1958/2285/2463 (cap high)
    //   • Diachina (adult-only): 4799/5599/6399 → 4799/5599/6274 (cap high)
    //   • Campo After Uni: 17380/20280/23174 → 14644/14644/14644 (cap both)
    //   • Frewing (with-child): formula high ~1078 → 1341 (extend high)
    findSs5050() {
      const payorIsA = this.ssagPayor === 'A'
      const cfaPerMonthA = (this.payorCFAAnnual || 0) / 12
      const cfaPerMonthB = (this.recipientCFAAnnual || 0) / 12
      const recipShareAt = (ss) => {
        const b = this.computeNDIBreakdown(ss)
        const aNDI = b.a.ndiM + cfaPerMonthA
        const bNDI = b.b.ndiM + cfaPerMonthB
        const total = aNDI + bNDI
        if (total <= 0) return 0
        const recipNDI = payorIsA ? bNDI : aNDI
        return recipNDI / total
      }
      // Sanity: recipient already at/above 50% with no SS — return null.
      if (recipShareAt(0) >= 0.5) return null
      // Upper bound: payor's gross monthly income. Beyond this the math
      // (negative payor NDI, extreme tax-on-recipient) becomes nonsense
      // and our share-calc is unreliable. At hi = payor_gross, recipient
      // share approaches 100% (payor has zero NDI, recipient gets all).
      const hiCap = (this.payorIncome || 0) / 12
      if (hiCap <= 0) return null
      // If even at the upper bound the share doesn't exceed 50%, no 50/50
      // split is reachable within meaningful SS amounts.
      if (recipShareAt(hiCap) < 0.5) return null
      let lo = 0, hi = hiCap
      for (let i = 0; i < 35; i++) {
        const mid = (lo + hi) / 2
        if (recipShareAt(mid) > 0.5) hi = mid
        else lo = mid
        if (hi - lo < 0.5) break
      }
      return Math.round((lo + hi) / 2)
    },

    // ── Scenarios (Phase B+) ──────────────────────────────────────
    // Compute the per-party monthly NDI breakdown at a given SS amount.
    // Mirrors Divorcemate's "Support Scenarios" table: same row labels for
    // both parties, with signs flipped for SS/CS based on payment direction.
    computeNDIBreakdown(ssMonthly) {
      const ss = ssMonthly || 0           // monthly
      const cs = this.netCS || 0          // monthly
      const ssAnnual = ss * 12
      const csAnnual = cs * 12
      const ssPayerIsA = this.ssagPayor === 'A'
      const csPayerIsA = this.netCSPayer === this.payorName

      // ─── Actual tax delta from SS deduction/inclusion ───
      // Prior version used marginal_rate × deduction, which understates tax
      // saving when the SS deduction crosses a bracket boundary (the lower
      // dollars sit in a lower bracket than the marginal rate). We now compute
      // the real tax delta:
      //   payor tax saving = Tax(income) − Tax(income − SS)
      //   recipient tax cost = Tax(income + SS) − Tax(income)
      // Credits are held constant (BPA, CEA, EDC, etc. apply at the new
      // taxable level, but their dollar value doesn't change for SS-sized
      // shifts in income). This matches Divorcemate's approach.
      const prov = this.effectiveProvince
      const taxAt = (party, deltaTaxable) => {
        const bundle = party === 'A' ? this.payorTaxBundle : this.recipientTaxBundle
        const newTaxable = Math.max(0, bundle.taxable + deltaTaxable)
        // Per-level clamped recomputation (credits, reductions, ON extras all
        // re-evaluated at the SS-adjusted taxable; fed and prov floor at zero
        // separately — matches DM's per-level clamping).
        if (bundle.taxPayableAt) return bundle.taxPayableAt(newTaxable)
        const newRawTax = getRawIncomeTax(prov, newTaxable, this.taxYearCfg)
        const newCred = bundle.computeCreditsAt
          ? bundle.computeCreditsAt(newTaxable)
          : bundle.totalCred
        const newOnExtra = bundle.computeOntarioExtraAt
          ? bundle.computeOntarioExtraAt(newTaxable)
          : 0
        const newProvRed = bundle.computeProvReductionAt
          ? bundle.computeProvReductionAt(newTaxable)
          : 0
        return Math.max(0, newRawTax - newCred - newProvRed) + newOnExtra
      }
      const baseTaxA = this.payorTaxBundle.taxPayable
      const baseTaxB = this.recipientTaxBundle.taxPayable
      const newTaxA = taxAt('A', ssPayerIsA ? -ssAnnual : +ssAnnual)
      const newTaxB = taxAt('B', ssPayerIsA ? +ssAnnual : -ssAnnual)

      const ssTaxSavingA = ssPayerIsA ? Math.max(0, baseTaxA - newTaxA) : 0
      const ssTaxSavingB = ssPayerIsA ? 0 : Math.max(0, baseTaxB - newTaxB)
      const ssTaxCostA   = ssPayerIsA ? 0 : Math.max(0, newTaxA - baseTaxA)
      const ssTaxCostB   = ssPayerIsA ? Math.max(0, newTaxB - baseTaxB) : 0

      // Refundable benefits at each party's SCENARIO income level — SS shifts
      // line 23600 between parties, so phase-outs (CWB, CGEB, BC Climate/Sales,
      // BC Family Benefit, CCB) must be evaluated at post-SS income. DM Tools
      // Cloud 2026 recomputes these per scenario; matching that.
      const scenAfniA = Math.max(0, this.payorTaxBundle.taxable + (ssPayerIsA ? -ssAnnual : +ssAnnual))
      const scenAfniB = Math.max(0, this.recipientTaxBundle.taxable + (ssPayerIsA ? +ssAnnual : -ssAnnual))
      // Benefits phase on NET income (line 23600): add back WC/social
      // assistance, which the taxable proxy has already deducted.
      const netAddA = netIncomeAddback(this.payorItems)
      const netAddB = netIncomeAddback(this.recipientItems)
      // s.7 claimed amounts per party — childcare (deduction), medical
      // (credit with the CRA floor), post-secondary tuition transfer (credit).
      // These drive DM's "Change in Taxes" and "Change in Benefits" columns.
      const s7cc = this.onCareChildcareClaimed
      const sumClaimed = cats => {
        let a = 0, b = 0
        for (const child of this.includedChildren) {
          for (const exp of (child.s7expenses || [])) {
            if (!cats.includes(exp.category)) continue
            a += exp.claimedByA || 0
            b += exp.claimedByB || 0
          }
        }
        return { a, b }
      }
      const s7med = sumClaimed(['medical_insurance', 'health'])
      const s7tui = sumClaimed(['postsecondary'])
      const benOptsA = {
        workingIncome: this.payorWorkingIncome,
        children: this.payorHasEligibleDependant ? this.ssagPayorChildren : this.ssagRecipientChildren,
        isSingle: this.payorPartnerIncome == null,
        isPrimaryParent: this.payorHasEligibleDependant || this.ssagPayorChildren.length > 0,
        hasEDC: this.payorHasEligibleDependant,
        cfg: this.taxYearCfg,
      }
      const benOptsB = {
        workingIncome: this.recipientWorkingIncome,
        children: this.recipientHasEligibleDependant ? this.ssagRecipientChildren : this.ssagPayorChildren,
        isSingle: this.recipientPartnerIncome == null,
        isPrimaryParent: this.recipientHasEligibleDependant || this.ssagRecipientChildren.length > 0,
        hasEDC: this.recipientHasEligibleDependant,
        cfg: this.taxYearCfg,
      }
      const benANoCare = computeRefundableBenefits(prov, { ...benOptsA, afni: scenAfniA + netAddA })
      const benBNoCare = computeRefundableBenefits(prov, { ...benOptsB, afni: scenAfniB + netAddB })
      // DM "Change in Benefits": the childcare deduction lowers the claiming
      // party's line 23600, so their income-tested benefits RISE. Folded into
      // the benefits row (like DM) and netted out of the s.7 settlement below.
      const benAAfterCc = s7cc.a > 0
        ? computeRefundableBenefits(prov, { ...benOptsA, afni: Math.max(0, scenAfniA + netAddA - s7cc.a) })
        : benANoCare
      const benBAfterCc = s7cc.b > 0
        ? computeRefundableBenefits(prov, { ...benOptsB, afni: Math.max(0, scenAfniB + netAddB - s7cc.b) })
        : benBNoCare
      const s7BenUpliftA = Math.max(0, benAAfterCc - benANoCare)
      const s7BenUpliftB = Math.max(0, benBAfterCc - benBNoCare)

      // Ontario CARE credit (refundable) — on top of the child-care DEDUCTION
      // (already in the tax line via s7TaxBenefit), ON adds a refundable credit
      // = rate(adjusted family income) × claimed child-care. Booked into the
      // party's benefits line at their SCENARIO net income (rate rises as SS
      // lowers the payor's income / a phase-out DM recomputes per scenario).
      // ON-only; scenAfni is the party's line-23600 proxy (computeONCARECredit
      // adds the child-care back to form adjusted family income). DM books this
      // in the tax section as a refund — same NDI effect as a benefit.
      let careA = 0, careB = 0
      if (prov === 'ON') {
        const claimed = this.onCareChildcareClaimed
        careA = computeONCARECredit(scenAfniA + netAddA, claimed.a)
        careB = computeONCARECredit(scenAfniB + netAddB, claimed.b)
      }
      const benA = benAAfterCc + careA
      const benB = benBAfterCc + careB

      // Per-party NDI computation
      // Effective tax line:
      //   baseline tax (income only)
      //   + SS tax cost (if receiving) − SS tax saving (if paying)
      //   − s.7 tax benefit (claiming party's deduction reduces their tax)
      // s.7 "Change in Taxes" per scenario (DM-verified live, 2026-09-24):
      // childcare is a REAL line 21400 deduction recomputed at the party's
      // post-SS taxable income (brackets, credits and provincial reductions
      // re-evaluate, which is why DM's values are not a flat marginal rate);
      // medical is the credit net of the CRA floor (lesser of 3% of net
      // income or the year cap); the post-secondary tuition transfer credits
      // at the lowest fed+prov rates (user inputs the transferable amount,
      // max 5,000/child).
      const ssDeltaA = ssPayerIsA ? -ssAnnual : +ssAnnual
      const ssDeltaB = ssPayerIsA ? +ssAnnual : -ssAnnual
      const ccTaxSavA = s7cc.a > 0 ? Math.max(0, newTaxA - taxAt('A', ssDeltaA - s7cc.a)) : 0
      const ccTaxSavB = s7cc.b > 0 ? Math.max(0, newTaxB - taxAt('B', ssDeltaB - s7cc.b)) : 0
      const medFloorCap = this.taxYearCfg.medicalFloor || 2891
      const medCredOf = (claimed, netInc) => claimed > 0
        ? Math.max(0, claimed - Math.min(0.03 * Math.max(0, netInc), medFloorCap)) * MEDICAL_CREDIT_RATE
        : 0
      const medCredA = medCredOf(s7med.a, scenAfniA + netAddA)
      const medCredB = medCredOf(s7med.b, scenAfniB + netAddB)
      const s7LowRate = (this.taxYearCfg.fedLowestRate || 0.14)
        + (((this.taxYearCfg.provTaxData || {})[prov] || {}).rate || 0.05)
      const tuiCredA = (s7tui.a || 0) * s7LowRate
      const tuiCredB = (s7tui.b || 0) * s7LowRate
      const s7Act = this.s7ActivityCreditsAnnual
      const s7TaxBenA = ccTaxSavA + medCredA + tuiCredA + s7Act.a
      const s7TaxBenB = ccTaxSavB + medCredB + tuiCredB + s7Act.b
      // Row total: tax at post-SS income (base + SS cost − SS saving) minus
      // the party's s.7 tax offsets, plus source deductions.
      const taxA = Math.max(0, newTaxA - s7TaxBenA)
        + this.payorTaxBundle.cpp + this.payorTaxBundle.ei
      const taxB = Math.max(0, newTaxB - s7TaxBenB)
        + this.recipientTaxBundle.cpp + this.recipientTaxBundle.ei
      const ssA = ssPayerIsA ? -ssAnnual : +ssAnnual
      const ssB = ssPayerIsA ? +ssAnnual : -ssAnnual
      const csA = csPayerIsA ? -csAnnual : +csAnnual
      const csB = csPayerIsA ? +csAnnual : -csAnnual
      // Cash Flow Adjustments (SSAG Ch. 6.6) — post-tax outflows that decrease
      // NDI but don't affect taxable income. Subtracted directly from each
      // party's NDI line. (Already factored into payorNetCashFlow / recipient-
      // NetCashFlow used by ssagRange's INDI solver above.)
      const cfaA = this.payorCFAAnnual
      const cfaB = this.recipientCFAAnnual
      // Use ACTUAL CASH inflow (not guidelines income), since for non-taxable
      // income with gross-up, guidelines includes the synthetic gross-up amount
      // but the real cash-in-hand is just the entered amount. NDI must reflect
      // the dollars that hit the bank account.
      const cashInA = aggregateCashIn(this.payorItems)
      const cashInB = aggregateCashIn(this.recipientItems)
      // Social assistance clawback (spousal-only variant): assistance programs
      // deduct spousal support received dollar-for-dollar, so the receiving
      // party keeps only max(0, SA − SS). Modelled as a cash-flow reduction of
      // min(SS received, SA) on the SS receiver's NDI — DM books this in the
      // "Cash Flow Adjustments" row. (The clawback-both variant is already
      // excluded from NDI entirely via indi: 'tax_only'.) Verified vs DM
      // SocialAssistCPPDis (NT): CFA −$1,000/mo at every scenario = full
      // $12,000 SA clawed back once SS ≥ $1,000/mo.
      const saOf = (items) => (items || []).reduce(
        (s, it) => s + (it.typeId === 'social_assistance_clawback_spousal' ? (it.amount || 0) : 0), 0)
      const saClawA = !ssPayerIsA ? Math.min(ssAnnual, saOf(this.payorItems)) : 0
      const saClawB =  ssPayerIsA ? Math.min(ssAnnual, saOf(this.recipientItems)) : 0

      // ─── s.7 special-and-extraordinary expense apportionment ───
      // Federal CSG s.7(2): expenses are shared in proportion to each parent's
      // income after adjusting for spousal support paid/received (since SS shifts
      // taxable income for s.7 purposes).
      const s7IncA = Math.max(0, this.payorIncome     + (ssPayerIsA ? -ssAnnual : +ssAnnual))
      const s7IncB = Math.max(0, this.recipientIncome + (ssPayerIsA ? +ssAnnual : -ssAnnual))
      const s7Total = s7IncA + s7IncB
      const s7PctA = s7Total > 0 ? (s7IncA / s7Total) * 100 : 50
      const s7PctB = s7Total > 0 ? (s7IncB / s7Total) * 100 : 50

      // s.7 obligation per party — DivorceMate two-row model:
      //
      //   Special Expenses (s.7) = each party's actual cash outflow
      //                            (paidByA, paidByB) — what left
      //                            their bank account.
      //   Child Support (s.7 Payment) = settlement between parties
      //                            (what they paid − their
      //                            apportioned share of total gross
      //                            paid). Sums to zero — one party's
      //                            credit is the other's debit.
      //
      // Apportion the NET cost (gross − tax benefits) per CSG s.7(2):
      // "The expenses, less the contributions, if any, from the child and
      // less any subsidies, benefits or income tax deductions or credits
      // relating to the expense" — net cost is what gets apportioned.
      //
      // Apportionment uses SS-ADJUSTED percentages: each party's
      // apportioning income = guidelines income − SS paid (+ SS received).
      // DM Lewis-Shaw PDF "Net Special Expenses" line confirms gross is
      // reduced by Change in Taxes + Change in Benefits before apportioning.
      //
      // For Lewis-Shaw (extraordinary education, no tax benefit) gross =
      // net = $93,400, so the change is invisible. For Nicolson-Stewart
      // (child care, $1,441 tax benefit), net = $3,669 not $5,110 — the
      // ~$167 ssagHigh drift collapses with this fix.
      const s7PaidA       = this.s7PaidByAAnnual || 0
      const s7PaidB       = this.s7PaidByBAnnual || 0
      const s7TotalPaid   = s7PaidA + s7PaidB
      // DM "Net Special Expenses" = gross − Change in Taxes − Change in
      // Benefits (both parties' offsets), verified live 2026-09-24.
      const s7OffsetA     = s7TaxBenA + s7BenUpliftA
      const s7OffsetB     = s7TaxBenB + s7BenUpliftB
      const s7TotalNet    = Math.max(0, s7TotalPaid - s7OffsetA - s7OffsetB)
      const s7ShareA      = s7TotalNet * s7PctA / 100
      const s7ShareB      = s7TotalNet * s7PctB / 100
      // Settlement: party's actual contribution = cash paid − own offsets;
      // they overpay relative to their (net cost × share) obligation.
      // Settlements always sum to zero by construction.
      const s7SettlementA = s7PaidA - s7OffsetA - s7ShareA
      const s7SettlementB = s7PaidB - s7OffsetB - s7ShareB

      // NDI flow per party: cash outflow on the s.7 line, then the
      // settlement adds back (or subtracts further). Net effect on
      // NDI is the party's share of the net cost.
      //   ndi = … − s7PaidX + s7SettlementX
      //       = … − s7PaidX + (s7PaidX − s7ShareX)
      //       = … − s7ShareX  (same NDI as the prior model)
      const ndiA = cashInA - taxA + benA + ssA + csA - cfaA - saClawA - s7PaidA + s7SettlementA
      const ndiB = cashInB - taxB + benB + ssB + csB - cfaB - saClawB - s7PaidB + s7SettlementB
      const total = ndiA + ndiB

      // ─── After-tax cost / benefit of spousal support ───
      // Net out-of-pocket effect after the actual tax delta computed above.
      //   Payor:    cost   = -(SS paid − tax saving)         [negative]
      //   Recipient: benefit = +(SS received − tax cost)     [positive]
      // Canada Workers Benefit delta. DM books the CWB in the "Taxes and
      // Deductions" line (it's a refundable credit on the return), NOT in the
      // "Benefits and Credits" line where CCB/GST/provincial benefits sit. So
      // the after-tax cost/benefit of support must include the CWB phase-out:
      // a low-income recipient loses CWB as support raises their income (real
      // cost), while CCB/GST clawback does NOT enter this line. Verified vs DM
      // Hughes-Peslova (recipient TD swings −130→+249 as CWB phases out) and
      // angus (Colin has no CWB → no adjustment, matches with tax only).
      // (CWB is still counted once, in the benefits line, for NDI — this delta
      // only re-attributes it in the aftTax display row.)
      const cwbDeltaFor = (workIncome, hasKids, scenAfni, baseAfni) =>
        computeCWB(prov, workIncome, scenAfni, hasKids, this.taxYearCfg) - computeCWB(prov, workIncome, baseAfni, hasKids, this.taxYearCfg)
      const hasKidsA = (this.payorHasEligibleDependant ? this.ssagPayorChildren : this.ssagRecipientChildren).length > 0
        && (this.payorHasEligibleDependant || this.ssagPayorChildren.length > 0)
      const hasKidsB = (this.recipientHasEligibleDependant ? this.ssagRecipientChildren : this.ssagPayorChildren).length > 0
        && (this.recipientHasEligibleDependant || this.ssagRecipientChildren.length > 0)
      const cwbDeltaA = cwbDeltaFor(this.payorWorkingIncome, hasKidsA, scenAfniA + netAddA, this.payorTaxBundle.taxable + netAddA)
      const cwbDeltaB = cwbDeltaFor(this.recipientWorkingIncome, hasKidsB, scenAfniB + netAddB, this.recipientTaxBundle.taxable + netAddB)
      const ssAfterTaxA = (ssPayerIsA
        ? -(ssAnnual - ssTaxSavingA)   // payor: paid out, less actual tax savings
        :  (ssAnnual - ssTaxCostA))    // recipient: received, less actual tax owed
        + cwbDeltaA
      const ssAfterTaxB = (ssPayerIsA
        ?  (ssAnnual - ssTaxCostB)
        : -(ssAnnual - ssTaxSavingB))
        + cwbDeltaB

      const m = (n) => Math.round(n / 12) // annual to monthly, rounded

      return {
        ssMonthly: ss,
        csMonthly: cs,
        a: {
          name: this.payorName,
          grossM:    m(this.payorIncome),
          taxesM:    m(taxA),
          benefitsM: m(benA),
          ssM:       m(ssA),       // signed: negative if paying, positive if receiving
          csM:       m(csA),
          cfaM:      m(cfaA),      // Cash Flow Adjustments (DM row)
          s7M:       m(s7ShareA), // per-scenario s.7 monthly obligation (legacy alias)
          s7PaidM:        m(s7PaidA),       // cash outflow — DM "Special Expenses (s.7)"
          s7SettlementM:  m(s7SettlementA), // settlement — DM "Child Support (s.7 Payment)"
          ndiM:      m(ndiA),
          pct:       total > 0 ? (ndiA / total) * 100 : 0,
          s7Pct:     s7PctA,
          ssAfterTaxM: m(ssAfterTaxA),
        },
        b: {
          name: this.recipientName,
          grossM:    m(this.recipientIncome),
          taxesM:    m(taxB),
          benefitsM: m(benB),
          ssM:       m(ssB),
          csM:       m(csB),
          cfaM:      m(cfaB),
          s7M:       m(s7ShareB),
          s7PaidM:        m(s7PaidB),
          s7SettlementM:  m(s7SettlementB),
          ndiM:      m(ndiB),
          pct:       total > 0 ? (ndiB / total) * 100 : 0,
          s7Pct:     s7PctB,
          ssAfterTaxM: m(ssAfterTaxB),
        },
      }
    },
    // Add a new scenario column. Seeds the SS amount at the current Mid value
    // (or 0 if no SSAG range yet) and starts in override mode so the column
    // doesn't drift when inputs change.
    // SS amount that produces a 50/50 NDI split between the parties.
    // Used by the "Equalize NDI" button next to the SSAG amount slider.
    // Returns null when 50/50 is unreachable (recipient already ≥ 50% at SS=0,
    // or payor's income is too low to support that level of SS).
    setEqualizeNDIAmount() {
      const amt = this.findSs5050()
      if (amt != null && amt >= 0) {
        this.ssagSelected = amt
      }
    },

    // ── Restructuring math (SSAG Ch. 10) ──────────────────────────────
    // Present-value factor for a stream of MONTHLY payments over `years` at
    // an annual discount rate, compounded monthly. Multiply a monthly payment
    // by this to get its present value. (DM uses ~1.81% real-return-bond.)
    _annuityPVFactor(years, annualRatePct) {
      const months = Math.round((years || 0) * 12)
      if (months <= 0) return 0
      const i = (annualRatePct || 0) / 100 / 12
      if (i === 0) return months
      return (1 - Math.pow(1 + i, -months)) / i
    },
    // After-tax lump-sum equivalent of a monthly periodic award over `years`.
    // Periodic spousal support is taxable to the recipient and deductible to
    // the payor; a lump sum is neither. So the fair lump is the midpoint of
    // the present value of the payor's after-tax COST and the recipient's
    // after-tax BENEFIT — exactly DM's "Spousal Support Lump Sum (NPV) →
    // Midpoint". ssAfterTaxM is the SS-only after-tax delta per party (payor
    // negative = cost, recipient positive = benefit).
    _restructureLumpValueOf(monthly, years) {
      if (!monthly || monthly <= 0) return 0
      const b = this.computeNDIBreakdown(monthly)
      const payorCost   = Math.abs(Math.min(b.a.ssAfterTaxM, b.b.ssAfterTaxM))
      const recipBenefit = Math.abs(Math.max(b.a.ssAfterTaxM, b.b.ssAfterTaxM))
      const factor = this._annuityPVFactor(years, this.restructureDiscountRate)
      return ((payorCost + recipBenefit) / 2) * factor
    },

    // ═══════════════════════════════════════════════════════════════════════
    // Case scenarios — named snapshots of the full input set
    // ═══════════════════════════════════════════════════════════════════════
    // Used when parties disagree on income (impute vs actual), or when the
    // mediator wants to model multiple positions. Each scenario is a complete
    // input snapshot; switching scenarios reloads everything.

    // Capture the fields that define a scenario (everything a "what-if"
    // would override). Excludes meta (caseScenarios, activeCaseScenarioId,
    // viewMode, picker state, lastAppliedScenarioId).
    captureScenarioState() {
      return {
        // Identity / context
        taxYear: this.taxYear,
        provinceLocal: this.provinceLocal,
        payorName: this.payorName,
        recipientName: this.recipientName,
        cohabitationDate: this.cohabitationDate,
        separationDate: this.separationDate,
        payorDOB: this.payorDOB,
        recipientDOB: this.recipientDOB,
        payorDisabled: this.payorDisabled,
        recipientDisabled: this.recipientDisabled,
        // Inputs — deep-clone so saved snapshots don't share refs with live state
        incomeItems: JSON.parse(JSON.stringify(this.incomeItems || [])),
        children:    JSON.parse(JSON.stringify(this.children    || [])),
        cfaItems:    JSON.parse(JSON.stringify(this.cfaItems    || [])),
        // SSAG
        ssagApplicable: this.ssagApplicable,
        ssagPayor: this.ssagPayor,
        ssagSelected: this.ssagSelected,
        ssagSelectedDuration: this.ssagSelectedDuration,
        edcClaim: this.edcClaim,
        // Per-scenario amount/duration columns
        scenarios: JSON.parse(JSON.stringify(this.scenarios || [])),
        // Auto-payor choice
        payorManuallyChosen: this.payorManuallyChosen,
        // Needs tab budgets + advisor answers (tabbed workspace)
        budgetLines: JSON.parse(JSON.stringify(this.budgetLines || {})),
        budgetCustom: JSON.parse(JSON.stringify(this.budgetCustom || {})),
        advisorAnswers: JSON.parse(JSON.stringify(this.advisorAnswers || {})),
      }
    },

    // Apply a captured scenario state to the live calculator. Uses the
    // hydration guard so the watcher chain doesn't fire one update-state per
    // mutated field.
    applyScenarioState(snap) {
      if (!snap || typeof snap !== 'object') return
      this._isHydrating = true
      try {
        if (snap.taxYear != null)        this.taxYear = snap.taxYear
        if (snap.provinceLocal != null)  this.provinceLocal = snap.provinceLocal
        if (snap.payorName != null)      this.payorName = snap.payorName
        if (snap.recipientName != null)  this.recipientName = snap.recipientName
        if (snap.cohabitationDate != null) this.cohabitationDate = snap.cohabitationDate
        if (snap.separationDate != null) this.separationDate = snap.separationDate
        if (snap.payorDOB != null)       this.payorDOB = snap.payorDOB
        if (snap.recipientDOB != null)   this.recipientDOB = snap.recipientDOB
        if (snap.payorDisabled != null)     this.payorDisabled = !!snap.payorDisabled
        if (snap.recipientDisabled != null) this.recipientDisabled = !!snap.recipientDisabled
        if (Array.isArray(snap.incomeItems)) this.incomeItems = JSON.parse(JSON.stringify(snap.incomeItems))
        if (Array.isArray(snap.children))    this.children    = JSON.parse(JSON.stringify(snap.children))
        if (Array.isArray(snap.cfaItems))    this.cfaItems    = JSON.parse(JSON.stringify(snap.cfaItems))
        if (snap.ssagApplicable != null) this.ssagApplicable = !!snap.ssagApplicable
        if (snap.ssagPayor != null)      this.ssagPayor = snap.ssagPayor
        if (snap.ssagSelected != null)   this.ssagSelected = snap.ssagSelected
        if (snap.ssagSelectedDuration != null) this.ssagSelectedDuration = snap.ssagSelectedDuration
        if (snap.edcClaim !== undefined) this.edcClaim = snap.edcClaim
        if (Array.isArray(snap.scenarios)) this.scenarios = JSON.parse(JSON.stringify(snap.scenarios))
        if (snap.payorManuallyChosen !== undefined) this.payorManuallyChosen = !!snap.payorManuallyChosen
        // Budgets + advisor answers: merge saved entries onto FRESH structures
        // so snapshots taken before a schedule/question change still load
        // (unknown keys are dropped, missing keys stay blank).
        if (snap.budgetLines && typeof snap.budgetLines === 'object') {
          const fresh = makeBudgetLines()
          for (const k of Object.keys(fresh)) {
            if (snap.budgetLines[k]) fresh[k] = { ...fresh[k], ...snap.budgetLines[k] }
          }
          this.budgetLines = fresh
        }
        if (snap.budgetCustom && typeof snap.budgetCustom === 'object') {
          const fresh = makeBudgetCustom()
          for (const k of Object.keys(fresh)) {
            if (Array.isArray(snap.budgetCustom[k])) fresh[k] = JSON.parse(JSON.stringify(snap.budgetCustom[k]))
          }
          this.budgetCustom = fresh
        }
        if (snap.advisorAnswers && typeof snap.advisorAnswers === 'object') {
          const fresh = makeEmptyAnswers()
          for (const k of Object.keys(fresh)) {
            if (snap.advisorAnswers[k] !== undefined) fresh[k] = snap.advisorAnswers[k]
          }
          this.advisorAnswers = fresh
        }
      } finally {
        this.$nextTick(() => {
          this._isHydrating = false
          // Re-emit ONCE after hydration so the parent persists the freshly
          // loaded scenario state (plus the new activeCaseScenarioId).
          this.emitStateUpdate()
        })
      }
    },

    // Generate a unique id for a new scenario. Combines time + random.
    newScenarioId() {
      return 'scen-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 7)
    },

    // Open the naming prompt for "Save as new..."
    openSaveNewScenarioPrompt() {
      this.scenarioPromptMode = 'new'
      this.scenarioPromptName = this.suggestNextScenarioName()
      this.scenarioPromptTargetId = null
      this.scenarioPromptOpen = true
    },

    // Open the naming prompt to rename a specific scenario
    openRenameScenarioPrompt(id) {
      const s = this.caseScenarios.find(x => x.id === id)
      if (!s) return
      this.scenarioPromptMode = 'rename'
      this.scenarioPromptName = s.name
      this.scenarioPromptTargetId = id
      this.scenarioPromptOpen = true
    },

    closeScenarioPrompt() {
      this.scenarioPromptOpen = false
      this.scenarioPromptName = ''
      this.scenarioPromptTargetId = null
    },

    // Suggest a reasonable default name based on the parties + count.
    suggestNextScenarioName() {
      const base = (this.payorName && this.recipientName)
        ? `${this.payorName} & ${this.recipientName}`
        : 'Scenario'
      const n = this.caseScenarios.length + 1
      return `${base} — Scenario ${n}`
    },

    // Confirm action from the prompt (Save / Rename).
    submitScenarioPrompt() {
      const name = (this.scenarioPromptName || '').trim()
      if (!name) return
      if (this.scenarioPromptMode === 'rename') {
        const s = this.caseScenarios.find(x => x.id === this.scenarioPromptTargetId)
        if (s) {
          s.name = name
          s.updatedAt = new Date().toISOString()
        }
      } else {
        // Save as new — capture current state, push, activate
        const id = this.newScenarioId()
        const now = new Date().toISOString()
        this.caseScenarios.push({
          id,
          name,
          notes: '',
          createdAt: now,
          updatedAt: now,
          state: this.captureScenarioState(),
        })
        this.activeCaseScenarioId = id
      }
      this.closeScenarioPrompt()
    },

    // Load a saved scenario into the live calculator. Scenarios are frozen
    // snapshots — loading REPLACES the live inputs. If the currently-active
    // scenario has unsaved edits, confirm before discarding them. Returns true
    // if the load happened, false if it was cancelled or a no-op (so the
    // <select> handler can snap the dropdown back on cancel).
    loadCaseScenario(id) {
      const s = this.caseScenarios.find(x => x.id === id)
      if (!s) return false
      if (id === this.activeCaseScenarioId) return false
      if (this.activeScenarioDirty) {
        const active = this.caseScenarios.find(x => x.id === this.activeCaseScenarioId)
        const ok = window.confirm(
          `You have unsaved changes to "${active ? active.name : 'the current scenario'}". ` +
          `Switch scenarios and discard them?`
        )
        if (!ok) return false
      }
      this.activeCaseScenarioId = id
      this.applyScenarioState(s.state)
      return true
    },

    // <select> @change handler — loads the chosen scenario, and if the load is
    // cancelled (unsaved-changes prompt declined) resets the dropdown to the
    // still-active scenario so the UI doesn't lie about what's loaded.
    onScenarioSelectChange(e) {
      const id = e.target.value
      if (!id) return
      const loaded = this.loadCaseScenario(id)
      if (!loaded) e.target.value = this.activeCaseScenarioId || ''
    },

    // Duplicate the current active scenario (or a specified one). The copy
    // becomes the active scenario so further edits diverge from the original.
    duplicateCaseScenario(id) {
      const sourceId = id || this.activeCaseScenarioId
      const src = this.caseScenarios.find(x => x.id === sourceId)
      if (!src) return
      const newId = this.newScenarioId()
      const now = new Date().toISOString()
      this.caseScenarios.push({
        id: newId,
        name: `${src.name} (copy)`,
        notes: src.notes || '',
        createdAt: now,
        updatedAt: now,
        state: JSON.parse(JSON.stringify(src.state)),
      })
      this.activeCaseScenarioId = newId
    },

    // Confirm-then-delete the active scenario (used by the toolbar button so
    // the lawyer doesn't lose a scenario with one stray click).
    confirmDeleteActiveScenario() {
      const id = this.activeCaseScenarioId
      if (!id) return
      const s = this.caseScenarios.find(x => x.id === id)
      if (!s) return
      const ok = window.confirm(`Delete "${s.name}"? This can't be undone.`)
      if (ok) this.deleteCaseScenario(id)
    },

    deleteCaseScenario(id) {
      const idx = this.caseScenarios.findIndex(x => x.id === id)
      if (idx < 0) return
      const wasActive = this.activeCaseScenarioId === id
      this.caseScenarios.splice(idx, 1)
      if (wasActive) {
        // Activate another scenario if available, else clear.
        this.activeCaseScenarioId = this.caseScenarios.length
          ? this.caseScenarios[Math.min(idx, this.caseScenarios.length - 1)].id
          : null
        if (this.activeCaseScenarioId) {
          this.applyScenarioState(
            this.caseScenarios.find(s => s.id === this.activeCaseScenarioId).state
          )
        }
      }
    },

    // Explicitly write the current live inputs back into the active scenario
    // ("Save changes" button). This is the ONLY path that mutates a saved
    // snapshot — edits never auto-save — so the user stays in control of when a
    // scenario is updated vs. forked ("Save as new…"). Emits so the new
    // snapshot persists.
    // ── Needs tab (budgets) helpers ─────────────────────────────────────
    budgetGroupSubtotal(groupKey, col) {
      let sum = 0
      const g = F8_BUDGET_GROUPS.find(x => x.key === groupKey)
      if (g) {
        for (const ln of g.lines) {
          const e = this.budgetLines[groupKey + '|' + ln]
          if (e) sum += (Number(e[col]) || 0) * budgetFreqFactor(e.freq)
        }
      }
      for (const c of this.budgetCustom[groupKey] || []) {
        sum += (Number(c[col]) || 0) * budgetFreqFactor(c.freq)
      }
      const auto = groupKey === 'compulsory' ? this.budgetAutoRows.compulsory
        : groupKey === 'supportOthers' ? this.budgetAutoRows.supportOthers : []
      for (const r of auto) sum += r[col] || 0
      return Math.round(sum)
    },
    // Converted-to-monthly echo for a budget entry. Returns a string like
    // "= $200/mo · $150/mo" when the row's frequency is not monthly and at
    // least one amount is entered; null otherwise (no echo needed).
    budgetEcho(entry) {
      if (!entry || entry.freq === 'mo') return null
      const f = budgetFreqFactor(entry.freq)
      const a = Number(entry.a) || 0
      const b = Number(entry.b) || 0
      if (!a && !b) return null
      const part = (v) => '$' + this.fmt(Math.round(v * f)) + '/mo'
      if (a && b) return '= ' + part(a) + ' · ' + part(b)
      return '= ' + part(a || b)
    },
    addBudgetCustomRow(groupKey) {
      if (!this.budgetCustom[groupKey]) return
      this.budgetCustom[groupKey].push({ id: newItemId(), label: '', a: null, b: null, freq: 'mo' })
    },
    removeBudgetCustomRow(groupKey, idx) {
      if (this.budgetCustom[groupKey]) this.budgetCustom[groupKey].splice(idx, 1)
    },
    saveActiveScenario() {
      if (!this.activeCaseScenarioId) return
      const s = this.caseScenarios.find(x => x.id === this.activeCaseScenarioId)
      if (!s) return
      s.state = this.captureScenarioState()
      s.updatedAt = new Date().toISOString()
      this.emitStateUpdate()
    },



    addScenario() {
      // Seed from the current slider values so a user dialing in "$3,200/mo
      // for 9 years" and clicking Add captures exactly that scenario. Falls
      // back to the SSAG mid-point only if the sliders haven't been touched.
      const seedSS = this.ssagSelected || (this.ssagRange ? this.ssagRange.mid : 0)
      const seedDur = this.ssagSelectedDuration || (this.ssagRange
        ? (this.ssagRange.durationLow + this.ssagRange.durationHigh) / 2
        : 0)
      this.scenarios.push({
        id: newItemId(),
        name: `$${this.fmt(seedSS)}/mo · ${Number(seedDur).toFixed(2).replace(/\.?0+$/, '')} yr`,
        bind: null,
        ssMonthly: seedSS,
        ssOverridden: true,
        durationYears: seedDur,
        durationOverridden: true,
      })
    },
    removeScenario(id) {
      this.scenarios = this.scenarios.filter(s => s.id !== id)
    },
    // Build the three default scenarios bound to live SSAG amount + duration.
    // Each tier tracks both the $ range AND the year range:
    //   Low  → ssagRange.low  /  durationLow  (years × 0.5)
    //   Mid  → ssagRange.mid  /  midpoint of durationLow & durationHigh
    //   High → ssagRange.high /  durationHigh (years × 1.0)
    makeDefaultScenarios() {
      return [
        { id: newItemId(), name: 'SSAG Low',  bind: 'low',  ssMonthly: 0, ssOverridden: false, durationYears: 0, durationOverridden: false },
        { id: newItemId(), name: 'SSAG Mid',  bind: 'mid',  ssMonthly: 0, ssOverridden: false, durationYears: 0, durationOverridden: false },
        { id: newItemId(), name: 'SSAG High', bind: 'high', ssMonthly: 0, ssOverridden: false, durationYears: 0, durationOverridden: false },
      ]
    },
    // Reverse who is payor vs. recipient. Touches every party-keyed field so
    // the swap is consistent across income items, children parenting, s.7
    // expense splits, and the SSAG payor selector.
    swapParties() {
      [this.payorName, this.recipientName] = [this.recipientName, this.payorName]
      ;[this.payorDOB, this.recipientDOB] = [this.recipientDOB, this.payorDOB]
      ;[this.payorDisabled, this.recipientDisabled] = [this.recipientDisabled, this.payorDisabled]
      this.incomeItems.forEach(it => { it.partyId = it.partyId === 'A' ? 'B' : 'A' })
      this.children.forEach(c => {
        if (c.parenting === 'primaryA') c.parenting = 'primaryB'
        else if (c.parenting === 'primaryB') c.parenting = 'primaryA'
        if (Array.isArray(c.s7expenses)) {
          c.s7expenses.forEach(exp => {
            const a = exp.paidByA || 0
            exp.paidByA = exp.paidByB || 0
            exp.paidByB = a
          })
        }
      })
      // Payor now sits in slot A (the payor slot) → SS payable by A. Set (don't
      // flip) so the SS-page "Payable by" toggle tracks the income-page payor.
      this.ssagPayor = 'A'
      // Orientation vs. builder party1/party2 is now reversed — toggle so the
      // sync engine re-orients incoming builder fields instead of offering the
      // swap back as a rename.
      this.builderOrientationSwapped = !this.builderOrientationSwapped
    },
    // Wipe all scenarios and re-seed the three defaults bound to live SSAG range.
    resetScenarios() {
      this.scenarios = this.makeDefaultScenarios()
    },
    // Lookup the source scenario object (the editable record in `this.scenarios`)
    // for a given displayedScenarios row id. Used by inline editors to mutate
    // the real state, since displayedScenarios returns derived/projected rows.
    sourceScenario(id) {
      return this.scenarios.find(s => s.id === id)
    },
    // Inline edit handlers — these flip ssOverridden = true the first time the
    // user changes the SS amount on a bound (Low/Mid/High) row, so it stops
    // tracking the range and becomes a fixed what-if from that point on.
    setScenarioName(id, name) {
      const s = this.sourceScenario(id)
      if (s) s.name = name
    },
    setScenarioSS(id, amount) {
      const s = this.sourceScenario(id)
      if (!s) return
      const num = Number(amount)
      s.ssMonthly = isNaN(num) ? 0 : Math.max(0, num)
      s.ssOverridden = true
    },
    setScenarioDuration(id, years) {
      const s = this.sourceScenario(id)
      if (!s) return
      const num = Number(years)
      s.durationYears = isNaN(num) ? 0 : Math.max(0, num)
      s.durationOverridden = true
    },
    // ─── Lump-sum spousal support (NPV of after-tax stream) ─────────────────
    // Divorcemate's methodology, summarized:
    //   • Discount rate 1.81% — yield on a Long-Term Government of Canada Real
    //     Return Bond, which is a real (inflation-adjusted) rate. Because it's
    //     real, we don't separately escalate the periodic payments for inflation.
    //   • Periodic payments and tax rates assumed constant over the duration.
    //   • NPV computed on the AFTER-TAX cost (payor) and AFTER-TAX benefit
    //     (recipient) of the periodic stream — since SS is deductible to the
    //     payor and taxable to the recipient.
    //   • Lump sum itself is non-deductible / non-taxable — so the NPV of the
    //     after-tax stream IS the lump sum amount (no further gross-up needed).
    //   • Not discounted for recipient's life expectancy — duration is taken
    //     at face value (the user-selected years).
    // Formula: PV = annualAfterTax × [1 − (1 + r)^(−N)] / r, where r = 0.0181.
    lumpSumValue(monthlyAfterTax, durationYears) {
      const r = 0.0181
      const N = Math.max(0, Number(durationYears) || 0)
      if (N === 0 || !monthlyAfterTax) return 0
      const annualAfterTax = monthlyAfterTax * 12
      const annuityFactor = (1 - Math.pow(1 + r, -N)) / r
      return Math.round(annualAfterTax * annuityFactor)
    },
    // Midpoint between the payor's after-tax lump-sum cost (absolute value)
    // and the recipient's after-tax lump-sum benefit. Because the two parties
    // generally have different marginal tax rates, the cost to the payor and
    // benefit to the recipient differ — the midpoint is a common
    // "split-the-difference" negotiation figure.
    // DIVII PATCH: the payor's after-tax lump-sum cost as a positive figure
    // (lumpSumValue returns it negative because it is a cost to the payor).
    lumpSumPayorCost(scenario) {
      if (!scenario || !scenario.b) return 0
      return Math.abs(this.lumpSumValue(scenario.b.a.ssAfterTaxM, scenario.durationYears))
    },
    lumpSumMidpoint(scenario) {
      const lumpA = this.lumpSumValue(scenario.b.a.ssAfterTaxM, scenario.durationYears)
      const lumpB = this.lumpSumValue(scenario.b.b.ssAfterTaxM, scenario.durationYears)
      return Math.round((Math.abs(lumpA) + Math.abs(lumpB)) / 2)
    },
    // ─── Life Insurance estimates (to secure support) ───────────────────────
    // Life insurance is typically maintained ON THE PAYOR'S LIFE for the
    // benefit of the RECIPIENT, securing the stream of support payments. We
    // therefore display $ values only on the recipient's column and zero on
    // the payor's column.
    //
    // Two components, NPV'd separately at 1.81% (matches Lump Sum methodology):
    //   1. Child Support — table amount over the years until the last child
    //      finishes high school (ageOfChildrenDuration). Zero if no kids.
    //   2. Spousal Support after-tax benefit to recipient — over the
    //      scenario's chosen duration.
    // Sum = Life Insurance Estimate (face value).
    isPayorPartyA(scenario) {
      // The payor in computeNDIBreakdown is always party A by construction
      // (the breakdown is computed with ssAfterTaxM signed accordingly:
      // negative for the SS payor, positive for the recipient). We use the
      // sign of party A's after-tax to identify who the payor is in this
      // scenario row — matches whatever the SSAG payor selector currently
      // points at.
      return scenario.b.a.ssAfterTaxM < 0 || (scenario.b.a.ssAfterTaxM === 0 && this.ssagPayor === 'A')
    },
    // Life insurance NPV for child support obligations — per party.
    //
    // Each party insures their OWN gross monthly CS obligation, NOT the
    // net set-off:
    //   • Shared parenting: party A's life insurance covers sharedTableA
    //     (their full table amount), party B's covers sharedTableB. The
    //     set-off is just a payment-netting convenience between living
    //     parents — when one dies, the survivor needs the gross amount
    //     to continue supporting the children.
    //   • Sole custody (kids primary with B): only party A has a CS
    //     obligation (csPayorToRecipient). Party B's life insurance for
    //     CS is $0.
    //   • Sole custody (kids primary with A): only party B has an
    //     obligation (csRecipientToPayor); party A's is $0.
    //   • Mixed: each party's gross is the sum of their shared-table
    //     amount + the table amount for any kids primary with the OTHER
    //     party.
    lifeInsuranceCSValueA(scenario) {
      return this._csNPVForGross(this.sharedTableA + this.csPayorToRecipient)
    },
    lifeInsuranceCSValueB(scenario) {
      return this._csNPVForGross(this.sharedTableB + this.csRecipientToPayor)
    },
    // Life insurance NPV for spousal support — shown in the PAYOR's column.
    // Life insurance for SS is held on the payor's life with the recipient
    // as beneficiary; if the payor dies, the recipient needs the NPV of
    // the after-tax SS stream they were going to receive. So the dollar
    // amount the payor must insure is computed from the RECIPIENT's
    // after-tax benefit (the positive ssAfterTaxM), and displayed in the
    // payor's column to read as "this is what you need to insure on your
    // own life."
    lifeInsuranceSSValueA(scenario) {
      // A's column populates when A is the SS payor (A's ssAfterTaxM < 0).
      // The NPV uses B's after-tax benefit — the value being secured.
      if (scenario.b.a.ssAfterTaxM >= 0) return 0
      return Math.abs(this.lumpSumValue(scenario.b.b.ssAfterTaxM, scenario.durationYears))
    },
    lifeInsuranceSSValueB(scenario) {
      if (scenario.b.b.ssAfterTaxM >= 0) return 0
      return Math.abs(this.lumpSumValue(scenario.b.a.ssAfterTaxM, scenario.durationYears))
    },
    // CS NPV for a given party's gross monthly CS obligation, discounted
    // over the CS duration (years until the last child finishes high
    // school). Returns 0 if no kids, no obligation, or zero duration.
    // Replaces _csNPV() — that helper used the net set-off, which
    // understated the life-insurance need in shared-parenting cases.
    _csNPVForGross(monthlyGross) {
      if (this.includedChildren.length === 0) return 0
      const csM = monthlyGross || 0
      const csYears = this.ageOfChildrenDuration
      if (csM === 0 || csYears === 0) return 0
      return Math.abs(this.lumpSumValue(csM, csYears))
    },
    // SVG path for a pie slice covering a given percentage (0-100) of a 100×100 circle,
    // starting at the top (12 o'clock) and sweeping clockwise. Returns '' for empty
    // or full slices (the underlying circle handles the 0%/100% cases).
    piePath(pct) {
      if (pct >= 99.99) return ''
      if (pct <= 0.01) return ''
      const angle = pct * 3.6
      const rad = (angle - 90) * Math.PI / 180
      const x = 50 + 48 * Math.cos(rad)
      const y = 50 + 48 * Math.sin(rad)
      const largeArc = pct > 50 ? 1 : 0
      return `M 50 50 L 50 2 A 48 48 0 ${largeArc} 1 ${x.toFixed(2)} ${y.toFixed(2)} Z`
    },

    // ── Date / table ──────────────────────────────────────────────
    onSepDateChange() {
      // tableYear computed automatically
    },

    // ── Children ──────────────────────────────────────────────────
    childAge(dob) {
      if (!dob) return null
      const today = new Date()
      const birth = new Date(dob)
      let age = today.getFullYear() - birth.getFullYear()
      const m = today.getMonth() - birth.getMonth()
      if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--
      return age
    },
    isAtAOM(dob) {
      // AOM is determined as of the CALCULATION YEAR (tax-year end), not the
      // separation date. Support is payable only while the child is a child
      // of the marriage NOW: a child who was 17 at separation but is 20 in
      // the calculation year is presumptively no longer entitled (unless
      // confirmed dependent under s.3(2) via the wizard question, which this
      // gate now correctly surfaces). Matches DM, which ages children to the
      // calculation date (angus fixture investigation), and the benefits
      // engine, which already ages children to the benefit year. The SSAG
      // DURATION tests are separate computeds and correctly keep using
      // age-at-separation.
      const ref = `${this.taxYear || new Date().getFullYear()}-12-31`
      const age = this.ageAtDate(dob, ref)
      return age !== null && age >= this.aom
    },
    // Federal Eligible Dependant Credit (line 30400) applies to a child only
    // while UNDER 18 (or 18+ with an impairment, which we don't model). This
    // is a different — and lower — threshold than provincial age of majority
    // (19 in BC), which governs support eligibility: an 18-year-old can still
    // have support payable but no longer attract the EDC.
    // DIVII PATCH: ITA s. 118(1)(b) requires the child to be under 18 at ANY
    // time in the year, so the test is the age on January 1 of the tax year
    // (a child turning 18 during the year still qualifies for that year).
    // Upstream tested the age at December 31, which dropped that child a year
    // early. Divii's builder and agreement text apply the same rule.
    edcAgeEligible(child) {
      if (!child || !child.dob) return false
      const age = this.ageAtDate(child.dob, `${this.taxYear}-01-01`)
      return age !== null && age < 18
    },
    // Age in whole years at a given reference date. Returns null if either
    // date is missing or invalid. Used for SSAG Rule of 65 (recipient age at
    // separation) and for displaying party ages on the worksheet.
    ageAtDate(dobISO, refISO) {
      if (!dobISO || !refISO) return null
      // Parse YYYY-MM-DD strings directly. `new Date('YYYY-MM-DD')` parses
      // as UTC midnight, which in a -7/-8 timezone (PST/PDT) is the previous
      // calendar day in local time — getFullYear/getMonth/getDate would
      // return values off by one. Direct numeric parse avoids the shift.
      const dobMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dobISO)
      const refMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(refISO)
      if (!dobMatch || !refMatch) return null
      const [, dY, dM, dD] = dobMatch.map(Number)
      const [, rY, rM, rD] = refMatch.map(Number)
      let age = rY - dY
      if (rM < dM || (rM === dM && rD < dD)) age--
      return age >= 0 ? age : null
    },
    childParentingLabel(child) {
      if (child.dob && this.isAtAOM(child.dob)) {
        return child.isDependent ? 'Adult, dependent' : 'Adult, independent'
      }
      switch (child.parenting) {
        case 'primaryA': return `Primarily ${this.payorName}`
        case 'primaryB': return `Primarily ${this.recipientName}`
        case 'shared': return 'Shared'
        default: return '—'
      }
    },
    childIsIncluded(child) {
      if (!child.dob) return true // no DOB set, include by default
      if (!this.isAtAOM(child.dob)) return true
      return child.isDependent === true // explicitly confirmed dependent
    },
    addChild() {
      this.children.push({ name: '', dob: '', parenting: 'primaryB', isDependent: null, s7expenses: [], edcClaimedBy: null })
    },
    // Eligible Dependant Credit — CRA rule: each parent can claim ONE
    // eligible dependant per year. Setting child X to 'A' must clear any
    // prior child claimed by 'A' (same for 'B'). Values:
    //   null   — Auto (system picks based on parenting)
    //   'A'    — payor claims this child
    //   'B'    — recipient claims this child
    //   'none' — explicit override: nobody claims this child
    setEDCClaimedBy(child, value) {
      if (value === 'A' || value === 'B') {
        // CRA one-per-parent: clear this party's claim on any other child.
        for (const c of this.children) {
          if (c !== child && c.edcClaimedBy === value) c.edcClaimedBy = null
        }
      }
      child.edcClaimedBy = value
      // Auto-balance for shared custody: when one parent claims a shared child
      // and the other parent has no claim yet, assign the other parent to a
      // different eligible shared child (the typical "one each" split). Only
      // fills an Auto (null) child — never overrides an explicit 'none' or an
      // existing assignment.
      if ((value === 'A' || value === 'B') && child.parenting === 'shared') {
        const other = value === 'A' ? 'B' : 'A'
        const otherHasClaim = this.children.some(c => c.edcClaimedBy === other)
        if (!otherHasClaim) {
          const candidate = this.edcEligibleChildren.find(c =>
            c !== child && c.parenting === 'shared' && c.edcClaimedBy == null)
          if (candidate) candidate.edcClaimedBy = other
        }
      }
    },
    // DIVII PATCH: there are no "Auto" or "Neither" options. Give every eligible
    // shared child with no parent chosen the default claim from
    // edcResolvedClaims (youngest to the recipient, next to the payor, one per
    // parent), and drop a parent choice left on a child who is no longer shared
    // (the primary parent claims that child automatically). A 'none' saved when
    // "Neither" existed is treated as no choice. Runs from the children watcher
    // and after hydration; only fills blanks, never overrides a parent choice.
    materializeEDCClaims() {
      let changed = false
      this.children.forEach(c => {
        if (c.parenting !== 'shared' && (c.edcClaimedBy === 'A' || c.edcClaimedBy === 'B')) {
          c.edcClaimedBy = null
          changed = true
        }
        if (c.parenting === 'shared' && c.edcClaimedBy === 'none') {
          c.edcClaimedBy = null
          changed = true
        }
      })
      const eligible = this.edcEligibleChildren
      const resolved = this.edcResolvedClaims
      eligible.forEach((c, i) => {
        if (c.parenting === 'shared' && c.edcClaimedBy == null && resolved[i] && (resolved[i].claimedBy === 'A' || resolved[i].claimedBy === 'B')) {
          c.edcClaimedBy = resolved[i].claimedBy
          changed = true
        }
      })
      return changed
    },
    removeChild(i) {
      this.children.splice(i, 1)
    },

    // ── Section 7 ─────────────────────────────────────────────────
    addS7(child) {
      if (!child.s7expenses) child.s7expenses = []
      child.s7expenses.push({
        category: 'childcare',
        annualAmount: 0,    // legacy — derived from paidByA+paidByB if both are set
        subsidy: 0,
        paidByA: 0,         // cash paid by party A (payor slot)
        paidByB: 0,         // cash paid by party B (recipient slot)
        claimedByA: 0,      // tax deduction claimed by A on T1 (line 21400 for childcare)
        claimedByB: 0,      // tax deduction claimed by B on T1
      })
    },
    removeS7(child, i) {
      child.s7expenses.splice(i, 1)
    },

    // ── Formatting ────────────────────────────────────────────────
    fmt(n) {
      return Math.round(n || 0).toLocaleString('en-CA')
    },
  },
}
</script>

<style scoped>
/* Case-level controls bar (above wizard/workspace) */
.case-controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  margin-bottom: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.case-controls__field {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.case-controls__label {
  font-size: var(--fs-body);
  font-weight: 600;
  color: #3F6772;
}
.case-controls__select {
  font: inherit;
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #1e293b;
  cursor: pointer;
}
.case-controls__warning {
  flex: 1 1 100%;
  margin: 4px 0 0;
  padding: var(--pad-box);
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 6px;
  font-size: var(--fs-body);
  line-height: 1.4;
  color: #78350f;
}

/* ───────────────────────────────────────────────────────────────────────────
   DIVII SUPPORT CALCULATOR — visual style (Phase A)
   Aligned with the Divii app shell: dark teal/slate banners, teal-blue
   subheadings, gold accent reserved for highlights only. Inter typography.
   Calculator renders as a content panel; the parent app provides nav chrome.
   ─────────────────────────────────────────────────────────────────────────── */
.support-calc {
  /* Divii palette */
  --banner-bg:      #2d4a55;
  --banner-text:    #ffffff;
  --heading:        #3F6772;
  --heading-strong: #2d4a55;
  --text:           #1e293b;
  --text-muted:     #64748b;
  --text-subtle:    #94a3b8;
  --bg-page:        #ffffff;
  --bg-card:        #f8fafc;
  --bg-row-alt:     #f1f5f9;
  --bg-highlight:   #d6e6ee;
  --accent-gold:    #e8b340;
  --accent-gold-bg: #fdf4dc;
  --accent-teal:    #4a7285;
  --border:         #e2e8f0;
  --border-input:   #cbd5e1;
  --cta-bg:         #3F6772;
  --cta-bg-hover:   #2d4a55;
  --cta-text:       #ffffff;
  --warn-bg:        #fef3c7;
  --warn-text:      #92400e;
  --danger:         #dc2626;
  --focus-ring:     0 0 0 3px rgba(58, 100, 112, 0.18);

  /* Type scale — every screen font-size maps to one of these tokens.
     (The print worksheet keeps its own pt sizes.) */
  --fs-micro:   0.65rem;  /* chips, badges */
  --fs-tiny:    0.72rem;  /* overlines, column heads, tag labels */
  --fs-small:   0.8rem;   /* hints, secondary text, dense tables */
  --fs-body:    0.875rem; /* standard UI text */
  --fs-lead:    0.95rem;  /* totals, emphasized rows */
  --fs-title:   1.05rem;  /* panel/subsection titles */
  --fs-h2:      1.15rem;  /* section banner headings */
  --fs-display: 1.25rem;  /* big result numbers */

  /* Spacing scale — structural surfaces share these insets so panels,
     banners, callouts, and group heads feel like one system. */
  --pad-card:   1rem 1.25rem;    /* card/panel interior */
  --pad-banner: 0.85rem 1.25rem; /* dark section banners */
  --pad-box:    0.6rem 0.8rem;   /* callouts: hints, notes, warnings */
  --pad-head:   0.45rem 0.75rem; /* group heads, collapsible summaries */
  --gap-stack:  1rem;            /* vertical gap between sibling blocks */

  font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: 15px;
  line-height: 1.5;
  color: var(--text);
  padding: 0;
  margin: 0;
  position: relative;
  /* This is a light-only design (palette calibrated in light, embedded in the
     light Divii app shell). Lock the scheme so OS/host "night mode" can't render
     native controls (radios, checkboxes, scrollbars, date pickers) dark or let a
     dark page canvas show through transparent areas. Explicit background covers
     any transparent gaps within the calculator's own subtree. */
  color-scheme: light;
  background: var(--bg-page);
}

/* Feature title — names the tool at the top of the Divii panel */
.calc-title h1 {
  margin: 0 0 var(--gap-stack);
  font-size: var(--fs-display);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--heading-strong);
}

/* Section banner — matches Divii's dark teal section header pattern */
h2 {
  background: var(--banner-bg);
  color: var(--banner-text);
  font-size: var(--fs-h2);
  font-weight: 600;
  letter-spacing: 0.01em;
  margin: 0 0 var(--gap-stack) 0;
  padding: var(--pad-banner);
  border-radius: 8px 8px 0 0;
}
/* When step section already has a wrapping container, banner spans content edge-to-edge */
.step h2 {
  margin: -1rem -1.25rem 1rem -1.25rem;
  border-radius: 8px 8px 0 0;
}

h3 {
  font-size: var(--fs-title);
  font-weight: 600;
  margin: 1.25rem 0 0.5rem;
  color: var(--heading);
  letter-spacing: -0.005em;
}
.sub {
  color: var(--text-muted);
  font-size: var(--fs-body);
  margin: -0.5rem 0 1.25rem 0;
}
.sub-hint {
  display: block;
  margin-top: 0.35rem;
  padding: 0.4rem 0.6rem;
  background: var(--bg-page, #f8fafc);
  border-left: 3px solid var(--accent-teal, #3F6772);
  border-radius: 4px;
  font-size: var(--fs-small);
  color: var(--text-muted, #64748b);
}
.sub-hint code {
  background: rgba(63, 103, 114, 0.08);
  color: var(--accent-teal, #3F6772);
  padding: 0.05rem 0.3rem;
  border-radius: 3px;
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 0.85em;
  font-weight: 600;
}
.hint-tiny {
  color: var(--text-subtle, #94a3b8);
  font-weight: 400;
  font-size: var(--fs-tiny);
  margin-left: 0.3rem;
}

/* Step container — each wizard step is a "panel" */
.step {
  background: var(--bg-page);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: var(--pad-card);
  margin-bottom: var(--gap-stack);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
}

/* Fields */
.field { margin-bottom: 1rem; }
.field label {
  display: block;
  font-size: var(--fs-small);
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 0.35rem;
  letter-spacing: 0;
  text-transform: none;
}
.field input[type="text"],
.field input[type="date"],
.field input[type="number"],
.field select {
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--border-input);
  border-radius: 8px;
  font-size: var(--fs-lead);
  font-family: inherit;
  background: var(--bg-page);
  color: var(--text);
  transition: border-color 0.12s, box-shadow 0.12s;
  box-sizing: border-box;
}
.field input:focus,
.field select:focus {
  outline: none;
  border-color: var(--accent-teal);
  box-shadow: var(--focus-ring);
}
.input-prefix {
  display: flex;
  align-items: center;
  border: 1px solid var(--border-input);
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.input-prefix:focus-within {
  border-color: var(--accent-teal);
  box-shadow: var(--focus-ring);
}
.input-prefix span {
  padding: 0.55rem 0.6rem;
  background: var(--bg-card);
  color: var(--text-muted);
  font-size: var(--fs-lead);
  border-right: 1px solid var(--border);
}
.input-prefix input {
  border: none !important;
  box-shadow: none !important;
  flex: 1;
  background: transparent;
  /* Explicit text color: these inputs sit on white cards but, without this,
     the value inherited an ambient white and rendered invisible. */
  color: var(--text);
}

/* Rows */
.row-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
.swap-row {
  margin: -0.25rem 0 1rem;
  display: flex;
  justify-content: flex-end;
}
.swap-row .btn-ghost-sm {
  font-size: var(--fs-small);
  color: var(--accent-teal);
  padding: 0.25rem 0.55rem;
}
.row-3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 0.75rem; }

/* Radio rows */
.radio-row { display: flex; flex-wrap: wrap; gap: 1rem; margin-top: 0.3rem; }
.radio-row label {
  display: flex; align-items: center; gap: 0.4rem;
  font-size: var(--fs-lead); font-weight: 400; text-transform: none;
  letter-spacing: 0; cursor: pointer; color: var(--text);
}
.radio-row input[type="radio"] { accent-color: var(--accent-teal); }
.radio-row input[type="checkbox"] { accent-color: var(--accent-teal); }

/* Hint / warning */
.hint { font-size: var(--fs-small); color: var(--text-muted); margin-top: 0.25rem; }
.calc-notes { margin: var(--gap-stack) 0 0; padding: var(--pad-box); background: rgba(63,103,114,0.06); border-left: 3px solid #3F6772; border-radius: 6px; }
.calc-notes-head { font-size: var(--fs-tiny); font-weight: 700; color: var(--text-subtle, #6b7c83); text-transform: uppercase; letter-spacing: 0.04em; margin-bottom: 0.3rem; }
.calc-notes ul { margin: 0; padding-left: 1.1rem; }
.calc-notes li { font-size: var(--fs-small); color: var(--text-muted, #55666d); margin: 0.25rem 0; }
.disability-check {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 0.4rem;
  font-size: var(--fs-small);
  font-weight: 400;
  color: var(--text-muted);
  cursor: pointer;
}
.disability-check input { cursor: pointer; margin: 0; }
.hint.warn { color: var(--warn-text); }

/* Income parties */
.income-party {
  background: var(--bg-card);
  border-radius: 10px;
  padding: var(--pad-card);
  margin-bottom: 1rem;
  border: 1px solid var(--border);
}
.party-header {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}
.party-header h3 {
  margin: 0;
  color: var(--heading-strong);
  font-size: var(--fs-title);
}
.party-total {
  font-size: var(--fs-body);
  color: var(--text-muted);
}
.party-total strong {
  color: var(--text);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
}

.item-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
.income-row {
  display: grid;
  grid-template-columns: 1fr 170px auto;
  gap: 0.5rem;
  align-items: center;
  padding: 0.55rem 0.75rem;
  background: var(--bg-page);
  border: 1px solid var(--border);
  border-radius: 8px;
  transition: border-color 0.12s;
}
.income-row:hover { border-color: var(--border-input); }
.row-amount { min-width: 0; }
.row-amount > span { flex-shrink: 0; }
.row-amount input {
  min-width: 0;
  width: 100%;
  padding: 0.6rem 0.7rem;
  font-size: var(--fs-title);
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.row-label {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
.row-title {
  font-size: var(--fs-title);
  font-weight: 500;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.row-line {
  font-size: var(--fs-small);
  color: var(--text-subtle);
  font-variant-numeric: tabular-nums;
}
.empty-row {
  font-size: var(--fs-body);
  color: var(--text-subtle);
  padding: 0.5rem 0;
  font-style: italic;
}

.picker-wrap { position: relative; }
.btn-add-income {
  background: var(--bg-page);
  border: 1.5px dashed var(--border-input);
  color: var(--accent-teal);
  padding: 0.55rem 0.95rem;
  border-radius: 8px;
  cursor: pointer;
  font-size: var(--fs-lead);
  font-weight: 500;
  font-family: inherit;
  transition: all 0.12s;
}
.btn-add-income:hover {
  background: var(--bg-card);
  border-color: var(--accent-teal);
  color: var(--heading-strong);
}
.picker-popover {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 100;
  background: var(--bg-page);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  max-height: 380px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.picker-search {
  border: none !important;
  border-bottom: 1px solid var(--border) !important;
  border-radius: 0 !important;
  padding: 0.75rem 0.95rem !important;
  font-size: var(--fs-lead) !important;
  outline: none;
  background: var(--bg-page);
  color: var(--text);
}
.picker-results { overflow-y: auto; flex: 1; padding: 0.25rem 0; }
.picker-group { padding: 0.25rem 0; }
.picker-group-header {
  font-size: var(--fs-tiny);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-subtle);
  padding: 0.45rem 0.95rem 0.2rem;
}
.picker-item {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.55rem 0.95rem;
  font-size: var(--fs-lead);
  color: var(--text);
  font-family: inherit;
  text-align: left;
  transition: background 0.08s;
}
.picker-item:hover { background: var(--bg-row-alt); }
.picker-item-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.picker-item-line {
  font-size: var(--fs-tiny);
  color: var(--text-subtle);
  font-variant-numeric: tabular-nums;
  margin-left: 0.5rem;
}
.picker-empty {
  padding: 1rem 0.95rem;
  text-align: center;
  font-size: var(--fs-body);
  color: var(--text-subtle);
}

/* Child cards */
.child-card {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: var(--pad-card);
  margin-bottom: 1rem;
}
/* Shown in Step 3 when there are no children — signals the without-child SSAG
   formula will be used and that this section can be safely skipped. */
.empty-children-card {
  background: var(--bg-card);
  border: 1px dashed var(--border);
  border-radius: 10px;
  padding: var(--pad-card);
  margin-bottom: 1rem;
  color: var(--text-muted);
}
.empty-children-card p { margin: 0; }
.empty-children-card p + p { margin-top: 0.3rem; font-size: var(--fs-body); }
.child-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
  color: var(--heading-strong);
  margin-bottom: 0.75rem;
}
.child-age {
  font-size: var(--fs-body);
  color: var(--text-muted);
  margin-bottom: 0.75rem;
}
.tag-aom {
  background: var(--warn-bg);
  color: var(--warn-text);
  border-radius: 4px;
  padding: 0.1rem 0.5rem;
  font-size: var(--fs-small);
  margin-left: 0.5rem;
  font-weight: 500;
}

/* S7 */
.s7-child { margin-bottom: var(--gap-stack); }
.s7-expense {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: var(--pad-card);
  margin-bottom: 0.75rem;
}

/* ─── s.7 per-party Paid/Claimed input grid (DM-style) ──────────── */
.s7-input-grid {
  width: 100%;
  border-collapse: collapse;
  margin-top: 0.5rem;
}
.s7-input-grid thead th {
  text-align: center;
  font-size: var(--fs-small);
  font-weight: 600;
  color: var(--accent-teal, #3F6772);
  padding: 0.35rem 0.5rem;
  border-bottom: 1px solid var(--border);
}
.s7-input-grid thead th:first-child {
  text-align: left;
  width: 30%;
}
.s7-input-grid tbody th {
  text-align: left;
  font-weight: 500;
  color: var(--text, #1e293b);
  padding: 0.5rem 0.5rem 0.5rem 0;
  font-size: var(--fs-body);
}
.s7-input-grid tbody th .hint-tiny {
  display: block;
  margin-top: 0.1rem;
}
.s7-input-grid tbody td {
  padding: 0.35rem 0.4rem;
}
.s7-input-grid .input-prefix {
  display: flex;
  align-items: center;
}
.s7-input-grid .input-prefix span {
  padding: 0 0.4rem;
  color: var(--text-muted);
  font-weight: 500;
}
.s7-input-grid input {
  flex: 1;
  width: 100%;
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 0.35rem 0.5rem;
  font-family: inherit;
  font-size: var(--fs-body);
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.s7-input-grid input:focus {
  outline: none;
  border-color: var(--accent-teal);
  box-shadow: 0 0 0 2px rgba(63, 103, 114, 0.15);
}

/* Support Summary (wizard step 5) — denser than the other steps: it is a
   read-mostly recap, so section cards sit closer together with less padding. */
.step-summary .ssag-range {
  padding: 0.75rem 0.9rem;
  margin: 0.6rem 0;
}
.step-summary .ssag-range h3 { margin: 0 0 0.4rem; }
.step-summary .range-row { margin: 0.4rem 0; }
.step-summary .field { margin-bottom: 0.6rem; }
.step-summary h2 { margin-bottom: 0.85rem; }
.step-summary .bd-row { padding: 0.3rem 0; }
.step-summary .bd-total { margin-top: 0.4rem; padding-top: 0.4rem; }
.step-summary .adv-pointer { margin: 0.6rem 0 0; }
.step-summary .calc-notes { margin-top: 0.6rem; }

/* SSAG range */
.ssag-range {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-left: 3px solid var(--accent-gold);
  border-radius: 10px;
  padding: var(--pad-card);
  margin: var(--gap-stack) 0;
}
.range-row {
  display: flex;
  justify-content: space-between;
  margin: 0.75rem 0;
}
.range-cell { text-align: center; }
.range-cell.mid { font-size: 1.1em; }
.range-label {
  display: block;
  font-size: var(--fs-tiny);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  font-weight: 600;
}
.range-val {
  font-size: var(--fs-display);
  font-weight: 700;
  color: var(--heading-strong);
  font-variant-numeric: tabular-nums;
}
.duration-row { font-size: var(--fs-body); color: var(--text-muted); text-align: center; }

/* Results */
.results-block {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: var(--pad-card);
  margin-bottom: var(--gap-stack);
}
.cs-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border);
  font-size: var(--fs-lead);
}
.cs-row:last-of-type { border-bottom: none; }
.cs-row.shared { flex-direction: column; }

/* Duration-position indicator on the results page — visual mini-bar showing
   where the user's selected duration sits in the SSAG-recommended range. */
.dur-pos-label {
  font-weight: 500;
  color: var(--text-muted);
  font-size: var(--fs-small);
  margin-left: 0.3rem;
}
.dur-position-bar {
  position: relative;
  /* Tall enough to fit the marker (top half) and the Low/Mid/High labels
     (bottom half) without them visually overlapping. */
  height: 48px;
  margin: 0.4rem 0 0.5rem;
}
.dur-position-track {
  position: absolute;
  left: 0; right: 0;
  top: 12px;
  height: 6px;
  background: linear-gradient(to right,
    var(--accent-teal) 0%,
    var(--accent-gold) 100%);
  border-radius: 3px;
  opacity: 0.4;
}
.dur-position-marker {
  position: absolute;
  top: 6px;
  width: 14px;
  height: 18px;
  margin-left: -7px;
  background: var(--cta-bg);
  border: 2px solid var(--bg-page);
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.18);
}
.dur-position-labels {
  position: absolute;
  left: 0; right: 0;
  bottom: 2px;
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-tiny);
  color: var(--text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

/* Sliders embedded in a results-block — give each slider room to breathe so
   the value label, range input, and Low/High caption stack without colliding. */
.results-slider {
  margin: 0.85rem 0 0.5rem;
  padding: 0.5rem 0;
}
.results-slider + .results-slider {
  border-top: 1px dashed var(--border);
  padding-top: 1rem;
  margin-top: 0.6rem;
}
.results-slider .slider-label {
  display: block;
  font-size: var(--fs-body);
  color: var(--text-muted);
  margin-bottom: 0.4rem;
}
.results-slider input[type="range"] {
  width: 100%;
  margin: 0.25rem 0 0.35rem;
}
.results-slider .slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-tiny);
  color: var(--text-subtle);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.setoff-detail { font-size: var(--fs-body); color: var(--text-muted); margin: 0.35rem 0; }
.setoff-result { font-weight: 600; color: var(--heading-strong); margin-top: 0.25rem; }
.cs-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  margin-top: 0.5rem;
  border-top: 2px solid var(--border);
  font-weight: 600;
  color: var(--heading-strong);
  font-variant-numeric: tabular-nums;
}
.s7-apportionment {
  display: flex;
  gap: 2rem;
  font-size: var(--fs-body);
  color: var(--text-muted);
  margin-bottom: 0.75rem;
}
.s7-row {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-body);
  padding: 0.35rem 0;
  border-bottom: 1px solid var(--border);
}

/* ─── Workspace s.7 full-width section ─────────────────────────────
   Sits between the workspace-grid (Inputs | Breakdown) and the
   Scenarios table. Matches the visual treatment of those siblings —
   white card with teal-banner header — so the s.7 detail reads as
   another major section rather than a cramped breakdown item. */
.ws-s7-fullwidth {
  background: var(--bg-card, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  padding: var(--pad-card);
  margin-top: 1.25rem;
}
.ws-s7-fullwidth .s7-detail-head {
  margin-bottom: 0.75rem;
}
.ws-s7-fullwidth-title {
  margin: 0;
  font-size: var(--fs-title);
  font-weight: 700;
  color: var(--accent-teal, #3F6772);
}

/* ─── s.7 detail table (matches the PDF worksheet layout) ───────────
   Used in both the wizard Step 6 Results section and the workspace
   breakdown. Same column structure as the printed worksheet so the
   on-screen presentation reads identically. */
.s7-detail-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.5rem;
  gap: 1rem;
}
.s7-detail-head h3,
.s7-detail-head .breakdown-title {
  margin: 0;
}
.s7-detail-sub {
  font-size: var(--fs-small);
  color: var(--text-muted, #64748b);
}
.s7-detail-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-body);
  background: var(--bg-card, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 4px;
  overflow: hidden;
}
.s7-detail-table thead th {
  text-align: left;
  background: var(--bg-page, #f8fafc);
  color: var(--accent-teal, #3F6772);
  font-size: var(--fs-tiny);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 0.35rem 0.65rem;
  border-bottom: 1px solid var(--border, #e2e8f0);
}
.s7-detail-table thead th.num { text-align: right; }
.s7-detail-table tbody td {
  padding: 0.35rem 0.65rem;
  border-bottom: 1px solid var(--border, #f1f5f9);
  color: var(--text, #1e293b);
}
.s7-detail-table tbody td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}
.s7-detail-child { font-weight: 600; color: var(--heading-strong, #1e293b); }
.s7-detail-cat   { color: var(--text-muted, #64748b); }
.s7-detail-table tfoot tr {
  background: var(--bg-page, #f8fafc);
}
.s7-detail-table tfoot td {
  font-weight: 700;
  color: var(--heading-strong, #1e293b);
  border-bottom: none;
  border-top: 2px solid var(--accent-teal, #3F6772);
  padding: 0.45rem 0.65rem;
}
.s7-detail-table tfoot td.num { text-align: right; }
.s7-detail-mo {
  color: var(--text-muted, #64748b);
  font-weight: 500;
  font-size: var(--fs-small);
}
/* "Claimed" sub-cell — distinguished from "Paid" with a subtle
   left divider and muted colour, so the per-party pair reads as
   two values rather than four independent numbers. */
.s7-detail-table .s7-claimed {
  color: var(--text-muted, #64748b);
  border-left: 1px dotted var(--border, #e2e8f0);
}

/* Buttons */
.btn-primary {
  background: var(--cta-bg);
  color: var(--cta-text);
  border: none;
  border-radius: 8px;
  padding: 0.65rem 1.5rem;
  font-size: var(--fs-lead);
  font-family: inherit;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, transform 0.04s;
  letter-spacing: 0.01em;
}
.btn-primary:hover { background: var(--cta-bg-hover); }
.btn-primary:active { transform: translateY(1px); }
.btn-primary:disabled {
  background: var(--text-subtle);
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-ghost {
  background: transparent;
  color: var(--accent-teal);
  border: 1px solid var(--border-input);
  border-radius: 8px;
  padding: 0.65rem 1.25rem;
  font-size: var(--fs-lead);
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.12s;
}
.btn-ghost:hover {
  background: var(--bg-card);
  border-color: var(--accent-teal);
  color: var(--heading-strong);
}

.btn-ghost-sm {
  background: transparent;
  color: var(--accent-teal);
  border: 1px dashed var(--border-input);
  border-radius: 6px;
  padding: 0.35rem 0.85rem;
  font-size: var(--fs-small);
  cursor: pointer;
  margin-top: 0.5rem;
  font-family: inherit;
  font-weight: 500;
  transition: all 0.12s;
}
.btn-ghost-sm:hover {
  border-color: var(--accent-teal);
  background: var(--bg-card);
}

.btn-remove {
  background: none;
  border: none;
  color: var(--text-subtle);
  font-size: var(--fs-display);
  cursor: pointer;
  line-height: 1;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  transition: color 0.12s, background 0.12s;
}
.btn-remove:hover {
  color: var(--danger);
  background: rgba(220, 38, 38, 0.06);
}
.btn-remove-sm {
  background: none;
  border: none;
  color: var(--danger);
  font-size: var(--fs-small);
  cursor: pointer;
  margin-top: 0.5rem;
  text-decoration: underline;
  font-family: inherit;
}

/* Step nav */
.step-nav {
  display: flex;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--border);
}

/* Step dots */
.step-dots {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
}
/* Bottom breathing room for every wizard screen. Uses padding on the wrapper
   (not margin on .step-dots) — a trailing child margin collapses through the
   container and escapes, so it had no visible effect. */
.wizard-wrap {
  padding-bottom: 2rem;
}
.dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: var(--border-input);
  transition: all 0.2s;
}
.dot.active { background: var(--accent-gold); transform: scale(1.4); }
.dot.done { background: var(--accent-teal); cursor: pointer; }

/* Slider */
input[type="range"] {
  width: 100%;
  accent-color: var(--accent-teal);
}
.slider-labels {
  display: flex;
  justify-content: space-between;
  font-size: var(--fs-small);
  color: var(--text-subtle);
  margin-top: 0.25rem;
  font-variant-numeric: tabular-nums;
}
/* Below-the-slider helpers: precise amount input + Equalize NDI button.
   The amount input mirrors ssagSelected via v-model, so typing here moves
   the slider too. Equalize NDI sets ssagSelected to findSs5050(). */
.slider-extras {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.6rem 1rem;
  margin-top: 0.55rem;
  font-size: var(--fs-body);
}
.amount-input-label {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--text-subtle);
}
.amount-input-label .dollar-prefix {
  color: var(--text-subtle);
}
.amount-input-label .amount-suffix {
  color: var(--text-subtle);
  margin-left: 0.15rem;
}
.slider-extras .amount-input-label {
  /* Override .field label { display: block } so the label + input + suffix
     stay on one row when slider-extras is inside a .field wrapper (step 5). */
  display: inline-flex !important;
  font-size: var(--fs-body) !important;
  font-weight: normal !important;
  text-transform: none !important;
  color: var(--text-subtle) !important;
  margin-bottom: 0 !important;
}
.slider-extras .amount-input {
  /* Override .field input[type="number"] { width: 100% } */
  width: 6.5rem !important;
  padding: 0.25rem 0.4rem !important;
  border: 1px solid var(--border-input, #ccc);
  border-radius: 4px !important;
  font-size: var(--fs-lead) !important;
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.equalize-btn:not(:disabled) {
  cursor: pointer;
}
.equalize-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ── Restructuring (SSAG Ch. 10) — opt-in lump-sum + monthly panel ── */
/* Full-width section below the scenarios table (matches .ws-s7-fullwidth). */
.ws-restructure-fullwidth {
  background: var(--bg-card, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 8px;
  padding: var(--pad-card);
  margin-top: 1.25rem;
}
.restructure {
  margin-top: 0.75rem;
  border-top: 1px dashed var(--border-input, #d8dee6);
  padding-top: 0.6rem;
}
.restructure-toggle {
  background: none;
  border: none;
  padding: 0.15rem 0;
  font-size: var(--fs-small);
  font-weight: 600;
  color: var(--text-subtle, #475569);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}
/* Darkened brand gold (var(--accent-gold) #e8b340) for legible hover text. */
.restructure-toggle:hover { color: #b5821a; }
.restructure-caret { font-size: var(--fs-tiny); }
.restructure-panel {
  margin-top: 0.6rem;
  padding: var(--pad-box);
  background: var(--bg-subtle, #f8fafc);
  border: 1px solid var(--border-input, #e2e8f0);
  border-radius: 8px;
}
.restructure-intro {
  margin: 0 0 0.6rem;
  font-size: var(--fs-small);
  color: var(--text-subtle, #64748b);
  line-height: 1.45;
}
.restructure-empty {
  font-size: 0.8rem;
  font-style: italic;
  color: var(--text-subtle, #64748b);
  line-height: 1.45;
}
.restructure-inputs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  margin-bottom: 0.7rem;
}
.restructure-field {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: var(--fs-small);
  color: var(--text-subtle, #64748b);
}
.restructure-field .amount-input {
  width: 6rem;
  padding: 0.25rem 0.4rem;
  border: 1px solid var(--border-input, #ccc);
  border-radius: 4px;
  font-size: var(--fs-body);
  font-variant-numeric: tabular-nums;
  text-align: right;
}
.restructure-select {
  padding: 0.25rem 0.4rem;
  border: 1px solid var(--border-input, #ccc);
  border-radius: 4px;
  font-size: var(--fs-body);
  font-family: inherit;
  background: #fff;
  color: var(--text-main, #1e293b);
}
.restructure-result-main {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 0.3rem;
}
.restructure-result-label {
  font-size: var(--fs-small);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-subtle, #64748b);
}
.restructure-result-val {
  font-size: var(--fs-h2);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.restructure-pkg {
  font-size: var(--fs-small);
  color: var(--text-main, #1e293b);
  margin-bottom: 0.5rem;
  line-height: 1.45;
}
.restructure-badge {
  font-size: var(--fs-small);
  padding: 0.4rem 0.55rem;
  border-radius: 6px;
  line-height: 1.4;
}
.restructure-badge.in  { background: #ecfdf5; color: #065f46; border: 1px solid #6ee7b7; }
.restructure-badge.out { background: #fffbeb; color: #92400e; border: 1px solid #fcd34d; }
.restructure-subnote {
  display: block;
  margin-top: 0.35rem;
  color: #475569;
  font-weight: 400;
}
.restructure-note {
  margin: 0.5rem 0 0;
  font-size: var(--fs-tiny);
  color: var(--text-subtle, #94a3b8);
  line-height: 1.45;
}

/* ───────────────────────────────────────────────────────────────────────────
   PHASE B — WORKSPACE MODE
   Single-page layout: inputs left, live breakdown right.
   ─────────────────────────────────────────────────────────────────────────── */
.workspace {
  background: var(--bg-page);
  padding-bottom: 2.5rem;
}

.workspace-header {
  background: var(--banner-bg);
  color: var(--banner-text);
  padding: var(--pad-banner);
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  position: sticky;
  top: 0;
  z-index: 50;
}
.workspace-title {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}
.workspace-case {
  font-size: var(--fs-title);
  font-weight: 600;
  letter-spacing: 0.005em;
}
.workspace-meta {
  font-size: var(--fs-small);
  opacity: 0.78;
  font-weight: 400;
}
/* Case scenarios panel — slim toolbar under the workspace header */
.case-scenarios-panel {
  background: var(--bg-page);
  border-bottom: 1px solid var(--border-input, #e4e4e4);
  padding: 0.6rem 1.2rem;
}
.case-scenarios-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  font-size: var(--fs-body);
}
/* Download-worksheet button lives at the end of the scenarios toolbar and
   pushes to the far right (own line when the row wraps). */
.scenario-download-btn {
  margin-left: auto;
  display: inline-block;
  text-decoration: none !important;
}
.case-scenarios-label {
  font-weight: 600;
  color: var(--text-subtle);
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-right: 0.25rem;
}
.case-scenarios-icon {
  font-size: 1.05em;
}
.case-scenarios-select {
  flex: 0 1 18rem;
  min-width: 12rem;
  padding: 0.3rem 0.5rem;
  border: 1px solid var(--border-input, #ccc);
  border-radius: 6px;
  font-size: var(--fs-body);
  background: white;
}
.case-scenarios-select:disabled {
  background: var(--bg-page);
  color: var(--text-subtle);
}
.case-scenarios-hint {
  margin: 0.45rem 0 0;
  font-size: var(--fs-small);
  color: var(--text-subtle);
}
/* Scenario name prompt — small centered modal */
.scenario-prompt-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.scenario-prompt-dialog {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  width: min(440px, 92vw);
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.25);
}
.scenario-prompt-title {
  margin: 0 0 1rem;
  font-size: var(--fs-title);
}
.scenario-prompt-label {
  display: block;
  font-size: var(--fs-body);
  font-weight: 600;
  color: var(--heading);
  margin-bottom: 0.3rem;
}
.scenario-prompt-input {
  width: 100%;
  padding: 0.6rem 0.75rem;
  border: 1px solid var(--border-input, #ccc);
  border-radius: 6px;
  font-size: var(--fs-lead);
  font-family: inherit;
  margin-top: 0.3rem;
  box-sizing: border-box;
}
.scenario-prompt-input:focus {
  outline: none;
  border-color: var(--accent-teal);
  box-shadow: var(--focus-ring);
}
.scenario-prompt-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1.2rem;
}

.workspace-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}
.workspace-actions .btn-ghost-sm {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.18);
  color: rgba(255, 255, 255, 0.92);
  border-style: solid;
}
.workspace-actions .btn-ghost-sm:hover {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
}

.workspace-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  gap: 1.25rem;
  background: var(--bg-page);
  border: 1px solid var(--border);
  border-top: none;
  border-radius: 0 0 8px 8px;
  padding: var(--pad-card);
}

/* Inputs column */
.workspace-inputs {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}

.ws-section {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 6px;
  /* No overflow: hidden — the income picker popover is position: absolute
     inside this container, and overflow: hidden was clipping the popover
     (and its search input) when it extended past the section's bottom. */
  font-size: var(--fs-body);
}
.ws-section[open] { background: var(--bg-page); }
.ws-section > summary {
  list-style: none;
  cursor: pointer;
  padding: var(--pad-head);
  font-size: var(--fs-small);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  background: var(--bg-card);
  /* Round the top corners to match the section's outer radius — needed since
     we removed overflow: hidden on .ws-section to allow the picker popover
     to render outside the section bounds. */
  border-radius: 6px 6px 0 0;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;
  position: relative;
}
.ws-section > summary::-webkit-details-marker { display: none; }
.ws-section > summary::before {
  content: "▸";
  position: absolute;
  left: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-subtle);
  transition: transform 0.15s;
  font-size: var(--fs-tiny);
}
.ws-section[open] > summary::before { transform: translateY(-50%) rotate(90deg); }
.ws-section[open] > summary { border-bottom: 1px solid var(--border); }
.ws-count {
  color: var(--text-subtle);
  font-weight: 500;
  margin-left: 0.4rem;
}
.ws-section-body { padding: 0.5rem 0.75rem; }
.ws-section-body > *:last-child { margin-bottom: 0; }

/* Statement-style summary rows (Parties & Case section) */
.ws-summary {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.ws-summary-row {
  display: flex;
  align-items: baseline;
  gap: 1.25rem;          /* was 0.5rem — extra breathing room between
                            the wider label column and its value */
  font-size: var(--fs-body);
  line-height: 1.4;
}
.ws-summary-label {
  flex-shrink: 0;
  width: 150px;          /* was 70px — needed for "Date of Cohabitation" */
  font-size: var(--fs-tiny);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-subtle);
  white-space: nowrap;   /* keep the whole label on one line */
}
.ws-summary-value {
  flex: 1;
  color: var(--text);
}
.ws-summary-value em {
  color: var(--text-muted);
  font-style: normal;
  font-size: var(--fs-small);
  font-weight: 500;
}
.ws-summary-aux {
  color: var(--text-muted);
  font-size: var(--fs-small);
  font-weight: 400;
}
/* Highlight rows that flag a SSAG rule fired (Rule of 65, indefinite, etc.) */
.ws-summary-row.warn-row {
  background: rgba(232, 179, 64, 0.12);
  border-radius: 4px;
  padding: 0.3rem 0.4rem;
  margin: 0 -0.4rem;
}
.ws-summary-row.warn-row .ws-summary-label {
  color: var(--accent-gold);
  width: auto;
  text-transform: none;
  letter-spacing: 0;
  font-size: var(--fs-small);
  margin-right: 0.5rem;
}
.ws-summary-row.warn-row .ws-summary-value {
  font-size: var(--fs-small);
  color: var(--text);
}
.ws-summary-edit {
  margin-top: 0.35rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--border);
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: space-between;
}
.ws-summary-edit .btn-ghost-sm {
  font-size: var(--fs-small);
  padding: 0.3rem 0.6rem;
  margin-top: 0;
}

/* Compact field overrides INSIDE workspace sections */
.ws-section-body .field { margin-bottom: 0.5rem; }
.ws-section-body .field label {
  font-size: var(--fs-tiny);
  font-weight: 600;
  margin-bottom: 0.2rem;
  color: var(--text-muted);
  text-transform: none;
  letter-spacing: 0;
}
.ws-section-body .field input[type="text"],
.ws-section-body .field input[type="date"],
.ws-section-body .field input[type="number"],
.ws-section-body .field select {
  padding: 0.4rem 0.55rem;
  font-size: var(--fs-body);
  border-radius: 6px;
}
.ws-section-body .row-2 { gap: 0.6rem; }
.ws-section-body .row-3 { gap: 0.5rem; }
.ws-static {
  display: inline-block;
  padding: 0.4rem 0.55rem;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: var(--fs-body);
  color: var(--text);
  width: 100%;
  box-sizing: border-box;
}

/* Inside workspace, income-party gets a lighter, denser wrapper */
/* ─── Workspace: ultra-compact income block (Divorcemate-style) ─── */
.workspace-inputs .income-party {
  background: transparent;
  margin-bottom: 0.6rem;
  padding: 0;
  border-radius: 0;
}
.workspace-inputs .income-party + .income-party {
  border-top: 1px dashed var(--border);
  padding-top: 0.5rem;
}
.workspace-inputs .income-party:last-child { margin-bottom: 0; }
.workspace-inputs .party-header {
  margin-bottom: 0.3rem;
  padding-bottom: 0.2rem;
}
.workspace-inputs .party-header h3 {
  font-size: var(--fs-small);
  font-weight: 600;
  color: var(--heading);
}
.workspace-inputs .party-total { font-size: var(--fs-small); }
.workspace-inputs .income-row {
  padding: 0.2rem 0;
  font-size: var(--fs-body);
  border: none;
  background: transparent;
  border-radius: 0;
  gap: 0.5rem;
}
.workspace-inputs .income-row:hover { background: rgba(58, 100, 112, 0.04); }
.workspace-inputs .income-row .row-title { font-size: var(--fs-body); }
.workspace-inputs .income-row .row-line { display: none; }
.workspace-inputs .row-amount input {
  padding: 0.25rem 0.45rem;
  font-size: var(--fs-body);
  width: 110px;
}
.workspace-inputs .income-row .btn-remove { opacity: 0; transition: opacity 0.12s; }
.workspace-inputs .income-row:hover .btn-remove { opacity: 1; }
.workspace-inputs .btn-add-income {
  padding: 0.25rem 0.5rem;
  font-size: var(--fs-small);
  border: none;
  background: transparent;
  color: var(--accent-teal);
  margin-top: 0.2rem;
}
.workspace-inputs .btn-add-income:hover {
  background: rgba(58, 100, 112, 0.06);
}

/* ─── Workspace: read-only children mini-table ─── */
.children-mini {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-small);
  margin-bottom: 0.4rem;
}
.children-mini th {
  text-align: left;
  font-size: var(--fs-tiny);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-subtle);
  padding: 0.2rem 0.4rem 0.3rem;
  border-bottom: 1px solid var(--border);
}
.children-mini td {
  padding: 0.3rem 0.4rem;
  color: var(--text);
  border-bottom: 1px dotted var(--border);
}
.children-mini tr:last-child td { border-bottom: none; }
.children-mini .tag-aom {
  font-size: var(--fs-micro);
  margin-left: 0.3rem;
  padding: 0.05rem 0.3rem;
}
.ws-edit-link {
  font-size: var(--fs-tiny);
  padding: 0.2rem 0.4rem;
  margin-top: 0.2rem;
  color: var(--accent-teal);
  border: none;
  background: transparent;
}
.ws-edit-link:hover { background: rgba(58, 100, 112, 0.06); }

/* ─── Workspace: SSAG section inline controls ─── */
.ws-inline-toggle {
  font-size: var(--fs-small);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
}
.ws-inline-select {
  padding: 0.2rem 0.4rem;
  font-size: var(--fs-small);
  border: 1px solid var(--border-input);
  border-radius: 4px;
  background: var(--bg-page);
}

/* Breakdown column */
.workspace-breakdown {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  min-width: 0;
}
.breakdown-block {
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: var(--pad-card);
}
.breakdown-block[open] { background: var(--bg-page); }
.breakdown-title {
  font-size: var(--fs-lead);
  font-weight: 600;
  color: var(--heading-strong);
  margin-bottom: 0.6rem;
  letter-spacing: 0;
}
.breakdown-title.clickable {
  cursor: pointer;
  user-select: none;
  list-style: none;
  display: flex;
  align-items: center;
  margin-bottom: 0;
}
.breakdown-title.clickable::-webkit-details-marker { display: none; }
.breakdown-title.clickable::before {
  content: "▸";
  display: inline-block;
  margin-right: 0.4rem;
  color: var(--text-subtle);
  transition: transform 0.15s;
}
details[open] > .breakdown-title.clickable::before { transform: rotate(90deg); }
details[open] > .breakdown-title.clickable {
  margin-bottom: 0.6rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid var(--border);
}
.breakdown-empty {
  color: var(--text-subtle);
  font-style: italic;
  font-size: var(--fs-body);
}
.bd-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.35rem 0;
  font-size: var(--fs-body);
  gap: 0.5rem;
}
.bd-row strong {
  color: var(--heading-strong);
  font-variant-numeric: tabular-nums;
}
.bd-row .range-label {
  display: inline-block;
  font-size: var(--fs-tiny);
  text-transform: uppercase;
  color: var(--text-muted);
  letter-spacing: 0.06em;
  font-weight: 600;
}
.bd-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.65rem;
  margin-top: 0.5rem;
  border-top: 2px solid var(--border);
  font-weight: 600;
  color: var(--heading-strong);
  font-variant-numeric: tabular-nums;
  font-size: var(--fs-lead);
}
.duration-row {
  color: var(--text-muted);
  font-size: var(--fs-small);
  margin-top: 0.5rem;
}
.indi-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-body);
  font-variant-numeric: tabular-nums;
}
.indi-table th,
.indi-table td {
  padding: 0.45rem 0.5rem;
  text-align: right;
  border-bottom: 1px solid var(--border);
}
.indi-table th:first-child,
.indi-table td:first-child {
  text-align: left;
  color: var(--text-muted);
  font-weight: 500;
}
.indi-table th { color: var(--heading); font-weight: 600; }
.indi-table tbody tr:last-child td { border-bottom: none; }
.bd-note {
  font-size: var(--fs-tiny);
  color: var(--text-subtle);
  margin-top: 0.5rem;
  margin-bottom: 0;
  line-height: 1.5;
  font-style: italic;
}

/* SSAG range display in workspace breakdown */
.ssag-range-row {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 0.5rem;
  margin: 0.5rem 0 0.75rem 0;
}
.ssag-range-row .range-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.5rem 0.25rem;
  border-radius: 6px;
  background: var(--bg-card);
  border: 1px solid var(--border);
}
.ssag-range-row .range-cell.mid {
  /* No special highlight — kept neutral to match Divorcemate's range box */
}
.ssag-range-row .range-label {
  font-size: var(--fs-micro);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  font-weight: 600;
  margin-bottom: 0.15rem;
}
.ssag-range-row .range-val {
  font-size: var(--fs-title);
  font-weight: 700;
  color: var(--heading-strong);
  font-variant-numeric: tabular-nums;
}

/* SS slider block within breakdown */
.slider-block {
  margin-top: 0.75rem;
  padding: var(--pad-box);
  background: var(--bg-card);
  border-radius: 8px;
  border: 1px solid var(--border);
}
.slider-label {
  display: block;
  font-size: var(--fs-body);
  color: var(--text-muted);
  margin-bottom: 0.5rem;
}
.slider-label strong {
  color: var(--heading-strong);
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

/* Scenario actions bar — sits above the scenarios table in workspace mode. */
.scenario-actions {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.6rem 0;
}
.scenario-actions .btn-primary {
  padding: 0.4rem 0.85rem;
  font-size: var(--fs-body);
}
.scenario-actions .btn-ghost-sm {
  font-size: var(--fs-small);
  color: var(--text-muted);
}

/* ───────────────────────────────────────────────────────────────────────
   Scenarios section — side-by-side comparison
   ─────────────────────────────────────────────────────────────────────── */
.scenarios {
  margin-top: 1.5rem;
  margin-bottom: var(--gap-stack);
  background: var(--bg-page);
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
  padding-bottom: 0.75rem;
}
/* Section divider rows inside the scenarios table — bold dark band that
   separates major groupings (Life Insurance, Spousal Support Lump Sum, etc.)
   from the rows above them. Spans the entire row width. */
.scenarios-table tr.section-divider td.section-divider-cell {
  background: var(--banner-bg);
  color: var(--banner-text);
  font-weight: 600;
  font-size: var(--fs-small);
  padding: 0.4rem 0.65rem;
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-top: 2px solid var(--banner-bg);
  border-bottom: none;
}
.lump-sum-note {
  margin: 0.75rem 1rem 0;
  padding: var(--pad-box);
  background: var(--bg-card);
  border-left: 3px solid var(--accent-teal);
  border-radius: 4px;
  font-size: var(--fs-small);
  line-height: 1.45;
  color: var(--text-muted);
}
.lump-sum-note strong { color: var(--text); }

/* ─── Apply to Agreement row ─────────────────────────────────────────
   Bottom row of each scenario table. One button per scenario column,
   styled prominently so it reads as the primary call-to-action.
   The "current" variant flips to a soft green to indicate which
   scenario is presently reflected in the LegalApp agreement form. */
.apply-row {
  border-top: 2px solid var(--border);
}
.apply-row-label {
  font-weight: 600;
  color: var(--text);
}
.apply-cell {
  padding: 0.65rem 0.4rem !important;
  text-align: center;
}
.apply-btn {
  width: 100%;
  padding: 0.45rem 0.5rem;
  font-size: var(--fs-small);
  font-weight: 600;
  font-family: inherit;
  background: var(--accent-teal, #3F6772);
  color: #fff;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  transition: background 120ms ease, transform 80ms ease;
  /* Let the label wrap to a second line in narrow scenario columns rather
     than clip ("→ Apply to Agreem…"). All three buttons share the same row,
     so they wrap consistently and stay the same height. */
  white-space: normal;
  line-height: 1.25;
}
.apply-btn:hover {
  background: #2d5560;
}
.apply-btn:active {
  transform: translateY(1px);
}
.apply-btn--current {
  background: #e6f4ea;
  color: #1a5d2b;
  border-color: #4f9e63;
}
.apply-btn--current:hover {
  background: #d6ecdb;
}
/* DIVII PATCH: disabled state (read-only roles) and the child-support-only button. */
.apply-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.apply-btn:disabled:hover { background: var(--accent-teal, #3F6772); transform: none; }
.apply-lump-basis { flex-direction: column; align-items: flex-start; gap: 0.35rem; }
.apply-lump-basis .apply-term-opt { display: block; }
.bd-record { margin-top: 0.6rem; }
.bd-record .apply-btn { width: 100%; }
.apply-btn-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-weight: 600;
}
/* Apply-to-Agreement confirm dialog — monthly vs lump-sum radio options. */
.apply-dialog-sub {
  margin: 0 0 1rem;
  font-size: var(--fs-small);
  color: var(--text-muted);
  line-height: 1.4;
}
.apply-option {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  padding: var(--pad-box);
  border: 1px solid var(--border-input);
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 0.6rem;
  transition: border-color 120ms, background 120ms;
}
.apply-option:hover { border-color: var(--accent-teal); }
.apply-option--active {
  border-color: var(--accent-teal);
  background: var(--bg-highlight);
}
.apply-option input[type="radio"] { margin-top: 0.15rem; flex-shrink: 0; }
.apply-option-body { display: flex; flex-direction: column; gap: 0.15rem; }
.apply-option-title { font-size: var(--fs-lead); font-weight: 600; color: var(--heading-strong); }
.apply-option-figure { font-size: var(--fs-body); color: var(--text); font-variant-numeric: tabular-nums; }
.apply-option-figure small { color: var(--text-muted); font-size: var(--fs-tiny); }

/* ─── Auto-payor indicator ──────────────────────────────────────────
   Compact direction line + Switch link, shown above the income pickers
   in wizard mode. When the auto-detector suggests a swap, the bar gets
   a soft amber accent and a small suggestion explanation. */
.payor-indicator {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
  padding: var(--pad-box);
  margin: 0.5rem 0 1rem;
  background: var(--bg-card, #f8fafc);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 6px;
  font-size: var(--fs-body);
}
.payor-indicator--suggested {
  background: #fff7e6;
  border-color: #f0c060;
}
.payor-indicator-text {
  color: var(--text, #1e293b);
}
.payor-indicator-text strong {
  color: var(--accent-teal, #3F6772);
}
.payor-indicator-suggestion {
  font-size: var(--fs-small);
  color: #7a5b00;
  flex-basis: 100%;
  margin-top: -0.1rem;
}
.payor-switch-link,
.payor-dismiss-link {
  background: transparent;
  border: 1px solid var(--border, #cbd5e1);
  color: var(--text, #1e293b);
  padding: 0.25rem 0.55rem;
  font-size: var(--fs-small);
  font-family: inherit;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  margin-left: auto;
}
.payor-switch-link:hover,
.payor-dismiss-link:hover {
  background: var(--bg, #fff);
  border-color: var(--accent-teal, #3F6772);
}
.payor-dismiss-link { margin-left: 0; }

/* ───────────────────────────────────────────────────────────────────────
   Builder→calculator sync banner — same amber "suggestion" palette as the
   auto-payor banner (.payor-indicator--suggested).
   ─────────────────────────────────────────────────────────────────────── */
.sync-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0 0 0.9rem;
  padding: 0.6rem 0.85rem;
  background: #fff7e6;
  border: 1px solid #f0c060;
  border-radius: 8px;
  font-size: 0.85rem;
}
.sync-banner-text { flex: 1; color: var(--text, #1e293b); line-height: 1.45; }
.sync-banner-text strong { color: #7a5b00; }
.sync-banner-list {
  margin: 0.35rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.8rem;
  color: var(--text-muted, #64748b);
}
.sync-banner-list li { margin: 0.1rem 0; }
.sync-banner-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}
.sync-apply-btn {
  padding: 0.3rem 0.75rem;
  font-size: 0.8rem;
}
.sync-dismiss-link {
  background: transparent;
  border: 1px solid var(--border, #cbd5e1);
  color: var(--text, #1e293b);
  padding: 0.25rem 0.55rem;
  font-size: 0.78rem;
  font-family: inherit;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
}
.sync-dismiss-link:hover { background: var(--bg, #fff); }

/* Workspace-mode version — same Switch link, inline with Parties row */
.ws-switch-link,
.ws-dismiss-link {
  background: transparent;
  border: 1px solid var(--border, #cbd5e1);
  color: var(--text-muted, #64748b);
  padding: 0.15rem 0.5rem;
  font-size: var(--fs-tiny);
  font-family: inherit;
  font-weight: 500;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 0.4rem;
}
.ws-switch-link:hover,
.ws-dismiss-link:hover {
  color: var(--accent-teal, #3F6772);
  border-color: var(--accent-teal, #3F6772);
}
.ws-summary-suggestion {
  color: #7a5b00;
  background: #fff7e6;
  padding: 0.3rem 0.55rem;
  border-radius: 4px;
  border: 1px solid #f0c060;
  font-size: var(--fs-small);
}
.scenarios-header {
  background: var(--banner-bg);
  color: var(--banner-text);
  padding: var(--pad-banner);
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
}
.scenarios-header h2 {
  margin: 0;
  padding: 0;
  background: transparent;
  font-size: var(--fs-title);
  border-radius: 0;
}
.scenarios-meta {
  font-size: var(--fs-small);
  opacity: 0.78;
  font-weight: 400;
}
/* Container query so all scenarios-tables size their scenario columns from the
   wrapper width — not from each table's own content. This makes the 3-scenario
   table fill the full window, and tables with fewer scenarios reuse that exact
   per-column width (with empty space + a spacer cell on the right). */
.scenarios-wrap { container-type: inline-size; }
.scenarios-table {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
  font-size: var(--fs-small);  /* was 0.85rem — overall tighten so labels fit */
  table-layout: fixed;
}
.scenarios-table + .scenarios-table {
  margin-top: 1rem;
  border-top: 2px dashed var(--border);
}
/* Column widths via colgroup: label fixed; sub-columns share remaining space evenly */
.scenarios-table col.col-label { width: 320px; }
/* Paint the label-column tint on the COLUMN, not the cells. A <col> background
   fills the entire column height; a cell background only paints its content box,
   so on tall rows (NDI-share pie, Duration input, Apply button) the cell tint
   stopped short and left white gaps. The col fills behind everything; total-row
   and pie-row cell backgrounds still paint over it. */
.scenarios-table col.col-label { background: var(--bg-card); }
/* Each scenario sub-column = (wrapper width − label) / 6, so 3 scenarios (= 6
   sub-columns) exactly fill the remaining width. Tables with 1 or 2 scenarios
   keep this same per-column width and let the spacer column take the slack. */
.scenarios-table col.col-scen { width: calc((100cqw - 320px) / 6); }
/* Vertical grid lines live on the COLUMNS (not the cells) so header and body
   lines always align. ONLY the scenario boundaries get a divider (col-scen-start
   = the party-A sub-column that begins a new scenario). We deliberately do NOT
   draw a line between the two party sub-columns within a scenario: merged rows
   (NDI-share pie, Duration, Apply-to-Agreement) use colspan=2 and span across
   that internal edge, which suppresses the line there and made it look broken /
   misaligned. Keeping lines only at scenario boundaries — positions no cell ever
   spans — guarantees they render identically on every row. Smith vs Jones is
   carried by the column headers, right-alignment, and the col-scen-alt tint. */
.scenarios-table col.col-scen-alt { background: rgba(63, 103, 114, 0.09); }
.scenarios-table col.col-scen-start { border-left: 2px solid var(--heading); }
/* Keep row labels on a single line. Combined with the smaller font and
   wider label column, the two longest labels — "After-tax Cost/Benefit
   of Spousal Support" and "CSG Special Expenses Apportioning %" — now
   fit comfortably without wrapping. */
.scenarios-table td.row-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.scenarios-table col.col-spacer { width: auto; }
/* The spacer cell is invisible but inherits the row's background, so banded
   rows like NDI extend their shading all the way to the right edge. */
.scenarios-table .spacer-cell {
  border: none;
  padding: 0;
  background: inherit;
}
/* Carry the NDI total-row shading through to its row-label and spacer cell */
.scenarios-table tbody tr.total-row td.row-label,
.scenarios-table tbody tr.total-row td.spacer-cell {
  background: var(--bg-row-alt);
}
.scenarios-table thead .scenario-header-row th {
  background: var(--bg-card);
  border-bottom: 1px solid var(--border);
  padding: 0.4rem 0.4rem;
}
.scenarios-table thead .scenario-party-row th {
  background: var(--bg-card);
  border-bottom: 2px solid var(--border);
  padding: 0.3rem 0.4rem;
  font-size: var(--fs-small);
  font-weight: 600;
  color: var(--heading);
  text-align: right;
}
.scenarios-table thead .scenario-party-row th.row-label-head {
  text-align: left;
  background: var(--bg-card);
}
.scenarios-table .row-label-head {
  width: 200px;
  background: var(--bg-card);
}
.scenarios-table .scenario-name-cell {
  text-align: center;
}
/* Vertical lines come from the colgroup (see col.col-scen rules) so header and
   body align; cells no longer draw their own left borders. */
.scenarios-table tbody td {
  padding: 0.3rem 0.4rem;
  border-bottom: 1px solid var(--border);
  text-align: right;
  color: var(--text);
}
/* Number cells stay single-line and ellipsed; the row-label column wraps. */
.scenarios-table tbody td:not(.row-label):not(.spacer-cell) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.scenarios-table thead th {
  overflow: hidden;
}
.scenarios-table tbody td.row-label {
  text-align: left;
  color: var(--text-muted);
  font-weight: 500;
  background: var(--bg-card);
  border-left: none;
  /* Wrap long labels (e.g. "CSG Special Expenses Apportioning %") across lines
     instead of ellipsing. Top-align so the label sits aligned with the first
     line of content in adjacent cells (especially helpful for the NDI Share
     pie row and the editable Spousal Support / Duration rows). */
  white-space: normal;
  line-height: 1.25;
  vertical-align: top;
}
.scenarios-table tbody td.neg {
  color: #b45309;
}
.scenarios-table tbody tr.total-row td {
  font-weight: 700;
  color: var(--heading-strong);
  background: var(--bg-row-alt);
  border-top: 2px solid var(--border);
  border-bottom: 2px solid var(--border);
}
.scenarios-table tbody tr.total-row td.row-label {
  background: var(--bg-row-alt);
}
.scenarios-table tbody tr:hover td:not(.row-label) {
  background: rgba(58, 100, 112, 0.04);
}

/* Scenario name editor in header */
.scenario-name-wrap {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: center;
}
.scenario-name-edit {
  background: transparent;
  border: 1px solid transparent;
  font: inherit;
  font-weight: 600;
  color: var(--heading-strong);
  padding: 0.3rem 0.5rem;
  border-radius: 4px;
  text-align: center;
  min-width: 0;
  flex: 1;
  max-width: 220px;
}
.scenario-name-edit:hover { border-color: var(--border); }
.scenario-name-edit:focus {
  outline: none;
  border-color: var(--accent-teal);
  background: var(--bg-page);
}
.scenarios-table .btn-remove {
  font-size: var(--fs-title);
  padding: 0.15rem 0.4rem;
}

/* ─── In-header SS amount + duration inputs ────────────────────────
   Sit below the scenario name. The body table then displays the
   resulting per-party direction (payor in parens, recipient
   positive) as read-only, making "who pays whom" unambiguous. */
.scen-inputs {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  margin-top: 0.3rem;
  padding: 0.25rem 0.4rem;
  background: var(--bg-page, #f8fafc);
  border-radius: 4px;
}
.scen-input {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: var(--fs-small);
  color: var(--text-muted, #64748b);
  cursor: text;
}
.scen-input-label {
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: var(--fs-micro);
  color: var(--text-muted, #94a3b8);
}
.scen-input-prefix,
.scen-input-suffix {
  color: var(--text-muted, #64748b);
  font-size: var(--fs-tiny);
}
.scen-input-amount,
.scen-input-dur {
  background: #fff;
  border: 1px solid var(--border, #cbd5e1);
  border-radius: 4px;
  font: inherit;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  color: var(--heading-strong, #1e293b);
  padding: 0.18rem 0.35rem;
  text-align: right;
  width: 5.5rem;
  -moz-appearance: textfield;
}
.scen-input-dur {
  width: 3.5rem;
}
.scen-input-amount::-webkit-outer-spin-button,
.scen-input-amount::-webkit-inner-spin-button,
.scen-input-dur::-webkit-outer-spin-button,
.scen-input-dur::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.scen-input-amount:focus,
.scen-input-dur:focus {
  outline: none;
  border-color: var(--accent-teal, #3F6772);
  box-shadow: 0 0 0 2px rgba(63, 103, 114, 0.15);
}
/* Subtle highlight for columns whose SS amount has been overridden, so the
   user can see at a glance which columns no longer track the live SSAG range. */
.scenarios-table .scenario-name-cell.is-overridden {
  background: var(--bg-card);
}
.scenarios-table .scenario-name-cell.is-overridden .scenario-name-edit {
  font-style: italic;
}

/* Spousal Support row — single editable input spanning both party sub-cols. */
.scenarios-table tr.ss-row td.ss-input-cell {
  padding: 0.45rem 0.5rem;
  text-align: center;
}
.ss-input-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
  border: 1px solid var(--border-input);
  border-radius: 6px;
  padding: 0.15rem 0.45rem;
  background: var(--bg-page);
  font-size: var(--fs-body);
}
.ss-input-wrap:focus-within {
  border-color: var(--accent-teal);
  box-shadow: var(--focus-ring);
}
.ss-input-wrap .ss-prefix,
.ss-input-wrap .ss-suffix {
  color: var(--text-muted);
  font-size: var(--fs-small);
}
.ss-input-wrap .ss-input {
  border: none;
  outline: none;
  background: transparent;
  text-align: right;
  font-family: inherit;
  font-size: var(--fs-body);
  color: var(--text);
  width: 80px;
  padding: 0.1rem 0;
}
/* Duration input is narrower than $ amount and hides the spinner controls
   so the cell stays as visually tidy as the SS input. */
.ss-input-wrap .ss-input.dur-input {
  width: 56px;
  -moz-appearance: textfield;
}
.ss-input-wrap .ss-input.dur-input::-webkit-outer-spin-button,
.ss-input-wrap .ss-input.dur-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
/* Tiny ∞ chip to flag indefinite-duration cases (marriage ≥ 20 years) */
.dur-indef-tag {
  font-size: var(--fs-tiny);
  font-weight: 700;
  color: var(--accent-gold);
  margin-left: 0.25rem;
}

/* NDI Share pie chart row — uniform shade across the whole row.

   Earlier attempts to paint the bg on each cell hit a Chrome quirk where a
   table cell's background paints only the intrinsic content area when the
   row is much taller than the cell. Pseudo-elements + box-shadow inset
   were both clipped to the cell's content box.

   Most reliable fix: paint the background on the <tr> element itself, and
   make every cell in the row transparent. Browsers paint <tr> backgrounds
   across the full row regardless of any single cell's content height. */
.scenarios-table tr.pie-row {
  background: var(--bg-row-alt);
}
.scenarios-table tr.pie-row td {
  padding: 0.85rem 0.5rem;
  vertical-align: top;
  background: transparent;
}
.scenarios-table tr.pie-row td.spacer-cell {
  padding: 0;
}
.scenarios-table tr.pie-row td.row-label {
  padding-top: 0.85rem;
}
.pie-cell {
  border-left: 1px solid var(--border);
}
.pie-wrap {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
}
.ndi-pie {
  width: 100%;
  max-width: 84px;
  aspect-ratio: 1;
  height: auto;
  display: block;
  margin: 0 auto;
}
.pie-labels {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  font-size: var(--fs-tiny);
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  text-align: left;
  white-space: nowrap;
}
.pie-label {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}
.pie-swatch {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 2px;
  flex-shrink: 0;
}

/* Wrap scenarios table for horizontal scroll on narrow screens */
.scenarios { overflow-x: auto; }

/* Single-column on narrow screens */
@media (max-width: 900px) {
  .workspace-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .row-2, .row-3 { grid-template-columns: 1fr; }
  .cs-row { flex-direction: column; gap: 0.25rem; }
  .workspace-header { flex-direction: column; align-items: flex-start; }
  .scenario-actions { flex-direction: column; align-items: stretch; }
}

/* ─── Download worksheet button (lives in scenarios header) ──────── */

/* ═══════════════════════════════════════════════════════════════════
   PRINTABLE WORKSHEET
   Hidden on screen; @media print swaps everything else out and reveals
   only `.print-worksheet`. Lawyer picks "Save as PDF" in the dialog.

   Divii brand palette used:
     --d-deep:  #243B42  (deepest teal — strong section headers)
     --d-teal:  #3F6772  (primary brand teal — body accents)
     --d-mid:   #678B95  (mid teal — secondary labels)
     --d-pale:  #9CB6BD  (pale teal — soft highlights)
     --d-orange:#F7A91B  (accent orange — recipient slice, dots)
     --d-cream: #F7F2ED  (cream — page background tone)
═══════════════════════════════════════════════════════════════════ */
.print-worksheet {
  display: none;
  --d-deep:   #243B42;
  --d-teal:   #3F6772;
  --d-mid:    #678B95;
  --d-pale:   #9CB6BD;
  --d-orange: #F7A91B;
  --d-cream:  #F7F2ED;
  --d-ink:    #1e293b;
  --d-muted:  #64748b;
  --d-line:   #e2e8f0;
  --d-line-soft: #f1f5f9;
}

/* Print rules + the cloned-host hide live in an unscoped <style>
   block at the bottom of this file — scoped CSS can't address body
   or the dynamically-appended .divii-print-host, both of which sit
   outside this component's scope-hash attribute. */

/* ─── Header strip ────────────────────────────────────────────────── */
.ws-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1.5rem;
  padding-bottom: 0.35rem;
  margin-bottom: 0.45rem;
  border-bottom: 2px solid var(--d-teal);
}
.ws-logo {
  height: 26px;
  width: auto;
  display: block;
}
.ws-header-meta {
  text-align: right;
  flex: 1;
}
.ws-case-name {
  font-size: 13pt;
  font-weight: 800;
  color: var(--d-deep);
  letter-spacing: -0.3px;
  line-height: 1.1;
}
.ws-doc-title {
  font-size: 8pt;
  font-weight: 600;
  color: var(--d-teal);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  margin-top: 0.15rem;
}
.ws-date {
  font-size: 7.5pt;
  color: var(--d-mid);
  margin-top: 0.1rem;
}

/* ─── Generic section ─────────────────────────────────────────────── */
.ws-section {
  margin-bottom: 0.4rem;
}
.ws-section--scenarios {
  margin-top: 0.45rem;
  /* No page-break-inside: avoid here either — let the table flow
     naturally. If it splits across pages on a busy worksheet,
     that's fine; rows are independent. The forced page break for
     additional scenario chunks (4+ scenarios) is handled by
     .worksheet-page--continuation in the print rules. */
}
.ws-section-title {
  font-size: 9pt;
  font-weight: 700;
  color: #fff;
  background: var(--d-deep);
  padding: 0.18rem 0.65rem;
  margin: 0 0 0.25rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 2px 2px 0 0;
}
.ws-section-subtitle {
  font-size: 8.5pt;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--d-pale);
  margin-left: 0.5rem;
}

/* ─── Matter facts grid ──────────────────────────────────────────── */
.ws-facts {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 0.2rem 1rem;
  padding: 0.15rem 0.4rem;
}
.ws-fact {
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  padding: 0.1rem 0;
}
.ws-fact-label {
  font-size: 7pt;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: var(--d-mid);
  font-weight: 600;
}
.ws-fact-value {
  font-size: 9pt;
  color: var(--d-ink);
}
.ws-fact-aux {
  color: var(--d-mid);
  font-size: 7.5pt;
  margin-left: 0.25rem;
}

/* ─── Income cards ───────────────────────────────────────────────── */
.ws-income-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
}
.ws-income-card {
  border: 1px solid var(--d-line);
  border-radius: 3px;
  overflow: hidden;
  background: #fff;
}
.ws-income-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.6rem;
  background: var(--d-cream);
  border-bottom: 1px solid var(--d-line);
}
.ws-income-name {
  font-size: 9.5pt;
  font-weight: 700;
  color: var(--d-deep);
}
.ws-income-role {
  font-size: 6.5pt;
  font-weight: 700;
  color: var(--d-teal);
  letter-spacing: 0.12em;
  background: #fff;
  border: 1px solid var(--d-pale);
  padding: 0.05rem 0.35rem;
  border-radius: 2px;
}
.ws-income-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 8.5pt;
}
.ws-income-table td {
  padding: 0.13rem 0.6rem;
  border-bottom: 1px solid var(--d-line-soft);
  color: var(--d-ink);
}
.ws-income-table td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  width: 35%;
}
.ws-income-table tr.empty td {
  color: var(--d-mid);
  font-style: italic;
  text-align: center;
  padding: 0.35rem;
}
.ws-income-table tfoot tr {
  background: var(--d-cream);
}
.ws-income-table tfoot td {
  border-bottom: none;
  border-top: 1.5px solid var(--d-pale);
  font-weight: 700;
  color: var(--d-deep);
  padding: 0.22rem 0.6rem;
}

/* ─── DM-style two-column top section ─────────────────────────────── */
.ws-page-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.55rem;
  margin-bottom: 0.55rem;
  /* No page-break-inside: avoid — fights with the same rule on
     .ws-section--scenarios and forces a blank page between them
     when both want to claim a fresh page. Natural flow is fine. */
}
.ws-input-col,
.ws-output-col {
  display: flex;
  flex-direction: column;
  gap: 0.28rem;
}
.ws-input-col .ws-section-title { margin-bottom: 0.22rem; }

/* Each party's compact input card on the left side */
.ws-input-party {
  border: 1px solid var(--d-line);
  border-radius: 3px;
  background: #fff;
  overflow: hidden;
}
.ws-input-party-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.14rem 0.5rem;
  background: var(--d-cream);
  border-bottom: 1px solid var(--d-line);
  gap: 0.5rem;
}
.ws-input-name {
  font-size: 9pt;
  font-weight: 700;
  color: var(--d-deep);
}
.ws-input-meta-line {
  font-size: 7pt;
  color: var(--d-mid);
}
.ws-input-income {
  width: 100%;
  border-collapse: collapse;
  font-size: 7.5pt;
}
.ws-input-income td {
  padding: 0.08rem 0.5rem;
  color: var(--d-ink);
  border-bottom: 1px solid var(--d-line-soft);
}
.ws-input-income td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  width: 40%;
}
.ws-input-income tr.empty td {
  text-align: center;
  color: var(--d-mid);
  font-style: italic;
  padding: 0.3rem;
}
.ws-input-income tfoot tr { background: var(--d-cream); }
.ws-input-income tfoot td {
  font-weight: 700;
  color: var(--d-deep);
  border-bottom: none;
  border-top: 1px solid var(--d-pale);
  padding: 0.14rem 0.5rem;
}
.ws-input-note {
  padding: 0.1rem 0.5rem;
  background: var(--d-cream);
  border-top: 1px solid var(--d-line);
  font-size: 6.5pt;
  color: var(--d-mid);
  font-style: italic;
}

/* Meta block — children, dates, options */
.ws-input-meta-block {
  border: 1px solid var(--d-line);
  border-radius: 3px;
  background: #fff;
  padding: 0.22rem 0.5rem;
  font-size: 7.5pt;
}

/* ─── s.7 Special Expenses detail block ───────────────────────────── */
.ws-s7-block {
  border: 1px solid var(--d-line);
  border-radius: 3px;
  background: #fff;
  overflow: hidden;
}
.ws-s7-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 0.1rem 0.4rem;
  background: var(--d-cream);
  border-bottom: 1px solid var(--d-line);
  gap: 0.4rem;
}
.ws-s7-head > span:first-child {
  font-size: 7.5pt;
  font-weight: 700;
  color: var(--d-deep);
}
.ws-s7-sub {
  font-size: 6.5pt;
  color: var(--d-mid);
}
.ws-s7-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 7pt;
}
.ws-s7-table thead th {
  text-align: left;
  padding: 0.08rem 0.4rem;
  font-size: 6pt;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--d-mid);
  border-bottom: 1px solid var(--d-line-soft);
}
.ws-s7-table thead th.num { text-align: right; }
.ws-s7-table tbody td {
  padding: 0.07rem 0.4rem;
  border-bottom: 1px solid var(--d-line-soft);
  color: var(--d-ink);
}
.ws-s7-table tbody td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}
.ws-s7-child { font-weight: 600; color: var(--d-deep); }
.ws-s7-cat   { color: var(--d-mid); }
.ws-s7-table tfoot tr {
  background: var(--d-cream);
}
.ws-s7-table tfoot td {
  border-bottom: none;
  border-top: 1px solid var(--d-pale);
  font-weight: 700;
  color: var(--d-deep);
  padding: 0.1rem 0.4rem;
}
.ws-s7-table tfoot td.num { text-align: right; }
.ws-s7-mo { color: var(--d-mid); font-weight: 500; font-size: 6pt; }
.ws-input-row {
  display: grid;
  grid-template-columns: 32% 1fr;
  gap: 0.35rem;
  padding: 0.1rem 0;
  border-bottom: 1px solid var(--d-line-soft);
  align-items: baseline;
}
.ws-input-row:last-child { border-bottom: none; }
.ws-input-label {
  font-size: 6.5pt;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--d-mid);
}
.ws-input-value {
  color: var(--d-ink);
  font-size: 7.5pt;
  line-height: 1.4;
}
.ws-input-aux {
  color: var(--d-mid);
  font-size: 7pt;
}

/* Right column — Cautions / CSG / SSAG mini-cards */
.ws-output-card {
  border: 1px solid var(--d-line);
  border-radius: 3px;
  overflow: hidden;
  background: #fff;
}
.ws-output-cautions {
  border-color: var(--d-orange);
}
.ws-output-card-head {
  background: var(--d-teal);
  color: #fff;
  padding: 0.14rem 0.5rem;
  font-size: 7pt;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.ws-output-cautions .ws-output-card-head {
  background: var(--d-orange);
  color: var(--d-deep);  /* dark teal on orange — better contrast than white */
}
.ws-cautions-list {
  margin: 0;
  padding: 0.2rem 0.5rem 0.2rem 1.1rem;
  font-size: 7pt;
  color: var(--d-ink);
  line-height: 1.35;
}
.ws-cautions-list li { margin-bottom: 0.1rem; }
.ws-cautions-list li:last-child { margin-bottom: 0; }
.ws-cautions-list strong { color: var(--d-deep); }

/* CSG / SSAG mini-table — three-column compact */
.ws-mini-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 7.5pt;
}
.ws-mini-table thead th {
  background: var(--d-cream);
  color: var(--d-teal);
  font-size: 6.5pt;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.12rem 0.5rem;
  text-align: right;
  border-bottom: 1px solid var(--d-pale);
}
.ws-mini-table thead th:first-child { text-align: left; }
.ws-mini-table tbody td {
  padding: 0.09rem 0.5rem;
  color: var(--d-ink);
  border-bottom: 1px solid var(--d-line-soft);
}
.ws-mini-table tbody td:first-child { color: var(--d-mid); }
.ws-mini-table tbody td.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
}
.ws-mini-table tr.ws-mini-total {
  background: var(--d-cream);
}
.ws-mini-total td {
  font-weight: 700;
  color: var(--d-deep);
  border-top: 1px solid var(--d-pale);
  border-bottom: none;
}

/* SSAG-specific bits */
.ws-ssag-meta {
  font-size: 6.8pt;
  padding: 0.12rem 0.5rem;
  background: #fff;
  border-bottom: 1px solid var(--d-line-soft);
  line-height: 1.35;
  color: var(--d-ink);
}
.ws-ssag-meta strong { color: var(--d-mid); font-weight: 500; }
.ws-ssag-formula {
  font-size: 7.5pt;
  font-weight: 700;
  color: var(--d-deep);
  text-align: center;
  padding: 0.13rem 0.5rem;
  background: var(--d-cream);
  border-bottom: 1px solid var(--d-pale);
}
.ws-ssag-range thead th { text-align: center; padding: 0.08rem 0.4rem; }
.ws-ssag-range tbody td { text-align: center !important; font-size: 9pt; font-weight: 700; color: var(--d-deep); padding: 0.22rem; }
.ws-ssag-note {
  font-size: 6.5pt;
  color: var(--d-ink);
  padding: 0.18rem 0.5rem;
  margin: 0;
  background: #fff;
  border-top: 1px solid var(--d-line-soft);
  line-height: 1.35;
}
.ws-ssag-note strong { color: var(--d-deep); }

/* ─── Children table ──────────────────────────────────────────────── */
.ws-children-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 8.5pt;
}
.ws-children-table thead th {
  background: var(--d-cream);
  text-align: left;
  padding: 0.2rem 0.6rem;
  font-weight: 600;
  font-size: 7pt;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--d-teal);
  border-bottom: 1px solid var(--d-pale);
}
.ws-children-table tbody td {
  padding: 0.18rem 0.6rem;
  border-bottom: 1px solid var(--d-line-soft);
  color: var(--d-ink);
}
.ws-child-name {
  font-weight: 600;
  color: var(--d-deep);
}

/* ─── Scenarios table ─────────────────────────────────────────────── */
.ws-scenarios-wrap {
  border: 1px solid var(--d-line);
  border-radius: 4px;
  overflow: hidden;
}
.ws-scenarios-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 7.5pt;
  background: #fff;
}
.ws-scenarios-table col.col-label { width: 36%; }
.ws-scenarios-table th,
.ws-scenarios-table td {
  padding: 0.1rem 0.45rem;
  border-bottom: 1px solid var(--d-line-soft);
}
/* Keep every row label on a single line — the column was widened
   to 36% (from 26%) so longer labels like "After-tax Cost / Benefit
   of Spousal Support" and "CSG Special Expenses Apportioning %"
   no longer wrap to two lines. */
.ws-scenarios-table .td-label {
  white-space: nowrap;
}

/* Scenario name header row (top) */
.row-scenario-name th {
  background: var(--d-teal);
  color: #fff;
  font-weight: 700;
  font-size: 8.5pt;
  text-align: center;
  letter-spacing: 0.03em;
  padding: 0.22rem 0.45rem;
}
.row-scenario-name th.th-scenario {
  border-left: 1px solid var(--d-deep);
}
.row-scenario-name th:first-child {
  background: var(--d-deep);
}

/* Party sub-header row */
.row-party-head th {
  background: var(--d-cream);
  color: var(--d-mid);
  font-weight: 600;
  font-size: 7.5pt;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  text-align: center;
  border-bottom: 1px solid var(--d-pale);
}

/* Body cells — label column left, numbers right-aligned */
.ws-scenarios-table tbody .td-label {
  font-weight: 500;
  color: var(--d-mid);
  font-size: 8.5pt;
}
.ws-scenarios-table tbody .num {
  text-align: right;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum';
  color: var(--d-ink);
}
/* Negatives stay in body ink — DM convention. Parens carry the
   meaning; the red coloring used elsewhere would compete with the
   accounting-style format on a printed page. */
.ws-scenarios-table .neg { color: var(--d-ink); }
.ws-unit { color: var(--d-mid); font-size: 7.5pt; margin-left: 1px; }
.ws-indef { color: var(--d-orange); margin-left: 0.3rem; font-weight: 700; }

/* "Monthly $" corner header — top-left cell of the scenarios table */
.ws-scenarios-table .th-monthly {
  font-style: italic;
  text-transform: none;
  letter-spacing: 0;
  color: var(--d-mid);
  font-size: 7.5pt;
}

/* Input rows (Spousal Support amount / Duration) — soft cream tint */
.row-input td { background: rgba(247, 169, 27, 0.06); }
.row-input .td-label { color: var(--d-deep); font-weight: 600; }
.row-input .td-input { font-weight: 700; color: var(--d-deep); }

/* Total rows (NDI, Lump Sum) — emphatic teal */
.row-total td {
  background: var(--d-cream);
  font-weight: 700;
}
.row-total .td-label { color: var(--d-deep); }
.row-total .num { color: var(--d-deep); }

/* Section break inside table — small label-only row, no body data */
.row-section-break .td-section-break {
  background: var(--d-deep);
  color: #fff;
  text-align: center;
  font-size: 6.5pt;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  font-weight: 600;
  padding: 0.1rem;
}

/* NDI share — pie row */
.row-pie .td-pie {
  background: #fff;
  padding: 0.28rem 0.4rem;
  border-bottom: 1px solid var(--d-line-soft);
}
.ws-pie-wrap {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  justify-content: center;
}
.ws-pie {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
}
.ws-pie-legend {
  font-size: 6.5pt;
  color: var(--d-ink);
  line-height: 1.35;
}
.ws-pie-legend div {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  white-space: nowrap;
}
.ws-pie-swatch {
  display: inline-block;
  width: 7px;
  height: 7px;
  border-radius: 1px;
  flex-shrink: 0;
}

/* ─── Footer (each page) ─────────────────────────────────────────── */
.ws-footer {
  margin-top: 0.35rem;
  padding-top: 0.22rem;
  border-top: 1px solid var(--d-line);
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 6pt;
  color: var(--d-mid);
  letter-spacing: 0.02em;
}
.ws-footer-right {
  font-weight: 600;
  color: var(--d-teal);
}
/* DIVII FIX — disabled controls must not show the click cursor. */
.support-calc :disabled,
.ro-fieldset:disabled label { cursor: not-allowed !important; }
</style>

<!-- ═══════════════════════════════════════════════════════════════════
     Unscoped styles — needed for the print worksheet because:
       1) body-level selectors (`body.divii-printing > *`) can't be
          scoped — body doesn't carry the component's data-v hash
       2) The cloned `.divii-print-host` is appended directly under
          <body>, outside this component's scope, so its CSS must
          also be unscoped to match
     Scoped CSS that targets elements WITHIN the worksheet still
     applies to the clone because cloneNode preserves data-v-* attrs.
═══════════════════════════════════════════════════════════════════ -->
<style>
/* On screen, the print-host is never visible. */
.divii-print-host {
  display: none;
}

@media print {
  /* When printWorksheet() runs, App-level chrome AND the live
     calculator UI are hidden via display:none; only the cloned
     worksheet inside .divii-print-host renders. */
  body.divii-printing > *:not(.divii-print-host) {
    display: none !important;
  }

  /* Reveal the host and its cloned worksheet. */
  .divii-print-host {
    display: block !important;
    width: 100%;
    background: #fff;
    color: #1e293b;
    font-family: 'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    font-size: 9.5pt;
    line-height: 1.45;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .divii-print-host .print-worksheet--active {
    display: block !important;
  }

  /* Re-declare the brand palette on the cloned worksheet so the
     scoped child styles inside it (.ws-header, .ws-section-title,
     etc.) still resolve their var() lookups. The original
     .print-worksheet declaration sets these but the clone lives
     outside that node, so the inheritance chain restarts here. */
  .divii-print-host .print-worksheet--active {
    --d-deep:   #243B42;
    --d-teal:   #3F6772;
    --d-mid:    #678B95;
    --d-pale:   #9CB6BD;
    --d-orange: #F7A91B;
    --d-cream:  #F7F2ED;
    --d-ink:    #1e293b;
    --d-muted:  #64748b;
    --d-line:   #e2e8f0;
    --d-line-soft: #f1f5f9;
  }

  @page {
    size: letter;
    margin: 0.4in 0.5in 0.4in 0.5in;
  }

  /* Pagination — break ONLY between worksheet pages. Earlier version
     had both page-break-after on every page AND page-break-before on
     continuations, which double-broke and produced a blank physical
     page between page 1 and page 2. Now only page-break-before on
     continuations does the work. */
  .divii-print-host .worksheet-page--continuation {
    page-break-before: always;
  }
}

/* ─── Workspace tabs (needs / means / circumstances / recommendation) ─── */
.ws-tabs { display: flex; flex-wrap: wrap; gap: 0.4rem; margin: 0.9rem 0 1rem; border-bottom: 2px solid var(--border, #d9e0e3); }
.ws-tab { display: flex; flex-direction: column; align-items: flex-start; gap: 0.05rem; background: none; border: none; border-bottom: 3px solid transparent; margin-bottom: -2px; font: inherit; padding: 0.5rem 0.9rem 0.45rem; cursor: pointer; color: var(--text-subtle, #6b7c83); }
.ws-tab:hover:not(:disabled) { color: #3F6772; }
.ws-tab.on { color: #3F6772; border-bottom-color: #3F6772; }
.ws-tab:disabled { opacity: 0.45; cursor: not-allowed; }
.ws-tab-name { font-weight: 700; font-size: var(--fs-lead); }
.ws-tab-num { display: inline-flex; align-items: center; justify-content: center; width: 1.15rem; height: 1.15rem; border-radius: 50%; background: rgba(63,103,114,0.12); color: #3F6772; font-size: var(--fs-micro); font-weight: 700; margin-right: 0.15rem; vertical-align: 1px; }
.adv-pointer { margin: var(--gap-stack) 0 0; padding: var(--pad-box); border: 1px solid var(--border, #d9e0e3); border-radius: 8px; background: rgba(63,103,114,0.05); font-size: var(--fs-small); color: var(--text, #24343a); }
.adv-pointer-link { background: none; border: none; padding: 0; font: inherit; font-weight: 700; color: #3F6772; text-decoration: underline; cursor: pointer; }
.ws-tab.on .ws-tab-num { background: #3F6772; color: #fff; }
.ws-tab-sub { font-size: var(--fs-tiny); }
.ws-tab-intro { font-size: var(--fs-body); color: var(--text-subtle, #6b7c83); max-width: 62rem; margin: 0 0 1rem; }
.ws-tab-empty { font-size: var(--fs-lead); color: var(--text-subtle, #6b7c83); font-style: italic; padding: 1.5rem 0; }

/* ─── Needs tab: F8-aligned budgets ─── */
.ws-needs { max-width: 62rem; }
.budget-colhead { display: grid; grid-template-columns: 1fr 7.5rem 7.5rem 8.5rem 2rem; gap: 0.5rem; align-items: center; padding: 0.3rem 0.6rem; font-size: var(--fs-tiny); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-subtle, #6b7c83); position: sticky; top: 0; background: var(--surface, #fbfdfe); z-index: 2; }
.budget-colhead-amt, .budget-colhead-freq { text-align: left; }
.budget-group { border: 1px solid var(--border, #d9e0e3); border-radius: 8px; margin-bottom: 0.7rem; background: var(--surface, #fbfdfe); overflow: hidden; }
.budget-group-head { display: flex; justify-content: space-between; align-items: baseline; padding: var(--pad-head); background: rgba(63,103,114,0.06); }
.budget-group-name { font-weight: 700; font-size: var(--fs-body); color: var(--text, #24343a); }
.budget-group-hint { font-weight: 400; font-size: var(--fs-small); color: var(--text-subtle, #6b7c83); }
.budget-group-sub { font-size: var(--fs-small); font-weight: 600; color: #3F6772; white-space: nowrap; }
.budget-row { display: grid; grid-template-columns: 1fr 7.5rem 7.5rem 8.5rem 2rem; gap: 0.5rem; align-items: center; padding: 0.22rem 0.6rem; }
.budget-row:nth-child(even) { background: rgba(63,103,114,0.025); }
.budget-line-label { font-size: var(--fs-small); }
.budget-amt { width: 100%; font: inherit; font-size: var(--fs-small); padding: 0.22rem 0.4rem; border: 1px solid var(--border, #d9e0e3); border-radius: 5px; }
.budget-freq { width: 100%; font: inherit; font-size: var(--fs-small); padding: 0.22rem 0.2rem; border: 1px solid var(--border, #d9e0e3); border-radius: 5px; background: #fff; }
.budget-custom-label { font: inherit; font-size: var(--fs-small); padding: 0.22rem 0.4rem; border: 1px solid var(--border, #d9e0e3); border-radius: 5px; }
.budget-remove { background: none; border: none; font: inherit; color: var(--text-subtle, #6b7c83); cursor: pointer; padding: 0 0.25rem; }
.budget-remove:hover { color: #b4633a; }
.budget-add { display: block; background: none; border: none; font: inherit; font-size: var(--fs-small); color: #3F6772; cursor: pointer; padding: 0.35rem 0.6rem 0.5rem; }
.budget-row-auto { background: rgba(63,103,114,0.045); }
.budget-cell-auto { font-size: var(--fs-small); font-weight: 600; color: var(--text, #24343a); }
.budget-cell-freq-auto { font-size: var(--fs-tiny); color: var(--text-subtle, #6b7c83); }
.budget-auto-chip { display: inline-block; font-size: var(--fs-micro); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #3F6772; background: rgba(63,103,114,0.12); border-radius: 4px; padding: 0.05rem 0.3rem; margin-left: 0.35rem; vertical-align: middle; }
.budget-empty-note { font-size: var(--fs-small); color: var(--text-subtle, #6b7c83); font-style: italic; }
.budget-echo { grid-column: 2 / 5; font-size: var(--fs-tiny); color: #3F6772; font-weight: 600; padding: 0 0.1rem 0.15rem; }

/* Narrow screens: compress the amount/frequency columns so the Frequency
   dropdown is never clipped off-screen (an annual amount left on the default
   "Monthly" counts 12x too high). */
@media (max-width: 760px) {
  .budget-colhead, .budget-row { grid-template-columns: minmax(5rem, 1fr) 5.2rem 5.2rem 5.6rem 1.2rem; gap: 0.3rem; }
  .budget-freq { font-size: var(--fs-tiny); padding: 0.22rem 0.05rem; }
  .budget-amt, .budget-custom-label { font-size: var(--fs-small); padding: 0.22rem 0.3rem; }
  .budget-line-label { font-size: var(--fs-small); }
  .budget-summary-row { grid-template-columns: 1fr 5.2rem 5.2rem; }
}

.budget-summary { border: 1px solid var(--border, #d9e0e3); border-radius: 8px; padding: var(--pad-box); margin-top: 1rem; background: var(--surface, #fbfdfe); }
.budget-summary-row { display: grid; grid-template-columns: 1fr 7.5rem 7.5rem; gap: 0.5rem; align-items: baseline; padding: 0.2rem 0; }
.budget-summary-label { font-weight: 700; font-size: var(--fs-body); }
.budget-summary-note { display: block; font-weight: 400; font-size: var(--fs-tiny); color: var(--text-subtle, #6b7c83); }
.budget-summary-amt { font-weight: 700; font-size: var(--fs-lead); color: #3F6772; }

.budget-vs-ndi, .rec-budget-strip { margin-top: 0.9rem; }
.budget-vs-head { font-size: var(--fs-small); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-subtle, #6b7c83); margin-bottom: 0.35rem; }
.budget-vs-table { border-collapse: collapse; font-size: var(--fs-small); }
.budget-vs-table th, .budget-vs-table td { padding: 0.28rem 0.7rem; text-align: right; border-bottom: 1px solid var(--border, #d9e0e3); }
.budget-vs-table th:first-child, .budget-vs-table td:first-child { text-align: left; }
.budget-vs-subhead th { font-size: var(--fs-tiny); color: var(--text-subtle, #6b7c83); }
.budget-vs-point { font-weight: 700; }
.budget-ok { color: #2f7d4f; font-weight: 600; }
.budget-short { color: #b4633a; font-weight: 600; }
.budget-vs-note { font-size: var(--fs-small); color: var(--text-subtle, #6b7c83); margin-top: 0.5rem; max-width: 52rem; }

.ws-circumstances, .ws-recommendation { max-width: 46rem; }
.rec-budget-strip { margin-bottom: 1rem; }

/* ─── Role access: read-only wrapper + banner ─── */
.ro-fieldset { border: none; padding: 0; margin: 0; min-width: 0; }
/* DIVII FIX — same colours as Divii's b-alert variant="primary". */
.ro-banner { margin: var(--gap-stack) 0 0; padding: var(--pad-box); border: 1px solid #cfd9dc; border-radius: 4px; background: #d9e1e3; font-size: var(--fs-body); color: #19292e; }
.ws-tab-mine .ws-tab-name { color: #8a5a1c; }
.ws-tab-mine.on { border-bottom-color: #8a5a1c; }

/* ─── Party mode (level-2 users) ─── */
.party-mode { max-width: 46rem; }
.party-mode-head h2 { margin: 0.4rem 0 0.3rem; }
.party-mode-sub { font-size: var(--fs-body); color: var(--text-subtle, #6b7c83); margin: 0 0 1.2rem; }
/* Party self-identification card */
.party-claim-card { display: flex; flex-direction: column; gap: 0.8rem; max-width: 26rem; background: var(--bg-card); border: 1px solid var(--border); border-radius: 10px; padding: var(--pad-card); }
.party-claim-field { display: flex; flex-direction: column; gap: 0.3rem; font-size: var(--fs-small); font-weight: 600; color: var(--heading); }
.party-claim-field select, .party-claim-field input { font: inherit; font-weight: 400; font-size: var(--fs-body); padding: 0.45rem 0.6rem; border: 1px solid var(--border-input); border-radius: 6px; background: #fff; color: var(--text); }
.party-claim-card .btn-primary { align-self: flex-start; }
.party-section { margin-bottom: 1.6rem; }
.party-section h3 { margin: 0 0 0.2rem; color: #3F6772; }
.party-hint { font-size: var(--fs-small); color: var(--text-subtle, #6b7c83); margin: 0 0 0.6rem; }
.party-done-note { font-size: var(--fs-body); color: var(--text-subtle, #6b7c83); font-style: italic; margin-top: 0.8rem; }
.budget-row-party { grid-template-columns: 1fr 7.5rem 8.5rem 2rem; }
.budget-echo-party { grid-column: 2 / 4; }
.budget-summary-party { grid-template-columns: 1fr 7.5rem; }
.self-reported-chip { display: inline-block; font-size: var(--fs-micro); font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: #8a5a1c; background: #fff3e0; border: 1px solid #f0d8b8; border-radius: 4px; padding: 0.05rem 0.3rem; margin-left: 0.4rem; vertical-align: middle; }

</style>
